/*
 * This legacy dashboard entrypoint intentionally keeps dormant page
 * components for backwards-compatible routes. Their warnings are scoped to
 * this file; feature modules remain strictly linted.
 */
/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-useless-escape */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Layout } from "./components/Layout";
import { TrendingUp, FolderCheck, FolderOpen, CalendarDays, List, Grid2X2, Clock, Mail, CheckCircle2, XCircle, FileText, ArrowRightLeft, Receipt, Landmark, Users, Table2, ScanLine, Sparkles, Search, Bell, PlusCircle, Camera, Tag, Building2, LogOut, LayoutDashboard, Zap, Shield, History, Car, UtensilsCrossed, BedDouble, Package, HardHat, Paperclip, AlertTriangle, Trash2, X, ChevronRight, MessageSquare, KeyRound, SlidersHorizontal, Eye, MoreVertical, ExternalLink } from "lucide-react";
import { getMockData, MOCK_USER } from "./mockData";
import ConciliacaoView from "./features/conciliacao/ConciliacaoView";
import { ACCEPT_EXTRATO, LEGENDA_FORMATOS_EXTRATO, mensagemErroImportacao } from "./lib/extratoFormatos";
import { EmpresaProvider, useEmpresa, lerUltimaEmpresa, temEmpresaGuardadaNaSessao } from "./context/EmpresaContext";
import { DATA_CHANGED_EVENT, notifyDataChanged } from "./lib/dataSync";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend as RLegend } from "recharts";
import './styles/tokens.css';
const IS_MOCK = process.env.REACT_APP_MOCK === "true";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ─── Gestão de sessão (JWT em sessionStorage) ────────────────────────────────
const SESSION_KEY = "accounting_agent_session";
const ADMIN_TOOLS_KEY = "hub_financeiro_admin_tools_v1";
const ADMIN_PAGES = new Set(["agente", "processamento-assistido", "utilizadores", "definicoes", "sistema", "manutencao"]);

const adminToolsVisiveis = (user) => {
  if (user?.role !== "admin") return false;
  try {
    const prefs = JSON.parse(localStorage.getItem(ADMIN_TOOLS_KEY) || "{}");
    return prefs[user?.email || ""] !== false;
  } catch { return true; }
};

const guardarAdminToolsVisiveis = (user, visivel) => {
  if (user?.role !== "admin") return;
  try {
    const prefs = JSON.parse(localStorage.getItem(ADMIN_TOOLS_KEY) || "{}");
    prefs[user?.email || ""] = !!visivel;
    localStorage.setItem(ADMIN_TOOLS_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new CustomEvent("admin-tools-changed", { detail: !!visivel }));
  } catch { /* preferência visual não deve bloquear a entrada */ }
};

// A sessão pode viver em sessionStorage (comportamento clássico: morre ao
// fechar o browser) ou, com "memorizar neste dispositivo", em localStorage —
// protegida por PIN/biometria no arranque (ver EcraPinLock).
const loadSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const saveSession = (s, lembrar = false) => {
  const raw = JSON.stringify({ ...s, persistente: !!lembrar });
  if (lembrar) {
    localStorage.setItem(SESSION_KEY, raw);
    sessionStorage.removeItem(SESSION_KEY);
  } else {
    sessionStorage.setItem(SESSION_KEY, raw);
  }
};
const atualizarTokenSessao = (token) => {
  const s = loadSession();
  if (s) saveSession({ ...s, token }, !!s.persistente);
};
const clearSession = () => {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_KEY);
};

// ─── PIN (desbloqueio rápido da sessão memorizada) ───────────────────────────
// O PIN não substitui a autenticação: apenas desbloqueia o token JWT guardado
// no dispositivo (mesmo modelo das apps bancárias — a credencial real é o
// token; o PIN protege contra alguém pegar no telemóvel).
const PIN_KEY = "hub_pin_v1"; // { salt, hash, email }

const _hashPin = async (pin, salt) => {
  const texto = `${salt}:${pin}`;
  if (window.crypto?.subtle) {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(texto));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
  }
  // Fallback para contextos sem crypto.subtle (http em dev) — nunca em produção HTTPS
  let h = 5381;
  for (let i = 0; i < texto.length; i++) h = ((h << 5) + h + texto.charCodeAt(i)) | 0;
  return `djb2_${h}`;
};

const pinConfigurado = () => {
  try { return !!JSON.parse(localStorage.getItem(PIN_KEY) || "null"); } catch { return false; }
};
const lerConfigPin = () => {
  try { return JSON.parse(localStorage.getItem(PIN_KEY) || "null"); } catch { return null; }
};
const guardarPin = async (pin, email = null) => {
  const salt = Array.from(crypto.getRandomValues(new Uint8Array(12))).map(b => b.toString(16).padStart(2, "0")).join("");
  // O email fica junto ao PIN: sobrevive ao logout (login seguinte do MESMO
  // utilizador reutiliza-o em vez de pedir configuração outra vez) e é
  // descartado se entrar outra pessoa neste dispositivo.
  localStorage.setItem(PIN_KEY, JSON.stringify({ salt, hash: await _hashPin(pin, salt), email }));
};
const verificarPin = async (pin) => {
  const cfg = lerConfigPin();
  if (!cfg) return false;
  return (await _hashPin(pin, cfg.salt)) === cfg.hash;
};
const limparPin = () => localStorage.removeItem(PIN_KEY);

// Wrapper de fetch que injecta o JWT da sessão actual.
// Em 401, limpa a sessão e recarrega — força volta ao login.
const apiFetch = (path, options = {}) => {
  const headers = { ...(options.headers || {}) };
  const session = loadSession();
  if (session?.token) {
    headers["Authorization"] = `Bearer ${session.token}`;
  }
  return fetch(`${API}${path}`, { ...options, headers }).then(r => {
    if (r.status === 401 && session?.token) {
      clearSession();
      window.location.reload();
    }
    notifyDataChanged(path, options, r);
    return r;
  });
};

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v ?? 0);
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString("pt-PT") : "—"; } catch { return d || "—"; } };

// NIF de recurso gravado pelo agente quando o Gemini não conseguiu identificar
// um NIF português válido para o emitente (ver PLACEHOLDER_NIF_DESCONHECIDO em
// src/services/ai_service.py) — mostra um aviso em vez de deixar passar em
// silêncio como se fosse um fornecedor real.
const PLACEHOLDER_NIF_DESCONHECIDO = "999999990";

// Nome amigável por tipo de grupo (ver grupos_documentos/tipo) — cai para
// "Grupo" genérico para qualquer tipo futuro que ainda não tenha rótulo próprio.
const GRUPO_TIPO_LABEL = {
  via_verde_mensal: "Via Verde",
  mesmo_emitente: "Conjunto",
  processamento_assistido: "Documento Composto",
};
const labelGrupo = (f) => `${GRUPO_TIPO_LABEL[f.grupo_tipo] || "Grupo"} (${f.grupo_tamanho || "?"})${f.grupo_valor_total != null ? ` — ${fmt(f.grupo_valor_total)}` : ""}`;

// Hook defensivo:
//   - data fica null se o pedido falhar (HTTP não-2xx, network, parse error)
//   - nunca atribui o body de erros (ex. {detail:"..."}) ao data
//   - opcional defaultValue para iniciar com [] em listas e evitar checks
function useApi(path, deps = [], defaultValue = null) {
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!path) return;
    if (IS_MOCK) {
      const d = getMockData(path);
      setData(d !== null ? d : defaultValue);
      setLoading(false);
      return Promise.resolve(d !== null ? d : defaultValue);
    }
    setLoading(true);
    setError(null);
    return apiFetch(path)
      .then(async r => {
        if (!r.ok) {
          let detail = `HTTP ${r.status}`;
          try { const j = await r.json(); detail = j.detail || detail; } catch {}
          setError(detail); setData(defaultValue); setLoading(false);
          return;
        }
        const d = await r.json();
        setData(d); setLoading(false);
      })
      .catch(e => {
        setError(e?.message || "Erro de rede"); setData(defaultValue); setLoading(false);
      });
  }, [path]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, ...deps]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!path) return undefined;
    const sync = () => load();
    window.addEventListener(DATA_CHANGED_EVENT, sync);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, sync);
  }, [path, load]);
  return { data, loading, error, refresh: load };
}

const C = {
  bg: "#F4F7F9", surface: "#ffffff", border: "#E4E7EC", borderDark: "#D0D5DD",
  text: "#101828", textMid: "#475467", textLight: "#98A2B3",
  // Azul-noite + teal: sóbrio, operacional e consistente em toda a aplicação.
  blue: "#0F766E", blueLight: "#E6F8F5", blueBorder: "#B9E9E1",
  green: "#157347", greenLight: "#EDF7F1", greenBorder: "#A3D9B7",
  orange: "#92640C", orangeLight: "#FEF8EC", orangeBorder: "#F5DFA0",
  red: "#C0302A", redLight: "#FEF0F0", redBorder: "#F5C0BF",
  yellow: "#92640C", yellowLight: "#FEF8EC", yellowBorder: "#F5DFA0",
  purple: "#5B21B6", purpleLight: "#F5F3FF", purpleBorder: "#DDD6FE",
  sidebar: "#101828", sidebarBorder: "rgba(255,255,255,.08)", sidebarActive: "rgba(20,184,166,.16)",
  sidebarText: "#CBD5E1", sidebarActiveText: "#5EEAD4",
};

// ─── Base components ──────────────────────────────────────────────────────────
const Card = ({ children, style = {}, p = "24px" }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: p, boxShadow: "0 1px 2px rgba(16,24,40,.03), 0 8px 24px rgba(16,24,40,.035)", ...style }}>
    {children}
  </div>
);

const Label = ({ children, style = {} }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: C.textLight, marginBottom: 12, ...style }}>
    {children}
  </div>
);

const PageHeading = ({ eyebrow = "Operação", title, description, action, style = {} }) => (
  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 18, marginBottom: 24, flexWrap: "wrap", ...style }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".13em", textTransform: "uppercase", color: C.blue, marginBottom: 7 }}>{eyebrow}</div>
      <h1 style={{ fontSize: 28, lineHeight: 1.1, fontWeight: 780, letterSpacing: "-.035em", color: C.text, margin: 0 }}>{title}</h1>
      {description && <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.5, margin: "7px 0 0", maxWidth: 760 }}>{description}</p>}
    </div>
    {action}
  </div>
);

const Pill = ({ color = "blue", children }) => {
  const map = {
    green:  [C.greenLight,  C.greenBorder,  C.green],
    orange: [C.orangeLight, C.orangeBorder, C.orange],
    red:    [C.redLight,    C.redBorder,    C.red],
    blue:   [C.blueLight,   C.blueBorder,   C.blue],
    yellow: [C.yellowLight, C.yellowBorder, C.yellow],
    purple: [C.purpleLight, C.purpleBorder, C.purple],
    gray:   ["#f1f5f9",     "#cbd5e1",      "#64748b"],
  };
  const [bg, border, color_] = map[color] || map.blue;
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, border: `1px solid ${border}`, color: color_, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
};

const Spinner = () => <div style={{ textAlign: "center", padding: 60, color: C.textLight, fontSize: 13 }}>A carregar...</div>;
const Empty = ({ msg = "Sem dados" }) => <div style={{ textAlign: "center", padding: 60, color: C.textLight, fontSize: 13 }}>{msg}</div>;

const Btn = ({ children, onClick, disabled, variant = "primary", size = "md", style = {} }) => {
  const v = {
    primary:   { bg: C.blue,        color: "#fff",    border: C.blue },
    secondary: { bg: C.surface,     color: C.text,    border: C.borderDark },
    success:   { bg: C.greenLight,  color: C.green,   border: C.greenBorder },
    danger:    { bg: C.redLight,    color: C.red,     border: C.redBorder },
    orange:    { bg: C.orangeLight, color: C.orange,  border: C.orangeBorder },
    yellow:    { bg: C.yellowLight, color: C.yellow,  border: C.yellowBorder },
  }[variant] || {};
  const pad = { sm: "5px 12px", md: "8px 18px", lg: "11px 24px" }[size];
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      padding: pad, borderRadius: 8, border: `1px solid ${v.border}`,
      background: disabled ? C.bg : v.bg, color: disabled ? C.textLight : v.color,
      fontSize: size === "sm" ? 12 : 13, fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
      transition: "background 0.12s, opacity 0.15s",
      ...style,
    }}>{children}</button>
  );
};

const THead = ({ cols }) => (
  <thead>
    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
      {cols.map(c => (
        <th key={c} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, whiteSpace: "nowrap", background: C.bg }}>
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

const TR = ({ children, onClick, highlight }) => {
  const [hov, setHov] = useState(false);
  return (
    <tr onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ borderBottom: `1px solid ${C.border}`, background: highlight ? C.yellowLight : hov ? "rgba(255,255,255,0.03)" : C.surface, cursor: onClick ? "pointer" : "default", transition: "background 0.1s" }}>
      {children}
    </tr>
  );
};

const TD = ({ children, mono, muted, style = {} }) => (
  <td style={{ padding: "12px 16px", fontSize: 13, color: muted ? C.textMid : C.text, fontFamily: mono ? "'Fira Code', monospace" : "inherit", whiteSpace: "nowrap", ...style }}>
    {children}
  </td>
);

// ─── Alert Banner ─────────────────────────────────────────────────────────────
function AlertBanner({ alerts }) {
  const [dismissed, setDismissed] = useState([]);
  const visible = (alerts || []).filter(a => !dismissed.includes(a.id));
  if (!visible.length) return null;

  const colorMap = { error: C.red, warn: C.orange, info: C.blue };
  const bgMap = { error: C.redLight, warn: C.orangeLight, info: C.blueLight };
  const borderMap = { error: C.redBorder, warn: C.orangeBorder, info: C.blueBorder };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
      {visible.map(a => (
        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 10, background: bgMap[a.type] || C.blueLight, border: `1px solid ${borderMap[a.type] || C.blueBorder}` }}>
          <span style={{ fontSize: 16 }}>{a.type === "error" ? "🚨" : a.type === "warn" ? "⚠️" : "ℹ️"}</span>
          <span style={{ flex: 1, fontSize: 13, color: colorMap[a.type] || C.blue, fontWeight: 600 }}>{a.msg}</span>
          <button onClick={() => setDismissed(p => [...p, a.id])} style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, fontSize: 16 }}>×</button>
        </div>
      ))}
    </div>
  );
}

function BannerSaude({ alertas }) {
  const problemas = (alertas || []).filter(a => a.type !== "info");
  const [aberto, setAberto] = useState(false);

  if (problemas.length === 0) {
    return (
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 20px", borderRadius:12,
        background: C.greenLight, border: `1px solid ${C.greenBorder}`, marginBottom:20 }}>
        <span style={{ fontSize:20 }}>✓</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.green }}>Tudo em ordem</div>
          <div style={{ fontSize:12, color:"#166534", marginTop:2 }}>Todos os documentos processados e sem anomalias.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ borderRadius:12, background:C.orangeLight, border:`1px solid ${C.orangeBorder}`, marginBottom:20, overflow:"hidden" }}>
      <button onClick={() => setAberto(a => !a)} style={{ width:"100%", display:"flex", alignItems:"center", gap:12,
        padding:"14px 20px", background:"none", border:"none", cursor:"pointer", textAlign:"left" }}>
        <span style={{ fontSize:20 }}>⚠️</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.orange }}>
            {problemas.length} {problemas.length === 1 ? "item requer" : "itens requerem"} a tua atenção
          </div>
        </div>
        <span style={{ fontSize:13, color:C.orange }}>{aberto ? "▲" : "▼"}</span>
      </button>
      {aberto && (
        <div style={{ padding:"0 20px 14px", borderTop:`1px solid ${C.orangeBorder}` }}>
          {problemas.map((a, i) => (
            <div key={i} style={{ fontSize:13, color:C.orange, padding:"6px 0",
              borderBottom: i < problemas.length-1 ? `1px solid ${C.orangeBorder}` : "none" }}>
              · {a.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Company Selector ─────────────────────────────────────────────────────────
function CompanySelector({ empresas, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const current = empresas?.find(e => e.nif === selected);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", background: C.bg, border: `1px solid ${C.borderDark}`, borderRadius: 10, cursor: "pointer", minWidth: 240 }}>
        <div style={{ width: 28, height: 28, borderRadius: 6, background: current ? C.blue : "#64748b", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
          {current ? current.nome[0] : "T"}
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{current ? current.nome : "Todas as Empresas"}</div>
          {current && <div style={{ fontSize: 10, color: C.textLight }}>NIF: {current.nif}</div>}
        </div>
        <span style={{ color: C.textMid, fontSize: 10 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: C.surface, border: `1px solid ${C.borderDark}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.4)", zIndex: 999, overflow: "hidden" }}>
          <button onClick={() => { onChange(""); setOpen(false); }} style={{ width: "100%", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, background: selected === "" ? C.blueLight : C.surface, border: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: "#64748b", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 800 }}>T</div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Todas as Empresas</div>
              <div style={{ fontSize: 10, color: C.textLight }}>Vista consolidada</div>
            </div>
          </button>
          {(empresas || []).map(e => (
            <button key={e.nif} onClick={() => { onChange(e.nif); setOpen(false); }} style={{ width: "100%", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, background: selected === e.nif ? C.blueLight : C.surface, border: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: e.ativa ? C.blue : "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 800 }}>{e.nome[0]}</div>
              <div style={{ textAlign: "left", flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: e.ativa ? C.text : C.textLight }}>{e.nome}</div>
                <div style={{ fontSize: 10, color: C.textLight }}>{e.total_faturas} faturas · {fmt(e.valor_total)}</div>
              </div>
              {!e.ativa && <Pill color="gray">Inactiva</Pill>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ecrã de escolha de empresa (pós-login, obrigatório em cada sessão) ────────
// Modelado visualmente no ecrã de Login (mesmo cabeçalho/cartão) -- NÃO no
// EcraPinLock, que é uma funcionalidade à parte, só para colaboradores com
// "lembrar neste dispositivo" ligado (ver Root/handleLogin). Este ecrã é só
// para quem tem acesso à app cheia (role === "admin").
function EcraEmpresaSelect({ user, onContinue, onLogout }) {
  const { empresas, loading, setEmpresa } = useEmpresa();
  const ultimaEmpresa = lerUltimaEmpresa();
  const [mostrarAdmin, setMostrarAdmin] = useState(() => adminToolsVisiveis(user));

  const escolher = (nif) => {
    setEmpresa(nif);
    onContinue();
  };

  return (
    <div style={{ minHeight: "100dvh", background: C.bg, fontFamily: "'Inter', system-ui, sans-serif", padding: "48px 24px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 36 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#fff", fontWeight: 800 }}>H</div>
            <span style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Hub Financeiro</span>
          </div>
          <button onClick={onLogout} style={{ background: "none", border: "none", color: C.textMid, fontSize: 12.5, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <LogOut size={14} /> Sair
          </button>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: "0 0 4px" }}>
          Olá, {(user?.nome || user?.email || "").split(" ")[0]}
        </h1>
        <p style={{ fontSize: 13.5, color: C.textMid, margin: "0 0 14px" }}>Escolhe a empresa com que queres trabalhar.</p>
        {user?.role === "admin" && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "9px 12px", marginBottom: 28, borderRadius: 9, background: C.surface, border: `1px solid ${C.border}`, color: C.textMid, fontSize: 12.5, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={mostrarAdmin}
              onChange={(e) => { setMostrarAdmin(e.target.checked); guardarAdminToolsVisiveis(user, e.target.checked); }}
            />
            Mostrar ferramentas de administração
          </label>
        )}

        {loading ? (
          <Spinner />
        ) : (
          <div className="empresa-select-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 18 }}>
            {empresas.map((e) => (
              <button
                key={e.nif}
                onClick={() => escolher(e.nif)}
                style={{
                  textAlign: "left", cursor: e.ativa ? "pointer" : "not-allowed", padding: "24px 22px", minHeight: 154, borderRadius: 16,
                  background: C.surface, border: `1.5px solid ${ultimaEmpresa === e.nif ? C.blue : C.border}`,
                  boxShadow: "0 4px 14px rgba(16,24,40,.06)",
                  opacity: e.ativa ? 1 : 0.7,
                }}
                disabled={!e.ativa}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 11, background: e.ativa ? C.blue : "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 17, fontWeight: 800 }}>
                    {(e.nome || "?")[0]}
                  </div>
                  {!e.ativa && <Pill color="gray">Inactiva</Pill>}
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{e.nome}</div>
                <div style={{ fontSize: 11.5, color: C.textLight, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>NIF {e.nif}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: e.ativa ? C.blue : C.textLight, marginTop: 14 }}>{e.ativa ? "Entrar na empresa →" : "Empresa inativa"}</div>
              </button>
            ))}
            <style>{`@media (max-width: 620px) { .empresa-select-grid { grid-template-columns: 1fr !important; } }`}</style>
          </div>
        )}
      </div>
    </div>
  );
}

// Sidebar e Topbar foram substituídos por src/components/Layout.jsx

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM — tokens, skeleton, micro-interactions
// ═══════════════════════════════════════════════════════════════════════════════

// Alinhado com C — um único acento (o azul institucional já usado na Sidebar
// e no resto da app) em vez do índigo que o Dashboard usava antes, à parte.
const D = {
  bg:           "#f9fafb",
  surface:      "#ffffff",
  border:       "rgba(0,0,0,0.07)",
  borderMid:    "rgba(0,0,0,0.11)",
  shadow:       "0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.03)",
  shadowHover:  "0 4px 18px rgba(16,24,40,.07)",
  radius:       "10px",
  radiusSm:     "8px",
  text:         "#1A1F2E",
  textMid:      "#4B5563",
  textLight:    "#9AA3B0",
  green:        "#157347",
  greenPulse:   "#157347",
  greenBg:      "#EDF7F1",
  greenBorder:  "#A3D9B7",
  amber:        "#92640C",
  amberBg:      "#FEF8EC",
  amberBorder:  "#F5DFA0",
  red:          "#C0302A",
  redBg:        "#FEF0F0",
  redBorder:    "#F5C0BF",
  blue:         "#03363D",
  blueBg:       "#BDD9D7",
  blueBorder:   "#9FC5C1",
  cyan:         "#03363D",
  violet:       "#5B21B6",
  purple:       "#5B21B6",
  grad1: "#03363D",
  grad2: "#157347",
  grad3: "#92640C",
  grad4: "#5B21B6",
};

// Injector de animações CSS (idempotente)
function useDashboardStyles() {
  useEffect(() => {
    if (document.getElementById("ds-styles")) return;
    const s = document.createElement("style");
    s.id = "ds-styles";
    s.textContent = `
      @keyframes ds-shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }
      @keyframes ds-pulse {
        0%, 100% { opacity:1; transform:scale(1); }
        50%       { opacity:.35; transform:scale(.7); }
      }
      @keyframes ds-pulse-fast {
        0%, 100% { opacity:1; transform:scale(1); }
        50%       { opacity:.4; transform:scale(.75); }
      }
      @keyframes ds-fadein {
        from { opacity:0; transform:translateY(6px); }
        to   { opacity:1; transform:translateY(0);   }
      }
      @keyframes ds-blink { 0%,100% { opacity:.25; } 50% { opacity:1; } }

      /* ── Skeleton ── */
      .ds-sk {
        background: linear-gradient(90deg,#eef1f6 25%,#e4e9f2 37%,#eef1f6 63%);
        background-size:600px 100%;
        animation: ds-shimmer 1.6s ease infinite;
        border-radius:6px;
      }

      .ds-dot      { animation: ds-pulse      2.4s ease-in-out infinite; }
      .ds-dot-fast { animation: ds-pulse-fast 0.9s ease-in-out infinite; }

      /* ── Card (sólido, leve, rápido) ── */
      .ds-card {
        position: relative;
        background: #ffffff;
        border: 1px solid #ecedf2;
        border-radius: 16px;
        box-shadow: 0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.03);
        transition: box-shadow 0.18s ease, border-color 0.18s ease;
      }
      .ds-card:hover {
        box-shadow: 0 4px 18px rgba(16,24,40,0.07);
        border-color: #e0e3ea;
      }

      .ds-row { transition: background 0.12s ease; }
      .ds-row:hover { background: #f7f8fb !important; }
      .ds-btn { transition: all 0.14s ease; }
      .ds-btn:hover { opacity:.82; transform:translateY(-1px); }
      .ds-anom { transition: background 0.12s ease; cursor:pointer; border-radius:8px; }
      .ds-anom:hover { background: #f7f8fb !important; }
      .ds-fadein { animation: ds-fadein 0.32s ease both; }

      /* ── Streaming dots (estado a processar) ── */
      .ds-typed b { animation: ds-blink 1.4s infinite; }
      .ds-typed b:nth-child(2){ animation-delay:.2s; }
      .ds-typed b:nth-child(3){ animation-delay:.4s; }

      @media (prefers-reduced-motion: reduce) {
        .ds-dot, .ds-dot-fast, .ds-typed b { animation: none !important; }
      }
    `;
    document.head.appendChild(s);
  }, []);
}

// Skeleton block reutilizável
const Sk = ({ w = "100%", h = 16, mb = 0, style = {} }) => (
  <div className="ds-sk" style={{ width: w, height: h, marginBottom: mb, borderRadius: 6, ...style }} />
);

// Card base
const DCard = ({ children, style = {}, className = "" }) => (
  <div className={`ds-card ${className}`} style={{ padding: "24px", ...style }}>
    {children}
  </div>
);

// Label de secção
const DLabel = ({ children, style = {} }) => (
  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: D.textLight, marginBottom: 16, ...style }}>
    {children}
  </div>
);

// Dot de status animado
const StatusDot = ({ status }) => {
  const color = status === "ok" ? D.greenPulse : status === "error" ? D.red : status === "running" ? D.amber : D.textLight;
  const cls = status === "running" ? "ds-dot-fast" : "ds-dot";
  return <div className={cls} style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />;
};

// Núcleo AI — badge estático com gradiente (leve, sem animação)
const AIBadge = ({ size = 46 }) => (
  <div style={{
    width: size, height: size, borderRadius: 13, flexShrink: 0,
    background: D.grad1,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 12px rgba(99,102,241,0.30)",
  }}>
    <Sparkles size={size * 0.48} color="#fff" strokeWidth={2.2} />
  </div>
);

// ─── Componentes do Dashboard ─────────────────────────────────────────────────

function DSMetricCard({ label, value, sub, accent = D.blue, loading, onClick }) {
  return (
    <div className="ds-card ds-fadein" onClick={onClick} style={{ padding: "22px 24px", cursor: onClick ? "pointer" : "default" }}>
      {loading ? (
        <>
          <Sk h={10} w="60%" mb={14} />
          <Sk h={32} w="45%" mb={10} />
          <Sk h={10} w="75%" />
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: D.textLight, marginBottom: 12 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: D.text, lineHeight: 1, marginBottom: 8, fontVariantNumeric: "tabular-nums" }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: D.textMid }}>{sub}</div>}
          <div style={{ width: 28, height: 2, background: accent, borderRadius: 2, marginTop: 14 }} />
        </>
      )}
    </div>
  );
}

function PurityStatCard({ label, value, sub, gradient, Icon, loading }) {
  return (
    <div className="ds-card ds-fadein" style={{ padding: "20px 22px", position: "relative", overflow: "hidden" }}>
      {/* Faixa de gradiente no topo */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: gradient }} />
      {loading ? (
        <><Sk h={10} w="60%" mb={16} /><Sk h={34} w="45%" mb={8} /><Sk h={10} w="70%" /></>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: D.textLight }}>{label}</div>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 3px 8px rgba(15,23,42,0.12)" }}>
              <Icon size={17} color="#fff" strokeWidth={2.5} />
            </div>
          </div>
          <div style={{ fontSize: "clamp(20px, 1.7vw, 26px)", fontWeight: 800, color: D.text, lineHeight: 1, marginBottom: 6, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.025em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
          <div style={{ fontSize: 12, color: D.textMid }}>{sub}</div>
        </>
      )}
    </div>
  );
}

function DSStatusBar({ config, alertas, loading }) {
  const status = config?.last_run_status;
  const isOk      = !status || status === "ok";
  const isRunning = status === "running";
  const isError   = status === "error" || status === "timeout";
  const criticals = (alertas || []).filter(a => a.severidade === "critical" || a.nivel === "critical").length;

  const statusLabel = isError ? "Agente com erros" : isRunning ? "A processar..." : "Online & Optimal";
  const statusColor = isError ? D.red : isRunning ? D.amber : D.green;
  const statusBg    = isError ? D.redBg : isRunning ? D.amberBg : D.greenBg;

  const lastRun = config?.last_run_em ? (() => {
    try {
      const d = new Date(config.last_run_em);
      const mins = Math.floor((Date.now() - d) / 60000);
      if (mins < 1)  return "agora mesmo";
      if (mins < 60) return `há ${mins} min`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)  return `há ${hrs}h`;
      return d.toLocaleDateString("pt-PT");
    } catch { return "—"; }
  })() : "—";

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", background: statusBg, border: `1px solid ${isError ? D.redBorder : isRunning ? D.amberBorder : D.greenBorder}`, borderRadius: D.radius, marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {loading ? <Sk w={8} h={8} style={{ borderRadius: "50%" }} /> : <StatusDot status={isOk ? "ok" : isError ? "error" : "running"} />}
        <span style={{ fontSize: 13, fontWeight: 700, color: statusColor }}>
          {loading ? "A verificar..." : statusLabel}
        </span>
        {criticals > 0 && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: D.redBg, border: `1px solid ${D.redBorder}`, color: D.red, fontWeight: 700 }}>{criticals} crítico{criticals > 1 ? "s" : ""}</span>}
      </div>
      <div style={{ display: "flex", align: "center", gap: 20 }}>
        {!loading && <span style={{ fontSize: 12, color: D.textLight }}>Último run: <strong style={{ color: D.textMid }}>{lastRun}</strong></span>}
        {!loading && config?.schedule_interval && config.schedule_interval !== "manual" && (
          <span style={{ fontSize: 12, color: D.textLight }}>Auto: <strong style={{ color: D.textMid }}>{config.schedule_interval}</strong></span>
        )}
      </div>
    </div>
  );
}

function DSAnomalyQueue({ alertas, loading, onNav }) {
  const lista = (alertas || []).slice(0, 6);
  const SEV = {
    critical: { color: D.red,   label: "Crítico" },
    warn:     { color: D.amber, label: "Aviso"   },
    info:     { color: D.blue,  label: "Info"    },
  };

  return (
    <DCard style={{ display: "flex", flexDirection: "column", minHeight: 200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <DLabel style={{ marginBottom: 0 }}>Anomalias</DLabel>
        {(alertas || []).length > 0 && (
          <span style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 20, background: D.redBg, border: `1px solid ${D.redBorder}`, color: D.red, fontWeight: 700, letterSpacing: "0.04em" }}>
            {(alertas || []).length}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Sk w={3} h={36} style={{ borderRadius: 2, flexShrink: 0 }} />
              <div style={{ flex: 1 }}><Sk h={11} mb={5} /><Sk h={9} w="55%" /></div>
            </div>
          ))}
        </div>
      ) : lista.length === 0 ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: "20px 0" }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: D.greenBg, border: `1px solid ${D.greenBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: D.green }}>✓</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: D.green }}>Sem anomalias</div>
          <div style={{ fontSize: 11, color: D.textLight }}>Tudo a funcionar correctamente</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {lista.map((a, i) => {
            const sev = SEV[a.severidade || a.nivel] || SEV.info;
            return (
              <div key={i} className="ds-anom" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 8px" }}>
                <div style={{ width: 3, alignSelf: "stretch", minHeight: 32, borderRadius: 2, background: sev.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: D.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.titulo || a.msg || a.mensagem}</div>
                  <div style={{ fontSize: 10.5, color: D.textLight, marginTop: 1 }}>{a.empresa || a.empresa_nif || ""}</div>
                </div>
                <button onClick={() => onNav("arquivo")} className="ds-btn" style={{ fontSize: 10.5, padding: "3px 9px", borderRadius: 6, border: `1px solid ${D.borderMid}`, background: D.surface, color: D.textMid, cursor: "pointer", flexShrink: 0, fontWeight: 600 }}>
                  Rever
                </button>
              </div>
            );
          })}
        </div>
      )}
    </DCard>
  );
}

function DSActivityTimeline({ actividade, loading }) {
  const TYPE_MAP = {
    fatura_ok:   { dot: D.green,     label: "Fatura processada" },
    extrato:     { dot: D.blue,      label: "Extrato importado" },
    erro:        { dot: D.red,       label: "Erro detectado"    },
    pendente:    { dot: D.amber,     label: "Pendente"          },
    processando: { dot: D.textLight, label: "A processar"       },
  };
  const fmtTs = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      const mins = Math.floor((Date.now() - d) / 60000);
      if (mins < 1)   return "agora";
      if (mins < 60)  return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)   return `${hrs}h`;
      return d.toLocaleDateString("pt-PT", { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  return (
    <DCard style={{ padding: "20px 22px" }}>
      <DLabel>Log de actividade</DLabel>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Sk w={10} h={10} style={{ borderRadius: "50%", flexShrink: 0 }} />
              <div style={{ flex: 1 }}><Sk h={11} mb={4} /><Sk h={9} w="50%" /></div>
              <Sk w={26} h={9} />
            </div>
          ))}
        </div>
      ) : !actividade?.length ? (
        <div style={{ textAlign: "center", padding: "24px 0", color: D.textLight, fontSize: 13 }}>Sem actividade recente</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
          {/* Timeline vertical line */}
          <div style={{ position: "absolute", left: 4, top: 8, bottom: 8, width: 1, background: "rgba(0,0,0,0.06)" }} />
          {actividade.slice(0, 8).map((e, i) => {
            const t = TYPE_MAP[e.tipo] || TYPE_MAP.processando;
            return (
              <div key={i} className="ds-row" style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "7px 6px 7px 0", borderRadius: 6 }}>
                <div style={{ width: 9, height: 9, borderRadius: "50%", background: t.dot, flexShrink: 0, border: "2px solid #fff", boxShadow: `0 0 0 1px ${t.dot}50`, marginTop: 3, zIndex: 1 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: D.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.label}</div>
                  <div style={{ fontSize: 10.5, color: D.textLight, marginTop: 1 }}>{e.empresa}</div>
                </div>
                <div style={{ fontSize: 10, color: D.textLight, flexShrink: 0, fontFamily: "monospace", letterSpacing: "-0.02em", marginTop: 1 }}>{fmtTs(e.ts)}</div>
              </div>
            );
          })}
        </div>
      )}
    </DCard>
  );
}

const EMPRESA_COLORS = ["#03363D","#157347","#92640C","#5B21B6","#0F6CBD","#8A3B12"];

function DSEmpresasList({ empresas, loading, onNav }) {
  const ativas = (empresas || []).filter(e => e.ativa);
  const maxVal = Math.max(...ativas.map(e => e.valor_total || 0), 1);

  return (
    <DCard>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <DLabel style={{ marginBottom: 0 }}>Por empresa</DLabel>
        <button onClick={() => onNav("empresas")} className="ds-btn" style={{ fontSize: 11, color: D.textMid, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Ver todas →</button>
      </div>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {[1,2,3].map(i => <div key={i}><div style={{ display:"flex", gap:10, marginBottom:8 }}><Sk w={34} h={34} style={{ borderRadius: 8, flexShrink:0 }} /><div style={{ flex:1 }}><Sk h={11} mb={5} /><Sk h={9} w="60%" /></div></div><Sk h={3} /></div>)}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {ativas.map((e, i) => {
            const pct = Math.round((e.valor_total || 0) / maxVal * 100);
            const cor = EMPRESA_COLORS[i % EMPRESA_COLORS.length];
            const initials = e.nome.split(/\s+/).slice(0, 2).map(w => w[0] || "").join("").toUpperCase();
            return (
              <div key={e.nif}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: `${cor}12`, border: `1px solid ${cor}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: cor, flexShrink: 0 }}>{initials}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: D.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.nome}</div>
                    <div style={{ fontSize: 10.5, color: D.textLight, fontFamily: "monospace" }}>{e.total_faturas ?? "—"} fat · {fmt(e.valor_total)}</div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: cor, fontFamily: "monospace" }}>{pct}%</div>
                </div>
                <div style={{ height: 3, background: "rgba(0,0,0,0.05)", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${cor}, ${cor}bb)`, borderRadius: 2, boxShadow: `0 0 6px ${cor}50` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DCard>
  );
}

function DSRecentFaturas({ faturas, loading, onNav }) {
  const fmtChegou = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts.replace(" ", "T") + "Z");
      const mins = Math.floor((Date.now() - d) / 60000);
      if (mins < 1)   return "agora";
      if (mins < 60)  return `há ${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)   return `há ${hrs}h`;
      const dias = Math.floor(hrs / 24);
      if (dias < 7)   return `há ${dias}d`;
      return d.toLocaleDateString("pt-PT", { day: "numeric", month: "short" });
    } catch { return "—"; }
  };

  return (
    <DCard style={{ padding: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 0" }}>
        <DLabel style={{ marginBottom: 0 }}>Últimos documentos processados</DLabel>
        <button onClick={() => onNav("arquivo")} className="ds-btn" style={{ fontSize: 11, color: D.textMid, background: "none", border: "none", cursor: "pointer", fontWeight: 600, marginBottom: 0 }}>Ver todas →</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: D.bg }}>
              {["Chegou", "Nº Fatura", "Fornecedor", "Empresa", "Total", "Estado"].map(col => (
                <th key={col} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: D.textLight, borderBottom: `1px solid ${D.border}`, whiteSpace: "nowrap" }}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? [1,2,3,4,5].map(i => (
              <tr key={i}>
                {[1,2,3,4,5,6].map(j => <td key={j} style={{ padding: "14px 16px" }}><Sk h={11} /></td>)}
              </tr>
            )) : (faturas || []).slice(0, 10).map((f, i) => {
              const isReceita = f.tipo_documento === "Fatura_Cliente";
              return (
              <tr key={f.id} className="ds-row" style={{ borderTop: `1px solid ${D.border}`, background: "transparent" }}>
                <td style={{ padding: "12px 16px", fontSize: 11.5, color: D.textLight, fontFamily: "monospace", whiteSpace: "nowrap" }}>{fmtChegou(f.criada_em)}</td>
                <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: D.blue, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                  <span
                    onClick={() => openPDF(f.drive_link, f.numero_documento, { numero: f.numero_documento, nome: f.nome_emitente, data: f.data_documento, nif: f.nif_emitente, valor: f.valor_total })}
                    style={{ cursor: f.drive_link ? "pointer" : "default", textDecoration: f.drive_link ? "underline" : "none", textDecorationColor: "rgba(3, 54, 61,0.25)" }}
                    title={f.drive_link ? "Ver PDF" : undefined}
                  >
                    {f.numero_documento}
                  </span>
                  {!!f.possivel_duplicado && <span style={{ marginLeft: 6, fontSize: 9, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)", color: D.amber, borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>dup</span>}
                  {isReceita && <span style={{ marginLeft: 6, fontSize: 9, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)", color: D.green, borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>Receita</span>}
                  {!!f.eh_reembolso && <span style={{ marginLeft: 6, fontSize: 9, background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.25)", color: "#5B21B6", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>↩ Reembolso</span>}
                  {!!f.eh_nota_credito && <span style={{ marginLeft: 6, fontSize: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#C0302A", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>↘ Nota de Crédito</span>}
                  <span onClick={() => openDetalhe(f.id)} title="Ver detalhe" style={{ marginLeft: 6, cursor: "pointer", color: D.textLight, fontSize: 11 }}>ⓘ</span>
                </td>
                <td style={{ padding: "12px 16px", fontSize: 12, color: D.text, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nome_emitente}</td>
                <td style={{ padding: "12px 16px", fontSize: 11, color: D.textLight }}>{f.nome_empresa || f.nif_recetor}</td>
                <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700, color: isReceita ? D.green : f.valor_total > 5000 ? D.amber : D.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{isReceita ? "+" : ""}{fmt(f.valor_total)}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: f.pago ? D.greenBg : f.drive_ok && f.sheets_ok ? D.blueBg : D.amberBg, color: f.pago ? D.green : f.drive_ok && f.sheets_ok ? D.blue : D.amber, border: `1px solid ${f.pago ? D.greenBorder : f.drive_ok && f.sheets_ok ? D.blueBorder : D.amberBorder}` }}>
                    {f.pago ? "Paga" : f.drive_ok && f.sheets_ok ? "Gravada" : "Pendente"}
                  </span>
                </td>
              </tr>
              ); })}
          </tbody>
        </table>
        {!loading && !(faturas || []).length && <div style={{ padding: "40px 24px", textAlign: "center", color: D.textLight, fontSize: 13 }}>Nenhuma fatura encontrada</div>}
      </div>
    </DCard>
  );
}

// ─── Batch de Pagamentos ────────────────────────────────────────────────────
function BatchPagamentosCard() {
  const { empresa } = useEmpresa();
  const { data: historico, refresh: refreshHistorico } = useApi(
    `/api/batch/historico${empresa ? `?empresa=${empresa}` : ""}`, [empresa]
  );
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionados, setSelecionados] = useState({});
  const [ibansInline, setIbansInline] = useState({});
  const [guardandoIban, setGuardandoIban] = useState({});
  const [referencia, setReferencia] = useState("");
  const [gerando, setGerando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [filtroPreparacao, setFiltroPreparacao] = useState("prontos");
  const [pesquisaPreparacao, setPesquisaPreparacao] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/batch/pendentes${empresa ? `?empresa=${empresa}` : ""}`);
      if (r.ok) {
        const data = await r.json();
        setLista(data);
        if (data.length > 0) setAberto(true);
        const sel = {};
        data.forEach(item => { sel[`${item.tipo}_${item.id}`] = !!item.iban; });
        setSelecionados(sel);
      }
    } catch {
      // sem ligação — mantém a lista anterior
    } finally {
      setLoading(false);
    }
  }, [empresa]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const handler = () => { carregar(); refreshHistorico(); };
    window.addEventListener("batch-pendentes-mudou", handler);
    return () => window.removeEventListener("batch-pendentes-mudou", handler);
  }, [carregar, refreshHistorico]);

  const nBatchesPendentes = (historico || []).filter(b => b.estado === "gerado").length;

  const getKey = item => `${item.tipo}_${item.id}`;
  const getIban = item => (ibansInline[getKey(item)] !== undefined ? ibansInline[getKey(item)] : item.iban) || "";
  const temIban = item => !!getIban(item).trim();

  const itensSel = lista.filter(i => selecionados[getKey(i)]);
  const total = itensSel.reduce((s, i) => s + (i.valor || 0), 0);
  const semIbanSel = itensSel.filter(i => !getIban(i).trim()).length;
  const comIban = lista.filter(i => temIban(i));
  const semIban = lista.filter(i => !temIban(i));
  const pesquisaNormalizada = pesquisaPreparacao.trim().toLocaleLowerCase("pt-PT");
  const listaVisivel = lista.filter(item => {
    const correspondeEstado = filtroPreparacao === "todos"
      || (filtroPreparacao === "prontos" && temIban(item))
      || (filtroPreparacao === "incompletos" && !temIban(item));
    if (!correspondeEstado) return false;
    if (!pesquisaNormalizada) return true;
    return [item.nome, item.descricao, item.nif_emitente, getIban(item), item.valor]
      .filter(valor => valor !== null && valor !== undefined)
      .some(valor => String(valor).toLocaleLowerCase("pt-PT").includes(pesquisaNormalizada));
  });
  // Mesma chave de agrupamento do backend (_agrupar_itens_batch_por_linha) —
  // várias despesas do MESMO IBAN saem como UMA só transferência no Excel.
  const nLinhasBancarias = new Set(
    itensSel.map(i => `${getIban(i).replace(/\s/g, "").toUpperCase()}__${(i.nome || "").trim().toUpperCase()}`)
  ).size;

  const guardarIban = async (item) => {
    const key = getKey(item);
    const iban = (ibansInline[key] || "").trim();
    if (!iban) return;
    setGuardandoIban(p => ({ ...p, [key]: true }));
    try {
      let endpoint, body;
      if (item.tipo === "fatura" && item.nif_emitente) {
        endpoint = `/api/fornecedores-iban/${item.nif_emitente}`;
        body = { iban, nome: item.nome };
      } else {
        endpoint = "/api/socios/iban-por-nome";
        body = { nome: item.nome, iban };
      }
      const r = await apiFetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        await carregar();
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || "Erro ao guardar IBAN");
      }
    } catch {
      alert("Erro de rede.");
    } finally {
      setGuardandoIban(p => ({ ...p, [key]: false }));
    }
  };

  const gerarBatch = async () => {
    if (!itensSel.length || semIbanSel > 0) return;
    setGerando(true);
    try {
      const r = await apiFetch("/api/batch/gerar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itens: itensSel.map(i => ({
            tipo: i.tipo, origem: i.origem, id: i.id,
            nome: i.nome, valor: i.valor,
            iban: getIban(i), descricao: i.descricao,
          })),
          descricao_ref: referencia,
        }),
      });
      if (!r.ok) { alert("Erro ao gerar ficheiro."); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `batch_pagamentos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setSelecionados({});
      setReferencia("");
      await carregar();
      window.dispatchEvent(new CustomEvent("batch-pendentes-mudou"));
    } catch { alert("Erro de rede."); }
    setGerando(false);
  };

  const BADGE = {
    reembolso: { label: "Reembolso", bg: "#dbeafe", color: "#1d4ed8" },
    fatura:    { label: "Fatura",    bg: "#f3e8ff", color: "#7e22ce" },
  };

  return (
    <div style={{ marginBottom: 18, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,.04)" }}>
      <div style={{ padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: aberto ? `1px solid ${C.border}` : "none" }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: "#E6F5F3", color: C.blue, display: "grid", placeItems: "center", fontSize: 18 }}>▤</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>Preparar novo batch</div>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 3 }}>
            Seleciona pagamentos validados. Os itens com dados em falta ficam separados até serem corrigidos.
          </div>
        </div>
        {nBatchesPendentes > 0 && (
          <span style={{ fontSize: 11, fontWeight: 800, color: "#92640C", background: "#FEF8EC", border: "1px solid #F5DFA0", borderRadius: 20, padding: "3px 11px", whiteSpace: "nowrap" }}>
            {nBatchesPendentes} pendente{nBatchesPendentes !== 1 ? "s" : ""}
          </span>
        )}
        <button type="button" onClick={() => setAberto(a => !a)} aria-expanded={aberto}
          style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 8, padding: "7px 10px", color: C.textMid, cursor: "pointer", fontWeight: 700 }}>
          {aberto ? "Recolher" : "Abrir"}
        </button>
      </div>

      {aberto && (
        lista.length === 0 ? (
          <div style={{ padding: "28px 16px", textAlign: "center", color: C.textMid, fontSize: 13 }}>
            {loading ? "A carregar…" : "Sem itens pendentes de pagamento neste momento."}
          </div>
        ) : (
        <div>
          <div style={{ padding: "16px 20px", background: "#F8FAFC", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
              {[
                ["Prontos a incluir", comIban.length, "#087F73", "#E6F5F3"],
                ["Com dados em falta", semIban.length, "#92640C", "#FEF8EC"],
                ["Selecionados", `${itensSel.length} · ${fmt(total)}`, C.text, "#FFFFFF"],
              ].map(([rotulo, valor, cor, fundo]) => (
                <div key={rotulo} style={{ padding: "12px 14px", border: `1px solid ${C.border}`, borderRadius: 10, background: fundo }}>
                  <div style={{ fontSize: 10, color: C.textLight, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em" }}>{rotulo}</div>
                  <div style={{ fontSize: 18, color: cor, fontWeight: 800, marginTop: 4 }}>{valor}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: "1 1 300px" }}>
                <span style={{ position: "absolute", left: 12, top: 9, color: C.textLight }}>⌕</span>
                <input value={pesquisaPreparacao} onChange={e => setPesquisaPreparacao(e.target.value)}
                  placeholder="Pesquisar por nome, descrição, NIF, IBAN ou valor"
                  style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 12, color: C.text, outline: "none" }} />
              </div>
              <div style={{ display: "flex", padding: 3, border: `1px solid ${C.border}`, borderRadius: 9, background: "#fff" }}>
                {[["prontos", `Prontos (${comIban.length})`], ["incompletos", `Dados em falta (${semIban.length})`], ["todos", `Todos (${lista.length})`]].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setFiltroPreparacao(id)}
                    style={{ border: 0, borderRadius: 6, padding: "7px 10px", background: filtroPreparacao === id ? "#DDF3F0" : "transparent", color: filtroPreparacao === id ? C.blue : C.textMid, fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ width: 32, padding: "8px 12px", textAlign: "center" }}>
                    <input type="checkbox"
                      checked={comIban.length > 0 && itensSel.length === comIban.length}
                      onChange={e => {
                        const sel = {};
                        lista.forEach(i => { sel[getKey(i)] = e.target.checked && temIban(i); });
                        setSelecionados(sel);
                      }} />
                  </th>
                  {["Tipo", "Nome / Fornecedor", "Descrição", "IBAN", "Valor"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: C.textLight, textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {listaVisivel.map(item => {
                  const key = getKey(item);
                  const ibanAtual = getIban(item);
                  const hasIban = !!ibanAtual.trim();
                  const badge = BADGE[item.tipo] || BADGE.reembolso;
                  const editandoIban = ibansInline[key] !== undefined;
                  return (
                    <tr key={key} style={{ borderBottom: `1px solid ${C.border}`, background: selecionados[key] ? "#F9FFFE" : "#fff" }}>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        <input type="checkbox" disabled={!hasIban}
                          checked={!!selecionados[key]}
                          onChange={e => setSelecionados(p => ({ ...p, [key]: e.target.checked }))} />
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, background: badge.bg, color: badge.color, borderRadius: 6, padding: "2px 7px", whiteSpace: "nowrap" }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: C.text, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.nome}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 11, color: C.textMid, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.descricao}
                      </td>
                      <td style={{ padding: "8px 12px", minWidth: 270 }}>
                        {hasIban && !editandoIban ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, fontFamily: "monospace", color: C.text }}>{ibanAtual}</span>
                            <button onClick={() => setIbansInline(p => ({ ...p, [key]: ibanAtual }))}
                              style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: `1px solid ${C.border}`, background: "none", color: C.textMid, cursor: "pointer" }}>
                              Editar
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <input value={ibansInline[key] || ""}
                              onChange={e => setIbansInline(p => ({ ...p, [key]: e.target.value }))}
                              placeholder="IBAN..."
                              style={{ flex: 1, padding: "7px 9px", borderRadius: 6, border: "1px solid #E9C46A", background: "#FFFCF4", fontSize: 11, fontFamily: "monospace", color: C.text, outline: "none", minWidth: 140 }} />
                            {(ibansInline[key] || "").trim() && (
                              <button onClick={() => guardarIban(item)} disabled={guardandoIban[key]}
                                style={{ padding: "5px 10px", fontSize: 11, borderRadius: 6, border: "none", background: C.blue, color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>
                                {guardandoIban[key] ? "…" : "Guardar"}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: C.text, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {fmt(item.valor)}
                      </td>
                    </tr>
                  );
                })}
                {listaVisivel.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 28, textAlign: "center", color: C.textMid, fontSize: 12 }}>
                    Nenhum pagamento corresponde à pesquisa e ao filtro selecionado.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ padding: "15px 20px", borderTop: `1px solid ${C.border}`, background: "#fff", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <label style={{ display: "block", fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: C.textLight, marginBottom: 5 }}>Nome do batch</label>
              <input value={referencia} onChange={e => setReferencia(e.target.value)}
                placeholder="Ex.: Pagamentos de setembro 2026"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, outline: "none" }} />
            </div>
            <div style={{ fontSize: 13, color: C.text, whiteSpace: "nowrap", paddingTop: 15 }}>
              <strong>{itensSel.length}</strong> selecionado{itensSel.length !== 1 ? "s" : ""} · <strong>{fmt(total)}</strong>
              {nLinhasBancarias > 0 && nLinhasBancarias < itensSel.length && (
                <span style={{ fontSize: 11.5, color: C.textMid }}> · junta em <strong>{nLinhasBancarias}</strong> transferência{nLinhasBancarias !== 1 ? "s" : ""} (mesmo IBAN)</span>
              )}
            </div>
            {semIbanSel > 0 && (
              <span style={{ fontSize: 11, color: C.red, whiteSpace: "nowrap" }}>⚠ {semIbanSel} sem IBAN</span>
            )}
            <div style={{ paddingTop: 15 }}>
            <Btn variant="primary" disabled={!itensSel.length || semIbanSel > 0 || gerando} onClick={gerarBatch}>
              {gerando ? "A criar…" : "Criar batch e descarregar Excel"}
            </Btn>
            </div>
          </div>
        </div>
        )
      )}
    </div>
  );
}

// ─── Reembolsos (estados partilhados entre Dashboard e o modal "Todos os Reembolsos") ──
// Só 3 estados reais: "submetido" (por decidir), "aprovado", "pago". Não
// existe "pendente" como estado à parte — era um separador morto que nunca
// tinha nenhum registo (o backend nunca produz esse valor), só confundia
// com "submetido" ao lado. "submetido" É o estado pendente.
const REEMB_EST = {
  submetido: { label: "Submetido", cor: "#92640C", bg: "#FEF8EC", bord: "#F5DFA0" },
  aprovado:  { label: "Aprovado",  cor: "#03363D", bg: "#BDD9D7", bord: "#9FC5C1" },
  pago:      { label: "Pago",      cor: "#157347", bg: "#EDF7F1", bord: "#A3D9B7" },
};

// Valor de socioEscolhido (modal "Editar e registar") quando o beneficiário
// ainda não está registado como sócio -- distinto de qualquer nome real
// possível, nunca confundível com um sócio genuíno chamado "novo" ou "".
const SOCIO_NOVO_SENTINEL = "__novo_colaborador__";

// ─── Dashboard principal ──────────────────────────────────────────────────────
function PageDashboard({ onNav, user }) {
  const { empresa, empresas } = useEmpresa();
  useDashboardStyles();
  const { data: config }        = useApi("/api/agente/config", []);
  const { data: reembolsos }    = useApi(`/api/reembolsos/todos${empresa ? `?empresa=${empresa}` : ""}`, [empresa]);
  const { data: socios }        = useApi("/api/socios", []);
  // A mesma fonte do Arquivo alimenta a atividade recente do Dashboard.
  const { data: todasFaturas }  = useApi(`/api/faturas/com-duplicados?limit=5000${empresa ? `&empresa=${empresa}` : ""}`, [empresa]);
  const { data: casosPend, refresh: refreshCasos } = useApi("/api/pendentes", [], []);
  const { data: sugestoesPagamento, refresh: refreshSugestoesPagamento } = useApi("/api/sugestoes-pagamento", [], []);
  const { data: divergenciasFiscais } = useApi(empresa ? `/api/efatura/divergencias?empresa_nif=${empresa}` : null, [empresa]);
  const { data: resumoIngestao } = useApi("/api/emails/resumo-ingestao", [], null);

  const [respondendo,    setRespondendo]    = useState({});
  const [modalRegistar,  setModalRegistar]  = useState(null);
  const [nomeRegistar,   setNomeRegistar]   = useState("");
  const [emailRegistar,  setEmailRegistar]  = useState("");
  const [ibanRegistar,   setIbanRegistar]   = useState("");
  // Sócio escolhido no modal "Editar e registar" -- "" (nenhum ainda) ou
  // SOCIO_NOVO_SENTINEL (é alguém que ainda não está em `socios`, mostra os
  // campos de texto livre); caso contrário é o nome exacto de um sócio já
  // registado, escolhido no <select> em vez de escrito à mão.
  const [socioEscolhido, setSocioEscolhido] = useState("");

  const now = new Date();
  const anoAtual = now.getFullYear();
  const mesAtual = now.getMonth();
  const MESES_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const nomeMes = MESES_PT[mesAtual];
  const ultimoDia = new Date(anoAtual, mesAtual + 1, 0).getDate();
  const diasRestantes = ultimoDia - now.getDate();
  const mesPrefixo = `${anoAtual}-${String(mesAtual + 1).padStart(2, "0")}`;
  const saudacao = now.getHours() < 13 ? "Bom dia" : "Boa tarde";

  const faturasMes = (todasFaturas || []).filter(f => (f.data_documento || "").startsWith(mesPrefixo));
  const nProcessadas  = faturasMes.length;
  const nSemExtrato   = faturasMes.filter(f => !f.reconciliada && !f.pago).length;
  const nSemArquivo   = faturasMes.filter(f => !f.drive_ok || !f.sheets_ok).length;
  const nReembPend    = (reembolsos || []).filter(r => r.estado === "submetido").length;
  const nCasosPend    = (casosPend || []).length;
  const nDivergenciasFiscais = (divergenciasFiscais?.faturas_sem_efatura?.length || 0) + (divergenciasFiscais?.efatura_sem_fatura?.length || 0);
  const nDocumentosRever = resumoIngestao?.pendentes_revisao || 0;
  const nProcessamentosRepetir = (resumoIngestao?.pendentes_nova_tentativa || 0) + (resumoIngestao?.emails_sem_estado_final || 0);
  const nTrabalhoPendente = nDocumentosRever + nProcessamentosRepetir + nCasosPend + nReembPend;

  const ultimasFaturas = [...(todasFaturas || [])]
    .sort((a, b) => String(b.criada_em || "").localeCompare(String(a.criada_em || "")))
    .slice(0, 6);

  // Reembolsos de sócios: prefere o socio_id (associação real, devolvida por
  // /api/reembolsos/todos) — só cai para o cruzamento por nome em registos
  // antigos que ainda não têm socio_id preenchido. reembolsos vem de
  // /api/reembolsos/todos (manual + deteção automática por email) — as duas
  // origens têm de aparecer aqui, não só a manual.
  const socioNomes = new Set((socios || []).map(s => (s.nome || "").toLowerCase().trim()).filter(Boolean));
  const reembSocios = (reembolsos || []).filter(r =>
    r.estado !== "pago" && (r.socio_id != null || socioNomes.has((r.nome || "").toLowerCase().trim()))
  );
  // Se não há cruzamento, mostra todos os pendentes como fallback
  const listaReembSocios = reembSocios.length > 0
    ? reembSocios
    : (reembolsos || []).filter(r => r.estado !== "pago");

  // Responder caso pendente inline
  const responderCaso = async (pendenteId, opcao) => {
    setRespondendo(prev => ({ ...prev, [pendenteId]: true }));
    try {
      await apiFetch(`/api/pendentes/${pendenteId}/responder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resposta: opcao }),
      });
      refreshCasos();
    } finally {
      setRespondendo(prev => ({ ...prev, [pendenteId]: false }));
    }
  };

  // Resolver sugestão de pagamento detetada no corpo de um email -- nunca
  // marca pago sozinho no frontend, só chama o endpoint que já reaproveita
  // o caminho de "confirmar pagamento sem linha de extrato" (ver api.py).
  const [resolvendoSugestao, setResolvendoSugestao] = useState({});
  const resolverSugestaoPagamento = async (sugestaoId, acao) => {
    setResolvendoSugestao(prev => ({ ...prev, [sugestaoId]: true }));
    try {
      const r = await apiFetch(`/api/sugestoes-pagamento/${sugestaoId}/${acao}`, { method: "POST" });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Erro ao processar a sugestão.");
      } else {
        refreshSugestoesPagamento();
      }
    } catch {
      alert("Erro de rede.");
    } finally {
      setResolvendoSugestao(prev => ({ ...prev, [sugestaoId]: false }));
    }
  };

  const checklist = [
    {
      id: "agente", label: "Faturas processadas",
      sub: nProcessadas > 0 ? `${nProcessadas} documento${nProcessadas !== 1 ? "s" : ""} este mês` : "Nenhuma fatura processada",
      done: nProcessadas > 0, error: false,
    },
    {
      id: "arquivo", label: "Arquivo Drive + Sheets",
      sub: nSemArquivo === 0 ? "Todos os documentos arquivados" : `${nSemArquivo} sem arquivar`,
      done: nSemArquivo === 0, error: false,
      onClick: nSemArquivo > 0 ? () => onNav("arquivo") : null,
      cta: nSemArquivo > 0 ? "Ver faturas →" : null,
    },
    {
      id: "conciliacao", label: "Conciliação bancária",
      sub: nSemExtrato === 0 ? "Todas as faturas conciliadas" : `${nSemExtrato} fatura${nSemExtrato !== 1 ? "s" : ""} sem movimento bancário`,
      done: nSemExtrato === 0, error: nSemExtrato > 2,
      onClick: nSemExtrato > 0 ? () => onNav("conciliacao") : null,
      cta: nSemExtrato > 0 ? "Conciliar →" : null,
    },
    {
      id: "reembolsos", label: "Reembolsos a aprovar",
      sub: nReembPend === 0 ? "Nenhum pedido pendente" : `${nReembPend} pedido${nReembPend !== 1 ? "s" : ""} por aprovar`,
      done: nReembPend === 0, error: nReembPend > 0,
      onClick: nReembPend > 0 ? () => openTodosReembolsos() : null,
      cta: nReembPend > 0 ? "Aprovar →" : null,
    },
    {
      id: "casos", label: "Casos a decidir",
      sub: nCasosPend === 0 ? "Nenhuma decisão pendente" : `${nCasosPend} caso${nCasosPend !== 1 ? "s" : ""} aguarda${nCasosPend === 1 ? "" : "m"} resposta`,
      done: nCasosPend === 0, error: nCasosPend > 0,
    },
    {
      id: "efatura", label: "Validação fiscal (e-Fatura)",
      sub: nDivergenciasFiscais === 0 ? "Tudo validado com o portal das Finanças" : `${nDivergenciasFiscais} divergência${nDivergenciasFiscais !== 1 ? "s" : ""} a rever`,
      done: nDivergenciasFiscais === 0, error: nDivergenciasFiscais > 0,
      onClick: nDivergenciasFiscais > 0 ? () => onNav("efatura") : null,
      cta: nDivergenciasFiscais > 0 ? "Rever →" : null,
    },
  ];
  const nProblemas = checklist.filter(c => !c.done).length;

  return (
    <div className="ds-fadein pgdash">

      {/* Cabeçalho operacional: contexto primeiro, sem parecer um relatório fechado. */}
      <div style={{ background: "linear-gradient(135deg,#101828 0%,#16263A 62%,#0F766E 140%)", borderRadius: 18, padding: "24px 28px", marginBottom: 22, boxShadow: "0 16px 36px rgba(16,24,40,.14)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 240, height: 240, borderRadius: "50%", right: -70, top: -140, background: "rgba(45,212,191,.10)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".14em", textTransform: "uppercase", color: "#5EEAD4", marginBottom: 8 }}>Visão operacional · {nomeMes} {anoAtual}</div>
            <div style={{ fontSize: 26, fontWeight: 780, color: "#fff", lineHeight: 1.15, letterSpacing: "-.025em" }}>{saudacao}, {(user?.nome || "").split(" ")[0] || "equipa"}.</div>
            <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.66)", marginTop: 7 }}>
              Vê o que precisa da tua atenção e continua exatamente de onde paraste.
            </div>
          </div>
          <div className="pgdash-month-counters" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { n: nProcessadas, label: "PROCESSADOS", alert: false },
              { n: nSemExtrato,  label: "SEM EXTRATO",  alert: nSemExtrato > 0 },
              { n: nCasosPend,   label: "CASOS",         alert: nCasosPend > 0 },
              { n: listaReembSocios.length, label: "SÓCIOS",  alert: listaReembSocios.length > 0 },
            ].map(p => (
              <div key={p.label} style={{ background: p.alert ? "rgba(247,144,9,.18)" : "rgba(255,255,255,.08)", border: `1px solid ${p.alert ? "rgba(247,144,9,.28)" : "rgba(255,255,255,.12)"}`, borderRadius: 10, padding: "8px 14px", textAlign: "center", minWidth: 68, backdropFilter: "blur(8px)" }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{p.n}</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.6)", marginTop: 2, letterSpacing: "0.05em" }}>{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Caixa de trabalho do contabilista: só intervenções humanas reais. */}
      <div style={{ background: "#fff", border: "1px solid #E4E7EC", borderRadius: 16, overflow: "hidden", marginBottom: 22, boxShadow: "0 8px 24px rgba(16,24,40,.035)" }}>
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #EAECF0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 780, color: "#101828", letterSpacing: "-.01em" }}>Fila de trabalho</div>
            <div style={{ fontSize: 12, color: "#667085", marginTop: 3 }}>Só aparecem tarefas que exigem revisão, correção ou aprovação humana.</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 12, background: nTrabalhoPendente ? "#FFF4D6" : "#EDF7F1", color: nTrabalhoPendente ? "#92640C" : "#157347", whiteSpace: "nowrap" }}>
            {nTrabalhoPendente ? `${nTrabalhoPendente} por tratar` : "Fila limpa"}
          </span>
        </div>
        <div className="pgdash-work-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
          {[
            { label: "Documentos para rever", n: nDocumentosRever, sub: "Extração incompleta, formato desconhecido ou sem fatura", action: () => onNav("emails-pendentes"), cta: "Rever documentos" },
            { label: "Processamento a repetir", n: nProcessamentosRepetir, sub: "Emails com erro ou ainda sem estado final", action: () => onNav("emails-pendentes"), cta: "Ver falhas" },
            { label: "Decisões do agente", n: nCasosPend, sub: "Perguntas que bloqueiam a conclusão de documentos", action: null, cta: "Ver abaixo" },
            { label: "Reembolsos por aprovar", n: nReembPend, sub: "Pedidos que aguardam confirmação do contabilista", action: nReembPend ? () => openTodosReembolsos() : null, cta: "Rever pedidos" },
          ].map((item, i) => (
            <div key={item.label} style={{ padding: "18px 20px", borderRight: i < 3 ? "1px solid #EAECF0" : "none", minWidth: 0, background: item.n ? "linear-gradient(180deg,#fff,#FFFCF5)" : "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#344054" }}>{item.label}</div>
                <div style={{ fontSize: 24, lineHeight: 1, fontWeight: 780, color: item.n ? "#B54708" : "#039855", letterSpacing: "-.03em" }}>{item.n}</div>
              </div>
              <div style={{ minHeight: 32, fontSize: 10.5, lineHeight: 1.45, color: "#6B7280", marginTop: 7 }}>{item.n ? item.sub : "Sem casos pendentes."}</div>
              {item.n > 0 && <button onClick={item.action || undefined} style={{ marginTop: 10, padding: "5px 0", border: 0, background: "none", color: "#0F766E", fontSize: 11, fontWeight: 750, cursor: item.action ? "pointer" : "default" }}>{item.cta} →</button>}
            </div>
          ))}
        </div>
      </div>

      {/* ── 3 colunas: Checklist | Atividade recente | Reembolsos sócios ── */}
      <div className="pgdash-main-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1.1fr", gap: 16, marginBottom: 18 }}>

        {/* ─ Checklist de fecho ─ */}
        <div style={{ background: "#fff", border: "1px solid #D8DDE6", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #E8ECF2", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1F2E" }}>Checklist de fecho</div>
            {nProblemas > 0
              ? <span style={{ fontSize: 10, fontWeight: 800, background: "#C0302A", color: "#fff", borderRadius: 10, padding: "1px 7px" }}>{nProblemas}</span>
              : <span style={{ fontSize: 10, fontWeight: 700, background: "#EDF7F1", color: "#157347", borderRadius: 10, padding: "1px 7px", border: "1px solid #A3D9B7" }}>✓ ok</span>
            }
          </div>
          {checklist.map((item, i) => {
            const dotCor = item.done ? "#157347" : item.error ? "#C0302A" : "#92640C";
            return (
              <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 16px", borderBottom: i < checklist.length - 1 ? "1px solid #F0F2F5" : "none" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: dotCor, flexShrink: 0, marginTop: 5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1A1F2E" }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 1 }}>{item.sub}</div>
                  {item.cta && (
                    <button onClick={item.onClick} style={{ marginTop: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", background: "#fff", color: "#03363D", border: "1px solid #9FC5C1", borderRadius: 5, cursor: "pointer" }}>
                      {item.cta}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ─ Últimas faturas processadas ─ */}
        <div style={{ background: "#fff", border: "1px solid #D8DDE6", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #E8ECF2", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1F2E" }}>Últimas faturas processadas</div>
              <div style={{ fontSize: 10.5, color: "#9AA3B0", marginTop: 2 }}>Atividade mais recente do agente nesta empresa</div>
            </div>
            <button onClick={() => onNav("arquivo")} style={{ border: 0, background: "none", color: "#0F766E", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Ver arquivo →</button>
          </div>
          {ultimasFaturas.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#344054" }}>Ainda não há faturas processadas</div>
              <div style={{ fontSize: 11, color: "#9AA3B0", marginTop: 3 }}>As próximas faturas tratadas pelo agente aparecerão aqui.</div>
            </div>
          ) : (
            <div style={{ overflowY: "auto", maxHeight: 320 }}>
              {ultimasFaturas.map((f, i) => (
                <div key={f.id || `${f.numero_documento}-${i}`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 12, alignItems: "center", padding: "11px 16px", borderBottom: i < ultimasFaturas.length - 1 ? "1px solid #F0F2F5" : "none" }}>
                  <div style={{ minWidth: 0 }}>
                    <button
                      onClick={() => f.drive_link ? openPDF(f.drive_link, f.numero_documento, { numero: f.numero_documento, nome: f.nome_emitente, data: f.data_documento, nif: f.nif_emitente, valor: f.valor_total }) : onNav("arquivo")}
                      style={{ display: "block", width: "100%", padding: 0, border: 0, background: "none", textAlign: "left", color: "#1A1F2E", fontSize: 12, fontWeight: 700, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.nome_emitente || "Fornecedor não identificado"}
                    </button>
                    <div style={{ display: "flex", gap: 7, alignItems: "center", marginTop: 3, minWidth: 0, color: "#667085", fontSize: 10.5 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.numero_documento || "Sem número"}</span>
                      <span>·</span>
                      <span style={{ flexShrink: 0 }}>{fmtDate(f.data_documento)}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#101828", fontSize: 12, fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt(f.valor_total)}</div>
                    <div style={{ color: f.reconciliada || f.pago ? "#157347" : "#92640C", fontSize: 9.5, fontWeight: 700, marginTop: 3, whiteSpace: "nowrap" }}>{f.reconciliada ? "Conciliada" : f.pago ? "Paga" : "Processada"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* ─ Sugestões de pagamento detetadas em emails ─ */}
        <div style={{ background: "#fff", border: "1px solid #D8DDE6", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #E8ECF2", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1F2E" }}>Sugestões de pagamento (emails)</div>
            {(sugestoesPagamento || []).length > 0
              ? <span style={{ fontSize: 10, fontWeight: 800, background: "#92640C", color: "#fff", borderRadius: 10, padding: "1px 7px" }}>{sugestoesPagamento.length}</span>
              : <span style={{ fontSize: 10, fontWeight: 700, background: "#EDF7F1", color: "#157347", borderRadius: 10, padding: "1px 7px", border: "1px solid #A3D9B7" }}>✓ ok</span>
            }
          </div>
          {(sugestoesPagamento || []).length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#157347" }}>Sem sugestões pendentes</div>
              <div style={{ fontSize: 11, color: "#9AA3B0", marginTop: 3 }}>Nenhum email mencionou um pagamento já feito.</div>
            </div>
          ) : (
            <div style={{ overflowY: "auto", maxHeight: 320 }}>
              {sugestoesPagamento.map((s, i) => {
                const isLoading = !!resolvendoSugestao[s.id];
                const faturaResolvida = s.fatura_id != null;
                return (
                  <div key={s.id} style={{ padding: "12px 16px", borderBottom: i < sugestoesPagamento.length - 1 ? "1px solid #F0F2F5" : "none", background: "#FFFBF0" }}>
                    <div style={{ fontSize: 12, color: "#1A1F2E", fontStyle: "italic", lineHeight: 1.45, marginBottom: 8 }}>
                      "{s.trecho_email}"
                    </div>
                    {faturaResolvida ? (
                      <div style={{ display: "flex", gap: 16, marginBottom: 8, padding: "7px 12px", background: "#fff", border: "1px solid #F0E6C8", borderRadius: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#9AA3B0" }}>Fatura</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1F2E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.fatura_nome_emitente} · {s.fatura_numero_documento}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#9AA3B0" }}>Valor</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1F2E", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt(s.fatura_valor_total)}</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11.5, color: "#92640C", marginBottom: 8 }}>
                        ⚠ Ainda não encontrámos a fatura mencionada no sistema — só podes ignorar por agora.
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {faturaResolvida && (
                        <button
                          disabled={isLoading}
                          onClick={() => resolverSugestaoPagamento(s.id, "confirmar")}
                          style={{ fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 5, border: "1px solid #03363D", background: "#03363D", color: "#fff", cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.6 : 1 }}>
                          {isLoading ? "…" : "Confirmar pagamento"}
                        </button>
                      )}
                      <button
                        disabled={isLoading}
                        onClick={() => resolverSugestaoPagamento(s.id, "rejeitar")}
                        style={{ fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 5, border: "1px solid #D8DDE6", background: "#fff", color: "#6B7280", cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.6 : 1 }}>
                        Ignorar
                      </button>
                    </div>
                    <div style={{ fontSize: 10, color: "#9AA3B0", marginTop: 5 }}>
                      Detetado {s.criada_em ? new Date(s.criada_em).toLocaleDateString("pt-PT") : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─ Reembolsos a sócios ─ */}
        <div style={{ background: "#fff", border: "1px solid #D8DDE6", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #E8ECF2", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1F2E" }}>Reembolsos a sócios</div>
            {listaReembSocios.length > 0
              ? <span style={{ fontSize: 10, fontWeight: 800, background: "#92640C", color: "#fff", borderRadius: 10, padding: "1px 7px" }}>{listaReembSocios.length}</span>
              : <span style={{ fontSize: 10, fontWeight: 700, background: "#EDF7F1", color: "#157347", borderRadius: 10, padding: "1px 7px", border: "1px solid #A3D9B7" }}>✓ ok</span>
            }
          </div>
          {listaReembSocios.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#157347" }}>Sem reembolsos pendentes</div>
              <div style={{ fontSize: 11, color: "#9AA3B0", marginTop: 3 }}>Nenhum sócio com reembolso por processar.</div>
            </div>
          ) : (
            <>
              <div style={{ overflowY: "auto", maxHeight: 320 }}>
                {listaReembSocios.map((r, i) => {
                  const est = REEMB_EST[r.estado] || REEMB_EST.submetido;
                  const isSocio = r.socio_id != null || socioNomes.has((r.nome || "").toLowerCase().trim());
                  return (
                    <div key={`${r.origem}-${r.id}`} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px", borderBottom: i < listaReembSocios.length - 1 ? "1px solid #F0F2F5" : "none" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1F2E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nome}</div>
                          {isSocio && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", background: "#BDD9D7", color: "#03363D", border: "1px solid #9FC5C1", borderRadius: 3, whiteSpace: "nowrap" }}>SÓCIO</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{r.descricao}</div>
                        <div style={{ fontSize: 10, color: "#9AA3B0", marginTop: 2 }}>{fmtDate(r.data)} · {r.categoria}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1F2E", fontVariantNumeric: "tabular-nums" }}>{fmt(r.valor)}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: est.bg, color: est.cor, border: `1px solid ${est.bord}`, display: "inline-block", marginTop: 4 }}>{est.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Rodapé FORA da área de scroll — visível sem percorrer a lista */}
              <div style={{ padding: "10px 16px", borderTop: "1px solid #E8ECF2", textAlign: "center", background: "#fff" }}>
                <button onClick={() => openTodosReembolsos()} style={{ fontSize: 11.5, fontWeight: 700, color: "#03363D", background: "none", border: "none", cursor: "pointer" }}>
                  Ver todos os reembolsos ({listaReembSocios.length}) →
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Batch de Pagamentos ── */}
      <BatchResumoCard onNav={onNav} />

      {/* ── Últimos documentos + Empresas ── */}
      <div className="pgdash-bottom-grid" style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>
        <DSRecentFaturas faturas={todasFaturas} loading={!todasFaturas} onNav={onNav} />
        <DSEmpresasList empresas={empresas} loading={!empresas} onNav={onNav} />
      </div>

      <style>{`
        .pgdash-month-counters { display: none !important; }
        .pgdash-main-grid { grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) !important; }
        .pgdash-main-grid > div:first-child { display: none !important; }
        .pgdash-main-grid > div:nth-child(3) { display: none !important; }
        .pgdash-bottom-grid { display: none !important; }
        @media (max-width: 1100px) {
          .pgdash-work-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .pgdash-kpi-grid  { grid-template-columns: repeat(2, 1fr) !important; }
          .pgdash-main-grid { grid-template-columns: 1fr 1fr !important; }
          .pgdash-bottom-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 700px) {
          .pgdash-work-grid { grid-template-columns: 1fr !important; }
          .pgdash-work-grid > div { border-right: none !important; border-bottom: 1px solid #E8ECF2; }
          .pgdash-kpi-grid  { grid-template-columns: 1fr 1fr !important; }
          .pgdash-main-grid { grid-template-columns: 1fr !important; }
          .pgdash-bottom-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Modal: Editar e Registar Reembolso ── */}
      {modalRegistar && (() => {
        const ehNovo = socioEscolhido === SOCIO_NOVO_SENTINEL || socioEscolhido === "";
        const nomeFinal = ehNovo ? nomeRegistar.trim() : socioEscolhido;
        const fecharModal = () => {
          setModalRegistar(null); setSocioEscolhido("");
          setNomeRegistar(""); setEmailRegistar(""); setIbanRegistar("");
        };
        return (
        <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(15,23,42,.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 480, boxShadow: "0 24px 60px rgba(0,0,0,.2)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>Editar e Registar Reembolso</div>

            {/* Contexto da fatura */}
            <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 14px", marginBottom: 18, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 3 }}>
                Despesa em <b style={{ color: C.text }}>{modalRegistar.nome_emitente || "—"}</b>
              </div>
              <div style={{ fontSize: 12, color: C.textMid }}>
                Valor: <b style={{ color: C.text }}>{fmt(modalRegistar.valor_total)}</b>
                {modalRegistar.data_documento && <span> · {fmtDate(modalRegistar.data_documento)}</span>}
              </div>
              {modalRegistar.numero_documento && (
                <div style={{ fontSize: 11, fontFamily: "ui-monospace,monospace", color: C.blue, marginTop: 4 }}>{modalRegistar.numero_documento}</div>
              )}
              {(modalRegistar.contexto || {}).nome_no_assunto && (
                <div style={{ fontSize: 11, color: "#92640C", marginTop: 6 }}>
                  O agente detetou "<b>{(modalRegistar.contexto || {}).nome_no_assunto}</b>" — confirma abaixo ou escolhe o sócio certo.
                </div>
              )}
            </div>

            {/* Sócio -- prioridade é sempre escolher alguém já registado; só se
                não estiver na lista é que se preenche o nome à mão (e fica
                registado como sócio novo ao confirmar). */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Sócio *</div>
              <select
                value={socioEscolhido}
                onChange={e => setSocioEscolhido(e.target.value)}
                autoFocus
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" }}
              >
                <option value="" disabled>— Escolhe um sócio —</option>
                {(socios || []).filter(s => s.ativo).map(s => (
                  <option key={s.id} value={s.nome}>{s.nome}</option>
                ))}
                <option value={SOCIO_NOVO_SENTINEL}>➕ Novo colaborador (ainda não está no sistema)</option>
              </select>
            </div>

            {/* Nome/Email/IBAN -- só para registar alguém genuinamente novo;
                um sócio já existente não precisa de nada disto outra vez. */}
            {ehNovo && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Nome do Colaborador *</div>
                  <input
                    value={nomeRegistar}
                    onChange={e => setNomeRegistar(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box" }}
                    placeholder="Nome completo do colaborador"
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Email</div>
                  <input
                    value={emailRegistar}
                    onChange={e => setEmailRegistar(e.target.value)}
                    type="email"
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box" }}
                    placeholder="email@exemplo.com (opcional)"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>IBAN</div>
                  <input
                    value={ibanRegistar}
                    onChange={e => setIbanRegistar(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box", fontFamily: "ui-monospace,monospace" }}
                    placeholder="PT50 0000 0000 0000 0000 0000 0 (opcional)"
                  />
                  <div style={{ fontSize: 11, color: C.textMid, marginTop: 5 }}>
                    O colaborador pode ter vários IBANs. Podes adicionar mais na página de Sócios.
                  </div>
                </div>
              </>
            )}

            {/* Botões */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={fecharModal}
                style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, cursor: "pointer", color: C.textMid }}>
                Cancelar
              </button>
              <button
                disabled={!nomeFinal}
                onClick={async () => {
                  if (!nomeFinal) return;
                  const p = modalRegistar;
                  fecharModal();
                  setRespondendo(prev => ({ ...prev, [p.id]: true }));
                  try {
                    await apiFetch(`/api/pendentes/${p.id}/responder`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        resposta: "Reembolsar e registar",
                        nome_colaborador: nomeFinal,
                        ...(ehNovo ? {
                          email_colaborador: emailRegistar.trim() || undefined,
                          iban_colaborador:  ibanRegistar.trim()  || undefined,
                        } : {}),
                      }),
                    });
                    refreshCasos();
                  } finally {
                    setRespondendo(prev => ({ ...prev, [p.id]: false }));
                  }
                }}
                style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: nomeFinal ? C.blue : C.border, color: "#fff", fontSize: 13, fontWeight: 700, cursor: nomeFinal ? "pointer" : "not-allowed" }}>
                {ehNovo ? "Confirmar e Registar" : "Confirmar Sócio"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ─── Faturas ──────────────────────────────────────────────────────────────────
// Neutro por design — o tipo é informação de apoio, não precisa de uma cor
// própria por categoria (isso é que tornava a tabela "aos quadradinhos").
// Tipos de documento -- mapa único usado tanto na tabela de Faturas como nos
// cartões do Arquivo (antes eram dois mapas quase iguais, TIPO_DOC_CFG e
// TIPO_CFG, com cor/fundo idênticos e só o Arquivo com ícone). Neutro por
// design -- o ícone/label já distingue o tipo, não precisa de mais cor.
const TIPO_DOC_CFG = {
  Fatura_Fornecedor: { label: "Fatura Fornecedor", cor: C.textMid, bg: C.bg, bord: C.border, Icon: Receipt },
  Fatura_Cliente:    { label: "Fatura Cliente",    cor: C.textMid, bg: C.bg, bord: C.border, Icon: FileText },
  Recibo_Vencimento: { label: "Recibo Vencimento", cor: C.textMid, bg: C.bg, bord: C.border, Icon: Users },
  Recibo_Generico:   { label: "Recibo Genérico",   cor: C.textMid, bg: C.bg, bord: C.border, Icon: FileText },
  Extrato_Bancario:  { label: "Extrato Bancário",  cor: C.textMid, bg: C.bg, bord: C.border, Icon: Landmark },
};

const QUICK_FILTERS = [
  { id: "",             label: "Todas",           fn: () => true },
  { id: "sem_extrato",  label: "Aguarda extrato", fn: f => !f.reconciliada && !f.pago && !f.conciliacao_ignorada },
  { id: "pagas",        label: "Pagas",           fn: f => !!f.pago },
  { id: "sem_arquivo",  label: "Sem arquivo",     fn: f => !f.drive_ok || !f.sheets_ok },
  { id: "duplicados",   label: "Duplicados",      fn: f => !!f.possivel_duplicado },
  { id: "ignorados",    label: "Ignorados",       fn: f => !!f.conciliacao_ignorada },
  { id: "notas_credito", label: "Notas de Crédito", fn: f => !!f.eh_nota_credito },
];

// Menu de ações (⋮) por linha da tabela de Faturas — apagar, editar, enviar para fundo.
function FaturaAcoesMenu({ onApagar, onEditar, onEnviarFundo, onMarcarNotaCredito, ehNotaCredito, mostrarNotaCredito }) {
  const [aberto, setAberto] = useState(false);
  const [posicao, setPosicao] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    const fechar = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setAberto(false);
    };
    const fecharAoMoverPagina = () => setAberto(false);
    document.addEventListener("mousedown", fechar);
    window.addEventListener("resize", fecharAoMoverPagina);
    window.addEventListener("scroll", fecharAoMoverPagina, true);
    return () => {
      document.removeEventListener("mousedown", fechar);
      window.removeEventListener("resize", fecharAoMoverPagina);
      window.removeEventListener("scroll", fecharAoMoverPagina, true);
    };
  }, []);
  const opcoes = [
    { label: "Editar",             action: onEditar },
    { label: "Enviar para fundo",  action: onEnviarFundo },
    ...(mostrarNotaCredito ? [{ label: ehNotaCredito ? "Desmarcar Nota de Crédito" : "Marcar como Nota de Crédito", action: onMarcarNotaCredito }] : []),
    { label: "Apagar",             action: onApagar, danger: true },
  ];
  const alternar = () => {
    if (aberto) { setAberto(false); return; }
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const largura = 190;
    const alturaEstimada = opcoes.length * 38 + 8;
    const espacoAbaixo = window.innerHeight - rect.bottom;
    setPosicao({
      left: Math.max(8, Math.min(rect.right - largura, window.innerWidth - largura - 8)),
      top: espacoAbaixo >= alturaEstimada ? rect.bottom + 4 : Math.max(8, rect.top - alturaEstimada - 4),
    });
    setAberto(true);
  };
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={alternar}
        style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 6, fontSize: 16, color: C.textMid, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
        onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
        <MoreVertical size={16} />
      </button>
      {aberto && posicao && createPortal(
        <div ref={menuRef} style={{ position: "fixed", left: posicao.left, top: posicao.top, zIndex: 5000, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,.18)", width: 190, overflow: "hidden" }}>
          {opcoes.map(op => (
            <button key={op.label} onClick={() => { setAberto(false); op.action(); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 12.5, fontWeight: 500, border: "none", background: "none", cursor: "pointer", color: op.danger ? C.red : C.text }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
              {op.label}
            </button>
          ))}
        </div>, document.body
      )}
    </div>
  );
}

function ModalEditarFatura({ fatura, empresas, socios, onClose, onSaved }) {
  const [numero, setNumero] = useState(fatura.numero_documento || "");
  const [nome, setNome]     = useState(fatura.nome_emitente || "");
  const [nif, setNif]       = useState(fatura.nif_emitente || "");
  const [valor, setValor]   = useState(fatura.valor_total ?? "");
  const [data, setData]     = useState((fatura.data_documento || "").slice(0, 10));
  const [empresaNif, setEmpresaNif] = useState(fatura.nif_recetor || "");
  const [socio, setSocio]   = useState(fatura.nome_socio_reembolso || "");
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await apiFetch(`/api/faturas/${fatura.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          numero_documento: numero.trim(),
          nome_emitente: nome.trim(),
          nif_emitente: nif.trim(),
          valor_total: parseFloat(valor) || 0,
          data_documento: data || null,
          ...(empresaNif ? { nif_recetor: empresaNif } : {}),
          ...(fatura.eh_reembolso ? { nome_socio_reembolso: socio } : {}),
        }),
      });
      if (r.ok) { onSaved(); onClose(); }
      else alert("Erro ao guardar alterações.");
    } catch {
      alert("Erro de rede.");
    } finally {
      setGuardando(false);
    }
  };

  const campoStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3600, background: "rgba(15,23,42,.6)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 24, width: 420, maxWidth: "90vw", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>Editar fatura</div>
        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Nº Documento</div>
          <input value={numero} onChange={e => setNumero(e.target.value)} style={campoStyle} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Fornecedor / Emitente</div>
          <input value={nome} onChange={e => setNome(e.target.value)} style={campoStyle} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>NIF Emitente</div>
          <input value={nif} onChange={e => setNif(e.target.value)} style={campoStyle} />
        </div>
        {!!(empresas && empresas.length) && (
          <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>Empresa</div>
            <select value={empresaNif} onChange={e => setEmpresaNif(e.target.value)} style={campoStyle}>
              <option value="">Seleciona a empresa…</option>
              {empresas.map(e => <option key={e.nif} value={e.nif}>{e.nome}</option>)}
            </select>
            <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 4 }}>
              Só corrige a empresa no sistema — não move o ficheiro já arquivado no Drive.
            </div>
          </div>
        )}
        {!!fatura.eh_reembolso && (
          <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>Sócio / Colaborador (beneficiário do reembolso)</div>
            {socios && socios.length ? (
              <select value={socio} onChange={e => setSocio(e.target.value)} style={campoStyle}>
                <option value="">Seleciona o sócio/colaborador…</option>
                {socios.map(s => <option key={s.id} value={s.nome}>{s.nome}</option>)}
              </select>
            ) : (
              <input value={socio} onChange={e => setSocio(e.target.value)} placeholder="Nome de quem recebe o reembolso" style={campoStyle} />
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Valor (€)</div>
            <input type="number" value={valor} onChange={e => setValor(e.target.value)} style={campoStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Data</div>
            <input type="date" value={data} onChange={e => setData(e.target.value)} style={campoStyle} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", color: C.textMid, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <Btn onClick={guardar} disabled={guardando}>{guardando ? "A guardar…" : "Guardar"}</Btn>
        </div>
      </div>
    </div>
  );
}

function ModalEnviarFundo({ fatura, empresas, onClose, onSaved }) {
  const { data: fundos } = useApi(`/api/fundos?empresa=${fatura.nif_recetor}`, [fatura.nif_recetor]);
  const [fundoId, setFundoId]     = useState("");
  const [rubricaId, setRubricaId] = useState("");
  const [enviando, setEnviando]   = useState(false);
  const nomeEmpresa = (empresas || []).find(e => e.nif === fatura.nif_recetor)?.nome || fatura.nif_recetor;

  const fundoSel = (fundos || []).find(f => String(f.id) === String(fundoId));
  const campoStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: "#fff", boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };

  const enviar = async () => {
    if (!rubricaId) return;
    setEnviando(true);
    try {
      const r = await apiFetch(`/api/fundos/rubricas/${rubricaId}/despesas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tipo: "fatura", id: fatura.id }),
      });
      if (r.ok) { onSaved(); onClose(); }
      else alert("Erro ao associar ao fundo.");
    } catch {
      alert("Erro de rede.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3600, background: "rgba(15,23,42,.6)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 24, width: 400, maxWidth: "90vw", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>Enviar para fundo</div>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 18 }}>{fatura.numero_documento} · {fmt(fatura.valor_total)} · <strong>{nomeEmpresa}</strong></div>

        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Fundo</div>
          <select value={fundoId} onChange={e => { setFundoId(e.target.value); setRubricaId(""); }} style={campoStyle}>
            <option value="">Seleciona um fundo...</option>
            {(fundos || []).map(f => <option key={f.id} value={f.id}>{f.numero_fundo} — {f.nome}</option>)}
          </select>
          {fundos && fundos.length === 0 && (
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>
              A empresa <strong>{nomeEmpresa}</strong> (NIF {fatura.nif_recetor}) ainda não tem fundos criados. Os fundos só aparecem aqui se tiverem sido criados para esta empresa exata — confirma em Fundos qual é a empresa do fundo que criaste.
            </div>
          )}
        </div>

        {fundoSel && (
          <div style={{ marginBottom: 20 }}>
            <div style={labelStyle}>Rubrica</div>
            <select value={rubricaId} onChange={e => setRubricaId(e.target.value)} style={campoStyle}>
              <option value="">Seleciona uma rubrica...</option>
              {fundoSel.rubricas.map(r => <option key={r.id} value={r.id}>{r.nome} ({fmt(r.valor)})</option>)}
            </select>
            {fundoSel.rubricas.length === 0 && (
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>Este fundo ainda não tem rubricas.</div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", color: C.textMid, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <Btn onClick={enviar} disabled={!rubricaId || enviando}>{enviando ? "A associar…" : "Associar"}</Btn>
        </div>
      </div>
    </div>
  );
}

// Padrão AT (campo D do QR): números de Nota de Crédito começam por "NC".
// Usado para só oferecer a ação "Marcar como Nota de Crédito" em documentos
// que plausivelmente o sejam, em vez de aparecer em todas as faturas.
function pareceNotaCredito(numeroDocumento) {
  return /^NC\b/i.test((numeroDocumento || "").trim());
}

// Pede ao utilizador para escolher, de entre as faturas do MESMO fornecedor,
// qual é a que esta Nota de Crédito corrige — nunca aceita o número à mão,
// para não arriscar associações erradas por typo/formato diferente.
function ModalMarcarNotaCredito({ fatura, onClose, onSaved }) {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [aBuscar, setABuscar] = useState(true);
  const [aGuardar, setAGuardar] = useState(false);

  useEffect(() => {
    let ativo = true;
    setABuscar(true);
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ nif: fatura.nif_emitente, excluir_id: fatura.id });
        if (termo) qs.set("termo", termo);
        const r = await apiFetch(`/api/faturas/busca-associar?${qs.toString()}`);
        const data = r.ok ? await r.json() : [];
        if (ativo) setResultados(data);
      } catch {
        if (ativo) setResultados([]);
      } finally {
        if (ativo) setABuscar(false);
      }
    }, 300);
    return () => { ativo = false; clearTimeout(t); };
  }, [termo, fatura.nif_emitente, fatura.id]);

  const confirmar = async () => {
    if (!selecionada) return;
    setAGuardar(true);
    try {
      const r = await apiFetch(`/api/faturas/${fatura.id}/marcar-nota-credito`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eh_nota_credito: true, fatura_referenciada_id: selecionada.id }),
      });
      if (r.ok) { onSaved(); onClose(); }
      else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || "Erro ao marcar como Nota de Crédito.");
      }
    } catch {
      alert("Erro de rede.");
    } finally {
      setAGuardar(false);
    }
  };

  const campoStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3600, background: "rgba(15,23,42,.6)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 24, width: 440, maxWidth: "90vw", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>Marcar como Nota de Crédito</div>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 16 }}>{fatura.numero_documento} · {fmt(fatura.valor_total)} · <strong>{fatura.nome_emitente}</strong></div>

        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Qual fatura é que esta Nota de Crédito corrige?</div>
          <input value={termo} onChange={e => setTermo(e.target.value)} placeholder="Procurar por nº de documento…" style={campoStyle} autoFocus />
        </div>

        <div style={{ maxHeight: 220, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 16 }}>
          {aBuscar ? (
            <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: C.textLight }}>A procurar…</div>
          ) : resultados.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: C.textLight }}>
              Nenhuma fatura de <strong>{fatura.nome_emitente}</strong> encontrada{termo ? " com esse termo" : ""}.
            </div>
          ) : resultados.map(r => (
            <div key={r.id} onClick={() => setSelecionada(r)}
              style={{ padding: "9px 12px", borderBottom: `1px solid ${C.border}`, cursor: "pointer", background: selecionada?.id === r.id ? "#eff6ff" : "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, fontFamily: "ui-monospace, monospace" }}>{r.numero_documento}</div>
                <div style={{ fontSize: 11, color: C.textLight }}>{fmtDate(r.data_documento)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(r.valor_total)}</div>
                {selecionada?.id === r.id && <span style={{ color: C.blue, fontSize: 14 }}>✓</span>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", color: C.textMid, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <Btn onClick={confirmar} disabled={!selecionada || aGuardar}>{aGuardar ? "A guardar…" : "Confirmar"}</Btn>
        </div>
      </div>
    </div>
  );
}

// Encaixa cada Nota de Crédito logo a seguir à fatura original que corrige,
// para ficar visualmente óbvio a que fatura se refere. A associação é feita
// por fatura_referenciada_id (chave real, atribuída no backend — nunca por
// comparação de texto, que falharia com pequenas diferenças de formato).
// Notas de crédito sem associação (ainda por confirmar) ficam soltas na
// lista, marcadas com naoAssociada, em vez de seremencaixadas por adivinhação.
function agruparNotasCredito(lista) {
  const porId = new Map();
  lista.forEach(f => porId.set(f.id, f));

  const filhasPorId = new Map();
  const jaEncaixadas = new Set();
  lista.forEach(nc => {
    if (!nc.eh_nota_credito || !nc.fatura_referenciada_id) return;
    const pai = porId.get(nc.fatura_referenciada_id);
    if (pai && pai.id !== nc.id) {
      if (!filhasPorId.has(pai.id)) filhasPorId.set(pai.id, []);
      filhasPorId.get(pai.id).push(nc);
      jaEncaixadas.add(nc.id);
    }
  });

  const resultado = [];
  lista.forEach(f => {
    if (jaEncaixadas.has(f.id)) return; // aparece encaixada a seguir ao pai, não aqui
    const naoAssociada = !!f.eh_nota_credito && !f.fatura_referenciada_id;
    resultado.push({ ...f, aninhada: false, naoAssociada });
    const filhas = filhasPorId.get(f.id);
    if (filhas) filhas.forEach(nc => resultado.push({ ...nc, aninhada: true, naoAssociada: false }));
  });
  return resultado;
}

// Grupo de faturas — N faturas que chegaram juntas no mesmo documento de
// origem (ver grupos_documentos/GET /api/faturas/grupo/{id}), ex: extrato
// mensal Via Verde com uma fatura por operadora de portagem diferente.
// Reutilizado em Faturas, Arquivo e no painel de detalhe do documento — o
// mesmo grupo tem de aparecer igual em qualquer sítio onde é mostrado.
// Polimórfico -- um grupo pode ligar faturas_processadas (sempre foi assim,
// ver Via Verde/mesmo emitente) OU reembolsos (novo, só via gravação em
// lote do Processamento Assistido). O backend diz qual em data.origem; os
// dois têm campos diferentes (numero_documento/nome_emitente/valor_total
// vs descricao/colaborador/valor) mas a mesma ideia de "clica para ver o
// PDF partilhado do grupo".
function ModalGrupoFaturas({ grupoId, onClose }) {
  const { data, loading } = useApi(`/api/faturas/grupo/${grupoId}`, [grupoId]);
  const grupo = data?.grupo;
  const itens = data?.faturas || [];
  const ehReembolso = data?.origem === "reembolsos";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: C.surface, borderRadius: 16, padding: 28, width: 560, maxWidth: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.5)", border: `1px solid ${C.borderDark}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>🔗 {grupo?.descricao || "Grupo de documentos"}</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>
              {itens.length} {ehReembolso ? "reembolso(s)" : "fatura(s)"}{grupo?.valor_total != null ? ` · ${fmt(grupo.valor_total)} no total` : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textLight, flexShrink: 0 }}>✕</button>
        </div>

        {loading ? <Spinner /> : itens.length === 0 ? <Empty msg="Sem documentos neste grupo." /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {itens.map(f => ehReembolso ? (
              <div key={f.id}
                onClick={() => f.drive_link && openPDF(f.drive_link, f.descricao)}
                style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", cursor: f.drive_link ? "pointer" : "default" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{f.descricao}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt(f.valor)}</div>
                </div>
                <div style={{ fontSize: 12, color: C.textMid, marginTop: 3 }}>{f.colaborador}{f.nome_empresa ? ` · ${f.nome_empresa}` : ""}</div>
              </div>
            ) : (
              <div key={f.id}
                onClick={() => f.drive_link && openPDF(f.drive_link, f.numero_documento, { numero: f.numero_documento, nome: f.nome_emitente, data: f.data_documento, nif: f.nif_emitente, valor: f.valor_total })}
                style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", cursor: f.drive_link ? "pointer" : "default" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: "ui-monospace, monospace" }}>{f.numero_documento}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt(f.valor_total)}</div>
                </div>
                <div style={{ fontSize: 12, color: C.textMid, marginTop: 3 }}>{f.nome_emitente} · NIF {f.nif_emitente}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Arquivo Drive ────────────────────────────────────────────────────────────

// Geometria das colunas da tabela do Arquivo — fonte ÚNICA para o <colgroup>
// e para o <thead>, e o `align` de cada coluna é o mesmo que as células usam.
//
// Antes, a largura vivia no <th> e o alinhamento era decidido caso a caso no
// corpo; bastava alguém mudar um lado para o cabeçalho deixar de assentar
// sobre o conteúdo. Era o que acontecia em "Ações": cabeçalho à esquerda,
// botões à direita.
//
// "Ações" leva largura FIXA em px, não percentagem: o seu conteúdo (3 ícones
// + menu) tem uma largura real que não encolhe, e a 10% de 980px sobravam-lhe
// ~98px para ~109px de botões — daí saírem da célula. As percentagens das
// restantes somam 87%, deixando espaço para esses 132px sem o browser ter de
// as reescalar.
const ARQUIVO_COLUNAS = [
  { label: "Documento / Tipo", width: "21%",  align: "left" },
  { label: "Fornecedor",       width: "18%",  align: "left" },
  { label: "Data",             width: "10%",  align: "center" },
  { label: "Empresa",          width: "12%",  align: "left" },
  { label: "Total",            width: "10%",  align: "right" },
  { label: "Pagamento",        width: "10%",  align: "center" },
  { label: "Conciliação",      width: "12%",  align: "center" },
  { label: "Ações",            width: "140px", align: "center" },
];

export function documentoArquivoConcluido(f) {
  return !!(f.drive_ok && f.pago && (f.reconciliada || f.match_id || f.conciliacao_ignorada));
}

function PageArquivo() {
  const { empresa, empresas } = useEmpresa();
  const url = `/api/arquivo${empresa ? `?empresa=${empresa}` : ""}`;
  const { data: arquivo, loading, refresh: refreshArquivo } = useApi(url, [empresa]);
  const { data: sociosList } = useApi("/api/socios", [], []);
  const [sel, setSel] = useState({ empNif: null, periodo: null });
  // "processar" (default) = precisa de ação: anexo incompleto OU por pagar OU
  // por reconciliar. "historico" = arquivo fechado: anexo completo E pago E
  // reconciliado. Trocar de tab não limpa os outros filtros -- só limpa o
  // filtro rápido (fQuick), que só faz sentido dentro de "A Processar".
  const [tab, setTab] = useState("processar");
  const [pesquisa, setPesquisa] = useState("");
  const [fQuick, setFQuick] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fPago, setFPago] = useState("");
  const [fAnexo, setFAnexo] = useState("");
  const [fPeriodo, setFPeriodo] = useState("tudo");
  const [fDataDe, setFDataDe] = useState("");
  const [fDataAte, setFDataAte] = useState("");
  const [fValorMin, setFValorMin] = useState("");
  const [fValorMax, setFValorMax] = useState("");
  const [modalGrupo, setModalGrupo] = useState(null);
  const [ignorando, setIgnorando] = useState({});
  const [vistaArquivo, setVistaArquivo] = useState(() => localStorage.getItem("hub_arquivo_vista") || "lista");
  const [mostrarFiltrosArquivo, setMostrarFiltrosArquivo] = useState(false);
  const mudarVistaArquivo = (vista) => { setVistaArquivo(vista); localStorage.setItem("hub_arquivo_vista", vista); };

  // Trocar de tab troca o universo de documentos (A Processar vs Histórico)
  // -- um filtro válido num universo pode não fazer sentido nenhum no outro
  // (ex: "Estado de Pagamento: Pendente" não existe em Histórico, onde tudo
  // está sempre pago por definição) e ficava a filtrar silenciosamente para
  // uma pasta vazia, sem o utilizador perceber porquê. Limpa TODOS os
  // filtros ativos ao trocar, não só o filtro rápido.
  const mudarTab = (novaTab) => { setTab(novaTab); limparFiltros(); };
  const [modalEditar, setModalEditar] = useState(null);
  const [modalFundo, setModalFundo]   = useState(null);
  const [modalNotaCredito, setModalNotaCredito] = useState(null);

  // Pesquisa inteligente: se o que se escreveu é só um número (com vírgula
  // ou ponto, com ou sem €), procura pelo VALOR exacto do documento em vez de
  // texto — é o que um contabilista faz quando sabe o valor de uma despesa
  // mas não o nome exacto do fornecedor nem o número do documento.
  const pesquisaTrim = pesquisa.trim();
  const normalizarPesquisa = (valor) => String(valor ?? "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const textoValor = pesquisaTrim.replace(/[€\s]/g, "");
  const valorNormalizado = textoValor.includes(",")
    ? textoValor.replace(/\./g, "").replace(",", ".")
    : textoValor;
  const pesquisaValorNum = Number(valorNormalizado);
  const pesquisaEhValor = pesquisaTrim !== "" && Number.isFinite(pesquisaValorNum) && /^[\d.,\s€+-]+$/.test(pesquisaTrim);

  // O painel de definições (DocDetalheModal) resolve ações no documento sem
  // saber nada desta grelha — dispara este evento genérico para se atualizar.
  useEffect(() => {
    const onAtualizada = () => refreshArquivo();
    window.addEventListener("fatura-atualizada", onAtualizada);
    return () => window.removeEventListener("fatura-atualizada", onAtualizada);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ações rápidas (⋮) migradas de Faturas -- mesmo comportamento, só troca
  // refreshFaturas por refreshArquivo.
  const apagarFatura = async (fatura) => {
    if (!window.confirm(`Apagar a fatura ${fatura.numero_documento}? O PDF no Drive é movido para "Apagados", não é eliminado.`)) return;
    try {
      const r = await apiFetch(`/api/faturas/${fatura.id}`, { method: "DELETE" });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        refreshArquivo();
        const avisos = d?.avisos || {};
        const msgs = Object.values(avisos).filter(Boolean);
        if (msgs.length) {
          alert(`Fatura apagada da base de dados, mas com avisos:\n\n${msgs.join("\n")}`);
        }
      }
      else alert("Erro ao apagar fatura.");
    } catch {
      alert("Erro de rede.");
    }
  };

  const desmarcarNotaCredito = async (fatura) => {
    if (!window.confirm(`Desmarcar "${fatura.numero_documento}" como Nota de Crédito? O valor volta a positivo e a associação à fatura original é removida.`)) return;
    try {
      const r = await apiFetch(`/api/faturas/${fatura.id}/marcar-nota-credito`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eh_nota_credito: false }),
      });
      if (r.ok) refreshArquivo();
      else alert("Erro ao atualizar.");
    } catch {
      alert("Erro de rede.");
    }
  };

  // Estado de conciliação por cartão -- migrado de Faturas, que tinha isto
  // numa coluna dedicada sempre visível; no Arquivo não havia indicação
  // nenhuma no cartão (só no painel de detalhe, um clique extra).
  const desassociarConciliacao = async (f) => {
    if (!f.match_id) return;
    if (!window.confirm("Remover a associação ao extrato desta fatura? Se estava marcada como paga, volta a ficar por pagar.")) return;
    setIgnorando(prev => ({ ...prev, [f.id]: true }));
    try {
      await apiFetch(`/api/reconciliacao/matches/${f.match_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado: "rejeitado" }),
      });
      refreshArquivo();
    } catch {
      // falhou — utilizador pode tentar novamente
    } finally {
      setIgnorando(prev => ({ ...prev, [f.id]: false }));
    }
  };

  const reativarConciliacao = async (faturaId) => {
    setIgnorando(prev => ({ ...prev, [faturaId]: true }));
    try {
      await apiFetch(`/api/faturas/${faturaId}/reativar-conciliacao`, { method: "POST" });
      refreshArquivo();
    } catch {
      // falhou — utilizador pode tentar novamente
    } finally {
      setIgnorando(prev => ({ ...prev, [faturaId]: false }));
    }
  };

  // Árvore de navegação — abrir a primeira empresa/período por defeito
  useEffect(() => {
    if (arquivo?.length && !sel.empNif) {
      setSel({ empNif: arquivo[0].nif, periodo: arquivo[0].periodos?.[0]?.periodo || null });
    }
  }, [arquivo]);

  if (loading) return <div style={{ padding: 48, textAlign: "center" }}><Spinner /></div>;
  if (!arquivo?.length) return <Empty msg="Nenhum ficheiro no arquivo" />;

  const inpS ={ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: "#fff", outline: "none", cursor: "pointer" };
  const filtrosAtivos = !!(fTipo || fPago || fAnexo || fPeriodo !== "tudo" || fDataDe || fDataAte || fValorMin || fValorMax || pesquisa || fQuick);
  const limparFiltros = () => {
    setFTipo(""); setFPago(""); setFAnexo(""); setFPeriodo("tudo"); setFDataDe(""); setFDataAte("");
    setFValorMin(""); setFValorMax(""); setPesquisa(""); setFQuick("");
  };

  // Determinar ficheiros a mostrar
  const empAtual = arquivo.find(e => e.nif === sel.empNif);
  const todasPeriodos = sel.empNif && !sel.periodo
    ? (empAtual?.periodos || [])
    : sel.periodo
      ? [empAtual?.periodos?.find(p => p.periodo === sel.periodo)].filter(Boolean)
      : arquivo.flatMap(e => e.periodos);

  const todosFicheirosDaPasta = todasPeriodos.flatMap(p => (p.ficheiros || []).map(f => ({ ...f, periodo: p.periodo })));

  // Tabs -- "A Processar" (default) é tudo o que ainda precisa de alguma
  // ação; "Histórico" é só o que já está 100% fechado. Isto aplica-se ANTES
  // dos outros filtros -- trocar de tab troca o universo em que os outros
  // filtros trabalham, não substitui-os.
  //
  // "Fechado" exige sempre o anexo no Drive, mais UMA das duas frentes de
  // pagamento: casado com um movimento bancário real (reconciliada ou
  // match_id) OU pago fora do circuito bancário e explicitamente marcado
  // como tal (pago && conciliacao_ignorada -- ex: dinheiro, cartão pessoal,
  // "Confirmar sem linha do extrato" num batch). Sem o segundo ramo, um
  // pagamento manual ficava preso em "A Processar" para sempre à espera de
  // um extrato bancário que nunca vai existir.
  const naTab = (f) => {
    const concluido = documentoArquivoConcluido(f);
    return tab === "processar" ? !concluido : concluido;
  };

  // Contagens da Sidebar (Pastas/períodos) e do cabeçalho -- a API devolve
  // total_ficheiros sem noção nenhuma de tab (é o total absoluto do
  // arquivo, empresa ou período); sem recalcular aqui, mudar de tab só
  // mudava a grelha, nunca os números da árvore -- dava a entender que
  // "A Processar" tinha mais documentos nessa pasta do que os que lá
  // apareciam mesmo. Percorre o arquivo TODO (não só a pasta aberta),
  // porque a Sidebar mostra sempre todas as empresas/períodos.
  const totalFicheirosTab = arquivo.reduce(
    (s, e) => s + e.periodos.reduce((s2, p) => s2 + (p.ficheiros || []).filter(naTab).length, 0),
    0
  );
  const totalPorEmpresaTab = {};
  const totalPorPeriodoTab = {};
  arquivo.forEach(e => {
    let totalEmp = 0;
    e.periodos.forEach(p => {
      const n = (p.ficheiros || []).filter(naTab).length;
      totalPorPeriodoTab[`${e.nif}::${p.periodo}`] = n;
      totalEmp += n;
    });
    totalPorEmpresaTab[e.nif] = totalEmp;
  });

  // Período — presets (Este mês, Trimestre…) reaproveitam a mesma lógica de
  // datas de calendário já usada em Faturas/Análise; "custom" usa os campos
  // De/Até livres.
  const periodoCalc = fPeriodo === "custom" ? { de: fDataDe, ate: fDataAte } : calcularPeriodo(fPeriodo);
  const quickFn = QUICK_FILTERS.find(q => q.id === fQuick)?.fn || (() => true);

  const ficheirosDaTab = todosFicheirosDaPasta.filter(naTab);

  const ficheirosFiltrados = ficheirosDaTab.filter(f => {
    if (tab === "processar" && !quickFn(f)) return false;
    if (fTipo && f.tipo_documento !== fTipo) return false;
    // Ciclo de carteira MIIO fechado (saldo todo consumido/creditado) não
    // conta como "Pendente" nem como "Pago" — é um estado à parte, nunca
    // filtra por nenhum dos dois quando se escolhe um destes filtros.
    const cicloWalletFechado = f.eh_carteira_wallet && f.saldo_carteira_aberto === 0;
    if (fPago === "pago" && (!f.pago || cicloWalletFechado)) return false;
    if (fPago === "pendente" && (!!f.pago || cicloWalletFechado)) return false;
    if (fAnexo === "completo" && !(f.drive_ok && f.sheets_ok)) return false;
    if (fAnexo === "incompleto" && (f.drive_ok && f.sheets_ok)) return false;
    if (periodoCalc.de && (f.data_documento || "") < periodoCalc.de) return false;
    if (periodoCalc.ate && (f.data_documento || "") > periodoCalc.ate) return false;
    const valorAbs = Math.abs(f.valor_total || 0);
    if (fValorMin !== "" && valorAbs < parseFloat(fValorMin)) return false;
    if (fValorMax !== "" && valorAbs > parseFloat(fValorMax)) return false;
    if (pesquisaEhValor) {
      return Math.abs(valorAbs - Math.abs(pesquisaValorNum)) < 0.005;
    }
    if (pesquisaTrim) {
      const estadoPagamento = f.pago ? "pago" : "pendente";
      const estadoConciliacao = f.reconciliada ? "com extrato conciliada" : f.conciliacao_ignorada ? "ignorada" : "sem extrato";
      const tipoLabel = TIPO_DOC_CFG[f.tipo_documento]?.label || f.tipo_documento || "documento";
      const valor = Number(f.valor_total || 0);
      const partesData = String(f.data_documento || "").split("-");
      const nomeMes = ["", "janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"][Number(partesData[1])] || "";
      const termosDocumento = [
        f.numero_documento, tipoLabel, f.nome_emitente, f.nif_emitente,
        f.nome_empresa, f.nif_recetor, f.data_documento, fmtDate(f.data_documento),
        f.periodo, nomeMes, partesData[0], estadoPagamento, estadoConciliacao,
        valor.toFixed(2), valor.toFixed(2).replace(".", ","), fmt(valor),
      ];
      const conteudo = normalizarPesquisa(termosDocumento.join(" "));
      return normalizarPesquisa(pesquisaTrim).split(/\s+/).every(termo => conteudo.includes(termo));
    }
    return true;
  });
  // Aproxima cada Nota de Crédito da fatura que corrige na ordem de leitura da
  // grelha (mesma lógica usada em Faturas), para não ficarem espalhadas e
  // indistinguíveis no meio de dezenas de cartões iguais.
  const ficheirosMostrar = agruparNotasCredito(ficheirosFiltrados);

  const breadcrumb = [
    { label: "Arquivo", action: () => setSel({ empNif: null, periodo: null }) },
    ...(sel.empNif ? [{ label: empAtual?.nome || sel.empNif, action: () => setSel(s => ({ ...s, periodo: null })) }] : []),
    ...(sel.periodo ? [{ label: sel.periodo, action: null }] : []),
  ];

  return (
    <div>
      {/* ── Emails em revisão manual ── */}
      <RevisaoManualCard />

      {/* Header */}
      <PageHeading eyebrow="Documentos" title="Arquivo" description="Consulta, valida e acompanha todos os documentos contabilísticos." />

      {/* Barra única de trabalho: contexto, pesquisa, filtros e modo de vista. */}
      <div className="arq-commandbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: 10, marginBottom: 14, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: "0 1px 2px rgba(15,23,42,.04)" }}>
        <div style={{ display: "flex", gap: 4, padding: 3, borderRadius: 10, background: C.bg }}>
          {[{ id: "processar", label: "A processar" }, { id: "historico", label: "Histórico" }].map(t => (
            <button key={t.id} onClick={() => mudarTab(t.id)} style={{ padding: "8px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 750, cursor: "pointer", border: "none", background: tab === t.id ? "#fff" : "transparent", color: tab === t.id ? C.blue : C.textMid, boxShadow: tab === t.id ? "0 1px 3px rgba(15,23,42,.10)" : "none" }}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="arq-command-actions" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flex: 1 }}>
          <button onClick={() => setMostrarFiltrosArquivo(v => !v)} style={{ height: 36, display: "inline-flex", alignItems: "center", gap: 7, padding: "0 12px", borderRadius: 9, border: `1px solid ${filtrosAtivos ? C.blueBorder : C.border}`, background: filtrosAtivos ? C.blueLight : "#fff", color: filtrosAtivos ? C.blue : C.textMid, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            <SlidersHorizontal size={14} /> {mostrarFiltrosArquivo ? "Ocultar filtros" : "Filtros"}{filtrosAtivos ? " ativos" : ""}
          </button>
          <div style={{ height: 36, display: "flex", alignItems: "center", padding: 3, border: `1px solid ${C.border}`, borderRadius: 9, background: C.bg }}>
            <button aria-label="Vista em lista" title="Vista em lista" onClick={() => mudarVistaArquivo("lista")} style={{ width: 30, height: 28, display: "grid", placeItems: "center", border: "none", borderRadius: 6, background: vistaArquivo === "lista" ? "#fff" : "transparent", color: vistaArquivo === "lista" ? C.blue : C.textLight, boxShadow: vistaArquivo === "lista" ? "0 1px 2px rgba(15,23,42,.10)" : "none", cursor: "pointer" }}><List size={15} /></button>
            <button aria-label="Vista em cartões" title="Vista em cartões" onClick={() => mudarVistaArquivo("cards")} style={{ width: 30, height: 28, display: "grid", placeItems: "center", border: "none", borderRadius: 6, background: vistaArquivo === "cards" ? "#fff" : "transparent", color: vistaArquivo === "cards" ? C.blue : C.textLight, boxShadow: vistaArquivo === "cards" ? "0 1px 2px rgba(15,23,42,.10)" : "none", cursor: "pointer" }}><Grid2X2 size={14} /></button>
          </div>
        </div>
      </div>

      {/* ── Filtros rápidos — só fazem sentido em "A Processar" (atalhos para
          os filtros normais, mesmos 7 que existiam em Faturas) ── */}
      {tab === "processar" && mostrarFiltrosArquivo && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {QUICK_FILTERS.map(q => {
            const active = fQuick === q.id;
            const count = q.id === "" ? ficheirosDaTab.length : ficheirosDaTab.filter(q.fn).length;
            return (
              <button key={q.id} onClick={() => setFQuick(q.id)}
                style={{
                  padding: "6px 14px", borderRadius: 20, border: active ? `1px solid ${C.blueBorder}` : `1px solid ${C.border}`,
                  background: active ? C.blueLight : "#fff", color: active ? C.blue : C.textMid,
                  fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                {q.label}
                <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: active ? "#fff" : "#f1f5f9", color: active ? C.blue : C.textLight }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Barra de filtros avançados — trabalha dentro da pasta selecionada
          na árvore à esquerda (ou em "Todos os documentos"), para caçar uma
          despesa específica por tipo, período, ou faixa de valor. "Estado de
          Pagamento" e "Estado do Anexo" têm nomes explícitos de propósito —
          são dois conceitos diferentes (pago vs arquivado) que só de
          coincidência ambos se chamavam "Estado" antes desta fusão. ── */}
      {mostrarFiltrosArquivo && (
      <div className="arq-filtros" style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Período</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PERIODO_PRESETS.filter(p => p.id !== "ano-passado").map(p => (
              <button key={p.id} onClick={() => setFPeriodo(p.id === "tudo" ? "tudo" : p.id)}
                style={{ padding: "7px 12px", borderRadius: 20, border: fPeriodo === p.id ? `1px solid ${C.blueBorder}` : `1px solid ${C.border}`, background: fPeriodo === p.id ? C.blueLight : "#fff", color: fPeriodo === p.id ? C.blue : C.textMid, fontSize: 12.5, fontWeight: fPeriodo === p.id ? 700 : 500, cursor: "pointer" }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {fPeriodo === "custom" && (
          <>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>De</div>
              <input type="date" value={fDataDe} onChange={e => setFDataDe(e.target.value)} style={inpS} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Até</div>
              <input type="date" value={fDataAte} onChange={e => setFDataAte(e.target.value)} style={inpS} />
            </div>
          </>
        )}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Tipo</div>
          <select style={inpS} value={fTipo} onChange={e => setFTipo(e.target.value)}>
            <option value="">Todos</option>
            <option value="Fatura_Fornecedor">Fatura Fornecedor</option>
            <option value="Fatura_Cliente">Fatura Cliente</option>
            <option value="Recibo_Vencimento">Recibo Vencimento</option>
            <option value="Recibo_Generico">Recibo Genérico</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Estado de Pagamento</div>
          <select style={inpS} value={fPago} onChange={e => setFPago(e.target.value)}>
            <option value="">Todos</option>
            <option value="pago">Pago</option>
            <option value="pendente">Pendente</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Estado do Anexo</div>
          <select style={inpS} value={fAnexo} onChange={e => setFAnexo(e.target.value)}>
            <option value="">Todos</option>
            <option value="completo">Completo</option>
            <option value="incompleto">Incompleto</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Valor mín.</div>
          <input type="number" inputMode="decimal" placeholder="0,00" value={fValorMin} onChange={e => setFValorMin(e.target.value)} style={{ ...inpS, width: 90, cursor: "text" }} />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Valor máx.</div>
          <input type="number" inputMode="decimal" placeholder="—" value={fValorMax} onChange={e => setFValorMax(e.target.value)} style={{ ...inpS, width: 90, cursor: "text" }} />
        </div>
        {filtrosAtivos && (
          <button onClick={limparFiltros} style={{ ...inpS, color: C.textMid, fontSize: 12 }}>✕ Limpar filtros</button>
        )}
      </div>
      )}

      {/* Layout dois painéis */}
      <div className="arq-layout" style={{ display: "grid", gridTemplateColumns: "250px minmax(0, 1fr)", gap: 16, alignItems: "start" }}>

        {/* ── Painel esquerdo — árvore de pastas ── */}
        <div className="arq-pastas" style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,.05)" }}>
          {/* Header do painel */}
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, background: "#f8fafc" }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight }}>Pastas</span>
          </div>

          {/* Todas as empresas */}
          <button
            onClick={() => setSel({ empNif: null, periodo: null })}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "10px 16px", background: !sel.empNif ? C.blueLight : "transparent", border: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}
          >
            <FolderOpen size={15} color={!sel.empNif ? C.blue : C.textLight} />
            <span style={{ fontSize: 13, fontWeight: 600, color: !sel.empNif ? C.blue : C.text }}>Todos os documentos</span>
            <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: !sel.empNif ? C.blue : C.textLight, background: !sel.empNif ? C.blueLight : "#f1f5f9", border: `1px solid ${!sel.empNif ? C.blueBorder : C.border}`, borderRadius: 20, padding: "1px 7px" }}>{totalFicheirosTab}</span>
          </button>

          {/* Empresas e períodos */}
          {arquivo.map(emp => {
            const empActive = sel.empNif === emp.nif;
            return (
              <div key={emp.nif}>
                {/* Empresa */}
                <button
                  onClick={() => setSel({ empNif: emp.nif, periodo: null })}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "10px 16px", background: empActive && !sel.periodo ? C.blueLight : "transparent", border: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}
                >
                  <div style={{ width: 22, height: 22, borderRadius: 5, background: C.blue, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{emp.nome?.[0]}</div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: empActive && !sel.periodo ? C.blue : C.text, flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.nome}</span>
                  <span style={{ fontSize: 11, color: C.textLight, flexShrink: 0 }}>{totalPorEmpresaTab[emp.nif] || 0}</span>
                </button>

                {/* Períodos (só visíveis se empresa seleccionada) */}
                {empActive && emp.periodos.map(p => {
                  const perActive = sel.empNif === emp.nif && sel.periodo === p.periodo;
                  return (
                    <button
                      key={p.periodo}
                      onClick={() => setSel({ empNif: emp.nif, periodo: p.periodo })}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "8px 16px 8px 40px", background: perActive ? C.blueLight : "transparent", border: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}
                    >
                      <CalendarDays size={13} color={perActive ? C.blue : C.textLight} />
                      <span style={{ fontSize: 12, fontWeight: perActive ? 700 : 400, color: perActive ? C.blue : C.textMid, flex: 1, textAlign: "left" }}>{p.periodo}</span>
                      <span style={{ fontSize: 11, color: perActive ? C.blue : C.textLight, flexShrink: 0 }}>{totalPorPeriodoTab[`${emp.nif}::${p.periodo}`] || 0}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* ── Painel direito — documentos ── */}
        <div>
          {/* Pesquisa universal colocada junto dos resultados que controla. */}
          <div className="arq-magic-search" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, padding: "11px 14px", background: "#fff", border: `1px solid ${pesquisaTrim ? C.blueBorder : C.border}`, borderRadius: 12, boxShadow: pesquisaTrim ? "0 0 0 3px rgba(13,148,136,.07)" : "0 1px 2px rgba(15,23,42,.04)" }}>
            <div style={{ width: 36, height: 36, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 9, background: C.blueLight, color: C.blue }}><Search size={18} /></div>
            <label style={{ display: "block", minWidth: 0, flex: 1 }}>
              <span style={{ display: "block", marginBottom: 2, color: C.textLight, fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>Pesquisa universal</span>
              <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Fornecedor, NIF, documento, valor, data, empresa ou estado…" style={{ width: "100%", boxSizing: "border-box", padding: 0, border: "none", outline: "none", background: "transparent", color: C.text, fontSize: 14, fontWeight: 550 }} />
            </label>
            {pesquisaTrim && <>
              <span style={{ color: C.textLight, fontSize: 11.5, whiteSpace: "nowrap" }}>{ficheirosMostrar.length} resultado{ficheirosMostrar.length !== 1 ? "s" : ""}</span>
              <button onClick={() => setPesquisa("")} aria-label="Limpar pesquisa" title="Limpar pesquisa" style={{ width: 30, height: 30, display: "grid", placeItems: "center", padding: 0, border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface, color: C.textMid, cursor: "pointer" }}><X size={14} /></button>
            </>}
          </div>

          {/* Breadcrumb + info */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "10px 14px", minHeight: 42, boxSizing: "border-box", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12 }}>
            {breadcrumb.map((b, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {i > 0 && <span style={{ color: C.textLight, fontSize: 12 }}>›</span>}
                {b.action
                  ? <button onClick={b.action} style={{ fontSize: 12, fontWeight: 600, color: C.blue, background: "none", border: "none", cursor: "pointer", padding: 0 }}>{b.label}</button>
                  : <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{b.label}</span>}
              </span>
            ))}
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight }}>{ficheirosMostrar.length} documento{ficheirosMostrar.length !== 1 ? "s" : ""}</span>
          </div>

          {/* Grelha de documentos (cards) */}
          {ficheirosMostrar.length === 0 ? (
            <div style={{ padding: "56px 24px", textAlign: "center", background: "#fff", border: "1px solid #ecedf2", borderRadius: 12 }}>
              <FolderOpen size={32} color={C.textLight} style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Pasta vazia</div>
              <div style={{ fontSize: 12, color: C.textMid }}>Nenhum documento encontrado nesta pasta.</div>
            </div>
          ) : (
            vistaArquivo === "lista" ? (
              <div className="arquivo-tabela-wrap" style={{ overflowX: "auto", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.04)" }}>
                <table className="arquivo-tabela" style={{ width: "100%", minWidth: 1180, borderCollapse: "collapse", tableLayout: "fixed" }}>
                  {/* A geometria das colunas é declarada UMA vez, no colgroup,
                      e o thead só herda dela. Antes a largura vivia no <th> e
                      o alinhamento era decidido célula a célula, por isso o
                      cabeçalho e o corpo divergiam (o caso visível: "Ações"
                      alinhado à esquerda por cima de botões encostados à
                      direita). Ver ARQUIVO_COLUNAS. */}
                  <colgroup>
                    {ARQUIVO_COLUNAS.map((c) => <col key={c.label} style={{ width: c.width }} />)}
                  </colgroup>
                  <thead>
                    <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                      {ARQUIVO_COLUNAS.map((c) => (
                        <th key={c.label} scope="col" style={{ padding: "12px 12px", textAlign: c.align, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.055em", textTransform: "uppercase", color: C.textLight, whiteSpace: "nowrap" }}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ficheirosMostrar.map((f, i) => {
                      const cfgBase = TIPO_DOC_CFG[f.tipo_documento] || { cor: "#64748b", bg: "#f1f5f9", label: "Documento", Icon: FileText };
                      const cfg = f.eh_nota_credito
                        ? { cor: "#C0302A", bg: "#FEF0F0", label: "Nota de Crédito", Icon: cfgBase.Icon }
                        : cfgBase;
                      const TipoIcon = cfg.Icon || FileText;
                      const hasSheets = f.sheets_ok && f.sheets_id;
                      const aguardaExtrato = !!f.pago && !f.reconciliada && !f.conciliacao_ignorada;
                      const pagoParcial = !f.pago && (f.valor_pago || 0) > 0;
                      const rowBg = f.eh_nota_credito ? "#fffbfb" : f.possivel_duplicado ? "#fffdf7" : "#fff";
                      // overflow:hidden é o que impede um número de documento
                      // ou um nome de fornecedor comprido (ambos nowrap) de
                      // transbordar para a coluna do lado — com
                      // table-layout:fixed a célula não cresce, o conteúdo é
                      // que saía por cima. Cada célula trata do seu ellipsis.
                      const cellStyle = { padding: "11px 12px", borderBottom: i < ficheirosMostrar.length - 1 ? `1px solid ${C.border}` : "none", verticalAlign: "middle", overflow: "hidden" };
                      return (
                        <tr key={f.id || i} className="arquivo-row" style={{ background: rowBg }}>
                          <td style={cellStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.bg, border: `1px solid ${cfg.cor}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <TipoIcon size={15} color={cfg.cor} strokeWidth={2.2} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <button onClick={() => f.drive_link ? openPDF(f.drive_link, f.numero_documento, { numero: f.numero_documento, nome: f.nome_emitente, data: f.data_documento, nif: f.nif_emitente, valor: f.valor_total }) : openDetalhe(f.id)} title={f.drive_link ? "Ver PDF" : "Ver detalhe"} style={{ display: "block", maxWidth: "100%", padding: 0, border: "none", background: "none", color: C.text, fontSize: 12.5, lineHeight: 1.35, fontFamily: "monospace", fontWeight: 700, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: f.drive_link ? "underline" : "none", textDecorationColor: "#cbd5e1" }}>
                                  {f.aninhada && <span style={{ color: "#C0302A" }}>↳ </span>}{f.numero_documento || "—"}
                                </button>
                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2, overflow: "hidden" }}>
                                  <span style={{ color: cfg.cor, fontSize: 10.5, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cfg.label}</span>
                                  {!!f.possivel_duplicado && <span title="Possível duplicado" style={{ color: "#92640C", fontSize: 10 }}>⚠</span>}
                                  {!!f.eh_reembolso && <span title="Reembolso" style={{ color: "#5B21B6", fontSize: 10 }}>↩</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={cellStyle}>
                            <div title={f.nome_emitente || ""} style={{ fontSize: 12.5, fontWeight: 650, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nome_emitente || "—"}</div>
                            <div style={{ fontSize: 10.5, color: f.nif_emitente === PLACEHOLDER_NIF_DESCONHECIDO ? "#92640C" : C.textLight, marginTop: 2, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nif_emitente === PLACEHOLDER_NIF_DESCONHECIDO ? "⚠ NIF por confirmar" : (f.nif_emitente || "")}</div>
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center", fontSize: 12, color: C.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmtDate(f.data_documento)}</td>
                          <td style={cellStyle}>
                            <div title={f.nome_empresa || f.nif_recetor || ""} style={{ fontSize: 12, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nome_empresa || f.nif_recetor || "—"}</div>
                            {f.periodo && <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 2, fontFamily: "monospace" }}>{f.periodo}</div>}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right", fontSize: 13, fontWeight: 800, color: f.eh_nota_credito ? "#C0302A" : C.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt(f.valor_total)}</td>
                          <td style={{ ...cellStyle, textAlign: "center" }}>
                            <span style={{ display: "inline-block", padding: "4px 8px", borderRadius: 12, background: f.pago ? "#EDF7F1" : "#FEF8EC", color: f.pago ? "#157347" : "#92640C", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>{f.pago ? "Pago" : pagoParcial ? `Parcial · ${f.percentagem_paga}%` : "Pendente"}</span>
                            {f.pago && f.data_pagamento && <div style={{ fontSize: 9.5, color: C.textLight, marginTop: 2, whiteSpace: "nowrap" }}>{fmtDate(f.data_pagamento)}</div>}
                            {pagoParcial && <div style={{ fontSize: 9.5, color: C.textLight, marginTop: 2, whiteSpace: "nowrap" }}>{fmt(f.valor_pago)} pagos · {fmt(f.valor_em_divida)} em dívida</div>}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, color: aguardaExtrato ? "#92640C" : C.textMid, fontSize: 11.5, fontWeight: aguardaExtrato ? 700 : 500, whiteSpace: "nowrap" }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: f.reconciliada ? C.green : aguardaExtrato ? "#D9932A" : "#D1D5DB", flexShrink: 0 }} />
                              {f.reconciliada ? "Com extrato" : f.conciliacao_ignorada ? "Ignorada" : aguardaExtrato ? "Aguarda extrato" : "Sem extrato"}
                            </div>
                            {f.reconciliada && !!f.match_id && <button onClick={() => desassociarConciliacao(f)} disabled={!!ignorando[f.id]} style={{ marginTop: 2, padding: 0, border: "none", background: "none", color: C.textLight, fontSize: 9.5, cursor: ignorando[f.id] ? "not-allowed" : "pointer", textDecoration: "underline" }}>Desassociar</button>}
                            {f.conciliacao_ignorada && <button onClick={() => reativarConciliacao(f.id)} disabled={!!ignorando[f.id]} style={{ marginTop: 2, padding: 0, border: "none", background: "none", color: C.blue, fontSize: 9.5, cursor: ignorando[f.id] ? "not-allowed" : "pointer", textDecoration: "underline" }}>{ignorando[f.id] ? "…" : "Reativar"}</button>}
                          </td>
                          {/* Mesmo padding horizontal do <th> "Ações" (10px) e
                              mesmo alinhamento à direita — é o que põe o
                              cabeçalho exatamente por cima dos botões. */}
                          <td style={{ ...cellStyle, textAlign: "center" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 3, whiteSpace: "nowrap" }}>
                              <button onClick={() => openDetalhe(f.id)} aria-label={`Ver detalhe de ${f.numero_documento || "documento"}`} title="Ver detalhe" style={{ width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, background: "transparent", color: C.textMid, cursor: "pointer", padding: 0 }}><Eye size={14} /></button>
                              {f.drive_link && <button onClick={() => openPDF(f.drive_link, f.numero_documento)} aria-label={`Abrir PDF de ${f.numero_documento || "documento"}`} title="Abrir PDF" style={{ width: 25, height: 25, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 5, background: "transparent", color: C.blue, cursor: "pointer" }}><FileText size={13} /></button>}
                              {hasSheets && <button onClick={() => openSheets(f.sheets_id)} aria-label={`Abrir Sheets de ${f.numero_documento || "documento"}`} title="Abrir Sheets" style={{ width: 25, height: 25, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 5, background: "transparent", color: "#157347", cursor: "pointer" }}><Table2 size={13} /></button>}
                              <FaturaAcoesMenu
                                onApagar={() => apagarFatura(f)}
                                onEditar={() => setModalEditar(f)}
                                onEnviarFundo={() => setModalFundo(f)}
                                onMarcarNotaCredito={() => f.eh_nota_credito ? desmarcarNotaCredito(f) : setModalNotaCredito(f)}
                                ehNotaCredito={!!f.eh_nota_credito}
                                mostrarNotaCredito={!!f.eh_nota_credito || pareceNotaCredito(f.numero_documento)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className="arquivo-documentos arquivo-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(272px, 1fr))", gap: 14 }}>
              {ficheirosMostrar.map((f, i) => {
                const cfgBase = TIPO_DOC_CFG[f.tipo_documento] || { cor: "#64748b", bg: "#f1f5f9", label: "Documento", Icon: FileText };
                const cfg = f.eh_nota_credito
                  ? { cor: "#C0302A", bg: "#FEF0F0", label: "Nota de Crédito", Icon: cfgBase.Icon }
                  : cfgBase;
                const TipoIcon = cfg.Icon || FileText;
                const hasSheets = f.sheets_ok && f.sheets_id;
                // Paga mas ainda sem extrato casado -- a tag verde "Pago" já
                // dá a entender que o ciclo terminou; sem isto, "Sem extrato"
                // (ponto cinzento, discreto) não deixava claro que falta
                // mesmo uma ação (importar o extrato bancário) para o
                // documento sair de "A Processar".
                const aguardaExtrato = !!f.pago && !f.reconciliada && !f.conciliacao_ignorada;
                const pagoParcial = !f.pago && (f.valor_pago || 0) > 0;
                // Contorno de aviso -- NC ganha sempre a duplicado quando as
                // duas coincidem (é o aviso mais grave: valor errado no
                // sistema, não só "talvez repetido").
                const avisoBorder = f.eh_nota_credito ? "#F5C0BF" : f.possivel_duplicado ? "#F5DFA0" : "#e2e8f0";
                return (
                  <div key={i}
                    style={{ background: f.eh_nota_credito ? "#fffbfb" : "#fff", border: `1px solid ${avisoBorder}`, borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 3px rgba(0,0,0,.06)", transition: "box-shadow .18s, transform .18s" }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.10)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,.06)"; e.currentTarget.style.transform = "none"; }}>

                    {/* Barra de cor no topo */}
                    <div style={{ height: 4, background: cfg.cor }} />

                    {/* Cabeçalho: ícone + tipo + nº (linha própria, nunca cortado) + badges (linha própria) */}
                    <div style={{ padding: "12px 14px 10px", display: "flex", flexDirection: "column", gap: 7 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 9, background: cfg.bg, border: `1px solid ${cfg.cor}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <TipoIcon size={16} color={cfg.cor} strokeWidth={2.2} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: cfg.cor, whiteSpace: "nowrap" }}>{f.eh_nota_credito ? "↘ Nota de Crédito" : cfg.label}</div>
                          <div
                            onClick={() => f.drive_link && openPDF(f.drive_link, f.numero_documento, { numero: f.numero_documento, nome: f.nome_emitente, data: f.data_documento, nif: f.nif_emitente, valor: f.valor_total })}
                            style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: C.text, marginTop: 1, wordBreak: "break-word", cursor: f.drive_link ? "pointer" : "default", textDecoration: f.drive_link ? "underline" : "none", textDecorationColor: "#cbd5e1" }}
                            title={f.drive_link ? "Ver PDF" : undefined}
                          >
                            {f.aninhada && <span style={{ color: "#C0302A" }}>↳ </span>}
                            {f.numero_documento || "—"}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                          <span onClick={() => openDetalhe(f.id)} title="Ver detalhe" style={{ cursor: "pointer", color: C.textLight, fontSize: 13, padding: "0 4px" }}>ⓘ</span>
                          <FaturaAcoesMenu
                            onApagar={() => apagarFatura(f)}
                            onEditar={() => setModalEditar(f)}
                            onEnviarFundo={() => setModalFundo(f)}
                            onMarcarNotaCredito={() => f.eh_nota_credito ? desmarcarNotaCredito(f) : setModalNotaCredito(f)}
                            ehNotaCredito={!!f.eh_nota_credito}
                            mostrarNotaCredito={!!f.eh_nota_credito || pareceNotaCredito(f.numero_documento)}
                          />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {!!f.possivel_duplicado && <span style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#FEF8EC", color: "#92640C", border: "1px solid #F5DFA0", whiteSpace: "nowrap" }}>⚠ Possível duplicado</span>}
                        {!!f.eh_reembolso && <span style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#F3EEFA", color: "#5B21B6", border: "1px solid #D4C5EC", whiteSpace: "nowrap" }}>↩ Reembolso</span>}
                        {f.eh_carteira_wallet
                          ? (f.saldo_carteira_aberto > 0
                              ? <span title="Carregamento de carteira MIIO — saldo ainda por consumir, não é despesa em atraso" style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#EEF3FB", color: "#03363D", border: "1px solid #9FC5C1", whiteSpace: "nowrap" }}>💳 Saldo em carteira {fmt(f.saldo_carteira_aberto)}</span>
                              : <span title="Carregamento de carteira MIIO — saldo todo consumido/creditado, ciclo fechado" style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#EDF7F1", color: "#157347", border: "1px solid #A3D9B7", whiteSpace: "nowrap" }}>✓ Saldo consumido</span>)
                          : f.pago
                          ? <span style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#EDF7F1", color: "#157347", border: "1px solid #A3D9B7", whiteSpace: "nowrap" }}>✓ Pago</span>
                          : pagoParcial
                          ? <span title={`${fmt(f.valor_pago)} pagos · ${fmt(f.valor_em_divida)} em dívida`} style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#FEF8EC", color: "#92640C", border: "1px solid #F5DFA0", whiteSpace: "nowrap" }}>Parcial · {f.percentagem_paga}%</span>
                          : <span style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: "#FEF8EC", color: "#92640C", border: "1px solid #F5DFA0", whiteSpace: "nowrap" }}>Pendente</span>
                        }
                      </div>
                    </div>

                    {/* Emitente */}
                    <div style={{ padding: "0 14px 12px" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nome_emitente || "—"}</div>
                      {f.nif_emitente && <div style={{ fontSize: 11, color: C.textLight, marginTop: 2, fontFamily: "monospace" }}>NIF {f.nif_emitente}</div>}
                      {f.grupo_id && (
                        <div onClick={() => setModalGrupo(f.grupo_id)} title={f.grupo_descricao || "Faz parte de um conjunto de faturas"}
                          style={{ fontSize: 11, color: C.blue, marginTop: 4, fontWeight: 600, cursor: "pointer", textDecoration: "underline dashed" }}>
                          🔗 {labelGrupo(f)}
                        </div>
                      )}
                      {f.nif_emitente === PLACEHOLDER_NIF_DESCONHECIDO && (
                        <div onClick={() => openDetalhe(f.id)} title="O Gemini não identificou o NIF real do fornecedor — clica para corrigir"
                          style={{ fontSize: 11, color: "#92640C", marginTop: 4, fontWeight: 700, cursor: "pointer", textDecoration: "underline dashed" }}>
                          ⚠ NIF por confirmar
                        </div>
                      )}
                      {!!f.eh_nota_credito && (
                        f.fatura_referenciada_id
                          ? <div style={{ fontSize: 11, color: "#C0302A", marginTop: 4, fontWeight: 600 }}>Corrige a fatura {f.documento_referenciado}</div>
                          : <div onClick={() => openDetalhe(f.id)} style={{ fontSize: 11, color: "#C0302A", marginTop: 4, fontWeight: 600, cursor: "pointer", textDecoration: "underline dashed" }} title="Clica para ver detalhe e associar">⚠ Ainda não associada a nenhuma fatura</div>
                      )}
                    </div>

                    {/* Meta: data fatura + data pagamento ou período */}
                    <div style={{ display: "flex", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ flex: 1, padding: "9px 14px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight }}>Data</div>
                        <div style={{ fontSize: 12, color: C.text, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{fmtDate(f.data_documento)}</div>
                      </div>
                      {f.pago && f.data_pagamento ? (
                        <div style={{ flex: 1, padding: "9px 14px", borderLeft: "1px solid #f1f5f9" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight }}>Pago em</div>
                          <div style={{ fontSize: 12, color: "#157347", marginTop: 2, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtDate(f.data_pagamento)}</div>
                        </div>
                      ) : f.periodo ? (
                        <div style={{ flex: 1, padding: "9px 14px", borderLeft: "1px solid #f1f5f9" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight }}>Período</div>
                          <div style={{ fontSize: 12, color: C.textMid, marginTop: 2, fontFamily: "monospace" }}>{f.periodo}</div>
                        </div>
                      ) : null}
                    </div>

                    {/* Extrato bancário -- estado + ação inline, migrado da
                        coluna "Conciliação" de Faturas (antes só visível
                        abrindo o painel de detalhe). */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderBottom: "1px solid #f1f5f9", background: aguardaExtrato ? "#FEF8EC" : "transparent" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: aguardaExtrato ? 700 : 400, color: aguardaExtrato ? "#92640C" : C.textMid }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: f.reconciliada ? C.green : aguardaExtrato ? "#D9932A" : "#D1D5DB", flexShrink: 0 }} />
                        {f.reconciliada ? "Casada com extrato" : f.conciliacao_ignorada ? "Ignorada da conciliação" : aguardaExtrato ? "⚠ Aguarda Extrato" : "Sem extrato"}
                      </div>
                      {f.reconciliada && !!f.match_id && (
                        <button
                          onClick={() => desassociarConciliacao(f)}
                          disabled={!!ignorando[f.id]}
                          title="Fatura mal associada? Remove a ligação ao extrato"
                          style={{ width: 16, height: 16, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#fff", color: C.textLight, cursor: ignorando[f.id] ? "not-allowed" : "pointer", fontSize: 10, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
                        >
                          ✕
                        </button>
                      )}
                      {f.conciliacao_ignorada && (
                        <button
                          onClick={() => reativarConciliacao(f.id)}
                          disabled={!!ignorando[f.id]}
                          style={{ fontSize: 10.5, fontWeight: 600, color: C.blue, background: "none", border: "none", padding: 0, cursor: ignorando[f.id] ? "not-allowed" : "pointer", textDecoration: "underline" }}
                        >
                          {ignorando[f.id] ? "…" : "Reativar"}
                        </button>
                      )}
                    </div>

                    {/* Total */}
                    <div style={{ padding: "11px 14px", display: "flex", alignItems: "baseline", justifyContent: "space-between", flex: 1 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.textMid }}>Total</span>
                      <span style={{ fontSize: 21, fontWeight: 800, color: f.eh_nota_credito ? "#C0302A" : C.text, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.03em" }}>{fmt(f.valor_total)}</span>
                    </div>

                    {/* Ações */}
                    <div style={{ display: "flex", borderTop: "1px solid #f1f5f9" }}>
                      {f.drive_link
                        ? <button onClick={() => openPDF(f.drive_link, f.numero_documento)}
                            style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", border: "none", borderRight: hasSheets ? "1px solid #f1f5f9" : "none", background: "transparent", color: C.blue, fontSize: 11.5, fontWeight: 700, cursor: "pointer", transition: "background .12s" }}
                            onMouseEnter={e => e.currentTarget.style.background = C.blueLight}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <FileText size={13} strokeWidth={2.5} /> Abrir PDF
                          </button>
                        : <span style={{ flex: 1, textAlign: "center", padding: "10px 0", color: C.textLight, fontSize: 11 }}>sem PDF</span>}
                      {hasSheets &&
                        <button onClick={() => openSheets(f.sheets_id)}
                          style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", border: "none", background: "transparent", color: "#157347", fontSize: 11.5, fontWeight: 700, cursor: "pointer", transition: "background .12s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <Table2 size={13} strokeWidth={2.5} /> Sheets
                        </button>}
                    </div>
                  </div>
                );
              })}
            </div>
            )
          )}
        </div>
      </div>
      <style>{`
        .arquivo-row { transition: background-color .14s ease; }
        .arquivo-row:hover { background: #f8fafc !important; }
        .arquivo-tabela thead th { background: #f8fafc; }
        @media (min-width: 1101px) {
          .arq-pastas { position: sticky; top: 12px; }
        }
        @media (max-width: 1100px) {
          .arq-commandbar { align-items: stretch !important; flex-direction: column !important; }
          .arq-command-actions { justify-content: flex-start !important; }
          .arq-layout { grid-template-columns: 220px minmax(0, 1fr) !important; }
        }
        @media (max-width: 700px) {
          .arq-header { flex-direction: column !important; align-items: stretch !important; gap: 12px !important; }
          .arq-command-actions { align-items: stretch !important; flex-wrap: wrap !important; }
          .arq-search { width: 100% !important; }
          .arq-search input { width: 100% !important; box-sizing: border-box; }
          .arq-filtros { gap: 10px !important; }
          .arq-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {modalGrupo && (
        <ModalGrupoFaturas grupoId={modalGrupo} onClose={() => setModalGrupo(null)} />
      )}
      {modalEditar && (
        <ModalEditarFatura fatura={modalEditar} empresas={empresas} socios={sociosList} onClose={() => setModalEditar(null)} onSaved={refreshArquivo} />
      )}
      {modalFundo && (
        <ModalEnviarFundo fatura={modalFundo} empresas={empresas} onClose={() => setModalFundo(null)} onSaved={refreshArquivo} />
      )}
      {modalNotaCredito && (
        <ModalMarcarNotaCredito fatura={modalNotaCredito} onClose={() => setModalNotaCredito(null)} onSaved={refreshArquivo} />
      )}
    </div>
  );
}

// ─── Processamento Assistido (sandbox de extração para documentos atípicos) ──
// POST /api/extracao/analisar-documento nunca grava nada -- só devolve os
// dados em bruto para o utilizador rever/corrigir. A gravação definitiva só
// acontece quando se escolhe uma ação (POST /api/faturas ou /api/reembolsos,
// os mesmos endpoints normais de sempre).
const CONFIANCA_CFG = {
  alta:  { label: "Alta",  cor: C.green,  bg: C.greenLight,  bord: C.greenBorder },
  média: { label: "Média", cor: C.orange, bg: C.orangeLight, bord: C.orangeBorder },
  baixa: { label: "Baixa", cor: C.red,    bg: C.redLight,    bord: C.redBorder },
};

const ACAO_EXTRACAO_CFG = {
  criar_fatura:    { label: "Gravar como Fatura",    variant: "primary" },
  criar_reembolso: { label: "Gravar como Reembolso", variant: "secondary" },
  ignorar:         { label: "Ignorar / Descartar",   variant: "danger" },
};

function PageProcessamentoAssistido() {
  const { empresa, empresas } = useEmpresa();
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [analisando, setAnalisando] = useState(false);
  const [resultado, setResultado] = useState(null); // { dados_extraidos, confianca, acoes_sugeridas, documento_composto }
  const [form, setForm] = useState(null);           // campos editáveis (modo 1 documento)
  const [erro, setErro] = useState(null);
  const [aGravar, setAGravar] = useState(false);
  const inputRef = useRef(null);

  // Documento composto (ex: extrato Via Verde, várias faturas EDP no mesmo
  // PDF) -- formsGrupo substitui `form` por uma lista, uma entrada por
  // documento. destinoGrupo é um destino ÚNICO para o lote inteiro (ao
  // contrário do modo 1 documento, onde cada acção sugerida é o próprio
  // destino) -- gravar em lote como fatura E como reembolso ao mesmo tempo
  // não faz sentido, é sempre um ou outro.
  const [modoGrupo, setModoGrupo] = useState(false);
  const [formsGrupo, setFormsGrupo] = useState([]);
  const [destinoGrupo, setDestinoGrupo] = useState("faturas"); // "faturas" | "reembolsos"

  const campoVazioGrupo = () => ({
    tipo_documento: "Fatura_Fornecedor", numero_documento: "", nif_emitente: "",
    nome_emitente: "", nif_recetor: empresa || "", data_documento: "", valor_total: "",
  });

  const limpar = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setFile(null); setPdfUrl(null); setResultado(null); setForm(null); setErro(null);
    setModoGrupo(false); setFormsGrupo([]); setDestinoGrupo("faturas");
    if (inputRef.current) inputRef.current.value = "";
  };

  const escolherFicheiro = (f) => {
    if (!f) return;
    limpar();
    setFile(f);
    setPdfUrl(URL.createObjectURL(f));
  };

  const analisar = async () => {
    if (!file) return;
    setAnalisando(true);
    setErro(null);
    try {
      const fd = new FormData();
      fd.append("ficheiro", file);
      const r = await apiFetch("/api/extracao/analisar-documento", { method: "POST", body: fd });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setResultado(data);
        if (data.documento_composto) {
          // A IA já detetou vários documentos -- liga logo o modo grupo,
          // mas o utilizador continua a poder desligar (era só 1 mesmo) ou
          // adicionar mais blocos (a IA não apanhou todos).
          setModoGrupo(true);
          setFormsGrupo((data.dados_extraidos || []).map(d => ({ ...d, nif_recetor: d.nif_recetor || empresa || "" })));
        } else {
          // Pré-preenche com a empresa do contexto se o documento não
          // trouxer NIF recetor identificado -- mesmo espírito de ModalExtrato.
          setForm({ ...data.dados_extraidos, nif_recetor: data.dados_extraidos.nif_recetor || empresa || "" });
        }
      } else {
        setErro(data.detail || "Não foi possível analisar o documento.");
      }
    } catch {
      setErro("Erro de rede.");
    } finally {
      setAnalisando(false);
    }
  };

  const atualizarCampo = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  // Liga/desliga o modo grupo à mão -- o utilizador pode ligar mesmo que a
  // IA só tenha visto 1 documento (percebeu que há mais, a IA falhou) ou
  // desligar um documento_composto que afinal é só 1 (falso positivo).
  const alternarModoGrupo = () => {
    if (!modoGrupo) {
      setFormsGrupo(fg => fg.length ? fg : (form ? [form] : [campoVazioGrupo()]));
    } else {
      setForm(formsGrupo[0] || null);
    }
    setModoGrupo(m => !m);
  };

  const atualizarCampoGrupo = (idx, campo, valor) =>
    setFormsGrupo(fg => fg.map((f, i) => (i === idx ? { ...f, [campo]: valor } : f)));
  const adicionarBlocoGrupo = () => setFormsGrupo(fg => [...fg, campoVazioGrupo()]);
  const removerBlocoGrupo = (idx) => setFormsGrupo(fg => fg.filter((_, i) => i !== idx));

  // Sobe o MESMO PDF que já está no ecrã ao Drive, para um registo já
  // criado (fatura ou reembolso) -- chamado logo a seguir à criação, nunca
  // antes: o utilizador já tinha o ficheiro carregado na Split View, não
  // pode perder-se só porque o registo passou a existir na BD. Falhar aqui
  // não desfaz a criação -- o registo fica só por arquivar (drive_ok=0),
  // o mesmo estado "Pendente" que qualquer documento ainda não arquivado já
  // tem no Arquivo.
  const arquivarNoDrive = async (path) => {
    const fd = new FormData();
    fd.append("ficheiro", file);
    const r = await apiFetch(path, { method: "POST", body: fd });
    if (r.ok) return { ok: true };
    const d = await r.json().catch(() => ({}));
    return { ok: false, detail: d.detail || "erro desconhecido" };
  };

  const gravarComoFatura = async () => {
    setAGravar(true);
    try {
      const r = await apiFetch("/api/faturas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          numero_documento: form.numero_documento || "",
          nome_emitente: form.nome_emitente || "",
          nif_emitente: form.nif_emitente || "",
          nif_recetor: form.nif_recetor || "",
          valor_total: parseFloat(form.valor_total) || 0,
          data_documento: form.data_documento || null,
          tipo_documento: form.tipo_documento || "Fatura_Fornecedor",
          eh_nota_credito: !!form.eh_nota_credito,
          documento_referenciado: form.documento_referenciado || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Erro ao gravar fatura.");
        return;
      }
      const fatura = await r.json();
      const arquivo = await arquivarNoDrive(`/api/faturas/${fatura.id}/arquivar`);
      alert(arquivo.ok
        ? "Fatura gravada e arquivada no Drive."
        : `Fatura gravada, mas não foi possível arquivar o PDF no Drive (${arquivo.detail}). Fica como pendente de arquivo -- podes tentar de novo mais tarde a partir do Arquivo.`);
      limpar();
    } catch {
      alert("Erro de rede.");
    } finally {
      setAGravar(false);
    }
  };

  const gravarComoReembolso = async () => {
    setAGravar(true);
    try {
      const r = await apiFetch("/api/reembolsos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          colaborador: form.nome_emitente || "",
          data_despesa: form.data_documento || new Date().toISOString().slice(0, 10),
          descricao: form.numero_documento ? `Doc ${form.numero_documento}` : "Despesa por classificar",
          categoria: "outro",
          valor: parseFloat(form.valor_total) || 0,
          empresa_nif: form.nif_recetor || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Erro ao gravar reembolso.");
        return;
      }
      const reembolso = await r.json();
      const arquivo = await arquivarNoDrive(`/api/reembolsos/${reembolso.id}/arquivar`);
      alert(arquivo.ok
        ? "Reembolso gravado e comprovativo arquivado no Drive."
        : `Reembolso gravado, mas não foi possível arquivar o comprovativo no Drive (${arquivo.detail}).`);
      limpar();
    } catch {
      alert("Erro de rede.");
    } finally {
      setAGravar(false);
    }
  };

  const executarAcao = (acao) => {
    if (acao === "criar_fatura") return gravarComoFatura();
    if (acao === "criar_reembolso") return gravarComoReembolso();
    if (acao === "ignorar") return limpar();
  };

  // Gravação em lote (documento composto) -- cria N registos com o MESMO
  // grupo_id, depois um único upload ao Drive aplicado a todos (Upload
  // Inteligente: nunca um upload por documento).
  const gravarGrupo = async () => {
    if (!formsGrupo.length) return;
    setAGravar(true);
    try {
      const ehReembolso = destinoGrupo === "reembolsos";
      const endpointBase = ehReembolso ? "/api/reembolsos/lote" : "/api/faturas/lote";
      const chave = ehReembolso ? "reembolsos" : "faturas";
      const itens = formsGrupo.map(f => ehReembolso ? ({
        colaborador: f.nome_emitente || "",
        data_despesa: f.data_documento || new Date().toISOString().slice(0, 10),
        descricao: f.numero_documento ? `Doc ${f.numero_documento}` : "Despesa por classificar",
        categoria: "outro",
        valor: parseFloat(f.valor_total) || 0,
        empresa_nif: f.nif_recetor || null,
      }) : ({
        numero_documento: f.numero_documento || "",
        nome_emitente: f.nome_emitente || "",
        nif_emitente: f.nif_emitente || "",
        nif_recetor: f.nif_recetor || "",
        valor_total: parseFloat(f.valor_total) || 0,
        data_documento: f.data_documento || null,
        tipo_documento: f.tipo_documento || "Fatura_Fornecedor",
        eh_nota_credito: !!f.eh_nota_credito,
        documento_referenciado: f.documento_referenciado || null,
      }));

      const corpo = { [chave]: itens };
      const r = await apiFetch(endpointBase, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corpo),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Erro ao gravar o grupo.");
        return;
      }
      const { grupo_id } = await r.json();
      const arquivo = await arquivarNoDrive(`${endpointBase}/${grupo_id}/arquivar`);
      alert(arquivo.ok
        ? `Grupo de ${formsGrupo.length} documentos gravado e arquivado no Drive.`
        : `Grupo gravado, mas não foi possível arquivar o PDF no Drive (${arquivo.detail}). Fica como pendente de arquivo.`);
      limpar();
    } catch {
      alert("Erro de rede.");
    } finally {
      setAGravar(false);
    }
  };

  const campoStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: "#fff", boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 };
  const confCfg = resultado ? (CONFIANCA_CFG[resultado.confianca] || CONFIANCA_CFG.média) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 110px)" }}>
      <div style={{ marginBottom: 14, flexShrink: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>Processamento Assistido</h1>
        <p style={{ color: C.textMid, fontSize: 13, marginTop: 3 }}>
          Para documentos fora do normal — o agente extrai os dados, tu confirmas o destino. Nada fica gravado até escolheres uma ação.
        </p>
      </div>

      {!file ? (
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) escolherFicheiro(f); }}
          onClick={() => inputRef.current?.click()}
          style={{ flex: 1, border: `2px dashed ${C.border}`, borderRadius: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", background: "#fff" }}
        >
          <div style={{ fontSize: 40 }}>📄</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Arrasta um PDF para aqui, ou clica para escolher</div>
          <div style={{ fontSize: 12, color: C.textLight }}>Documentos que o agente automático não conseguiu classificar sozinho</div>
          <input ref={inputRef} type="file" accept="application/pdf" style={{ display: "none" }}
            onChange={e => escolherFicheiro(e.target.files?.[0])} />
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 420px", gap: 16, flex: 1, minHeight: 0 }}>
          {/* Esquerda: visualizador do PDF submetido (embebido, não é o
              PDFModal global -- este precisa de ficar lado a lado com o
              formulário, não a cobrir o ecrã todo) */}
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", background: "#525659", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "8px 14px", background: "#fff", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
              <button onClick={limpar} style={{ fontSize: 11, color: C.textMid, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", flexShrink: 0 }}>Trocar ficheiro</button>
            </div>
            <iframe src={pdfUrl} title="Pré-visualização do PDF" style={{ flex: 1, border: "none" }} />
          </div>

          {/* Direita: resultados da extração / formulário editável */}
          <div style={{ display: "flex", flexDirection: "column", overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 12, background: "#fff", padding: 18 }}>
            {!resultado && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
                {analisando ? <Spinner /> : (
                  <>
                    <div style={{ fontSize: 13, color: C.textMid, textAlign: "center" }}>Pronto a analisar este documento.</div>
                    <Btn onClick={analisar}>Analisar documento</Btn>
                  </>
                )}
                {erro && (
                  <div style={{ padding: "9px 12px", borderRadius: 8, fontSize: 12, background: C.redLight, color: C.red, border: `1px solid ${C.redBorder}` }}>{erro}</div>
                )}
              </div>
            )}

            {resultado && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 12.5, fontWeight: 600, color: C.text, cursor: "pointer" }}>
                  <input type="checkbox" checked={modoGrupo} onChange={alternarModoGrupo} />
                  Documento Múltiplo / Agrupar
                </label>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Dados extraídos</div>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: confCfg.bg, color: confCfg.cor, border: `1px solid ${confCfg.bord}` }}>
                    Confiança {confCfg.label}
                  </span>
                </div>

                {modoGrupo ? (
                  <>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {formsGrupo.map((f, idx) => (
                        <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.bg }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <div style={{ fontSize: 10.5, fontWeight: 800, color: C.textMid, textTransform: "uppercase", letterSpacing: 0.5 }}>Documento {idx + 1}</div>
                            <button onClick={() => removerBlocoGrupo(idx)} style={{ fontSize: 11, color: C.red, background: "none", border: "none", cursor: "pointer" }}>✕ remover</button>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <div>
                                <div style={labelStyle}>Tipo</div>
                                <select value={f.tipo_documento || ""} onChange={e => atualizarCampoGrupo(idx, "tipo_documento", e.target.value)} style={campoStyle}>
                                  <option value="Fatura_Fornecedor">Fatura Fornecedor</option>
                                  <option value="Fatura_Cliente">Fatura Cliente</option>
                                  <option value="Recibo_Generico">Recibo Genérico</option>
                                  <option value="Desconhecido">Desconhecido</option>
                                </select>
                              </div>
                              <div>
                                <div style={labelStyle}>Nº Documento</div>
                                <input value={f.numero_documento || ""} onChange={e => atualizarCampoGrupo(idx, "numero_documento", e.target.value)} style={campoStyle} />
                              </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <div>
                                <div style={labelStyle}>Fornecedor / Emitente</div>
                                <input value={f.nome_emitente || ""} onChange={e => atualizarCampoGrupo(idx, "nome_emitente", e.target.value)} style={campoStyle} />
                              </div>
                              <div>
                                <div style={labelStyle}>NIF Emitente</div>
                                <input value={f.nif_emitente || ""} onChange={e => atualizarCampoGrupo(idx, "nif_emitente", e.target.value)} style={campoStyle} />
                              </div>
                            </div>
                            <div>
                              <div style={labelStyle}>Empresa (recetor)</div>
                              <select value={f.nif_recetor || ""} onChange={e => atualizarCampoGrupo(idx, "nif_recetor", e.target.value)} style={campoStyle}>
                                <option value="">— Selecionar —</option>
                                {(empresas || []).map(e => <option key={e.nif} value={e.nif}>{e.nome}</option>)}
                              </select>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <div>
                                <div style={labelStyle}>Data</div>
                                <input type="date" value={f.data_documento || ""} onChange={e => atualizarCampoGrupo(idx, "data_documento", e.target.value)} style={campoStyle} />
                              </div>
                              <div>
                                <div style={labelStyle}>Valor (€)</div>
                                <input type="number" step="0.01" value={f.valor_total ?? ""} onChange={e => atualizarCampoGrupo(idx, "valor_total", e.target.value)} style={campoStyle} />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button onClick={adicionarBlocoGrupo}
                      style={{ marginTop: 10, padding: "8px 0", borderRadius: 8, border: `1px dashed ${C.border}`, background: "none", color: C.blue, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                      + Adicionar {destinoGrupo === "reembolsos" ? "reembolso" : "fatura"} a este grupo
                    </button>

                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                      <div style={labelStyle}>Destino</div>
                      <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text, cursor: "pointer" }}>
                          <input type="radio" name="destinoGrupo" checked={destinoGrupo === "faturas"} onChange={() => setDestinoGrupo("faturas")} /> Faturas
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text, cursor: "pointer" }}>
                          <input type="radio" name="destinoGrupo" checked={destinoGrupo === "reembolsos"} onChange={() => setDestinoGrupo("reembolsos")} /> Reembolsos
                        </label>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <Btn disabled={aGravar || !formsGrupo.length} onClick={gravarGrupo}>
                          {aGravar ? "A gravar…" : "Gravar Grupo"}
                        </Btn>
                        <Btn variant="danger" disabled={aGravar} onClick={limpar}>Ignorar / Descartar</Btn>
                      </div>
                    </div>
                  </>
                ) : form && (
                  <>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div>
                        <div style={labelStyle}>Tipo de documento</div>
                        <select value={form.tipo_documento || ""} onChange={e => atualizarCampo("tipo_documento", e.target.value)} style={campoStyle}>
                          <option value="Fatura_Fornecedor">Fatura Fornecedor</option>
                          <option value="Fatura_Cliente">Fatura Cliente</option>
                          <option value="Recibo_Generico">Recibo Genérico</option>
                          <option value="Extrato_Bancario">Extrato Bancário</option>
                          <option value="Desconhecido">Desconhecido</option>
                        </select>
                      </div>
                      <div>
                        <div style={labelStyle}>Nº Documento</div>
                        <input value={form.numero_documento || ""} onChange={e => atualizarCampo("numero_documento", e.target.value)} style={campoStyle} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <div style={labelStyle}>Fornecedor / Emitente</div>
                          <input value={form.nome_emitente || ""} onChange={e => atualizarCampo("nome_emitente", e.target.value)} style={campoStyle} />
                        </div>
                        <div>
                          <div style={labelStyle}>NIF Emitente</div>
                          <input value={form.nif_emitente || ""} onChange={e => atualizarCampo("nif_emitente", e.target.value)} style={campoStyle} />
                        </div>
                      </div>
                      <div>
                        <div style={labelStyle}>Empresa (recetor)</div>
                        <select value={form.nif_recetor || ""} onChange={e => atualizarCampo("nif_recetor", e.target.value)} style={campoStyle}>
                          <option value="">— Selecionar —</option>
                          {(empresas || []).map(e => <option key={e.nif} value={e.nif}>{e.nome}</option>)}
                        </select>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <div style={labelStyle}>Data</div>
                          <input type="date" value={form.data_documento || ""} onChange={e => atualizarCampo("data_documento", e.target.value)} style={campoStyle} />
                        </div>
                        <div>
                          <div style={labelStyle}>Valor Total (€)</div>
                          <input type="number" step="0.01" value={form.valor_total ?? ""} onChange={e => atualizarCampo("valor_total", e.target.value)} style={campoStyle} />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                      {(resultado.acoes_sugeridas || []).map(acao => {
                        const cfg = ACAO_EXTRACAO_CFG[acao] || { label: acao, variant: "secondary" };
                        return (
                          <Btn key={acao} variant={cfg.variant} disabled={aGravar} onClick={() => executarAcao(acao)}>
                            {aGravar ? "A gravar…" : cfg.label}
                          </Btn>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fecho Mensal ─────────────────────────────────────────────────────────────
function NotasInput({ nif, periodo, valorInicial, onSave }) {
  const [notas, setNotas] = useState(valorInicial || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotas(valorInicial || ""); }, [valorInicial, nif, periodo]);
  const save = async () => { setSaving(true); await onSave(nif, notas); setSaving(false); };
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Notas</div>
      <textarea
        value={notas}
        onChange={e => setNotas(e.target.value)}
        onBlur={save}
        placeholder="Observações para este mês..."
        rows={2}
        style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, resize: "vertical", fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
      />
      {saving && <div style={{ fontSize: 11, color: C.textLight }}>A guardar...</div>}
    </div>
  );
}

function PageFechoMensal({ empresa }) {
  const hoje = new Date();
  const [ano, setAno]   = useState(hoje.getFullYear());
  const [mes, setMes]   = useState(hoje.getMonth() + 1);
  const periodo = `${ano}-${String(mes).padStart(2, "0")}`;

  const url = `/api/fecho-mensal?periodo=${periodo}${empresa ? `&empresa=${empresa}` : ""}`;
  const { data: fechos, loading, refresh } = useApi(url, [empresa, periodo], []);

  const patchFecho = async (nif, body) => {
    await apiFetch(`/api/fecho-mensal/${nif}/${periodo}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    refresh();
  };

  const prevMes = () => { if (mes === 1) { setMes(12); setAno(a => a - 1); } else setMes(m => m - 1); };
  const nextMes = () => {
    const max = new Date(); if (ano < max.getFullYear() || mes < max.getMonth() + 1)
    { if (mes === 12) { setMes(1); setAno(a => a + 1); } else setMes(m => m + 1); }
  };

  const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const CHECK = [
    { campo: "iva_declarado",    label: "IVA declarado na AT",        icon: "📋" },
    { campo: "ss_pago",          label: "Segurança Social paga",      icon: "🏥" },
    { campo: "irs_entregue",     label: "IRS retido entregue à AT",   icon: "💸" },
    { campo: "extrato_recebido", label: "Extrato bancário recebido",  icon: "🏦" },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>Fecho Mensal</h1>
          <p style={{ color: C.textMid, fontSize: 13, marginTop: 4 }}>Completude de documentos e declarações por empresa</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "6px 10px" }}>
          <button onClick={prevMes} style={{ background: "none", border: "none", cursor: "pointer", color: C.text, fontSize: 18, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6 }}>‹</button>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text, minWidth: 148, textAlign: "center" }}>{MESES[mes - 1]} {ano}</span>
          <button onClick={nextMes} style={{ background: "none", border: "none", cursor: "pointer", color: C.text, fontSize: 18, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6 }}>›</button>
        </div>
      </div>

      {loading ? <Spinner /> : !fechos?.length ? <Empty>Sem empresas activas.</Empty> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
          {fechos.map(f => {
            const done  = Object.values(f.checklist).filter(Boolean).length;
            const pct   = Math.round((done / 4) * 100);
            const fechado = f.estado === "fechado";
            const topColor = fechado ? C.green : pct === 100 ? C.green : pct >= 50 ? C.orange : C.red;

            return (
              <Card key={f.empresa_nif} style={{ borderTop: `3px solid ${topColor}` }}>
                {/* Cabeçalho */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{f.empresa_nome}</div>
                    <div style={{ fontSize: 11, color: C.textLight, fontFamily: "monospace" }}>NIF: {f.empresa_nif}</div>
                  </div>
                  <Pill color={fechado ? "green" : "orange"}>{fechado ? "✓ Fechado" : "Em curso"}</Pill>
                </div>

                {/* Stats de documentos */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    { label: "Documentos",  value: f.total_documentos,     color: C.text },
                    { label: "Completos",   value: f.documentos_completos,  color: f.documentos_completos === f.total_documentos && f.total_documentos > 0 ? C.green : C.text },
                    { label: "Valor",       value: fmt(f.valor_total),      color: C.blue },
                  ].map(s => (
                    <div key={s.label} style={{ background: C.bg, borderRadius: 8, padding: "10px 10px", border: `1px solid ${C.border}`, textAlign: "center" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.textLight, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 4 }}>{s.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* Barra de progresso declarações */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.textMid }}>Declarações</span>
                    <span style={{ fontSize: 11, color: C.textMid }}>{done}/4</span>
                  </div>
                  <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden", border: `1px solid ${C.border}` }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: topColor, borderRadius: 3, transition: "width .3s" }} />
                  </div>
                </div>

                {/* Checklist */}
                <div style={{ marginBottom: 4 }}>
                  {CHECK.map(item => (
                    <button
                      key={item.campo}
                      onClick={() => patchFecho(f.empresa_nif, { [item.campo]: !f.checklist[item.campo] })}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", background: "none", border: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}`, textAlign: "left" }}
                    >
                      <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${f.checklist[item.campo] ? C.green : C.borderDark}`, background: f.checklist[item.campo] ? C.green : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: ".15s" }}>
                        {f.checklist[item.campo] && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 13, color: f.checklist[item.campo] ? C.textLight : C.text, textDecoration: f.checklist[item.campo] ? "line-through" : "none" }}>{item.icon} {item.label}</span>
                    </button>
                  ))}
                </div>

                <NotasInput nif={f.empresa_nif} periodo={periodo} valorInicial={f.notas} onSave={(nif, notas) => patchFecho(nif, { notas })} />

                <Btn
                  variant={fechado ? "secondary" : "success"}
                  onClick={() => patchFecho(f.empresa_nif, { estado: fechado ? "em_curso" : "fechado" })}
                  style={{ width: "100%", marginTop: 14 }}
                >
                  {fechado ? "↩ Reabrir mês" : "✓ Fechar este mês"}
                </Btn>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Helpers partilhados ─────────────────────────────────────────────────────
const MESES_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const relDate = (iso) => {
  if (!iso) return "—";
  const d = Math.round((Date.now() - new Date(iso)) / 86400000);
  if (d === 0) return "hoje";
  if (d === 1) return "ontem";
  if (d <= 7)  return `há ${d} dias`;
  if (d <= 30) return `há ${Math.round(d/7)} sem.`;
  return new Date(iso).toLocaleDateString("pt-PT", {day:"2-digit",month:"short"});
};

const urgClass = (dias) =>
  dias < 0 ? "red" : dias < 4 ? "red" : dias < 8 ? "orange" : dias < 15 ? "yellow" : "blue";

function PageAgenda({ onSelectEmpresa }) {
  const hoje = new Date();
  const [ano, setAno]   = useState(hoje.getFullYear());
  const [mes, setMes]   = useState(hoje.getMonth() + 1);
  const periodo = `${ano}-${String(mes).padStart(2,"0")}`;

  const { data, loading, refresh } = useApi(`/api/agenda?periodo=${periodo}`, [periodo], null);

  const patchFecho = async (nif, body) => {
    await apiFetch(`/api/fecho-mensal/${nif}/${periodo}`, {
      method: "PATCH", headers: {"Content-Type":"application/json"}, body: JSON.stringify(body),
    });
    refresh();
  };

  const prevMes = () => { if (mes===1){setMes(12);setAno(a=>a-1);}else setMes(m=>m-1); };
  const nextMes = () => {
    if (ano < hoje.getFullYear() || (ano===hoje.getFullYear() && mes < hoje.getMonth()+1))
      { if (mes===12){setMes(1);setAno(a=>a+1);}else setMes(m=>m+1); }
  };

  const obrigacoes = data?.obrigacoes || [];
  const empresas   = data?.empresas   || [];

  // Urgência da cor do badge de dias restantes
  const diasColor = { red: C.red, orange: C.orange, yellow: C.yellow, blue: C.blue };
  const diasBg    = { red: C.redLight, orange: C.orangeLight, yellow: C.yellowLight, blue: C.blueLight };
  const diasBorder= { red: C.redBorder, orange: C.orangeBorder, yellow: C.yellowBorder, blue: C.blueBorder };

  const CHECKLIST_MAP = {
    iva_declarado:    { label: "IVA",     icone: "📋", cor: "#5B21B6" },
    ss_pago:          { label: "SS",      icone: "🏥", cor: "#0891b2" },
    irs_entregue:     { label: "IRS",     icone: "💸", cor: "#C0302A" },
    extrato_recebido: { label: "Extrato", icone: "🏦", cor: "#059669" },
  };

  return (
    <div>
      {/* ── Cabeçalho ── */}
      <div style={{ marginBottom: 24, display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:C.text, margin:0 }}>Agenda Fiscal</h1>
          <p style={{ color:C.textMid, fontSize:13, marginTop:4 }}>Obrigações fiscais e estado mensal das empresas</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:4, background:"#fff", border:`1px solid ${C.border}`, borderRadius:10, padding:"6px 10px" }}>
          <button onClick={prevMes} style={{ background:"none", border:"none", cursor:"pointer", color:C.text, fontSize:20, width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:6 }}>‹</button>
          <span style={{ fontSize:14, fontWeight:700, color:C.text, minWidth:152, textAlign:"center" }}>{MESES_PT[mes-1]} {ano}</span>
          <button onClick={nextMes} style={{ background:"none", border:"none", cursor:"pointer", color:C.text, fontSize:20, width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:6 }}>›</button>
        </div>
      </div>

      {/* ── Calendário fiscal ── */}
      <div style={{ marginBottom:28 }}>
        <Label>Próximas obrigações fiscais — 90 dias</Label>
        {obrigacoes.length === 0 ? (
          <Empty msg="Nenhuma obrigação nos próximos 90 dias." />
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {obrigacoes.map(o => {
              const uc = urgClass(o.dias);
              const overdue = o.dias < 0;
              return (
                <div key={o.id} style={{
                  display:"flex", alignItems:"center", gap:14,
                  padding:"12px 16px", borderRadius:10, background:"#fff",
                  border:`1px solid ${overdue ? C.redBorder : C.border}`,
                  borderLeft:`4px solid ${o.cor}`,
                }}>
                  <span style={{ fontSize:18, flexShrink:0 }}>{o.icone}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{o.tipo}</div>
                    <div style={{ fontSize:11, color:C.textMid, marginTop:2 }}>{o.desc}</div>
                  </div>
                  <div style={{ fontSize:11, color:C.textLight, textAlign:"right", flexShrink:0 }}>
                    {new Date(o.due).toLocaleDateString("pt-PT",{day:"2-digit",month:"short",year:"numeric"})}
                  </div>
                  <div style={{
                    padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:700,
                    background: diasBg[uc], border:`1px solid ${diasBorder[uc]}`, color: diasColor[uc],
                    flexShrink:0, minWidth:70, textAlign:"center",
                  }}>
                    {overdue ? `${Math.abs(o.dias)}d atraso` : o.dias === 0 ? "hoje" : `${o.dias}d`}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Estado das empresas ── */}
      <Label>{MESES_PT[mes-1]} {ano} — {empresas.length} empresas</Label>
      {loading ? <Spinner /> : empresas.length === 0 ? <Empty msg="Sem empresas activas." /> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:20 }}>
          {empresas.map(e => {
            const fechado = e.fecho_estado === "fechado";
            const semDocs = e.n_docs === 0;
            const topColor = fechado ? C.green : semDocs ? C.red : C.blue;
            const tend = e.tendencia;
            const temTipos = (e.n_receitas + e.n_compras + e.n_salarios) > 0;

            return (
              <Card key={e.nif} style={{ borderTop:`3px solid ${topColor}` }}>
                {/* Cabeçalho */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:38, height:38, borderRadius:9, background:topColor, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:15, fontWeight:800 }}>
                      {(e.nome||"?")[0]}
                    </div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{e.nome}</div>
                      <div style={{ fontSize:10, color:C.textLight, fontFamily:"monospace" }}>NIF {e.nif}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <Pill color={fechado ? "green" : semDocs ? "red" : "blue"}>{fechado ? "✓ Fechado" : semDocs ? "Sem docs" : "Em curso"}</Pill>
                    <button onClick={() => onSelectEmpresa && onSelectEmpresa(e.nif)} title="Dashboard desta empresa"
                      style={{ padding:"4px 8px", borderRadius:6, border:`1px solid ${C.border}`, background:"#fff", fontSize:11, color:C.blue, cursor:"pointer", fontWeight:600 }}>↗</button>
                  </div>
                </div>

                {/* Documentos — volume e tendência */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                  <div style={{ background: semDocs ? C.redLight : C.bg, borderRadius:10, padding:"12px 14px", border:`1px solid ${semDocs ? C.redBorder : C.border}` }}>
                    <div style={{ fontSize:9, fontWeight:700, color:C.textLight, letterSpacing:1.2, textTransform:"uppercase", marginBottom:6 }}>Documentos</div>
                    <div style={{ display:"flex", alignItems:"flex-end", gap:8 }}>
                      <span style={{ fontSize:28, fontWeight:900, color: semDocs ? C.red : C.text, fontFamily:"monospace", lineHeight:1 }}>{e.n_docs}</span>
                      {tend !== null && (
                        <span style={{ fontSize:11, fontWeight:700, color: tend >= 0 ? C.green : C.red, marginBottom:3 }}>
                          {tend >= 0 ? "↑" : "↓"}{Math.abs(tend)}%
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:11, color:C.textLight, marginTop:4 }}>vs {e.n_docs_ant} no mês anterior</div>
                  </div>
                  <div style={{ background:C.bg, borderRadius:10, padding:"12px 14px", border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:9, fontWeight:700, color:C.textLight, letterSpacing:1.2, textTransform:"uppercase", marginBottom:6 }}>Valor total</div>
                    <div style={{ fontSize:20, fontWeight:800, color:C.blue, fontFamily:"monospace", lineHeight:1, marginBottom:4 }}>{fmt(e.valor)}</div>
                    <div style={{ fontSize:11, color:C.textLight }}>último doc: {relDate(e.ultimo_doc)}</div>
                  </div>
                </div>

                {/* Breakdown por tipo */}
                {temTipos ? (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:14 }}>
                    {[
                      { label:"📤 Receitas",  n:e.n_receitas, v:e.receitas, cor:C.green },
                      { label:"📥 Compras",   n:e.n_compras,  v:e.compras,  cor:C.orange },
                      { label:"👥 Salários",  n:e.n_salarios, v:e.salarios, cor:C.purple },
                    ].map(t => (
                      <div key={t.label} style={{ background:"#fafafa", borderRadius:8, padding:"8px", border:`1px solid ${C.border}`, textAlign:"center" }}>
                        <div style={{ fontSize:9, color:C.textLight, marginBottom:3, fontWeight:600 }}>{t.label}</div>
                        <div style={{ fontSize:15, fontWeight:800, color:t.cor, fontFamily:"monospace" }}>{t.n}</div>
                        <div style={{ fontSize:10, color:C.textLight }}>{fmt(t.v)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding:"10px 12px", background:"#fafafa", borderRadius:8, marginBottom:14, fontSize:12, color:C.textLight, border:`1px solid ${C.border}` }}>
                    Tipo de documento não disponível para registos anteriores.
                  </div>
                )}

                {/* Declarações — checklist compacto */}
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:C.textMid, marginBottom:8 }}>Declarações fiscais</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5 }}>
                    {Object.entries(CHECKLIST_MAP).map(([campo, meta]) => {
                      const feito = e.checklist[campo];
                      return (
                        <button key={campo} onClick={() => patchFecho(e.nif, {[campo]: !feito})}
                          style={{ display:"flex", alignItems:"center", gap:7, padding:"7px 10px",
                            background: feito ? C.greenLight : "#fafafa",
                            border:`1px solid ${feito ? C.greenBorder : C.border}`,
                            borderRadius:8, cursor:"pointer", textAlign:"left" }}>
                          <div style={{ width:16, height:16, borderRadius:3, border:`2px solid ${feito ? C.green : C.borderDark}`, background: feito ? C.green : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            {feito && <span style={{ color:"#fff", fontSize:9, fontWeight:900 }}>✓</span>}
                          </div>
                          <span style={{ fontSize:11, color: feito ? C.green : C.text, fontWeight: feito ? 600 : 400, textDecoration: feito ? "line-through" : "none" }}>
                            {meta.icone} {meta.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Notas */}
                <NotasInput nif={e.nif} periodo={periodo} valorInicial={e.notas}
                  onSave={(nif, notas) => patchFecho(nif, {notas})} />

                {/* Fechar mês */}
                <Btn variant={fechado ? "secondary" : "success"}
                  onClick={() => patchFecho(e.nif, {estado: fechado ? "em_curso" : "fechado"})}
                  style={{ width:"100%", marginTop:12 }}>
                  {fechado ? "↩ Reabrir mês" : "✓ Fechar este mês"}
                </Btn>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Estatísticas & Analytics ────────────────────────────────────────────────
const TIPO_LABELS = {
  Fatura_Cliente:    { label: "Faturas Emitidas",  cor: "#157347", icone: "📤" },
  Fatura_Fornecedor: { label: "Faturas Recebidas", cor: "#ea580c", icone: "📥" },
  Recibo_Vencimento: { label: "Salários",          cor: "#5B21B6", icone: "👥" },
  Recibo_Generico:   { label: "Recibos",           cor: "#0891b2", icone: "🧾" },
  "Sem tipo":        { label: "Sem tipo",          cor: "#94a3b8", icone: "❓" },
};

// ─── E-Fatura AT ──────────────────────────────────────────────────────────────
const EFATURA_TIPO_CFG = {
  "Fatura":              { cor: "#4B5563", bg: "#EEF1F6" },
  "Fatura-recibo":       { cor: "#4B5563", bg: "#EEF1F6" },
  "Nota de crédito":     { cor: "#C0302A", bg: "#FEF0F0" },
  "Fatura simplificada": { cor: "#4B5563", bg: "#EEF1F6" },
};

function ModalImportEfatura({ onClose, onSuccess }) {
  const { empresa, empresas } = useEmpresa();
  const nomeEmpresa = (empresas || []).find(e => e.nif === empresa)?.nome;
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState("");

  const submit = async () => {
    if (!empresa) { setStatus("error"); setMsg("Escolhe uma empresa (não \"Todas as Empresas\") antes de importar."); return; }
    if (!file)    { setStatus("error"); setMsg("Selecciona um ficheiro CSV."); return; }
    setStatus("loading"); setMsg("");
    const fd = new FormData();
    fd.append("empresa_nif", empresa);
    fd.append("file", file);
    const r = await apiFetch("/api/efatura/importar", { method: "POST", body: fd });
    const txt = await r.text();
    let d = {};
    try { d = JSON.parse(txt); } catch {}
    if (r.ok) {
      setStatus("ok");
      setMsg(`✅ ${d.inseridos ?? 0} linha(s) importadas. ${d.ja_existiam ?? 0} já existiam. ${d.anulados_ignorados ?? 0} anulados ignorados.`);
      setTimeout(() => { onSuccess(); onClose(); }, 2500);
    } else {
      const detail = typeof d?.detail === "string" ? d.detail : d?.detail?.msg;
      setStatus("error"); setMsg(detail || `Erro HTTP ${r.status}`);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 36, width: 460, boxShadow: "0 20px 60px rgba(0,0,0,.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>📋 Importar E-Fatura</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>CSV exportado do portal e-fatura AT</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textLight }}>✕</button>
        </div>
        {/* Sem seletor de empresa aqui de propósito -- importa sempre para a
            empresa ativa na app (ver EcraEmpresaSelect/useEmpresa); só mostra
            a quem vai, para confirmação rápida antes de submeter. */}
        <div style={{ marginBottom: 16, padding: "9px 12px", borderRadius: 8, background: empresa ? C.blueLight : C.orangeLight, border: `1px solid ${empresa ? C.blueBorder : C.orangeBorder}`, fontSize: 12.5, color: empresa ? C.blue : C.orange, fontWeight: 600 }}>
          {empresa ? `A importar para: ${nomeEmpresa || empresa}` : "Escolhe uma empresa (não \"Todas as Empresas\") para importar."}
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMid, marginBottom: 6 }}>Ficheiro CSV</div>
          <label style={{ display: "block", border: `2px dashed ${file ? C.blue : C.borderDark}`, borderRadius: 10, padding: "20px 16px", textAlign: "center", cursor: "pointer", background: file ? C.blueLight : "#fafafa" }}>
            <input type="file" accept=".csv,.txt" onChange={e => setFile(e.target.files[0])} style={{ display: "none" }} />
            {file
              ? <><div style={{ fontSize: 14, fontWeight: 700, color: C.blue }}>📎 {file.name}</div><div style={{ fontSize: 11, color: C.textMid }}>{(file.size/1024).toFixed(0)} KB</div></>
              : <><div style={{ fontSize: 14, color: C.textMid }}>Clica para seleccionar</div><div style={{ fontSize: 11, color: C.textLight }}>CSV do portal e-fatura AT</div></>}
          </label>
        </div>
        {msg && <div style={{ fontSize: 13, color: status === "ok" ? C.green : status === "error" ? C.red : C.textMid, marginBottom: 16, background: status === "ok" ? C.greenLight : status === "error" ? C.redLight : "transparent", padding: "10px 14px", borderRadius: 8 }}>{msg}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: C.text }}>Cancelar</button>
          <button onClick={submit} disabled={status === "loading"} style={{ flex: 2, padding: "11px 0", borderRadius: 8, border: "none", background: C.blue, color: "#fff", fontSize: 13, fontWeight: 700, cursor: status === "loading" ? "default" : "pointer", opacity: status === "loading" ? 0.7 : 1 }}>
            {status === "loading" ? "A importar..." : "Importar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PageEfatura() {
  return (
    <div style={{ minHeight: "calc(100dvh - 150px)", display: "grid", placeItems: "center", padding: "32px 16px" }}>
      <div style={{ width: "100%", maxWidth: 620, padding: "52px 38px", textAlign: "center", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, boxShadow: "0 8px 28px rgba(16,24,40,.07)" }}>
        <div style={{ width: 64, height: 64, margin: "0 auto 20px", borderRadius: 18, display: "grid", placeItems: "center", background: C.blueLight, color: C.blue }}>
          <ScanLine size={30} strokeWidth={1.8} />
        </div>
        <span style={{ display: "inline-block", padding: "4px 10px", marginBottom: 12, borderRadius: 20, background: C.blueLight, border: `1px solid ${C.blueBorder}`, color: C.blue, fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>Em desenvolvimento</span>
        <h1 style={{ margin: 0, color: C.text, fontSize: 25, fontWeight: 800 }}>Integração E-Fatura</h1>
        <p style={{ margin: "10px auto 0", maxWidth: 440, color: C.textMid, fontSize: 14, lineHeight: 1.65 }}>Estamos a preparar uma forma segura de comparar os documentos processados com os registos da Autoridade Tributária.</p>
      </div>
    </div>
  );
}

function PageEfaturaLegacy() {
  const { empresa } = useEmpresa();
  const [periodo, setPeriodo] = useState("");
  const [tipo,    setTipo]    = useState("");
  const [showImport, setShowImport] = useState(false);
  const [refresh,    setRefresh]    = useState(0);

  const params = new URLSearchParams();
  if (empresa) params.set("empresa_nif", empresa);
  if (periodo)  params.set("periodo", periodo);
  if (tipo)     params.set("tipo", tipo);
  const { data: linhas, loading } = useApi(`/api/efatura?${params}`, [empresa, periodo, tipo, refresh]);
  const { data: divergencias } = useApi(empresa ? `/api/efatura/divergencias?empresa_nif=${empresa}` : null, [empresa, refresh]);

  const rows = linhas || [];
  const totalValor = rows.reduce((s, r) => s + (r.total || 0), 0);
  const totalIva   = rows.reduce((s, r) => s + (r.iva   || 0), 0);
  const totalBase  = rows.reduce((s, r) => s + (r.base_tributavel || 0), 0);

  const periodos = [...new Set(rows.map(r => r.periodo).filter(Boolean))].sort().reverse();

  return (
    <div style={{ padding: "28px 32px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>E-Fatura AT</h1>
          <p style={{ color: C.textMid, fontSize: 13, marginTop: 3 }}>Faturas registadas no portal da Autoridade Tributária</p>
        </div>
        <button onClick={() => setShowImport(true)}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9, background: C.blue, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          <ScanLine size={15} strokeWidth={2.5} /> Importar CSV
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: C.border, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
        {[
          { label: "Linhas",       value: rows.length,    sub: "registos importados" },
          { label: "Base Trib.",   value: fmt(totalBase), sub: "sem IVA" },
          { label: "IVA total",    value: fmt(totalIva),  sub: "imposto suportado" },
        ].map(s => (
          <div key={s.label} style={{ background: "#fff", padding: "14px 18px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textLight, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums" }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Divergências fiscais — braço e-fatura do modelo em "V" */}
      {empresa && divergencias && (divergencias.faturas_sem_efatura.length > 0 || divergencias.efatura_sem_fatura.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {divergencias.faturas_sem_efatura.length > 0 && (
            <div style={{ padding: "12px 16px", borderRadius: 10, background: C.orangeLight, border: `1px solid ${C.orangeBorder}` }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.orange, marginBottom: 4 }}>
                ⚠ {divergencias.faturas_sem_efatura.length} fatura(s) processada(s) sem correspondência no e-fatura (+60 dias)
              </div>
              <div style={{ fontSize: 11.5, color: C.textMid, marginBottom: 6 }}>
                O fornecedor pode ainda não ter comunicado a fatura à AT, ou o NIF/número foram mal lidos.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {divergencias.faturas_sem_efatura.slice(0, 5).map(f => (
                  <div key={f.id} style={{ fontSize: 11.5, color: C.text }}>
                    {f.numero_documento} · {f.nome_emitente} · {fmt(f.valor_total)} · {f.data_documento}
                  </div>
                ))}
                {divergencias.faturas_sem_efatura.length > 5 && (
                  <div style={{ fontSize: 11, color: C.textLight }}>+ {divergencias.faturas_sem_efatura.length - 5} mais</div>
                )}
              </div>
            </div>
          )}
          {divergencias.efatura_sem_fatura.length > 0 && (
            <div style={{ padding: "12px 16px", borderRadius: 10, background: C.orangeLight, border: `1px solid ${C.orangeBorder}` }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.orange, marginBottom: 4 }}>
                ⚠ {divergencias.efatura_sem_fatura.length} registo(s) e-fatura sem fatura correspondente no sistema (+60 dias)
              </div>
              <div style={{ fontSize: 11.5, color: C.textMid, marginBottom: 6 }}>
                Pode ser uma despesa que nunca chegou por email — vale a pena confirmar com o fornecedor.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {divergencias.efatura_sem_fatura.slice(0, 5).map(e => (
                  <div key={e.id} style={{ fontSize: 11.5, color: C.text }}>
                    {e.numero_fatura} · {e.nome_emitente} · {fmt(e.total)} · {e.data_emissao}
                  </div>
                ))}
                {divergencias.efatura_sem_fatura.length > 5 && (
                  <div style={{ fontSize: 11, color: C.textLight }}>+ {divergencias.efatura_sem_fatura.length - 5} mais</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtros -- empresa saiu daqui, é a empresa ativa na app (useEmpresa) */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: "none", minWidth: 130 }}>
          <option value="">Todos os períodos</option>
          {periodos.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={tipo} onChange={e => setTipo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: "none", minWidth: 160 }}>
          <option value="">Todos os tipos</option>
          {["Fatura","Fatura-recibo","Nota de crédito","Fatura simplificada"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Tabela */}
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center" }}><Spinner /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: "56px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Sem registos</div>
            <div style={{ fontSize: 12, color: C.textMid }}>Importa um CSV do portal e-fatura para começar.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: `1px solid ${C.border}` }}>
                  {["Tipo","Nº Fatura","Emitente","NIF","Data","Base Trib.","IVA","Total","Situação","Validada"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: h === "Base Trib." || h === "IVA" || h === "Total" ? "right" : "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textLight, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const tcfg = EFATURA_TIPO_CFG[r.tipo] || { cor: "#64748b", bg: "#f1f5f9" };
                  return (
                    <tr key={r.id ?? i} style={{ borderBottom: `1px solid ${C.border}`, transition: "background .1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: tcfg.cor, background: tcfg.bg, padding: "3px 8px", borderRadius: 20, border: `1px solid ${tcfg.cor}25`, whiteSpace: "nowrap" }}>{r.tipo || "—"}</span>
                      </td>
                      <td style={{ padding: "11px 14px", fontFamily: "monospace", fontSize: 11, color: C.blue, whiteSpace: "nowrap" }}>{r.numero_fatura || "—"}</td>
                      <td style={{ padding: "11px 14px", color: C.text, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.nome_emitente}>{r.nome_emitente || "—"}</td>
                      <td style={{ padding: "11px 14px", fontFamily: "monospace", fontSize: 11, color: C.textMid, whiteSpace: "nowrap" }}>{r.nif_emitente || "—"}</td>
                      <td style={{ padding: "11px 14px", color: C.textMid, whiteSpace: "nowrap" }}>{r.data_emissao || "—"}</td>
                      <td style={{ padding: "11px 14px", fontVariantNumeric: "tabular-nums", textAlign: "right", color: C.text, whiteSpace: "nowrap" }}>{fmt(r.base_tributavel)}</td>
                      <td style={{ padding: "11px 14px", fontVariantNumeric: "tabular-nums", textAlign: "right", color: C.textMid, whiteSpace: "nowrap" }}>{fmt(r.iva)}</td>
                      <td style={{ padding: "11px 14px", fontVariantNumeric: "tabular-nums", textAlign: "right", fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{fmt(r.total)}</td>
                      <td style={{ padding: "11px 14px" }}>
                        <span style={{ fontSize: 10, color: r.situacao === "Registado" ? C.green : C.textMid, background: r.situacao === "Registado" ? C.greenLight : "#f1f5f9", padding: "2px 7px", borderRadius: 20, fontWeight: 600, whiteSpace: "nowrap" }}>{r.situacao || "—"}</span>
                      </td>
                      <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}>
                        {r.fatura_interna_id
                          ? <span style={{ fontSize: 10, color: C.green, background: C.greenLight, padding: "2px 7px", borderRadius: 20, fontWeight: 600 }}>✓ Ligada</span>
                          : <span style={{ fontSize: 10, color: C.textLight, background: "#f1f5f9", padding: "2px 7px", borderRadius: 20, fontWeight: 600 }}>— Por ligar</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f8fafc", borderTop: `2px solid ${C.border}` }}>
                  <td colSpan={5} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.textMid }}>{rows.length} registos</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(totalBase)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: C.textMid, fontVariantNumeric: "tabular-nums" }}>{fmt(totalIva)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(totalValor)}</td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {showImport && (
        <ModalImportEfatura
          onClose={() => setShowImport(false)}
          onSuccess={() => setRefresh(r => r + 1)}
        />
      )}
    </div>
  );
}

// ─── Análise ─────────────────────────────────────────────────────────────────
// Tipos de documento — cor fixa por tipo, reutilizada em toda a página de
// Análise (distribuição, agrupamento, exportações). Módulo, não estado: é
// configuração estática, não muda por render.
const TIPOS_CFG = {
  Fatura_Fornecedor: { label: "Fatura Fornecedor", cor: "#03363D" },
  Fatura_Cliente:    { label: "Fatura Cliente",    cor: "#157347" },
  Recibo_Vencimento: { label: "Recibo Vencimento", cor: "#5B21B6" },
  Recibo_Generico:   { label: "Recibo Genérico",   cor: "#92640C" },
};

// Dimensões pelas quais a tabela flexível pode agrupar — cada uma sabe tirar
// a sua própria chave/rótulo de uma fatura. "socio" filtra implicitamente
// para só documentos de reembolso (agrupar faturas normais por sócio não
// faz sentido nenhum).
const AGRUPAR_DIMENSOES = {
  fornecedor: {
    // chave em "nome primeiro" de propósito — tem de bater certo com o
    // filtro global fForn e com opcoesForn (ambos também nome-primeiro),
    // senão clicar numa linha para filtrar por ela não encontrava nada.
    label: "Fornecedor / Cliente",
    chave: f => f.nome_emitente || f.nif_emitente || "—",
    rotulo: f => f.nome_emitente || f.nif_emitente || "—",
    extra: f => f.nif_emitente || "",
  },
  tipo: {
    label: "Tipo de Documento",
    chave: f => f.tipo_documento || "—",
    rotulo: f => TIPOS_CFG[f.tipo_documento]?.label || f.tipo_documento || "Desconhecido",
  },
  mes: {
    label: "Mês",
    chave: f => (f.data_documento || "").slice(0, 7) || "—",
    rotulo: f => {
      const m = (f.data_documento || "").slice(0, 7);
      if (!m) return "—";
      const [ano, mes] = m.split("-");
      return `${MESES_PT_ABREV[parseInt(mes, 10) - 1]} ${ano}`;
    },
    ordemCronologica: true,
  },
  empresa: {
    label: "Empresa",
    chave: f => f.nif_recetor || "—",
    rotulo: f => f.nome_empresa || f.nif_recetor || "—",
    extra: f => f.nif_recetor || "",
  },
  rubrica: {
    label: "Rubrica / Fundo",
    chave: f => f.rubrica_nome ? `${f.fundo_nome}::${f.rubrica_nome}` : "Sem rubrica",
    rotulo: f => f.rubrica_nome ? `${f.fundo_nome} → ${f.rubrica_nome}` : "Sem rubrica atribuída",
  },
  socio: {
    label: "Sócio (reembolsos)",
    chave: f => f.nome_socio_reembolso || "Por identificar",
    rotulo: f => f.nome_socio_reembolso || "Por identificar",
    apenasReembolsos: true,
  },
};
const MESES_PT_ABREV = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

// Agrupa uma lista de faturas já filtrada pela dimensão escolhida — devolve
// {chave, rotulo, extra, count, valor, pago, porPagar}[], ordenado por valor
// (ou cronologicamente, para "mês"). Função pura — sem estado, fácil de
// verificar isoladamente.
function agruparFaturas(lista, dimensaoId) {
  const dim = AGRUPAR_DIMENSOES[dimensaoId] || AGRUPAR_DIMENSOES.fornecedor;
  const grupos = {};
  const ordem = [];
  for (const f of lista) {
    if (dim.apenasReembolsos && !f.eh_reembolso) continue;
    const chave = dim.chave(f);
    if (!grupos[chave]) {
      grupos[chave] = { chave, rotulo: dim.rotulo(f), extra: dim.extra?.(f) || "", count: 0, valor: 0, pago: 0, porPagar: 0 };
      ordem.push(chave);
    }
    const g = grupos[chave];
    g.count++;
    g.valor += f.valor_total || 0;
    if (f.pago) g.pago += f.valor_total || 0;
    else g.porPagar += f.valor_total || 0;
  }
  const linhas = ordem.map(k => grupos[k]);
  return dim.ordemCronologica
    ? linhas.sort((a, b) => a.chave.localeCompare(b.chave))
    : linhas.sort((a, b) => b.valor - a.valor);
}

// Início/fim (YYYY-MM-DD) de um preset de período, e do período EQUIVALENTE
// imediatamente anterior (para a comparação "vs período anterior") — datas
// naturais de calendário (mês/trimestre/ano), não uma janela deslizante de
// N dias, porque é assim que um contabilista pensa em períodos.
function calcularPeriodo(preset, hoje = new Date()) {
  const y = hoje.getFullYear(), m = hoje.getMonth();
  const iso = d => d.toISOString().slice(0, 10);
  const fimMes = (ano, mes) => new Date(ano, mes + 1, 0);
  switch (preset) {
    case "mes-atual":
      return { de: iso(new Date(y, m, 1)), ate: iso(fimMes(y, m)) };
    case "mes-passado":
      return { de: iso(new Date(y, m - 1, 1)), ate: iso(fimMes(y, m - 1)) };
    case "trimestre": {
      const inicioTrim = m - (m % 3);
      return { de: iso(new Date(y, inicioTrim, 1)), ate: iso(fimMes(y, inicioTrim + 2)) };
    }
    case "ano":
      return { de: iso(new Date(y, 0, 1)), ate: iso(new Date(y, 11, 31)) };
    case "ano-passado":
      return { de: iso(new Date(y - 1, 0, 1)), ate: iso(new Date(y - 1, 11, 31)) };
    default:
      return { de: "", ate: "" }; // "tudo" | "custom"
  }
}
function calcularPeriodoAnterior(preset, hoje = new Date()) {
  const y = hoje.getFullYear(), m = hoje.getMonth();
  switch (preset) {
    case "mes-atual":
      return calcularPeriodo("mes-passado", hoje);
    case "mes-passado":
      // O mês antes do "mês passado": desloca a referência um mês para trás
      // e volta a pedir "mês passado" a partir daí.
      return calcularPeriodo("mes-passado", new Date(y, m - 1, 1));
    case "trimestre": {
      const inicioTrim = m - (m % 3);
      return calcularPeriodo("trimestre", new Date(y, inicioTrim - 3, 1));
    }
    case "ano":
      return calcularPeriodo("ano-passado", hoje);
    case "ano-passado":
      return calcularPeriodo("ano", new Date(y - 2, 0, 1));
    default:
      return null; // "tudo" | "custom" — sem equivalente natural, comparação desligada
  }
}

const PERIODO_PRESETS = [
  { id: "mes-atual",   label: "Este mês" },
  { id: "mes-passado", label: "Mês passado" },
  { id: "trimestre",   label: "Este trimestre" },
  { id: "ano",         label: "Este ano" },
  { id: "ano-passado", label: "Ano passado" },
  { id: "tudo",        label: "Tudo" },
  { id: "custom",      label: "Personalizado" },
];

const KPIS_DISPONIVEIS = [
  { id: "total",    label: "Volume Total" },
  { id: "receitas", label: "Receitas" },
  { id: "despesas", label: "Despesas" },
  { id: "salarios", label: "Salários" },
  { id: "porPagar", label: "Por Pagar" },
  { id: "pagos",    label: "Pagos" },
  { id: "ticket",   label: "Ticket Médio" },
];

function PageEstatisticas() {
  const { empresa, empresas: empresasList } = useEmpresa();
  const [preset, setPreset]     = useState("tudo");
  const [fDe, setFDe]           = useState("");
  const [fAte, setFAte]         = useState("");
  const [fTipos, setFTipos]     = useState([]); // [] = todos
  const [fPago, setFPago]       = useState(""); // "" | "pago" | "nao-pago" — global, afeta tudo
  const [fForn, setFForn]       = useState(""); // filtro global por fornecedor/cliente
  const [comparar, setComparar] = useState(false);
  const [agruparPor, setAgruparPor] = useState("fornecedor");
  const [faturas, setFaturas]   = useState([]);
  const [loading, setLoading]   = useState(false);

  const [showP, setShowP] = useState(false);
  const PREFS_KEY = "analise_prefs_v2";
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "null") || {}; } catch { return {}; }
  });
  const secoesVisiveis = prefs.secoes || { kpis: true, evolucao: true, distribuicao: true, tabela: true };
  const kpisVisiveis   = prefs.kpis   || KPIS_DISPONIVEIS.map(k => k.id);
  const salvarPrefs = (novo) => {
    const n = { ...prefs, ...novo };
    setPrefs(n);
    localStorage.setItem(PREFS_KEY, JSON.stringify(n));
  };
  const toggleSecao = (id) => salvarPrefs({ secoes: { ...secoesVisiveis, [id]: !secoesVisiveis[id] } });
  const toggleKpi   = (id) => salvarPrefs({ kpis: kpisVisiveis.includes(id) ? kpisVisiveis.filter(k => k !== id) : [...kpisVisiveis, id] });

  const aplicarPreset = (id) => {
    setPreset(id);
    if (id !== "custom") { const { de, ate } = calcularPeriodo(id); setFDe(de); setFAte(ate); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const qs = empresa ? `?empresa=${empresa}&limit=5000` : "?limit=5000";
        const r = await apiFetch(`/api/faturas/com-duplicados${qs}`);
        const data = r.ok ? await r.json() : [];
        setFaturas(data || []);
      } catch(e) {}
      setLoading(false);
    })();
  }, [empresa]);

  // Filtro global — TODOS os widgets da página (KPIs, evolução, distribuição,
  // tabela) refletem a mesma seleção. Antes, "fornecedor" e "por pagar" só
  // afetavam a tabela de baixo; um contabilista a analisar um fornecedor
  // específico esperava ver isso reflectido em todo o lado.
  const filtrar = (fs) => fs.filter(f => {
    if (fTipos.length && !fTipos.includes(f.tipo_documento)) return false;
    if (fDe   && (f.data_documento || "") < fDe)  return false;
    if (fAte  && (f.data_documento || "") > fAte) return false;
    if (fForn && (f.nome_emitente || f.nif_emitente) !== fForn) return false;
    if (fPago === "pago"     && !f.pago) return false;
    if (fPago === "nao-pago" &&  f.pago) return false;
    return true;
  });
  const lista = filtrar(faturas || []);

  // Período anterior equivalente (só quando "Comparar" está ligado e o preset
  // atual tem um "anterior" natural — ver calcularPeriodoAnterior). Reaplica
  // os MESMOS filtros (tipo/fornecedor/pago) mas recorta pela janela de
  // datas do período anterior, não a atual.
  const periodoAnterior = comparar ? calcularPeriodoAnterior(preset) : null;
  const listaAnteriorReal = periodoAnterior
    ? (faturas || []).filter(f => {
        if (fTipos.length && !fTipos.includes(f.tipo_documento)) return false;
        if (fForn && (f.nome_emitente || f.nif_emitente) !== fForn) return false;
        if (fPago === "pago"     && !f.pago) return false;
        if (fPago === "nao-pago" &&  f.pago) return false;
        return (f.data_documento || "") >= periodoAnterior.de && (f.data_documento || "") <= periodoAnterior.ate;
      })
    : [];

  const somar = (fs, pred) => fs.filter(pred).reduce((s, f) => s + (f.valor_total || 0), 0);
  const kpiValores = (fs) => ({
    total:    fs.reduce((s, f) => s + (f.valor_total || 0), 0),
    receitas: somar(fs, f => f.tipo_documento === "Fatura_Cliente"),
    despesas: somar(fs, f => ["Fatura_Fornecedor", "Recibo_Generico"].includes(f.tipo_documento)),
    salarios: somar(fs, f => f.tipo_documento === "Recibo_Vencimento"),
    porPagar: somar(fs, f => !f.pago),
    pagos:    fs.filter(f => !!f.pago).length,
    ticket:   fs.length ? fs.reduce((s, f) => s + (f.valor_total || 0), 0) / fs.length : 0,
  });
  const kv = kpiValores(lista);
  const kvAnterior = periodoAnterior ? kpiValores(listaAnteriorReal) : null;
  const delta = (atual, anterior) => (anterior === null || anterior === undefined || Math.abs(anterior) < 0.005)
    ? null : ((atual - anterior) / Math.abs(anterior)) * 100;

  const porTipo = Object.entries(TIPOS_CFG).map(([tipo, cfg]) => {
    const itens = lista.filter(f => f.tipo_documento === tipo);
    return { ...cfg, tipo, count: itens.length, valor: itens.reduce((s, f) => s + (f.valor_total || 0), 0) };
  }).filter(t => t.count > 0);
  const maxV = Math.max(...porTipo.map(t => t.valor), 1);

  // Evolução mensal — Receitas vs Despesas. Sem filtro de data ativo, limita
  // aos últimos 12 meses (senão o gráfico ficava ilegível com anos de dados).
  const evolucaoMensal = useMemo(() => {
    const base = (fDe || fAte) ? lista : lista.filter(f => {
      const limite = new Date(); limite.setMonth(limite.getMonth() - 11); limite.setDate(1);
      return (f.data_documento || "") >= limite.toISOString().slice(0, 10);
    });
    const porMes = {};
    for (const f of base) {
      const m = (f.data_documento || "").slice(0, 7);
      if (!m) continue;
      if (!porMes[m]) porMes[m] = { mes: m, Receitas: 0, Despesas: 0 };
      if (f.tipo_documento === "Fatura_Cliente") porMes[m].Receitas += f.valor_total || 0;
      else if (["Fatura_Fornecedor", "Recibo_Generico"].includes(f.tipo_documento)) porMes[m].Despesas += f.valor_total || 0;
    }
    return Object.values(porMes).sort((a, b) => a.mes.localeCompare(b.mes)).map(r => {
      const [ano, mes] = r.mes.split("-");
      return { ...r, label: `${MESES_PT_ABREV[parseInt(mes, 10) - 1]}/${ano.slice(2)}` };
    });
  }, [lista, fDe, fAte]);

  // Opções do filtro global de fornecedor — sempre a lista completa da
  // empresa/tipo selecionados (não recalcula a partir de `lista` já
  // filtrada por fornecedor, senão escolher um fornecedor faz a própria
  // lista de opções desaparecer).
  const opcoesForn = useMemo(() => {
    const nomes = new Map();
    for (const f of (faturas || [])) {
      const k = f.nome_emitente || f.nif_emitente;
      if (k && !nomes.has(k)) nomes.set(k, f.nif_emitente || "");
    }
    return [...nomes.entries()].map(([nome, nif]) => ({ nome, nif })).sort((a, b) => a.nome.localeCompare(b.nome, "pt"));
  }, [faturas]);

  const agrupamento = useMemo(() => agruparFaturas(lista, agruparPor), [lista, agruparPor]);
  const dimAtual = AGRUPAR_DIMENSOES[agruparPor];

  // Drill-down simples: clicar numa linha estreita o filtro global em vez de
  // abrir um modal (a linha é um agregado, não um documento único).
  const aplicarDrill = (linha) => {
    if (agruparPor === "fornecedor") setFForn(prev => prev === linha.chave ? "" : linha.chave);
    else if (agruparPor === "mes") {
      // "mes" (ex: parseInt("07")=7) usado como mês 0-indexado dá o mês
      // SEGUINTE — dia 0 desse mês é sempre o último dia do mês certo.
      setPreset("custom");
      const [ano, mes] = linha.chave.split("-");
      setFDe(`${ano}-${mes}-01`);
      setFAte(new Date(parseInt(ano, 10), parseInt(mes, 10), 0).toISOString().slice(0, 10));
    }
    else if (agruparPor === "tipo") setFTipos(prev => prev.length === 1 && prev[0] === linha.chave ? [] : [linha.chave]);
  };

  const limparFiltros = () => { aplicarPreset("tudo"); setFTipos([]); setFForn(""); setFPago(""); };
  const filtrosAtivos = fTipos.length > 0 || !!fForn || !!fPago || preset !== "tudo";

  const exportarXlsx = async () => {
    const qs = new URLSearchParams({ fonte: "ambos", ...(empresa && { empresa }), ...(fDe && { data_inicio: fDe }), ...(fAte && { data_fim: fAte }) }).toString();
    const token = loadSession()?.token || "";
    const resp = await fetch(`/api/analise/exportar?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "analise_financeira.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportarPDF = () => {
    const empresaNome = (empresasList || []).find(e => e.nif === empresa)?.nome || "Todas as empresas";
    const periodo = fDe || fAte ? `${fDe || "início"} a ${fAte || "hoje"}` : "Todo o período";
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>Análise Financeira</title>
<style>body{font-family:Arial,sans-serif;padding:32px;color:#111;font-size:13px}h1{font-size:22px;margin:0 0 4px}.sub{color:#666;font-size:12px;margin-bottom:24px}.kpis{display:flex;gap:16px;margin-bottom:24px;flex-wrap:wrap}.kpi{flex:1;min-width:110px;border:1px solid #e5e7eb;border-radius:8px;padding:14px;text-align:center}.kv{font-size:20px;font-weight:800}.kl{font-size:10px;color:#6b7280;margin-top:3px;text-transform:uppercase;letter-spacing:.05em}h2{font-size:14px;margin:20px 0 10px;border-bottom:1px solid #e5e7eb;padding-bottom:5px}table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px}th{background:#f1f5f9;padding:7px 10px;text-align:left;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:.06em;color:#64748b}td{padding:7px 10px;border-bottom:1px solid #f1f5f9}@media print{body{padding:12px}@page{margin:1.5cm}}</style>
</head><body>
<h1>Análise Financeira</h1>
<div class="sub">${empresaNome} · ${periodo} · Gerado em ${new Date().toLocaleDateString("pt-PT")}</div>
<div class="kpis">
  <div class="kpi"><div class="kv">${fmt(kv.total)}</div><div class="kl">Volume Total</div></div>
  <div class="kpi"><div class="kv" style="color:#157347">${fmt(kv.receitas)}</div><div class="kl">Receitas</div></div>
  <div class="kpi"><div class="kv" style="color:#C0302A">${fmt(kv.despesas)}</div><div class="kl">Despesas</div></div>
  <div class="kpi"><div class="kv" style="color:#5B21B6">${fmt(kv.salarios)}</div><div class="kl">Salários</div></div>
  <div class="kpi"><div class="kv">${lista.length}</div><div class="kl">Documentos</div></div>
</div>
<h2>Distribuição por Tipo</h2>
<table><tr><th>Tipo</th><th>Docs</th><th>Valor</th></tr>${porTipo.map(t => `<tr><td>${t.label}</td><td>${t.count}</td><td>${fmt(t.valor)}</td></tr>`).join("")}</table>
<h2>${dimAtual.label}</h2>
<table><tr><th>#</th><th>${dimAtual.label}</th><th>Docs</th><th>Total</th></tr>${agrupamento.map((g, i) => `<tr><td>${i + 1}</td><td>${g.rotulo}</td><td>${g.count}</td><td>${fmt(g.valor)}</td></tr>`).join("")}</table>
</body></html>`);
    win.document.close(); win.print();
  };

  const inpS  = { padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: "#fff", outline: "none" };
  const thS   = { padding: "9px 12px", background: "#f8fafc", borderBottom: `2px solid ${C.border}`, textAlign: "left", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#64748b" };
  const card  = { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px" };
  const chip  = (ativo) => ({ padding: "6px 13px", borderRadius: 20, fontSize: 12, fontWeight: ativo ? 700 : 500, cursor: "pointer",
    border: ativo ? `1.5px solid ${C.blue}` : `1px solid ${C.border}`, background: ativo ? C.blueLight : "#f8fafc", color: ativo ? C.blue : C.textMid });

  const KPI_CFG = {
    total:    { label: "Volume Total", sub: () => `${lista.length} documentos` },
    receitas: { label: "Receitas",     cor: "#157347", sub: () => `${lista.filter(f => f.tipo_documento === "Fatura_Cliente").length} faturas` },
    despesas: { label: "Despesas",     cor: "#C0302A", sub: () => "fornecedor + recibos" },
    salarios: { label: "Salários",     cor: "#5B21B6", sub: () => `${lista.filter(f => f.tipo_documento === "Recibo_Vencimento").length} recibos` },
    porPagar: { label: "Por Pagar",    cor: "#C0302A", sub: () => "valor em aberto" },
    pagos:    { label: "Pagos",        sub: () => `de ${lista.length} docs` },
    ticket:   { label: "Ticket Médio", sub: () => "valor por documento" },
  };

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>Análise</h1>
          <p style={{ color: C.textMid, fontSize: 13, marginTop: 3 }}>Dashboard financeiro personalizável</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowP(v => !v)} style={{ ...inpS, cursor: "pointer", fontWeight: 600 }}>⚙ Personalizar</button>
          <button onClick={exportarXlsx} style={{ ...inpS, cursor: "pointer", color: "#157347", borderColor: "#A3D9B7", background: "#EDF7F1", fontWeight: 700 }}>↓ Excel</button>
          <button onClick={exportarPDF}  style={{ ...inpS, cursor: "pointer", color: "#C0302A", borderColor: "#F5C0BF", background: "#FEF0F0", fontWeight: 700 }}>↓ PDF</button>
        </div>
      </div>

      {/* ── Personalizar panel ── */}
      {showP && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>Mostrar / ocultar secções</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
            {[
              { id: "kpis",         label: "KPIs" },
              { id: "evolucao",     label: "Evolução Mensal" },
              { id: "distribuicao", label: "Por Tipo" },
              { id: "tabela",       label: "Tabela agrupada" },
            ].map(s => (
              <button key={s.id} onClick={() => toggleSecao(s.id)} style={chip(secoesVisiveis[s.id] !== false)}>
                {secoesVisiveis[s.id] !== false ? "✓ " : ""}{s.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>KPIs visíveis</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {KPIS_DISPONIVEIS.map(k => (
              <button key={k.id} onClick={() => toggleKpi(k.id)} style={chip(kpisVisiveis.includes(k.id))}>
                {kpisVisiveis.includes(k.id) ? "✓ " : ""}{k.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Período (sempre visível) — a empresa saiu daqui, é escolhida uma
          vez para toda a app (ver EcraEmpresaSelect/useEmpresa) ── */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Período</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {PERIODO_PRESETS.map(p => (
                <button key={p.id} onClick={() => aplicarPreset(p.id)} style={chip(preset === p.id)}>{p.label}</button>
              ))}
              {preset === "custom" && (
                <>
                  <input type="date" value={fDe} onChange={e => setFDe(e.target.value)} style={inpS} />
                  <span style={{ color: C.textLight, fontSize: 12 }}>até</span>
                  <input type="date" value={fAte} onChange={e => setFAte(e.target.value)} style={inpS} />
                </>
              )}
              {calcularPeriodoAnterior(preset) && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8, fontSize: 12, color: C.textMid, cursor: "pointer" }}>
                  <input type="checkbox" checked={comparar} onChange={e => setComparar(e.target.checked)} />
                  Comparar com período anterior
                </label>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Filtros adicionais ── */}
      <div style={{ ...card, marginBottom: 20, display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Tipo de documento</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(TIPOS_CFG).map(([id, cfg]) => (
              <button key={id} onClick={() => setFTipos(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id])}
                style={chip(fTipos.includes(id))}>{cfg.label}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Fornecedor / Cliente</div>
          <select value={fForn} onChange={e => setFForn(e.target.value)} style={{ ...inpS, minWidth: 220 }}>
            <option value="">Todos</option>
            {opcoesForn.map(f => <option key={f.nome} value={f.nome}>{f.nome}{f.nif ? ` — ${f.nif}` : ""}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Pagamento</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "", label: "Todos" }, { id: "nao-pago", label: "Por pagar" }, { id: "pago", label: "Pago" }].map(p => (
              <button key={p.id} onClick={() => setFPago(p.id)} style={chip(fPago === p.id)}>{p.label}</button>
            ))}
          </div>
        </div>
        {filtrosAtivos && (
          <button onClick={limparFiltros} style={{ ...inpS, cursor: "pointer", color: C.textMid, fontSize: 12 }}>✕ Limpar filtros</button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12.5, color: C.textMid, alignSelf: "center" }}>{loading ? "A carregar…" : `${lista.length} documentos`}</span>
      </div>

      {/* ── KPI cards ── */}
      {secoesVisiveis.kpis !== false && kpisVisiveis.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(kpisVisiveis.length, 7) || 1}, 1fr)`, gap: 1, background: C.border, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
          {KPIS_DISPONIVEIS.filter(k => kpisVisiveis.includes(k.id)).map(k => {
            const cfg = KPI_CFG[k.id];
            const valor = kv[k.id];
            const d = kvAnterior ? delta(valor, kvAnterior[k.id]) : null;
            const valorFmt = ["pagos"].includes(k.id) ? valor : fmt(valor);
            return (
              <div key={k.id} style={{ padding: "16px 18px", background: "#fff" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>{cfg.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: cfg.cor || C.text, letterSpacing: "-0.02em", lineHeight: 1.2, marginTop: 5 }}>{valorFmt}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <span style={{ fontSize: 11, color: C.textLight }}>{cfg.sub()}</span>
                  {d !== null && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: d >= 0 ? C.green : C.red }}>
                      {d >= 0 ? "▲" : "▼"} {Math.abs(d).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Evolução Mensal — Receitas vs Despesas ── */}
      {secoesVisiveis.evolucao !== false && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Evolução Mensal</div>
          <div style={{ fontSize: 11.5, color: C.textLight, marginBottom: 12 }}>Receitas vs Despesas{!(fDe || fAte) ? " · últimos 12 meses" : ""}</div>
          {evolucaoMensal.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textMid, padding: "24px 0", textAlign: "center" }}>Sem dados para o período selecionado</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={evolucaoMensal} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.textLight }} axisLine={{ stroke: C.border }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.textLight }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} width={44} />
                  <RTooltip
                    cursor={{ fill: "#f8fafc" }}
                    contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, boxShadow: "0 4px 16px rgba(0,0,0,.08)" }}
                    formatter={(v) => fmt(v)}
                  />
                  <RLegend wrapperStyle={{ fontSize: 12, color: C.textMid }} iconType="circle" iconSize={8} />
                  <Bar dataKey="Receitas" fill={C.green} radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Despesas" fill={C.red} radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── Distribuição por Tipo ── */}
      {secoesVisiveis.distribuicao !== false && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 18 }}>Distribuição por Tipo</div>
          {porTipo.length === 0
            ? <div style={{ fontSize: 13, color: C.textMid, padding: "24px 0", textAlign: "center" }}>Sem dados para o período selecionado</div>
            : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
                {porTipo.map(t => (
                  <div key={t.tipo} onClick={() => setFTipos(prev => prev.length === 1 && prev[0] === t.tipo ? [] : [t.tipo])} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t.label}</span>
                      <span style={{ fontSize: 12, color: C.textMid }}>{fmt(t.valor)}<span style={{ fontSize: 10, color: C.textLight }}> ({t.count})</span></span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 4, background: t.cor, width: `${Math.max(2, Math.round((t.valor / maxV) * 100))}%`, transition: "width 0.5s ease" }} />
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* ── Tabela flexível — agrupa por qualquer dimensão ── */}
      {secoesVisiveis.tabela !== false && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Agrupar por</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(AGRUPAR_DIMENSOES).filter(([id]) => id !== "empresa" || !empresa).map(([id, d]) => (
                <button key={id} onClick={() => setAgruparPor(id)} style={chip(agruparPor === id)}>{d.label}</button>
              ))}
            </div>
          </div>
          {dimAtual.apenasReembolsos && (
            <div style={{ fontSize: 11.5, color: C.purple, marginBottom: 10, fontStyle: "italic" }}>Mostra apenas documentos de reembolso.</div>
          )}
          {agrupamento.length === 0
            ? <div style={{ fontSize: 13, color: C.textMid, padding: "24px 0", textAlign: "center" }}>Sem dados para os filtros selecionados</div>
            : (
            <div style={{ maxHeight: 480, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}><tr>
                  {[dimAtual.label, "Docs", "Total", "Pago", "Por pagar"].map(h => (
                    <th key={h} style={{ ...thS, textAlign: h === "Docs" ? "center" : h === dimAtual.label ? "left" : "right" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {agrupamento.map((g) => (
                    <tr key={g.chave} onClick={() => aplicarDrill(g)}
                      style={{ borderBottom: `1px solid #f8fafc`, cursor: ["fornecedor", "mes", "tipo"].includes(agruparPor) ? "pointer" : "default" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "9px 8px", fontSize: 12, fontWeight: 600, color: "#111827", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {g.rotulo}{g.extra ? <span style={{ fontSize: 10.5, color: "#9AA3B0", fontFamily: "ui-monospace,monospace", marginLeft: 6 }}>{g.extra}</span> : null}
                      </td>
                      <td style={{ padding: "9px 8px", textAlign: "center", fontSize: 12, color: C.textMid }}>{g.count}</td>
                      <td style={{ padding: "9px 8px", textAlign: "right", fontSize: 13, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmt(g.valor)}</td>
                      <td style={{ padding: "9px 8px", textAlign: "right", fontSize: 12.5, color: "#157347", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{g.pago > 0.005 ? fmt(g.pago) : "—"}</td>
                      <td style={{ padding: "9px 8px", textAlign: "right", fontSize: 12.5, fontWeight: g.porPagar > 0.005 ? 700 : 400, color: g.porPagar > 0.005 ? "#C0302A" : C.textLight, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{g.porPagar > 0.005 ? fmt(g.porPagar) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f8fafc", borderTop: `2px solid ${C.border}` }}>
                    <td colSpan={2} style={{ padding: "9px 8px", fontSize: 11, fontWeight: 700, color: C.textMid }}>{agrupamento.length} {agrupamento.length === 1 ? "grupo" : "grupos"}</td>
                    <td style={{ padding: "9px 8px", textAlign: "right", fontWeight: 800, color: "#111827", fontVariantNumeric: "tabular-nums" }}>{fmt(agrupamento.reduce((s, g) => s + g.valor, 0))}</td>
                    <td style={{ padding: "9px 8px", textAlign: "right", fontWeight: 700, color: "#157347", fontVariantNumeric: "tabular-nums" }}>{fmt(agrupamento.reduce((s, g) => s + g.pago, 0))}</td>
                    <td style={{ padding: "9px 8px", textAlign: "right", fontWeight: 700, color: "#C0302A", fontVariantNumeric: "tabular-nums" }}>{fmt(agrupamento.reduce((s, g) => s + g.porPagar, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reconciliação ────────────────────────────────────────────────────────────
function SectionRecon({ title, count, color, children }) {
  const [open, setOpen] = useState(true);
  if (count === 0) return null;
  return (
    <Card style={{ marginBottom: 16, borderLeft: `4px solid ${color}` }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: open ? 16 : 0 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Pill color={color === C.red ? "red" : "orange"}>{count}</Pill>
          <span style={{ color: C.textLight, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
        </div>
      </button>
      {open && children}
    </Card>
  );
}

// ── Helpers de Reconciliação Bancária ────────────────────────────────────────
const CONF_COLOR = (c) =>
  c >= 0.80 ? C.green : c >= 0.60 ? C.orange : C.red;
const CONF_LABEL = (c) =>
  c >= 0.80 ? "Alta" : c >= 0.60 ? "Média" : "Baixa";

function BadgeConf({ confianca }) {
  const pct = Math.round(confianca * 100);
  const cor  = CONF_COLOR(confianca);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ width:36, height:36, borderRadius:"50%", border:`3px solid ${cor}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:cor }}>
        {pct}%
      </div>
      <span style={{ fontSize:11, fontWeight:700, color:cor }}>{CONF_LABEL(confianca)}</span>
    </div>
  );
}

function ModalProgresso({ passos, resultado, erro, onFechar }) {
  const tudo_feito = passos.length > 0 && passos.every(p => p.status === "done");

  return (
    <div style={{ position:"fixed", inset:0, zIndex:3000,
      background:"rgba(15,23,42,.75)", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:20, padding:36, width:440,
        boxShadow:"0 24px 60px rgba(0,0,0,.25)" }}>

        {/* Título */}
        <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:24 }}>
          {tudo_feito ? (erro ? "✗ Erro no processamento" : "✓ Extrato processado") : "A processar extrato..."}
        </div>

        {/* Passos */}
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
          {passos.map((p, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:24, height:24, borderRadius:"50%", flexShrink:0,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:12,
                background: p.status === "done" ? C.greenLight :
                            p.status === "active" ? C.blueLight : C.bg,
                border: `2px solid ${p.status === "done" ? C.greenBorder :
                                     p.status === "active" ? C.blue : C.border}`,
              }}>
                {p.status === "done"   && <span style={{ color:C.green, fontWeight:800 }}>✓</span>}
                {p.status === "active" && (
                  <span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%",
                    border:`2px solid ${C.blue}`, borderTopColor:"transparent",
                    animation:"spin 0.7s linear infinite" }} />
                )}
                {p.status === "waiting" && <span style={{ color:C.textLight }}>○</span>}
              </div>
              <span style={{ fontSize:13, fontWeight: p.status === "active" ? 700 : 500,
                color: p.status === "done" ? C.textMid :
                       p.status === "active" ? C.blue : C.textLight,
              }}>{p.label}</span>
            </div>
          ))}
        </div>

        {/* Sumário pós-processamento */}
        {tudo_feito && resultado && !erro && (
          <div style={{ background:C.bg, borderRadius:10, padding:"12px 16px", marginBottom:20 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
              {[
                { label: resultado.inseridos > 0 ? "Novos" : "Já existiam",
                  value: resultado.inseridos > 0 ? resultado.inseridos : (resultado.ja_existiam || 0),
                  color: resultado.inseridos > 0 ? C.blue : C.textMid },
                { label:"Casamentos", value: resultado.novos_matches, color: resultado.novos_matches > 0 ? C.green : C.textLight },
                { label:"Período",    value: resultado.periodo || "—", color: C.text, mono: false },
              ].map(s => (
                <div key={s.label} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:9, fontWeight:700, color:C.textLight, letterSpacing:1.2,
                    textTransform:"uppercase", marginBottom:4 }}>{s.label}</div>
                  <div style={{ fontSize:20, fontWeight:800, color:s.color,
                    fontFamily: s.mono !== false ? "monospace" : "inherit" }}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {erro && (
          <div style={{ background:"#FEF0F0", borderRadius:8, padding:"10px 14px",
            fontSize:13, color:C.red, marginBottom:16 }}>✗ {erro}</div>
        )}

        {(tudo_feito || erro) && (
          <Btn variant="primary" style={{ width:"100%" }} onClick={onFechar}>
            Ver detalhes
          </Btn>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function SecaoCasamentoBancario({ empresas }) {
  const { data: empresasList } = useApi("/api/empresas", [], []);
  const lista = empresas || empresasList || [];

  const [empresa,         setEmpresa]         = useState("");
  const [periodo,         setPeriodo]          = useState(null);
  const [uploading,       setUploading]        = useState(false);
  const [dragOver,        setDragOver]         = useState(false);
  const [uploadMsg,       setUploadMsg]        = useState(null);
  const [filePendente,    setFilePendente]     = useState(null);
  const [opcoes,          setOpcoes]           = useState(null);
  const [empresaEscolha,  setEmpresaEscolha]   = useState("");
  // Modal de progresso
  const [modalVisivel,    setModalVisivel]     = useState(false);
  const [modalPassos,     setModalPassos]      = useState([]);
  const [modalResultado,  setModalResultado]   = useState(null);
  const [modalErro,       setModalErro]        = useState(null);
  const timerRef = useRef(null);
  const fileRef  = useRef(null);

  const url = empresa && periodo
    ? `/api/reconciliacao/matches?empresa=${empresa}&periodo=${periodo}`
    : null;
  const { data: matchData, loading, refresh } = useApi(url, [empresa, periodo], null);

  const PASSOS_INICIAIS = [
    { label: "Ficheiro recebido",        status: "done" },
    { label: "A identificar empresa...", status: "active" },
    { label: "A ler movimentos...",      status: "waiting" },
    { label: "Movimentos extraídos",     status: "waiting" },
    { label: "Casamentos encontrados",   status: "waiting" },
  ];

  const enviarArquivo = async (file, empresa_nif = "") => {
    // Abrir modal e iniciar animação
    setModalErro(null);
    setModalResultado(null);
    setModalPassos(PASSOS_INICIAIS);
    setModalVisivel(true);
    setUploading(true);

    // Passo 2 → activo após 800ms, passo 3 fica activo (IA a ler)
    timerRef.current = setTimeout(() => {
      setModalPassos(p => p.map((s, i) =>
        i === 1 ? { ...s, status: "done" } :
        i === 2 ? { ...s, status: "active" } : s
      ));
    }, 800);

    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      if (empresa_nif) fd.append("empresa_nif", empresa_nif);

      const r = await apiFetch("/api/extratos/importar-arquivo", { method: "POST", body: fd });

      clearTimeout(timerRef.current);

      if (r.ok) {
        const d = await r.json();
        setEmpresa(d.empresa_nif);
        setPeriodo(d.periodo);
        setFilePendente(null);
        setOpcoes(null);
        setModalResultado(d);
        setModalPassos([
          { label: "Ficheiro recebido",                      status: "done" },
          { label: "Empresa identificada",                                                           status: "done" },
          { label: d.inseridos > 0 ? `${d.inseridos} movimento(s) novos inseridos` : `Extrato já importado — ${d.ja_existiam || 0} movimento(s) já existiam`, status: "done" },
          { label: d.novos_matches > 0 ? `${d.novos_matches} casamento(s) criados` : "Sem novos casamentos", status: "done" },
          { label: "Guardado",                                                                         status: "done" },
        ]);
      } else if (r.status === 422) {
        const d = await r.json().catch(() => {});
        setFilePendente(file);
        setOpcoes(d?.detail?.empresas || []);
        setEmpresaEscolha(d?.detail?.empresas?.[0]?.nif || "");
        setModalVisivel(false);
        setUploadMsg({ ok: false, msg: "Não consegui identificar a empresa — escolhe abaixo." });
      } else {
        const d = await r.json().catch(() => {});
        setModalErro(mensagemErroImportacao(d?.detail));
        setModalPassos(p => p.map((s, i) => i <= 1 ? { ...s, status: "done" } : { ...s, status: "waiting" }));
      }
    } catch (e) {
      clearTimeout(timerRef.current);
      setModalErro("Erro de rede — tenta de novo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleFile = (file) => { if (file) enviarArquivo(file); };

  const confirmarEmpresa = () => {
    if (filePendente && empresaEscolha) enviarArquivo(filePendente, empresaEscolha);
  };

  const fecharModal = () => {
    setModalVisivel(false);
    if (modalResultado) refresh();
  };

  const nOrfasF      = matchData?.faturas_sem_match?.length || 0;
  const nOrfasE      = matchData?.extratos_sem_match?.length || 0;
  const nReconciliados = matchData?.matches?.length || 0;
  const temAvisos    = nOrfasF > 0 || nOrfasE > 0;

  return (
    <div style={{ marginTop: 32 }}>
      {/* Modal de progresso */}
      {modalVisivel && (
        <ModalProgresso
          passos={modalPassos}
          resultado={modalResultado}
          erro={modalErro}
          onFechar={fecharModal}
        />
      )}

      <Label style={{ marginBottom: 16, fontSize: 13 }}>Reconciliação Bancária</Label>

      {/* Área de upload — acção principal */}
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        style={{
          border: `2px dashed ${dragOver ? C.blue : C.borderDark}`,
          borderRadius: 12,
          padding: "32px 20px",
          textAlign: "center",
          cursor: uploading ? "default" : "pointer",
          marginBottom: 16,
          background: dragOver ? "#eff6ff" : uploading ? "#f0f9ff" : "#fafafa",
          transition: "background 0.15s, border-color 0.15s",
        }}
      >
        <input ref={fileRef} type="file" accept={ACCEPT_EXTRATO} style={{ display: "none" }}
          onChange={e => handleFile(e.target.files[0])} />
        {uploading ? (
          <div style={{ fontSize: 13, color: C.blue, fontWeight: 600 }}>A processar extrato...</div>
        ) : (
          <>
            <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Arrasta o extrato bancário ou clica para escolher</div>
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{LEGENDA_FORMATOS_EXTRATO}</div>
          </>
        )}
      </div>

      {/* Resultado do upload */}
      {uploadMsg && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: opcoes ? 0 : 16, fontSize: 13,
          background: uploadMsg.ok ? C.greenLight : "#FEF0F0",
          color: uploadMsg.ok ? C.green : C.red,
          border: `1px solid ${uploadMsg.ok ? C.greenBorder : "#F5C0BF"}`,
          borderBottomLeftRadius: opcoes ? 0 : 8,
          borderBottomRightRadius: opcoes ? 0 : 8,
        }}>
          {uploadMsg.ok ? "✓ " : "✗ "}{uploadMsg.msg}
        </div>
      )}

      {/* Picker inline quando não foi possível detectar a empresa */}
      {opcoes && (
        <div style={{
          padding: "12px 14px", background: "#FEF8EC", border: "1px solid #F5DFA0",
          borderTop: "none", borderRadius: "0 0 8px 8px", marginBottom: 16,
          display: "flex", gap: 10, alignItems: "center",
        }}>
          <select value={empresaEscolha} onChange={e => setEmpresaEscolha(e.target.value)}
            style={{ flex: 1, padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.borderDark}`, fontSize: 13 }}>
            {opcoes.map(o => <option key={o.nif} value={o.nif}>{o.nome}</option>)}
          </select>
          <Btn variant="primary" disabled={uploading} onClick={confirmarEmpresa}>
            {uploading ? "..." : "Confirmar"}
          </Btn>
        </div>
      )}

      {loading ? <Spinner /> : !matchData ? null : (
        <>
          {/* Cards de sumário */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 24 }}>
            {[
              { label: "Reconciliados",        value: nReconciliados, color: nReconciliados > 0 ? C.green : C.textLight },
              { label: "Movimentos sem fatura", value: nOrfasE,       color: nOrfasE > 0 ? C.orange : C.green },
              { label: "Faturas sem movimento", value: nOrfasF,       color: nOrfasF > 0 ? C.orange : C.green },
            ].map(s => (
              <div key={s.label} style={{ background: C.bg, borderRadius: 10, padding: "12px 16px", border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.textLight, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Tudo OK */}
          {!temAvisos && nReconciliados > 0 && (
            <div style={{ textAlign: "center", padding: "24px", background: C.greenLight, borderRadius: 10, border: `1px solid ${C.greenBorder}` }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>Reconciliação completa</div>
              <div style={{ fontSize: 12, color: "#166534", marginTop: 4 }}>Todos os movimentos têm fatura correspondente.</div>
            </div>
          )}

          {/* Aviso: movimentos sem fatura */}
          {nOrfasE > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, marginBottom: 4 }}>
                ⚠ {nOrfasE} movimento(s) no extrato sem fatura correspondente
              </div>
              <div style={{ fontSize: 11, color: C.textMid, marginBottom: 10 }}>
                Estas transacções constam no extrato bancário mas não existe fatura a justificá-las.
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={["Data", "Descrição", "Valor", "Tipo"]} />
                <tbody>
                  {matchData.extratos_sem_match.map(e => (
                    <TR key={e.id}>
                      <TD muted>{e.data_mov}</TD>
                      <TD style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.descricao}</TD>
                      <TD mono style={{ color: e.tipo === "credito" ? C.green : C.orange }}>{fmt(Math.abs(e.valor))}</TD>
                      <TD><Pill color={e.tipo === "credito" ? "green" : "orange"}>{e.tipo}</Pill></TD>
                    </TR>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Info: faturas sem movimento bancário */}
          {nOrfasF > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.blue, marginBottom: 4 }}>
                ℹ {nOrfasF} fatura(s) ainda sem movimento bancário
              </div>
              <div style={{ fontSize: 11, color: C.textMid, marginBottom: 10 }}>
                Estas faturas foram registadas mas o pagamento ainda não apareceu no extrato.
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={["Documento", "Emitente", "Tipo", "Valor", "Data", "PDF"]} />
                <tbody>
                  {matchData.faturas_sem_match.map(f => (
                    <TR key={f.id}>
                      <TD mono>{f.numero_documento}</TD>
                      <TD>{f.nome_emitente}</TD>
                      <TD muted>{f.tipo_documento?.replace(/_/g, " ") || "—"}</TD>
                      <TD mono>{fmt(f.valor_total)}</TD>
                      <TD muted>{f.data_documento}</TD>
                      <TD>{f.drive_link ? <button onClick={() => openPDF(f.drive_link, f.numero_documento)} style={{ color: C.blue, fontSize: 12, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>PDF →</button> : "—"}</TD>
                    </TR>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DiagnosticoPDF() {
  const [aberto,    setAberto]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [resultado, setResultado] = useState(null);
  const fileRef = useRef(null);

  const testar = async (file) => {
    if (!file) return;
    setLoading(true);
    setResultado(null);
    const fd = new FormData();
    fd.append("arquivo", file);
    try {
      const r = await apiFetch("/api/extratos/diagnostico-pdf", { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      setResultado(d);
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <button
        onClick={() => setAberto(a => !a)}
        style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: 0 }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textLight, letterSpacing: 1, textTransform: "uppercase" }}>
          {aberto ? "▾" : "▸"} Diagnóstico de extrato PDF
        </span>
      </button>

      {aberto && (
        <div style={{ marginTop: 12, padding: 20, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
            Carrega um PDF de extrato bancário para perceber porque o parser falha (não insere nada na BD).
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
            <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }}
              onChange={e => testar(e.target.files[0])} />
            <Btn variant="secondary" disabled={loading} onClick={() => fileRef.current?.click()}>
              {loading ? "A analisar..." : "📂 Escolher PDF"}
            </Btn>
            {resultado && (
              <span style={{ fontSize: 13, fontWeight: 700,
                color: resultado.movimentos_encontrados > 0 ? C.green : C.red }}>
                {resultado.movimentos_encontrados > 0
                  ? `✓ ${resultado.movimentos_encontrados} movimento(s) encontrado(s)`
                  : "✗ Nenhum movimento encontrado"}
              </span>
            )}
          </div>

          {resultado && (
            <>
              {resultado.movimentos_encontrados > 0 && resultado.primeiros_5?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Primeiros movimentos</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <THead cols={["Data", "Descrição", "Valor", "Tipo"]} />
                    <tbody>
                      {resultado.primeiros_5.map((m, i) => (
                        <TR key={i}>
                          <TD muted>{m.data}</TD>
                          <TD style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.descricao}</TD>
                          <TD mono style={{ color: m.tipo === "credito" ? C.green : C.orange }}>{fmt(Math.abs(m.valor))}</TD>
                          <TD><Pill color={m.tipo === "credito" ? "green" : "orange"}>{m.tipo}</Pill></TD>
                        </TR>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Diagnóstico por página</div>
                <pre style={{
                  background: "#1e293b", color: "#94a3b8", borderRadius: 8,
                  padding: "12px 16px", fontSize: 11, fontFamily: "monospace",
                  overflowX: "auto", whiteSpace: "pre-wrap", maxHeight: 300, overflowY: "auto",
                }}>
                  {resultado.diagnostico}
                </pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PageReconciliacao({ onNav }) {
  const { data, loading, refresh } = useApi("/api/reconciliacao", [], null);
  const { data: empresas } = useApi("/api/empresas", [], []);
  const [verificandoDrive, setVerificandoDrive] = useState(false);
  const [driveMsg,         setDriveMsg]         = useState(null);

  const reprocessar = async () => {
    await apiFetch("/api/agent/reprocess-incomplete", { method: "POST" });
    refresh();
  };

  const [limpandoDups, setLimpandoDups] = useState(false);
  const limparDuplicados = async () => {
    if (!window.confirm("Remove movimentos de extrato duplicados na BD. Continuar?")) return;
    setLimpandoDups(true);
    try {
      const r = await apiFetch("/api/extratos/limpar-duplicados", { method: "POST" });
      if (r.ok) {
        const d = await r.json();
        alert(d.removidos > 0 ? `✓ ${d.removidos} duplicado(s) removido(s).` : "Nenhum duplicado encontrado.");
        refresh();
      }
    } finally { setLimpandoDups(false); }
  };

  const verificarDrive = async () => {
    setVerificandoDrive(true);
    setDriveMsg(null);
    try {
      const r = await apiFetch("/api/faturas/verificar-drive", { method: "POST" });
      if (r.ok) {
        const d = await r.json();
        if (d.ausentes === 0) {
          setDriveMsg({ ok: true, msg: `${d.verificadas} ficheiro(s) verificado(s) — tudo presente no Drive.` });
        } else {
          setDriveMsg({ ok: false, msg: `${d.ausentes} ficheiro(s) ausentes no Drive — faturas resetadas para reprocessamento: ${d.faturas.map(f => f.numero_documento).join(", ")}` });
          refresh();
        }
      } else {
        const d = await r.json().catch(() => {});
        setDriveMsg({ ok: false, msg: d?.detail || "Erro ao verificar Drive." });
      }
    } finally {
      setVerificandoDrive(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div style={{ borderLeft: `4px solid ${C.blue}`, paddingLeft: 16 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Reconciliação</h1>
          <p style={{ color: C.textMid, fontSize: 13, marginTop: 4 }}>Anomalias e discrepâncias detectadas automaticamente</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" disabled={limpandoDups} onClick={limparDuplicados}>
            {limpandoDups ? "A limpar..." : "🧹 Limpar duplicados"}
          </Btn>
          <Btn variant="secondary" disabled={verificandoDrive} onClick={verificarDrive}>
            {verificandoDrive ? "A verificar..." : "🔍 Verificar Drive"}
          </Btn>
          <Btn variant="secondary" onClick={refresh}>↻ Actualizar</Btn>
        </div>
      </div>
      {driveMsg && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: driveMsg.ok ? C.greenLight : "#FEF0F0",
          color: driveMsg.ok ? C.green : C.red,
          border: `1px solid ${driveMsg.ok ? C.greenBorder : "#F5C0BF"}`,
        }}>
          {driveMsg.ok ? "✓ " : "⚠ "}{driveMsg.msg}
        </div>
      )}

      {loading ? <Spinner /> : !data ? <Empty msg="Sem dados." /> : (
        <>
          {/* Resumo */}
          {data.total_anomalias === 0 ? (
            <Card style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, marginBottom: 24, textAlign: "center", padding: "28px" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>Sem anomalias detectadas</div>
              <div style={{ fontSize: 13, color: "#166534", marginTop: 4 }}>Todos os documentos estão consistentes.</div>
            </Card>
          ) : (
            <AlertBanner alerts={[{ id: "total", type: "warn", msg: `${data.total_anomalias} anomalia(s) detectada(s) — revê cada secção abaixo` }]} />
          )}

          {/* Duplicados suspeitos */}
          <SectionRecon title="Duplicados suspeitos" count={data.duplicados?.length || 0} color={C.orange}>
            {(data.duplicados || []).map((d, i) => (
              <div key={i} style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>⚠ {d.desc}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {d.docs.map((doc, j) => (
                    <div key={j} style={{ background: C.bg, borderRadius: 8, padding: "10px 12px", border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 11, fontFamily: "monospace", color: C.textMid }}>{doc.numero_documento}</div>
                      <div style={{ fontSize: 12, color: C.text, marginTop: 3 }}>{doc.data_documento} · {fmt(doc.valor_total)}</div>
                      {doc.drive_link && <button onClick={() => openPDF(doc.drive_link, doc.numero_documento)} style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Ver PDF →</button>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </SectionRecon>

          {/* Campos em falta */}
          <SectionRecon title="Campos críticos em falta" count={data.campos_em_falta?.length || 0} color={C.red}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <THead cols={["Documento", "Emitente", "NIF", "Tipo", "Valor", "Empresa", "PDF"]} />
              <tbody>
                {(data.campos_em_falta || []).map(f => (
                  <TR key={f.id} highlight={!f.nif_emitente || f.nif_emitente === "999999990"}>
                    <TD mono>{f.numero_documento || "—"}</TD>
                    <TD>{f.nome_emitente || "—"}</TD>
                    <TD mono muted={f.nif_emitente === "999999990"}>{f.nif_emitente || <span style={{ color: C.red }}>Em falta</span>}</TD>
                    <TD muted>{f.tipo_documento || <span style={{ color: C.red }}>Em falta</span>}</TD>
                    <TD mono style={{ color: f.valor_total === 0 ? C.red : C.text }}>{fmt(f.valor_total)}</TD>
                    <TD muted>{f.nome_empresa || f.nif_recetor || "—"}</TD>
                    <TD>{f.drive_link ? <button onClick={() => openPDF(f.drive_link, f.numero_documento)} style={{ color: C.blue, fontSize: 12, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>PDF →</button> : "—"}</TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </SectionRecon>

          {/* Incompletos (sem Drive ou Sheets) */}
          <SectionRecon title="Documentos sem Drive ou Sheets" count={data.incompletas?.length || 0} color={C.orange}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <THead cols={["Documento", "Emitente", "Valor", "Empresa", "Drive", "Sheets", "Acção"]} />
              <tbody>
                {(data.incompletas || []).map(f => (
                  <TR key={f.id}>
                    <TD mono>{f.numero_documento}</TD>
                    <TD>{f.nome_emitente}</TD>
                    <TD mono>{fmt(f.valor_total)}</TD>
                    <TD muted>{f.nome_empresa || "—"}</TD>
                    <TD><Pill color={f.drive_ok ? "green" : "red"}>{f.drive_ok ? "✓" : "✗"}</Pill></TD>
                    <TD><Pill color={f.sheets_ok ? "green" : "red"}>{f.sheets_ok ? "✓" : "✗"}</Pill></TD>
                    <TD><Btn variant="orange" size="sm" onClick={reprocessar}>Reprocessar</Btn></TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </SectionRecon>

          {/* Pendentes antigos */}
          <SectionRecon title="Decisões pendentes há +7 dias" count={data.pendentes_antigos?.length || 0} color={C.red}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <THead cols={["Motivo", "Pergunta", "Documento", "Empresa", "Valor", "Desde", "Acção"]} />
              <tbody>
                {(data.pendentes_antigos || []).map(p => (
                  <TR key={p.id}>
                    <TD><Pill color="red">{p.motivo?.replace(/_/g," ") || "—"}</Pill></TD>
                    <TD style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.pergunta}</TD>
                    <TD mono>{p.numero_documento}</TD>
                    <TD muted>{p.nome_empresa || "—"}</TD>
                    <TD mono>{fmt(p.valor_total)}</TD>
                    <TD muted>{fmtDate(p.criada_em)}</TD>
                    <TD><Btn variant="primary" size="sm" onClick={() => onNav && onNav("pendentes")}>Responder →</Btn></TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </SectionRecon>

          {/* ── Casamento Bancário ── */}
          <SecaoCasamentoBancario empresas={empresas} />

          {/* ── Diagnóstico de PDF ── */}
          <DiagnosticoPDF />
        </>
      )}
    </div>
  );
}

// ─── Fundos de Projeto ────────────────────────────────────────────────────────

// Gestão de despesas (faturas + reembolsos) associadas a uma rubrica — ver,
// remover, e pesquisar/associar despesas ainda sem rubrica atribuída.
function RubricaDespesas({ rubrica, empresaNif, onChanged }) {
  const [aberto, setAberto]         = useState(false);
  const [despesas, setDespesas]     = useState(null);
  const [pesquisa, setPesquisa]     = useState("");
  const [resultados, setResultados] = useState(null);
  const [ocupado, setOcupado]       = useState(false);

  const carregar = async () => {
    const r = await apiFetch(`/api/fundos/rubricas/${rubrica.id}/despesas`);
    if (r.ok) setDespesas(await r.json());
  };

  useEffect(() => { if (aberto && !despesas) carregar(); }, [aberto]); // eslint-disable-line

  const pesquisar = async (texto) => {
    setPesquisa(texto);
    if (!texto.trim()) { setResultados(null); return; }
    const r = await apiFetch(`/api/despesas-disponiveis?empresa=${empresaNif}&pesquisa=${encodeURIComponent(texto)}`);
    if (r.ok) setResultados(await r.json());
  };

  const associar = async (item) => {
    setOcupado(true);
    try {
      await apiFetch(`/api/fundos/rubricas/${rubrica.id}/despesas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tipo: item.tipo, id: item.id }),
      });
      setPesquisa(""); setResultados(null);
      await carregar();
      onChanged();
    } finally {
      setOcupado(false);
    }
  };

  const desassociar = async (item) => {
    setOcupado(true);
    try {
      await apiFetch(`/api/fundos/rubricas/${rubrica.id}/despesas/${item.tipo}/${item.id}`, { method: "DELETE" });
      await carregar();
      onChanged();
    } finally {
      setOcupado(false);
    }
  };

  const todasDespesas   = despesas   ? [...despesas.faturas, ...despesas.reembolsos]     : [];
  const todosResultados = resultados ? [...resultados.faturas, ...resultados.reembolsos] : [];

  return (
    <div style={{ marginTop: 6 }}>
      <button onClick={() => setAberto(a => !a)} style={{ fontSize: 11, fontWeight: 600, color: C.blue, background: "none", border: "none", cursor: "pointer", padding: "2px 0" }}>
        {aberto ? "▲ Ocultar despesas" : `▼ Ver/associar despesas${todasDespesas.length ? ` (${todasDespesas.length})` : ""}`}
      </button>
      {aberto && (
        <div style={{ marginTop: 8, padding: "10px 12px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8 }}>
          {despesas === null ? (
            <div style={{ fontSize: 12, color: C.textLight }}>A carregar…</div>
          ) : todasDespesas.length === 0 ? (
            <div style={{ fontSize: 12, color: C.textLight, marginBottom: 10 }}>Nenhuma despesa associada ainda.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10 }}>
              {todasDespesas.map(d => (
                <div key={`${d.tipo}-${d.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "5px 8px", background: C.bg, borderRadius: 6 }}>
                  <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: d.tipo === "fatura" ? C.blue : "#5B21B6", marginRight: 6 }}>{d.tipo === "fatura" ? "Fatura" : "Reembolso"}</span>
                    {d.descricao}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(d.valor)}</span>
                    <button onClick={() => desassociar(d)} disabled={ocupado} style={{ background: "none", border: "none", color: C.textLight, cursor: "pointer", fontSize: 14 }} title="Remover desta rubrica">×</button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <input
            value={pesquisa}
            onChange={e => pesquisar(e.target.value)}
            placeholder="Pesquisar fatura ou reembolso para associar..."
            style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, boxSizing: "border-box" }}
          />
          {pesquisa.trim() && resultados && (
            todosResultados.length > 0 ? (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4, maxHeight: 160, overflowY: "auto" }}>
                {todosResultados.map(d => (
                  <button key={`${d.tipo}-${d.id}`} onClick={() => associar(d)} disabled={ocupado}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "6px 8px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: d.tipo === "fatura" ? C.blue : "#5B21B6", marginRight: 6 }}>{d.tipo === "fatura" ? "Fatura" : "Reembolso"}</span>
                      {d.descricao}
                    </span>
                    <span style={{ fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>{fmt(d.valor)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>Nenhuma despesa por associar encontrada.</div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function PageFundos() {
  const { empresa } = useEmpresa();
  const url = `/api/fundos${empresa ? `?empresa=${empresa}` : ""}`;
  const { data: fundos, loading, refresh } = useApi(url, [empresa]);

  const [novoAberto, setNovoAberto] = useState(false);
  const [nNumero, setNNumero]   = useState("");
  const [nNome, setNNome]       = useState("");
  const [nValor, setNValor]     = useState("");
  const [nInicio, setNInicio]   = useState("");
  const [nFim, setNFim]         = useState("");
  const [criando, setCriando]   = useState(false);
  const [rubricaForm, setRubricaForm] = useState({});
  const [ocupado, setOcupado]   = useState({});

  const criarFundo = async () => {
    if (!nNumero.trim() || !nNome.trim() || !empresa) return;
    setCriando(true);
    try {
      await apiFetch("/api/fundos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          numero_fundo: nNumero.trim(), nome: nNome.trim(),
          valor: parseFloat(nValor) || 0,
          data_inicio: nInicio || null, data_fim: nFim || null,
          empresa_nif: empresa,
        }),
      });
      setNNumero(""); setNNome(""); setNValor(""); setNInicio(""); setNFim("");
      setNovoAberto(false);
      refresh();
    } finally {
      setCriando(false);
    }
  };

  const apagarFundo = async (id) => {
    if (!window.confirm("Apagar este fundo e todas as suas rubricas?")) return;
    setOcupado(o => ({ ...o, [`f${id}`]: true }));
    try {
      await apiFetch(`/api/fundos/${id}`, { method: "DELETE" });
      refresh();
    } finally {
      setOcupado(o => ({ ...o, [`f${id}`]: false }));
    }
  };

  const adicionarRubrica = async (fundoId) => {
    const form = rubricaForm[fundoId] || {};
    if (!(form.nome || "").trim()) return;
    setOcupado(o => ({ ...o, [`r${fundoId}`]: true }));
    try {
      await apiFetch(`/api/fundos/${fundoId}/rubricas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: form.nome.trim(), valor: parseFloat(form.valor) || 0 }),
      });
      setRubricaForm(p => ({ ...p, [fundoId]: { nome: "", valor: "" } }));
      refresh();
    } finally {
      setOcupado(o => ({ ...o, [`r${fundoId}`]: false }));
    }
  };

  const removerRubrica = async (fundoId, rubricaId) => {
    setOcupado(o => ({ ...o, [`rb${rubricaId}`]: true }));
    try {
      await apiFetch(`/api/fundos/${fundoId}/rubricas/${rubricaId}`, { method: "DELETE" });
      refresh();
    } finally {
      setOcupado(o => ({ ...o, [`rb${rubricaId}`]: false }));
    }
  };

  const inputStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" };

  return (
    <div>
      <PageHeading eyebrow="Gestão financeira" title="Fundos de projeto" description={`${fundos?.length ?? 0} fundo${(fundos?.length ?? 0) !== 1 ? "s" : ""} registado${(fundos?.length ?? 0) !== 1 ? "s" : ""}`} action={<Btn onClick={() => setNovoAberto(o => !o)}>{novoAberto ? "Cancelar" : "+ Novo Fundo"}</Btn>} />

      {novoAberto && (
        <Card style={{ marginBottom: 20 }}>
          <Label>Novo fundo</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 5 }}>Número do fundo</div>
              <input value={nNumero} onChange={e => setNNumero(e.target.value)} placeholder="Ex: POCI-01-0247-FEDER-012345" style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 5 }}>Nome</div>
              <input value={nNome} onChange={e => setNNome(e.target.value)} placeholder="Nome do projeto" style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 5 }}>Valor total (€)</div>
              <input type="number" value={nValor} onChange={e => setNValor(e.target.value)} placeholder="0.00" style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 5 }}>Data início</div>
              <input type="date" value={nInicio} onChange={e => setNInicio(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 5 }}>Data fim</div>
              <input type="date" value={nFim} onChange={e => setNFim(e.target.value)} style={inputStyle} />
            </div>
          </div>
          {/* Sem seletor de empresa aqui -- cria sempre na empresa ativa na
              app (useEmpresa); "Todas as Empresas" bloqueia a criação. */}
          {!empresa && (
            <div style={{ marginBottom: 14, padding: "9px 12px", borderRadius: 8, background: C.orangeLight, border: `1px solid ${C.orangeBorder}`, fontSize: 12.5, color: C.orange, fontWeight: 600 }}>
              Escolhe uma empresa (não "Todas as Empresas") para criar um fundo.
            </div>
          )}
          <Btn onClick={criarFundo} disabled={criando || !nNumero.trim() || !nNome.trim() || !empresa}>
            {criando ? "A criar…" : "Criar fundo"}
          </Btn>
        </Card>
      )}

      {loading ? <Spinner /> : !(fundos || []).length ? <Empty msg="Nenhum fundo registado" /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {fundos.map(f => {
            const excedido = f.valor_rubricado > f.valor;
            const pctGasto = f.valor > 0 ? Math.round((f.valor_gasto / f.valor) * 100) : 0;
            const rForm = rubricaForm[f.id] || { nome: "", valor: "" };
            return (
              <Card key={f.id} p="0" style={{ overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontFamily: "monospace", color: C.blue, fontWeight: 700, marginBottom: 3 }}>{f.numero_fundo}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{f.nome}</div>
                    <div style={{ fontSize: 12, color: C.textLight, marginTop: 3 }}>
                      {f.nome_empresa || f.empresa_nif}
                      {(f.data_inicio || f.data_fim) && <span> · {fmtDate(f.data_inicio)} — {fmtDate(f.data_fim)}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1 }}>Valor do fundo</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(f.valor)}</div>
                    </div>
                    <button onClick={() => apagarFundo(f.id)} disabled={ocupado[`f${f.id}`]}
                      style={{ padding: "6px 10px", borderRadius: 7, border: `1px solid ${C.redBorder}`, background: C.redLight, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                      Apagar
                    </button>
                  </div>
                </div>

                <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
                  {/* Orçamento repartido pelas rubricas — só planeamento, não é despesa */}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMid, marginBottom: 10 }}>
                    <span>Orçamento repartido em rubricas: <strong>{fmt(f.valor_rubricado)}</strong> de {fmt(f.valor)}</span>
                    {excedido && <span style={{ color: C.red, fontWeight: 600 }}>excede o valor do fundo</span>}
                  </div>
                  {/* Gasto real — soma das faturas associadas a rubricas (fica em 0€ até essa associação existir) */}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMid, marginBottom: 6 }}>
                    <span>Gasto: <strong>{fmt(f.valor_gasto)}</strong></span>
                    <span>{pctGasto}%</span>
                  </div>
                  <div style={{ height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(pctGasto, 100)}%`, background: C.blue, borderRadius: 3 }} />
                  </div>
                </div>

                <div style={{ padding: "14px 20px" }}>
                  <Label style={{ marginBottom: 10 }}>Rubricas ({f.rubricas.length})</Label>
                  {f.rubricas.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                      {f.rubricas.map(r => (
                        <div key={r.id} style={{ padding: "8px 12px", background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{r.nome}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 12, color: C.textLight, fontVariantNumeric: "tabular-nums" }}>{fmt(r.valor_gasto)} de</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(r.valor)}</span>
                              <button onClick={() => removerRubrica(f.id, r.id)} disabled={ocupado[`rb${r.id}`]}
                                style={{ background: "none", border: "none", color: C.textLight, cursor: "pointer", fontSize: 15, padding: "0 4px" }} title="Remover rubrica">
                                ×
                              </button>
                            </div>
                          </div>
                          <RubricaDespesas rubrica={r} empresaNif={f.empresa_nif} onChanged={refresh} />
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input
                      value={rForm.nome}
                      onChange={e => setRubricaForm(p => ({ ...p, [f.id]: { ...rForm, nome: e.target.value } }))}
                      placeholder="Nome da rubrica"
                      style={{ flex: "1 1 160px", padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, boxSizing: "border-box" }}
                    />
                    <input
                      type="number"
                      value={rForm.valor}
                      onChange={e => setRubricaForm(p => ({ ...p, [f.id]: { ...rForm, valor: e.target.value } }))}
                      placeholder="Valor (€)"
                      style={{ width: 120, padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, boxSizing: "border-box" }}
                    />
                    <Btn size="sm" variant="secondary" onClick={() => adicionarRubrica(f.id)} disabled={ocupado[`r${f.id}`] || !(rForm.nome || "").trim()}>
                      + Adicionar
                    </Btn>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Empresas ─────────────────────────────────────────────────────────────────

function PageEmpresas({ onSelectEmpresa }) {
  const { data: empresas, loading, refresh } = useApi("/api/empresas");
  const toggle = async (nif) => { await apiFetch(`/api/empresas/${nif}/toggle`, { method: "PATCH" }); refresh(); };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ borderLeft: `4px solid ${C.blue}`, paddingLeft: 16 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Empresas</h1>
          <p style={{ color: C.textMid, fontSize: 13, marginTop: 4 }}>{empresas?.length ?? 0} empresas registadas · gestão e configuração</p>
        </div>
      </div>
      {loading ? <Spinner /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(empresas || []).map(e => (
            <Card key={e.nif} p="16px 20px" style={{ borderLeft: `4px solid ${e.ativa ? C.blue : C.borderDark}` }}>
              <div className="emp-card-row" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: e.ativa ? C.blue : "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
                  {(e.nome || "?")[0]}
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{e.nome}</div>
                  <div style={{ fontSize: 11, color: C.textLight, fontFamily: "monospace", marginTop: 2 }}>NIF: {e.nif}</div>
                </div>
                <div className="emp-card-stats" style={{ display: "flex", gap: 20, alignItems: "center" }}>
                  {[
                    { label: "Faturas", value: e.total_faturas },
                    { label: "Volume",  value: fmt(e.valor_total) },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, letterSpacing: 1, textTransform: "uppercase" }}>{s.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text, fontFamily: "monospace" }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div className="emp-card-btns" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <Btn variant={e.ativa ? "success" : "secondary"} size="sm" onClick={() => toggle(e.nif)}>
                    {e.ativa ? "✓ Activa" : "Inactiva"}
                  </Btn>
                  <button onClick={() => onSelectEmpresa && onSelectEmpresa(e.nif)}
                    style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: "#fff", fontSize: 12, color: C.blue, cursor: "pointer", fontWeight: 600 }}>
                    Dashboard →
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Agente ───────────────────────────────────────────────────────────────────
const INTERVAL_LABELS = { manual: "Manual", "1h": "1 hora", "2h": "2 horas", "6h": "6 horas", "12h": "12 horas", "24h": "Diário" };

// Parseia uma linha do Python logging:
// "2026-06-02 10:43:57 | INFO     | src.services.ai_service | Mensagem aqui"
const parseLogLine = (raw) => {
  const m = raw.match(/^\d{4}-\d{2}-\d{2} (\d{2}:\d{2}:\d{2}) \| (\w+)\s*\| ([^|]+)\| (.+)$/);
  if (m) {
    const level   = m[2].trim();
    const modFull = m[3].trim();
    const module  = modFull.split(".").pop(); // só o último segmento
    return { time: m[1], level, module, message: m[4].trim() };
  }
  return { time: null, level: null, module: null, message: raw };
};

const LOG_BADGE = {
  INFO:     { bg: "#0c2340", fg: "#7dd3fc", label: "INFO" },
  WARNING:  { bg: "#3d1200", fg: "#fdba74", label: "WARN" },
  ERROR:    { bg: "#3b0000", fg: "#fca5a5", label: "ERR"  },
  CRITICAL: { bg: "#2d0050", fg: "#e9d5ff", label: "CRIT" },
  DEBUG:    { bg: "#1a1f28", fg: "#475569", label: "DBG"  },
};

// Estado + escrita da configuração do agente. Extraído de PageAgente quando
// a mesma configuração passou a ser editável também em Sistema → Configuração
// (pedido do dono do projeto) -- duas cópias do estado divergiriam.
function useAgenteConfig() {
  const [cfg, setCfg]             = useState({ mock_mode: false, schedule_interval: "manual", last_run_em: null, last_run_status: "", next_run_em: null, assistente_ativo: true });
  const [savingCfg, setSavingCfg] = useState(false);
  const [erroCfg, setErroCfg]     = useState(null);

  const loadCfg = () => apiFetch("/api/agente/config").then(r => r.ok ? r.json() : null).then(d => { if (d) setCfg(d); });
  useEffect(() => { loadCfg(); }, []); // eslint-disable-line

  // Nunca engolir a falha: antes isto ignorava a resposta por completo, por
  // isso um 403/401/500 (ou um preflight CORS bloqueado) fazia a UI recarregar
  // o valor ANTIGO sem dizer nada -- o sintoma reportado era "troco a
  // periodicidade e ele não troca, é como se desse erro", sem pista nenhuma
  // do que falhou. O erro fica agora visível ao lado do título "Configuração".
  const saveCfg = async (patch) => {
    setSavingCfg(true);
    setErroCfg(null);
    try {
      const r = await apiFetch("/api/agente/config", {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
      });
      if (!r.ok) {
        let detalhe = `HTTP ${r.status}`;
        try { const j = await r.json(); if (j?.detail) detalhe = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch {}
        setErroCfg(`Não foi possível guardar (${detalhe}).`);
      }
    } catch (e) {
      // fetch só rejeita em falha de rede/CORS -- distinguir ajuda a perceber
      // que o problema não é o servidor a recusar, é o pedido nem lá chegar.
      setErroCfg(`Não foi possível contactar o servidor: ${e?.message || "erro de rede"}.`);
    }
    await loadCfg();
    setSavingCfg(false);
  };

  return { cfg, saveCfg, savingCfg, erroCfg, loadCfg };
}

// Cartão de configuração do agente -- partilhado entre a página Agente e
// Sistema -> Configuracao. Puramente de apresentacao: o estado vive no hook
// useAgenteConfig, para nao haver duas copias a divergir.
function AgenteConfigCard({ cfg, saveCfg, savingCfg, erroCfg }) {
  // Formatador próprio: o original vivia dentro de PageAgente, e este cartão
  // passou a ser usado também em Sistema -> Configuracao, onde esse scope
  // não existe.
  const fmtTs = (iso) => {
    try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <Label style={{ marginBottom: 0 }}>Configuração</Label>
            {savingCfg && <span style={{ fontSize: 11, color: C.textLight }}>A guardar…</span>}
          </div>
          {erroCfg && (
            <div style={{ marginBottom: 14, padding: "9px 12px", borderRadius: 8, fontSize: 12, background: C.redLight, color: C.red, border: `1px solid ${C.redBorder}` }}>
              ✗ {erroCfg}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Mock Mode */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Modo de execução</div>
                <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{cfg.mock_mode ? "Simulação — sem chamadas reais às APIs" : "Produção — emails, Drive e Sheets reais"}</div>
              </div>
              <button
                onClick={() => saveCfg({ mock_mode: !cfg.mock_mode })}
                style={{ width: 44, height: 24, borderRadius: 12, background: cfg.mock_mode ? C.orange : C.green, position: "relative", border: "none", cursor: "pointer", flexShrink: 0, transition: ".2s" }}
              >
                <div style={{ position: "absolute", top: 3, left: cfg.mock_mode ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: ".2s", boxShadow: "0 1px 4px rgba(0,0,0,.25)" }} />
              </button>
            </div>

            {/* Periodicidade */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Periodicidade automática</div>
              <select
                value={cfg.schedule_interval}
                onChange={e => saveCfg({ schedule_interval: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: `1px solid ${C.borderDark}`, fontSize: 13, color: C.text, background: "#fff", cursor: "pointer" }}
              >
                {Object.entries(INTERVAL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>
                {cfg.schedule_interval === "manual" ? "Execução apenas manual (botão acima)." : cfg.next_run_em ? `Próxima execução automática: ${fmtTs(cfg.next_run_em)}` : "Agendado."}
              </div>
            </div>

            {cfg.schedule_interval === "24h" && <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Hora da execução diária</div>
              <input type="time" value={cfg.schedule_hora_execucao || "20:00"} onChange={e => saveCfg({ schedule_hora_execucao: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: `1px solid ${C.borderDark}`, fontSize: 13, color: C.text, background: "#fff" }} />
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>
                Às {cfg.schedule_hora_execucao || "20:00"}, o agente procura todos os emails novos e retoma os casos pendentes que já foram corrigidos. Se o serviço estiver indisponível nessa hora, executa quando voltar a ficar disponível.
              </div>
            </div>}

            {/* Utilização diária do Gemini — barra + limite editável */}
            <div style={{ padding: "12px 14px", background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              {(() => {
                const uso = cfg.gemini_uso_hoje ?? 0;
                const limite = cfg.gemini_limite_diario || 250;
                const pct = Math.min(100, Math.round((uso / limite) * 100));
                const cor = pct >= 90 ? C.red : pct >= 70 ? C.orange : C.green;
                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Chamadas à IA hoje</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: cor, fontVariantNumeric: "tabular-nums" }}>{uso} / {limite} ({pct}%)</div>
                    </div>
                    <div style={{ height: 8, borderRadius: 99, background: C.border, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: cor, transition: "width 0.3s" }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: C.textMid, flexShrink: 0 }}>Limite diário:</span>
                      <input type="number" min={1} defaultValue={limite} key={limite}
                        onBlur={e => { const v = parseInt(e.target.value, 10); if (v >= 1 && v !== limite) saveCfg({ gemini_limite_diario: v }); }}
                        style={{ width: 90, padding: "6px 9px", borderRadius: 8, border: `1px solid ${C.borderDark}`, fontSize: 13, color: C.text, background: "#fff" }} />
                      <span style={{ fontSize: 10.5, color: C.textLight }}>
                        {cfg.gemini_chave_paga
                          ? "Renova ~08:00 (fuso da Google). Ao esgotar a quota gratuita, o sistema passa automaticamente para a chave paga de reserva."
                          : "Renova ~08:00 (fuso da Google). Se a Google confirmar quota esgotada, a barra salta para 100% e o agente pára até renovar (1 aviso/dia)."}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Assistente (chat de consulta) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>💬 Assistente (chat)</div>
                <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{cfg.assistente_ativo === false ? "Desativado — botão flutuante escondido para todos" : "Ativo — botão flutuante visível no dashboard"}</div>
              </div>
              <button
                onClick={() => saveCfg({ assistente_ativo: !(cfg.assistente_ativo !== false) })}
                style={{ width: 44, height: 24, borderRadius: 12, background: cfg.assistente_ativo === false ? C.textLight : C.green, position: "relative", border: "none", cursor: "pointer", flexShrink: 0, transition: ".2s" }}
              >
                <div style={{ position: "absolute", top: 3, left: cfg.assistente_ativo === false ? 3 : 22, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: ".2s", boxShadow: "0 1px 4px rgba(0,0,0,.25)" }} />
              </button>
            </div>
          </div>
        </Card>
  );
}

function PageAgente() {
  const [logs, setLogs]           = useState([]);
  const [running, setRunning]     = useState(false);
  const [logTab, setLogTab]       = useState("live"); // "live" | "hist"
  const [histLogs, setHistLogs]   = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  // Só leitura aqui: a EDIÇÃO da configuração vive em Sistema → Configuração
  // (mesmo hook, mesmo cartão). Esta página continua a precisar do cfg para
  // saber o modo de execução, o estado do último run e a próxima execução.
  const { cfg, loadCfg } = useAgenteConfig();
  const logsRef = useRef(null);
  const { data: incompletas, refresh: refreshIncompletas } = useApi("/api/faturas/incompletas");

  useEffect(() => { if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight; }, [logs]);

  const loadHist = async () => {
    setHistLoading(true);
    const r = await apiFetch("/api/logs?n=400");
    if (r.ok) { const d = await r.json(); setHistLogs(d.lines || []); }
    setHistLoading(false);
  };
  useEffect(() => { if (logTab === "hist") loadHist(); }, [logTab]); // eslint-disable-line

  const addLog = (msg, typeHint = "info") => {
    const lm = msg.match(/\| (INFO|WARNING|ERROR|CRITICAL|DEBUG)\s*\|/);
    let type = typeHint;
    if (lm) {
      const lv = lm[1];
      type = lv === "ERROR" || lv === "CRITICAL" ? "error" : lv === "WARNING" ? "warn"
           : msg.includes("✅") || msg.includes("Concluído") ? "success" : "info";
    } else {
      type = msg.includes("❌") ? "error" : msg.includes("⏳") || msg.includes("WARNING") ? "warn"
           : msg.includes("✅") || msg.includes("Concluído") ? "success" : typeHint;
    }
    setLogs(p => [...p, { msg, type, ts: new Date().toLocaleTimeString("pt-PT") }]);
  };

  const runAgent = async () => {
    setLogs([]); setRunning(true); setLogTab("live");
    addLog(`▶ A iniciar o agente${cfg.mock_mode ? " (MODO TESTE)" : ""}...`, "info");
    try {
      const response = await apiFetch("/api/agent/run", { method: "POST" });
      // Sem isto, um 403/401/500 devolvia um corpo JSON normal (sem linhas
      // "data:"), o leitor não encontrava nada e a execução parecia terminar
      // instantaneamente e com sucesso -- sintoma real: "▶ A iniciar" seguido
      // de "■ Execução terminada." no mesmo segundo, sem explicação nenhuma.
      if (!response.ok) {
        let detalhe = `HTTP ${response.status}`;
        try { const j = await response.json(); if (j?.detail) detalhe = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch {}
        addLog(`❌ O servidor recusou o pedido: ${detalhe}`, "error");
        setRunning(false);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        decoder.decode(value).split("\n").filter(l => l.startsWith("data: ")).forEach(line => {
          const msg = line.replace("data: ", "").trim();
          if (msg) addLog(msg);
        });
      }
      refreshIncompletas(); loadCfg();
    } catch (e) { addLog(`❌ Erro: ${e.message}`, "error"); }
    setRunning(false);
    addLog("■ Execução terminada.", "info");
  };

  const reprocess = async () => {
    if (!incompletas?.length) return;
    if (!window.confirm(`Marcar ${incompletas.length} fatura(s) para reprocessamento?`)) return;
    const r = await apiFetch("/api/agent/reprocess-incomplete", { method: "POST" });
    const d = await r.json();
    addLog(`🔄 ${d.message}`, "success");
    refreshIncompletas();
  };

  const fmtTs = (iso) => { try { return new Date(iso).toLocaleString("pt-PT", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }); } catch { return iso; } };

  const statusColor = running ? C.orange : cfg.last_run_status === "ok" ? C.green : cfg.last_run_status === "error" ? C.red : C.textMid;
  const statusBg    = running ? C.orangeLight : cfg.last_run_status === "ok" ? C.greenLight : cfg.last_run_status === "error" ? C.redBg : C.bg;
  const statusBord  = running ? C.orangeBorder : cfg.last_run_status === "ok" ? C.greenBorder : cfg.last_run_status === "error" ? C.redBorder : C.border;
  const statusLabel = running ? "A correr…" : cfg.last_run_status === "ok" ? "Concluído com sucesso" : cfg.last_run_status === "error" ? "Erro na última execução" : cfg.last_run_em ? "Parado" : "Nunca executou";

  return (
    <div>
      {/* ── Banner de estado ── */}
      <div style={{ background: statusBg, border: `1px solid ${statusBord}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: statusColor + "20", border: `2px solid ${statusColor}40`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {running
              ? <div style={{ width: 18, height: 18, border: `3px solid ${statusColor}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              : <div style={{ width: 12, height: 12, borderRadius: "50%", background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
            }
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: statusColor }}>{statusLabel}</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
              {cfg.last_run_em ? `Último run: ${fmtTs(cfg.last_run_em)}` : "Configure e inicia o agente abaixo"}
              {cfg.schedule_interval !== "manual" && cfg.next_run_em && ` · Próximo: ${fmtTs(cfg.next_run_em)}`}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
          {incompletas?.length > 0 && (
            <button onClick={reprocess} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 9, border: `1px solid ${C.orangeBorder}`, background: C.orangeLight, color: C.orange, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              🔄 Reprocessar ({incompletas.length})
            </button>
          )}
          <button
            onClick={runAgent}
            disabled={running}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 10, border: "none", background: running ? C.textLight : cfg.mock_mode ? C.orange : C.blue, color: "#fff", fontSize: 14, fontWeight: 800, cursor: running ? "not-allowed" : "pointer", boxShadow: running ? "none" : `0 4px 14px ${cfg.mock_mode ? C.orange : C.blue}50`, transition: ".15s", letterSpacing: "-.01em" }}
          >
            {running ? "⏳ A processar…" : cfg.mock_mode ? "▶ Correr (TESTE)" : "▶ Correr Agente"}
          </button>
        </div>
      </div>

      {/* ── Terminal de logs ── */}
      <Card p="0" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Barra do terminal */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#0d1117" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", gap: 5 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff5f57" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#ffbd2e" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#28c840" }} />
            </div>
            <span style={{ fontSize: 11, color: "#475569", marginLeft: 8, fontFamily: "monospace" }}>agent · log</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Tabs live/hist */}
            {["live", "hist"].map(t => (
              <button key={t} onClick={() => setLogTab(t)}
                style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 5, border: "none", cursor: "pointer", fontFamily: "monospace", letterSpacing: "0.05em",
                  background: logTab === t ? "#1e3a5f" : "transparent", color: logTab === t ? "#7dd3fc" : "#475569" }}>
                {t === "live" ? "AO VIVO" : "HISTÓRICO"}
              </button>
            ))}
            {logTab === "live" && logs.length > 0 && (
              <button onClick={() => setLogs([])} style={{ fontSize: 10, color: "#475569", background: "none", border: "none", cursor: "pointer", fontFamily: "monospace" }}>limpar</button>
            )}
            {logTab === "hist" && (
              <button onClick={loadHist} style={{ fontSize: 10, color: "#475569", background: "none", border: "none", cursor: "pointer", fontFamily: "monospace" }}>↻ reload</button>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: running ? "#fb923c" : "#4ade80", boxShadow: running ? "0 0 6px #fb923c" : "0 0 6px #4ade80" }} />
              <span style={{ color: running ? "#fb923c" : "#4ade80", fontFamily: "monospace", fontSize: 10 }}>{running ? "a correr" : "pronto"}</span>
            </div>
          </div>
        </div>
        {/* Cabeçalho das colunas */}
        <div style={{ display: "flex", gap: 0, padding: "4px 16px", background: "#0d1117", borderBottom: "1px solid #1a2332" }}>
          <span style={{ color: "#2d3d4f", fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", minWidth: 72 }}>Hora</span>
          <span style={{ color: "#2d3d4f", fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", minWidth: 46, marginRight: 8 }}>Nível</span>
          <span style={{ color: "#2d3d4f", fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", width: 110, marginRight: 8 }}>Módulo</span>
          <span style={{ color: "#2d3d4f", fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Mensagem</span>
        </div>

        {/* ── Aba AO VIVO ── */}
        {logTab === "live" && (
          <div ref={logsRef} style={{ height: 480, overflowY: "auto", background: "#0d1117" }}>
            {logs.length === 0 ? (
              <div style={{ textAlign: "center", color: "#1e3a5f", paddingTop: 80 }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>▷</div>
                <div style={{ fontSize: 12, fontFamily: "monospace" }}>Clica em "▶ Correr Agente" para iniciar</div>
              </div>
            ) : logs.map((l, i) => {
              const p = parseLogLine(l.msg);
              const badge = LOG_BADGE[p.level] || null;
              const msgColor = l.type === "error" ? "#f87171" : l.type === "warn" ? "#fb923c" : l.type === "success" ? "#4ade80" : "#94a3b8";
              return (
                <div key={i} style={{ display: "flex", padding: "3px 16px", borderBottom: "1px solid #0f1923", alignItems: "flex-start", background: l.type === "error" ? "rgba(248,113,113,.06)" : "transparent" }}>
                  <span style={{ color: "#2d4a62", fontSize: 10, flexShrink: 0, minWidth: 72, paddingTop: 2, fontFamily: "monospace" }}>{p.time || l.ts}</span>
                  {badge
                    ? <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 3, background: badge.bg, color: badge.fg, minWidth: 38, textAlign: "center", marginRight: 8, marginTop: 1, letterSpacing: .5, fontFamily: "monospace" }}>{badge.label}</span>
                    : <span style={{ minWidth: 46, marginRight: 8, flexShrink: 0 }} />}
                  <span style={{ color: "#1e3d5c", fontSize: 10, flexShrink: 0, width: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingTop: 2, fontFamily: "monospace", marginRight: 8 }}>{p.module || ""}</span>
                  <span style={{ color: msgColor, fontSize: 12, lineHeight: 1.55, wordBreak: "break-word", fontFamily: "'Fira Code','Courier New',monospace", flex: 1 }}>{p.message || l.msg}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Aba HISTÓRICO ── */}
        {logTab === "hist" && (
          <div style={{ height: 480, overflowY: "auto", background: "#0d1117" }}>
            {histLoading ? (
              <div style={{ textAlign: "center", color: "#1e3a5f", paddingTop: 60, fontFamily: "monospace", fontSize: 12 }}>A carregar histórico…</div>
            ) : histLogs.length === 0 ? (
              <div style={{ textAlign: "center", color: "#1e3a5f", paddingTop: 80 }}>
                <div style={{ fontSize: 30, marginBottom: 10 }}>📋</div>
                <div style={{ fontSize: 12, fontFamily: "monospace" }}>Sem histórico disponível ainda.</div>
              </div>
            ) : histLogs.map((line, i) => {
              const p = parseLogLine(line);
              const badge = LOG_BADGE[p.level] || null;
              const msgColor = p.level === "ERROR" || p.level === "CRITICAL" ? "#f87171" : p.level === "WARNING" ? "#fb923c" : line.includes("✅") ? "#4ade80" : "#94a3b8";
              return (
                <div key={i} style={{ display: "flex", padding: "2px 16px", borderBottom: "1px solid #0a1420", alignItems: "flex-start" }}>
                  <span style={{ color: "#2d4a62", fontSize: 10, flexShrink: 0, minWidth: 72, paddingTop: 2, fontFamily: "monospace" }}>{p.time || ""}</span>
                  {badge
                    ? <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 3, background: badge.bg, color: badge.fg, minWidth: 38, textAlign: "center", marginRight: 8, marginTop: 1, letterSpacing: .5, fontFamily: "monospace" }}>{badge.label}</span>
                    : <span style={{ minWidth: 46, marginRight: 8, flexShrink: 0 }} />}
                  <span style={{ color: "#1e3d5c", fontSize: 10, flexShrink: 0, width: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingTop: 2, fontFamily: "monospace", marginRight: 8 }}>{p.module || ""}</span>
                  <span style={{ color: msgColor, fontSize: 11, lineHeight: 1.5, wordBreak: "break-word", fontFamily: "'Fira Code','Courier New',monospace", flex: 1 }}>{p.message || line}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

    </div>
  );
}

// ─── Lista de Segurança (remetentes autorizados) ───────────────────────────
function RemetentesAutorizadosCard() {
  const { data: lista, refresh } = useApi("/api/remetentes-autorizados", [], []);
  const { data: bloqueados, refresh: refreshBloqueados } = useApi("/api/seguranca/bloqueados", [], []);
  const [cfg, setCfg] = useState({ ativo: true, mensagem: "" });
  const [novoEmail, setNovoEmail] = useState("");
  const [novoNome, setNovoNome] = useState("");
  const [salvandoCfg, setSalvandoCfg] = useState(false);
  const [editandoMsg, setEditandoMsg] = useState(false);
  const [msgDraft, setMsgDraft] = useState("");

  const loadCfg = useCallback(() => {
    apiFetch("/api/seguranca/config").then(r => r.ok ? r.json() : null).then(d => { if (d) { setCfg(d); setMsgDraft(d.mensagem); } }).catch(() => {});
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const toggleAtivo = async () => {
    setSalvandoCfg(true);
    try {
      await apiFetch("/api/seguranca/config", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ativo: !cfg.ativo }) });
      await loadCfg();
    } catch {
      alert("Erro de rede.");
    }
    setSalvandoCfg(false);
  };

  const guardarMensagem = async () => {
    if (!msgDraft.trim()) return;
    setSalvandoCfg(true);
    try {
      await apiFetch("/api/seguranca/config", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mensagem: msgDraft }) });
      await loadCfg();
      setEditandoMsg(false);
    } catch {
      alert("Erro de rede.");
    }
    setSalvandoCfg(false);
  };

  const adicionar = async (emailOverride) => {
    const email = (emailOverride || novoEmail).trim();
    if (!email) return;
    try {
      const r = await apiFetch("/api/remetentes-autorizados", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, nome: novoNome.trim() }),
      });
      if (r.ok) {
        setNovoEmail(""); setNovoNome("");
        refresh(); refreshBloqueados();
      }
    } catch {
      alert("Erro de rede.");
    }
  };

  const remover = async (id) => {
    try {
      await apiFetch(`/api/remetentes-autorizados/${id}`, { method: "DELETE" });
      refresh();
    } catch {
      alert("Erro de rede.");
    }
  };

  return (
    <Card style={{ marginTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <div>
          <Label style={{ marginBottom: 2 }}>🔒 Lista de Segurança</Label>
          <p style={{ fontSize: 12, color: C.textMid, margin: 0 }}>
            Só emails desta lista são processados pelo agente. Os restantes recebem uma resposta automática e são arquivados sem processar.
          </p>
        </div>
        <button onClick={toggleAtivo} disabled={salvandoCfg}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 20, border: `1px solid ${cfg.ativo ? C.greenBorder : C.border}`, background: cfg.ativo ? C.greenLight : C.bg, cursor: salvandoCfg ? "not-allowed" : "pointer", flexShrink: 0 }}>
          <div style={{ width: 32, height: 18, borderRadius: 10, background: cfg.ativo ? C.green : C.textLight, position: "relative", transition: ".15s", flexShrink: 0 }}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: cfg.ativo ? 16 : 2, transition: ".15s" }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: cfg.ativo ? C.green : C.textMid }}>{cfg.ativo ? "Ativa" : "Inativa"}</span>
        </button>
      </div>

      {/* Formulário de adição */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Nome (opcional)"
          style={{ width: 180, padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
        <input value={novoEmail} onChange={e => setNovoEmail(e.target.value)} placeholder="email@exemplo.com"
          onKeyDown={e => e.key === "Enter" && adicionar()}
          style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
        <Btn variant="primary" onClick={() => adicionar()} disabled={!novoEmail.trim()}>＋ Autorizar</Btn>
      </div>

      {/* Lista de autorizados */}
      {(!lista || lista.length === 0) ? (
        <Empty msg="Sem remetentes autorizados ainda." />
      ) : (
        <div style={{ overflowX: "auto", marginBottom: 20 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
            <THead cols={["Email", "Nome", "Origem", "Ações"]} />
            <tbody>
              {lista.map(r => (
                <TR key={r.id}>
                  <TD mono>{r.email}</TD>
                  <TD>{r.nome || "—"}</TD>
                  <TD muted><Pill color={r.origem === "socio" ? "blue" : "gray"}>{r.origem === "socio" ? "Sócio" : "Manual"}</Pill></TD>
                  <td style={{ padding: "10px 16px" }}>
                    <Btn size="sm" variant="danger" onClick={() => window.confirm(`Remover ${r.email} da lista de segurança?`) && remover(r.id)}>Remover</Btn>
                  </td>
                </TR>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mensagem de resposta automática */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginBottom: bloqueados?.length ? 20 : 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <Label style={{ marginBottom: 0, fontSize: 11 }}>Mensagem de resposta automática</Label>
          {!editandoMsg && <button onClick={() => setEditandoMsg(true)} style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer" }}>Editar</button>}
        </div>
        {editandoMsg ? (
          <>
            <textarea value={msgDraft} onChange={e => setMsgDraft(e.target.value)} rows={6}
              style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
            <div style={{ fontSize: 10, color: C.textLight, margin: "4px 0 8px" }}>Usa {"{assunto}"} e {"{remetente}"} como variáveis — são substituídas automaticamente.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn size="sm" variant="primary" onClick={guardarMensagem} disabled={salvandoCfg || !msgDraft.trim()}>Guardar</Btn>
              <Btn size="sm" onClick={() => { setMsgDraft(cfg.mensagem); setEditandoMsg(false); }}>Cancelar</Btn>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: C.textMid, background: C.bg, padding: 10, borderRadius: 8, whiteSpace: "pre-wrap" }}>{cfg.mensagem}</div>
        )}
      </div>

      {/* Bloqueados recentes */}
      {bloqueados?.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
          <Label style={{ marginBottom: 8, fontSize: 11 }}>⚠ Bloqueados recentemente</Label>
          {bloqueados.map(b => (
            <div key={b.message_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.email_assunto || "(sem assunto)"}</div>
                <div style={{ fontSize: 11, color: C.textLight }}>{b.email_remetente || "(remetente desconhecido)"}</div>
              </div>
              {b.email_remetente && (
                <Btn size="sm" variant="secondary" onClick={() => adicionar(b.email_remetente)}>＋ Autorizar</Btn>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Revisão Manual (emails em 1_Aguardar_Revisao) ─────────────────────────
function RevisaoManualCard() {
  const { data: pendentes, refresh, loading } = useApi("/api/revisao/pendentes", [], []);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);

  if (loading) return null;
  if (!pendentes?.length) return null;

  const resolver = async (messageId) => {
    setBusy(messageId);
    const r = await apiFetch(`/api/revisao/${messageId}/resolver`, { method: "POST" });
    if (r.ok) refresh();
    setBusy(null);
  };

  const labelStatus = { REVISAO_MANUAL: "Revisão Manual", Falhou_Permanente: "Falha Técnica", Sem_Faturas: "Sem Faturas" };

  return (
    <div style={{ marginBottom: 18, border: `1px solid ${C.orangeBorder}`, borderRadius: 12, overflow: "hidden", background: C.orangeLight }}>
      {/* Header clicável */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer" }}
      >
        <span style={{ fontSize: 16 }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.orange }}>
            {pendentes.length} email{pendentes.length > 1 ? "s" : ""} aguarda{pendentes.length === 1 ? "" : "m"} revisão manual
          </span>
          <span style={{ fontSize: 12, color: C.textMid, marginLeft: 8 }}>— estes emails estão na pasta Gmail <strong>Contabilidade/1_Aguardar_Revisao</strong></span>
        </div>
        <span style={{ color: C.orange, fontSize: 12, fontWeight: 700 }}>{expanded ? "▲ Fechar" : "▼ Ver"}</span>
      </div>

      {expanded && (
        <div style={{ background: "#fff", borderTop: `1px solid ${C.orangeBorder}` }}>
          {pendentes.map(p => (
            <div key={p.message_id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.email_assunto || "(assunto desconhecido)"}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 3, flexWrap: "wrap" }}>
                  {p.email_remetente && <span style={{ fontSize: 11, color: C.textMid }}>De: {p.email_remetente}</span>}
                  <span style={{ fontSize: 11, color: C.textLight }}>{p.processed_at ? new Date(p.processed_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  <Pill color="orange">{labelStatus[p.status] || p.status}</Pill>
                  {p.notes && <span style={{ fontSize: 11, color: C.textLight }}>· {p.notes}</span>}
                </div>
              </div>
              <Btn
                size="sm"
                variant="success"
                disabled={busy === p.message_id}
                onClick={() => resolver(p.message_id)}
              >
                {busy === p.message_id ? "…" : "✓ Resolvido"}
              </Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
// ─── Pendentes (decisões à espera de humano) ────────────────────────────────
function PagePendentes() {
  const { data: pendentes, refresh, loading } = useApi("/api/pendentes", [], []);
  const [busyId, setBusyId] = useState(null);
  const [erro, setErro] = useState("");

  const responder = async (p, resposta) => {
    setBusyId(p.id); setErro("");
    try {
      const r = await apiFetch(`/api/pendentes/${p.id}/responder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resposta }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErro(d.detail || "Falha ao responder.");
      } else {
        refresh();
      }
    } catch (e) {
      setErro("Erro de rede.");
    }
    setBusyId(null);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>Pendentes</h1>
        <p style={{ color: C.textMid, fontSize: 13, marginTop: 4 }}>
          Casos que o agente parou à espera de uma decisão tua. Responde para o agente continuar.
        </p>
      </div>

      {erro && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: C.redLight, border: `1px solid ${C.redBorder}`, color: C.red, borderRadius: 8, fontSize: 13 }}>
          {erro}
        </div>
      )}

      {loading && <Empty msg="A carregar..." />}
      {!loading && (!pendentes || pendentes.length === 0) && (
        <Card>
          <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🎉</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Sem pendentes</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>O agente está a processar tudo sem dúvidas.</div>
          </div>
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {(pendentes || []).map(p => (
          <Card key={p.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <Pill color="orange">{p.motivo}</Pill>
                <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>
                  Criado {fmtDate(p.criada_em)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {p.drive_link && (
                  <button onClick={() => openPDF(p.drive_link, p.numero_documento)} style={{ textDecoration: "none", padding: "6px 10px", border: `1px solid ${C.greenBorder}`, background: C.greenLight, color: C.green, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    ↗ PDF
                  </button>
                )}
                {p.sheets_id && (
                  <a href={`https://docs.google.com/spreadsheets/d/${p.sheets_id}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none", padding: "6px 10px", border: `1px solid ${C.blueBorder}`, background: C.blueLight, color: C.blue, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                    ↗ Sheets
                  </a>
                )}
              </div>
            </div>

            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>
              {p.pergunta}
            </div>
            <div style={{ fontSize: 12, color: C.textMid, marginBottom: 14, fontFamily: "monospace" }}>
              {p.nome_emitente} · {p.numero_documento} · <strong>{fmt(p.valor_total)}</strong> · empresa: {p.nome_empresa || p.nif_recetor}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(p.opcoes || []).map(opt => (
                <Btn
                  key={opt}
                  variant={opt === "Não é reembolso" ? "secondary" : "primary"}
                  size="sm"
                  onClick={() => responder(p, opt)}
                  disabled={busyId === p.id}
                >
                  {busyId === p.id ? "..." : opt}
                </Btn>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}


// ─── Login ────────────────────────────────────────────────────────────────────
// Último email usado neste dispositivo — no PWA o autofill do browser nem
// sempre dispara, por isso lembramo-lo nós: o login seguinte começa logo no
// passo da palavra-passe, com o email já preenchido.
const LAST_EMAIL_KEY = "hub_last_email";

function Login({ onLogin, needsBootstrap }) {
  const emailGuardado = localStorage.getItem(LAST_EMAIL_KEY) || "";
  const [step, setStep] = useState(emailGuardado ? "password" : "email"); // "email" | "password"
  const [email, setEmail] = useState(emailGuardado);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(false);
  const [lembrar, setLembrar] = useState(true); // sessão longa + PIN no telemóvel

  const proximo = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError("");
    setStep("password");
  };

  const voltar = () => {
    setStep("email");
    setPassword("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, lembrar }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.detail || "Falha no login.");
        setLoading(false);
        return;
      }
      const data = await r.json();
      localStorage.setItem(LAST_EMAIL_KEY, email.trim().toLowerCase());
      saveSession({ token: data.token, user: data.user }, lembrar);
      onLogin(data.user, lembrar);
    } catch (err) {
      setError("Não foi possível ligar ao servidor.");
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", boxSizing: "border-box", outline: "none",
    border: `1.5px solid ${focus ? "#03363D" : "#D1D5DB"}`, borderRadius: 8, fontSize: 15,
    transition: "border-color 0.15s",
  };
  const btnStyle = (disabled) => ({
    width: "100%", padding: "13px", border: "none", borderRadius: 8, marginTop: 20,
    background: disabled ? "#9CA3AF" : "#03363D", color: "#fff", fontWeight: 700, fontSize: 14,
    cursor: disabled ? "default" : "pointer",
  });

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F3F4F6", fontFamily: "'Inter', system-ui, sans-serif", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: "#03363D", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#fff", fontWeight: 800 }}>H</div>
          <span style={{ fontSize: 17, fontWeight: 800, color: "#1A1F2E" }}>Hub Financeiro</span>
        </div>

        <div style={{ background: "#fff", borderRadius: 12, padding: "40px 36px", boxShadow: "0 2px 6px rgba(0,0,0,.08), 0 12px 32px rgba(0,0,0,.06)", border: "1px solid #E5E7EB" }}>
          {needsBootstrap && (
            <div style={{ background: "#FEF8EC", border: "1px solid #F5DFA0", borderRadius: 8, padding: 12, fontSize: 12, color: "#78350f", marginBottom: 20, lineHeight: 1.5 }}>
              ⚠️ Ainda não há nenhum utilizador na BD. Cria o primeiro na VPS com:<br/>
              <code style={{ fontFamily: "monospace", fontSize: 11 }}>python setup_utilizadores.py criar</code>
            </div>
          )}

          {step === "email" ? (
            <>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: "#1A1F2E", margin: "0 0 6px" }}>Iniciar sessão</h1>
              <p style={{ fontSize: 13, color: "#6B7280", margin: "0 0 24px" }}>Introduz o teu email para continuar.</p>
              <form onSubmit={proximo}>
                <input type="email" name="email" autoComplete="username" inputMode="email"
                  autoCapitalize="none" spellCheck={false}
                  required autoFocus value={email} onChange={e => setEmail(e.target.value)}
                  onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
                  placeholder="nome@empresa.pt" style={inputStyle} />
                <button type="submit" style={btnStyle(false)}>Seguinte</button>
              </form>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: "#1A1F2E", margin: "0 0 6px" }}>Palavra-passe</h1>
              <button onClick={voltar} type="button"
                style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: 0, marginBottom: 24, cursor: "pointer", fontSize: 13 }}>
                <span style={{ fontWeight: 600, color: "#1A1F2E" }}>{email}</span>
                <span style={{ color: "#03363D", fontWeight: 600 }}>Trocar</span>
              </button>
              <form onSubmit={submit}>
                {/* Campo de email presente (mas invisível) para o gestor de
                    palavras-passe associar email+password e oferecer autofill */}
                <input type="email" name="email" autoComplete="username" value={email} readOnly
                  tabIndex={-1} aria-hidden="true"
                  style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
                <input type="password" name="password" autoComplete="current-password"
                  required autoFocus value={password} onChange={e => setPassword(e.target.value)}
                  onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
                  placeholder="Palavra-passe" style={inputStyle} />
                <label style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 14, cursor: "pointer", userSelect: "none" }}>
                  <input type="checkbox" checked={lembrar} onChange={e => setLembrar(e.target.checked)}
                    style={{ width: 17, height: 17, accentColor: "#03363D", cursor: "pointer", margin: 0 }} />
                  <span style={{ fontSize: 13, color: "#4B5563" }}>Memorizar neste dispositivo</span>
                </label>
                {error && <div style={{ color: "#C0302A", fontSize: 12, marginTop: 12, padding: "9px 12px", background: "#FEF0F0", border: "1px solid #F5B8B8", borderRadius: 6 }}>{error}</div>}
                <button type="submit" disabled={loading} style={btnStyle(loading)}>{loading ? "A entrar…" : "Entrar"}</button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: "#9CA3AF", marginTop: 20 }}>
          Acesso restrito. Contacta o administrador se precisares de conta.
        </p>
      </div>
    </div>
  );
}

// ─── Utilizadores ─────────────────────────────────────────────────────────────
// ─── Definições ───────────────────────────────────────────────────────────────
function Toggle({ ativo, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!ativo)}
      style={{
        width: 44, height: 24, borderRadius: 12, cursor: disabled ? "default" : "pointer",
        background: ativo ? C.blue : C.borderDark,
        position: "relative", transition: "background .2s", flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute", top: 3, left: ativo ? 23 : 3, width: 18, height: 18,
        borderRadius: "50%", background: "#fff", transition: "left .2s",
        boxShadow: "0 1px 3px rgba(0,0,0,.2)",
      }} />
    </div>
  );
}

function PageDefinicoes({ onNav }) {
  const { data: notifs, refresh: refreshNotifs } = useApi("/api/definicoes/notificacoes");
  const [novaEmpresaNif, setNovaEmpresaNif] = useState("");
  const [novaEmpresaNome, setNovaEmpresaNome] = useState("");
  const [empresaMsg, setEmpresaMsg] = useState(null);
  const [saving, setSaving] = useState(null);

  const toggleNotif = async (key, ativo) => {
    setSaving(key);
    await apiFetch("/api/definicoes/notificacoes", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, ativo }),
    });
    await refreshNotifs();
    setSaving(null);
  };

  const criarEmpresa = async () => {
    if (!novaEmpresaNif.trim() || !novaEmpresaNome.trim()) {
      setEmpresaMsg({ ok: false, msg: "NIF e nome são obrigatórios." }); return;
    }
    const r = await apiFetch("/api/empresas", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nif: novaEmpresaNif.trim(), nome: novaEmpresaNome.trim() }),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) {
      setEmpresaMsg({ ok: true, msg: `Empresa "${novaEmpresaNome}" criada com sucesso.` });
      setNovaEmpresaNif(""); setNovaEmpresaNome("");
    } else {
      setEmpresaMsg({ ok: false, msg: d.detail || "Erro ao criar empresa." });
    }
  };

  const card = (children) => (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 28px", marginBottom: 24 }}>
      {children}
    </div>
  );

  const sectionTitle = (t) => (
    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 20, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>{t}</div>
  );

  const LABELS = {
    notif_extrato:           "Ao importar extrato bancário",
    notif_fatura:            "Resumo do run (faturas processadas)",
    notif_debito_sem_fatura: "Débitos sem fatura correspondente",
    notif_erros:             "Erros técnicos do agente",
    notif_fecho_mes:         "Alertas de fecho mensal",
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>Definições</div>
      <div style={{ color: C.textMid, fontSize: 13, marginBottom: 32 }}>Configura notificações, empresas e acessos.</div>

      {/* ── Notificações Chat ── */}
      {card(<>
        {sectionTitle("🔔 Notificações Google Chat")}
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 20 }}>
          Controla o que o agente envia para o Chat. Podes desactivar temporariamente enquanto está em diagnóstico.
        </div>
        {notifs ? Object.entries(notifs).map(([key, info]) => (
          <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{LABELS[key] || info.label}</div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{key}</div>
            </div>
            <Toggle ativo={info.ativo} onChange={(v) => toggleNotif(key, v)} disabled={saving === key} />
          </div>
        )) : <div style={{ color: C.textLight, fontSize: 13 }}>A carregar...</div>}
      </>)}

      {/* ── Nova Empresa ── */}
      {card(<>
        {sectionTitle("🏢 Adicionar Empresa")}
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textMid, marginBottom: 4 }}>NIF (9 dígitos)</div>
            <input
              value={novaEmpresaNif}
              onChange={e => setNovaEmpresaNif(e.target.value)}
              placeholder="000000000"
              style={{ width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ flex: 2 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textMid, marginBottom: 4 }}>Nome da empresa</div>
            <input
              value={novaEmpresaNome}
              onChange={e => setNovaEmpresaNome(e.target.value)}
              placeholder="Ex: Minha Empresa Lda"
              style={{ width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }}
            />
          </div>
        </div>
        {empresaMsg && (
          <div style={{ fontSize: 12, color: empresaMsg.ok ? C.green : C.red, marginBottom: 12 }}>
            {empresaMsg.msg}
          </div>
        )}
        <button
          onClick={criarEmpresa}
          style={{ padding: "9px 20px", background: C.blue, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Criar empresa
        </button>
        <button
          onClick={() => onNav("empresas")}
          style={{ marginLeft: 12, padding: "9px 16px", background: "none", color: C.blue, border: `1px solid ${C.blueBorder}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Ver todas as empresas →
        </button>
      </>)}

      {/* ── Utilizadores ── */}
      {card(<>
        {sectionTitle("👥 Utilizadores")}
        <div style={{ fontSize: 13, color: C.textMid, marginBottom: 16 }}>Gere os acessos ao dashboard.</div>
        <button
          onClick={() => onNav("utilizadores")}
          style={{ padding: "9px 20px", background: C.blue, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Gerir utilizadores →
        </button>
      </>)}
    </div>
  );
}

function ContasBancariasInline({ socioId, contas: contasInit, onRefresh, aprovacaoAutomatica, onToggleAprovacao }) {
  const [contas, setContas] = useState(contasInit || []);
  const [open, setOpen] = useState(false);
  const [novoIban, setNovoIban] = useState("");
  const [novoBanco, setNovoBanco] = useState("");

  useEffect(() => { setContas(contasInit || []); }, [contasInit]);

  const addConta = async () => {
    if (!novoIban.trim()) return;
    const r = await apiFetch(`/api/socios/${socioId}/contas`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ iban: novoIban.trim(), banco: novoBanco.trim() }),
    });
    if (r.ok) {
      const nova = await r.json();
      setContas(prev => [...prev, nova]);
      setNovoIban(""); setNovoBanco("");
      onRefresh();
    }
  };

  const removeConta = async (contaId) => {
    await apiFetch(`/api/socios/${socioId}/contas/${contaId}`, { method: "DELETE" });
    setContas(prev => prev.filter(c => c.id !== contaId));
    onRefresh();
  };

  const setPredefinida = async (contaId) => {
    const r = await apiFetch(`/api/socios/${socioId}/contas/${contaId}/predefinida`, { method: "PATCH" });
    if (r.ok) {
      setContas(prev => prev.map(c => ({ ...c, predefinida: c.id === contaId ? 1 : 0 })));
      onRefresh();
    }
  };

  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: C.blue, padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
        {open ? "▾" : "▸"} {contas.length} IBAN{contas.length !== 1 ? "s" : ""}
        {contas.some(c => c.predefinida) && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 8, background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`, marginLeft: 2 }}>
            ★ predefinido
          </span>
        )}
      </button>
      {open && (
        <div style={{ marginTop: 8, paddingLeft: 4 }}>
          {contas.length === 0 && <div style={{ fontSize: 11, color: C.textLight, marginBottom: 6 }}>Sem IBANs registados.</div>}
          {contas.map(c => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, padding: "4px 6px", borderRadius: 6, background: c.predefinida ? C.greenLight : "transparent", border: c.predefinida ? `1px solid ${C.greenBorder}` : "1px solid transparent" }}>
              <span style={{ fontFamily: "ui-monospace,monospace", fontSize: 11, color: C.text, flex: 1 }}>{c.iban}</span>
              {c.banco && <span style={{ fontSize: 11, color: C.textMid }}>{c.banco}</span>}
              <button
                onClick={() => setPredefinida(c.id)}
                title={c.predefinida ? "IBAN predefinido" : "Definir como predefinido"}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "0 2px", color: c.predefinida ? C.green : C.textLight, opacity: c.predefinida ? 1 : 0.5 }}>
                ★
              </button>
              <button onClick={() => removeConta(c.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.red, fontSize: 13, lineHeight: 1, padding: "0 2px" }}>×</button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
            <input
              value={novoBanco} onChange={e => setNovoBanco(e.target.value)}
              placeholder="Banco" style={{ padding: "5px 8px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, width: 90, outline: "none" }} />
            <input
              value={novoIban} onChange={e => setNovoIban(e.target.value)}
              placeholder="IBAN" style={{ padding: "5px 8px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, flex: 1, fontFamily: "ui-monospace,monospace", outline: "none" }}
              onKeyDown={e => e.key === "Enter" && addConta()} />
            <button onClick={addConta} disabled={!novoIban.trim()}
              style={{ padding: "5px 10px", background: novoIban.trim() ? C.blue : C.border, color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: novoIban.trim() ? "pointer" : "not-allowed" }}>＋</button>
          </div>
          <div style={{ fontSize: 10, color: C.textLight, marginTop: 4 }}>★ clica para definir como IBAN predefinido para reembolsos</div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Aprovação automática</div>
              <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 1, maxWidth: 260 }}>
                Reembolsos em nome deste sócio ficam logo "Aprovado", sem esperar decisão manual. O pagamento continua sempre a exigir o batch bancário.
              </div>
            </div>
            <Toggle ativo={!!aprovacaoAutomatica} onChange={onToggleAprovacao} />
          </div>
        </div>
      )}
    </div>
  );
}

function ModalSocio({ socio, onClose, onSaved }) {
  const editando = !!socio;
  const [form, setForm] = useState({ nome: socio?.nome || "", email: socio?.email || "", nif: socio?.nif || "" });
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const inputStyle = { padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };

  const guardar = async () => {
    if (!form.nome.trim()) { setErro("Nome é obrigatório."); return; }
    setSaving(true); setErro(null);
    const r = await apiFetch(editando ? `/api/socios/${socio.id}` : "/api/socios", {
      method: editando ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (r.ok) { onSaved(); onClose(); }
    else { const d = await r.json().catch(() => ({})); setErro(d.detail || "Erro ao guardar."); }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(15,23,42,.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 28, width: 420, maxWidth: "92vw", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 20 }}>{editando ? "Editar sócio" : "Novo sócio"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Nome completo *</div>
            <input autoFocus value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} style={inputStyle} placeholder="Ex: Maria Silva" />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Email</div>
            <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} placeholder="maria@empresa.pt" />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>NIF</div>
            <input value={form.nif} onChange={e => setForm(f => ({ ...f, nif: e.target.value }))} style={inputStyle} placeholder="000000000" />
          </div>
          {erro && <div style={{ fontSize: 12, color: C.red }}>{erro}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: "9px 20px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
            <button onClick={guardar} disabled={saving} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: C.blue, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageSocios() {
  const { data: socios, loading, refresh } = useApi("/api/socios", []);
  const [busca, setBusca] = useState("");
  const [verInativos, setVerInativos] = useState(false);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalEditar, setModalEditar] = useState(null);

  const todos = socios || [];
  const ativos = todos.filter(s => s.ativo);
  const semIban = ativos.filter(s => !(s.contas || []).some(c => c.predefinida));

  const lista = todos
    .filter(s => (verInativos ? !s.ativo : s.ativo))
    .filter(s => {
      const q = busca.trim().toLowerCase();
      if (!q) return true;
      return s.nome.toLowerCase().includes(q) || (s.nif || "").includes(q) || (s.email || "").toLowerCase().includes(q);
    });

  const desativar = async (s) => {
    if (!window.confirm(`Desativar "${s.nome}"? Deixa de aparecer nas listas de reembolso/batch até seres reativado.`)) return;
    await apiFetch(`/api/socios/${s.id}`, { method: "DELETE" });
    refresh();
  };
  const reativar = async (s) => {
    await apiFetch(`/api/socios/${s.id}/reativar`, { method: "POST" });
    refresh();
  };
  const toggleAprovacaoAutomatica = async (s, novoValor) => {
    await apiFetch(`/api/socios/${s.id}/aprovacao-automatica`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ativo: novoValor }),
    });
    refresh();
  };

  return (
    <div>
      <PageHeading eyebrow="Pessoas e reembolsos" title="Sócios" description="Colaboradores elegíveis para reembolso — o agente usa esta lista para reconhecer beneficiários e encontrar o IBAN certo nos batches de pagamento." action={<Btn onClick={() => setModalNovo(true)}>+ Novo Sócio</Btn>} />

      {semIban.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: C.orangeLight, border: `1px solid ${C.orangeBorder}`, borderRadius: 10, marginBottom: 16, fontSize: 13, color: C.orange, fontWeight: 600 }}>
          ⚠ {semIban.length} sócio{semIban.length !== 1 ? "s" : ""} sem IBAN predefinido — reembolsos para {semIban.length !== 1 ? "eles" : "ele/ela"} vão ficar bloqueados no Batch de Pagamentos até adicionares um.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Pesquisar por nome, email ou NIF..."
          style={{ flex: "1 1 240px", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, outline: "none" }} />
        <button onClick={() => setVerInativos(v => !v)}
          style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${verInativos ? C.blueBorder : C.border}`, background: verInativos ? C.blueLight : "#fff", color: verInativos ? C.blue : C.textMid, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          {verInativos ? "✓ A ver inativos" : "Ver inativos"}
        </button>
      </div>

      {loading ? <Spinner /> : lista.length === 0 ? (
        <Empty msg={verInativos ? "Sem sócios inativos." : busca ? "Nenhum sócio encontrado." : "Nenhum sócio registado."} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lista.map(s => {
            const predef = (s.contas || []).find(c => c.predefinida);
            return (
              <Card key={s.id} p="0" style={{ overflow: "hidden", opacity: s.ativo ? 1 : 0.6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", flexWrap: "wrap" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.blueLight, color: C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                    {(s.nome || "?")[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                      {s.nome} {!s.ativo && <span style={{ fontSize: 10, fontWeight: 700, color: C.textLight, marginLeft: 6 }}>INATIVO</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMid, marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {s.email && <span>{s.email}</span>}
                      {s.nif && <span style={{ fontFamily: "ui-monospace,monospace" }}>{s.nif}</span>}
                    </div>
                  </div>
                  {predef ? (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`, fontFamily: "ui-monospace,monospace", whiteSpace: "nowrap" }}>
                      ✓ {predef.iban}
                    </span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: C.orangeLight, color: C.orange, border: `1px solid ${C.orangeBorder}`, whiteSpace: "nowrap" }}>
                      ⚠ Sem IBAN
                    </span>
                  )}
                  {!!s.aprovacao_automatica && (
                    <span title="Reembolsos deste sócio são aprovados automaticamente" style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: C.blueLight, color: C.blue, border: `1px solid ${C.blueBorder}`, whiteSpace: "nowrap" }}>
                      Aprovação automática
                    </span>
                  )}
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => setModalEditar(s)}
                      style={{ padding: "6px 12px", background: "none", color: C.blue, border: `1px solid ${C.blueBorder}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>Editar</button>
                    {s.ativo ? (
                      <button onClick={() => desativar(s)}
                        style={{ padding: "6px 10px", background: "none", color: C.red, border: `1px solid ${C.redBorder}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>Desativar</button>
                    ) : (
                      <button onClick={() => reativar(s)}
                        style={{ padding: "6px 10px", background: "none", color: C.green, border: `1px solid ${C.greenBorder}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>Reativar</button>
                    )}
                  </div>
                </div>
                <div style={{ padding: "0 18px 14px", background: C.bg, borderTop: `1px solid ${C.border}` }}>
                  <div style={{ paddingTop: 10 }}>
                    <ContasBancariasInline
                      socioId={s.id} contas={s.contas || []} onRefresh={refresh}
                      aprovacaoAutomatica={s.aprovacao_automatica}
                      onToggleAprovacao={(novoValor) => toggleAprovacaoAutomatica(s, novoValor)}
                    />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {modalNovo && <ModalSocio onClose={() => setModalNovo(false)} onSaved={refresh} />}
      {modalEditar && <ModalSocio socio={modalEditar} onClose={() => setModalEditar(null)} onSaved={refresh} />}
    </div>
  );
}

// ─── Tabelas de configuração (QR + IVA) ────────────────────────────────────────
function TabelasConfigCard() {
  const [subTab, setSubTab] = useState("qr");
  const { data: qrCampos, refresh: refreshQr }     = useApi("/api/config/qr-campos", []);
  const { data: taxasIva, refresh: refreshTaxas }  = useApi("/api/config/taxas-iva", []);
  const [editingQr, setEditingQr]   = useState(null); // { codigo, campo_modelo, notas }
  const [editingIva, setEditingIva] = useState(null); // { id, percentagem, codigo_qr_base, codigo_qr_iva }
  const [saving, setSaving] = useState(false);

  const saveQr = async () => {
    if (!editingQr) return;
    setSaving(true);
    await apiFetch(`/api/config/qr-campos/${editingQr.codigo}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ campo_modelo: editingQr.campo_modelo || null, notas: editingQr.notas || null }),
    });
    setEditingQr(null); refreshQr(); setSaving(false);
  };

  const toggleQrAtivo = async (c) => {
    await apiFetch(`/api/config/qr-campos/${c.codigo}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ativo: !c.ativo }),
    });
    refreshQr();
  };

  const saveIva = async () => {
    if (!editingIva) return;
    setSaving(true);
    await apiFetch(`/api/config/taxas-iva/${editingIva.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ percentagem: parseFloat(editingIva.percentagem), codigo_qr_base: editingIva.codigo_qr_base || null, codigo_qr_iva: editingIva.codigo_qr_iva || null }),
    });
    setEditingIva(null); refreshTaxas(); setSaving(false);
  };

  const toggleIvaAtivo = async (t) => {
    await apiFetch(`/api/config/taxas-iva/${t.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ativo: !t.ativo }),
    });
    refreshTaxas();
  };

  const thStyle = { padding: "9px 14px", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.textLight, textAlign: "left", borderBottom: `2px solid ${C.border}`, background: "#f8fafc", whiteSpace: "nowrap" };
  const tdStyle = { padding: "10px 14px", fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" };
  const inputSm = { padding: "5px 8px", border: `1px solid ${C.borderDark}`, borderRadius: 6, fontSize: 12, width: "100%", outline: "none", boxSizing: "border-box" };

  // Agrupar taxas IVA por região
  const taxasPorRegiao = (taxasIva || []).reduce((acc, t) => {
    if (!acc[t.regiao]) acc[t.regiao] = [];
    acc[t.regiao].push(t);
    return acc;
  }, {});

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: C.bg, borderRadius: 10, padding: 4, border: `1px solid ${C.border}`, width: "fit-content" }}>
        {[{ id: "qr", label: "QR Code AT" }, { id: "iva", label: "Taxas de IVA" }].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            style={{ padding: "7px 16px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700,
              background: subTab === t.id ? "#fff" : "transparent",
              color: subTab === t.id ? C.blue : C.textMid,
              boxShadow: subTab === t.id ? "0 1px 3px rgba(0,0,0,.08)" : "none" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── QR Campos AT ── */}
      {subTab === "qr" && (
        <div>
          <div style={{ fontSize: 13, color: C.textMid, marginBottom: 16, lineHeight: 1.6 }}>
            Mapeamento dos campos do QR code AT para os campos do modelo de documento.
            O <strong>Campo Modelo</strong> define qual propriedade da fatura é preenchida com cada campo QR.
          </div>
          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 700, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Código AT</th>
                  <th style={thStyle}>Descrição</th>
                  <th style={thStyle}>Campo Modelo</th>
                  <th style={thStyle}>Tipo</th>
                  <th style={thStyle}>Notas</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Ativo</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {(qrCampos || []).map(c => (
                  <tr key={c.codigo} style={{ background: c.ativo ? "#fff" : "#fafafa" }}>
                    <td style={tdStyle}><span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, color: C.blue }}>{c.codigo}</span></td>
                    <td style={{ ...tdStyle, color: c.ativo ? C.text : C.textLight, maxWidth: 200 }}>{c.descricao}</td>
                    <td style={tdStyle}>
                      {editingQr?.codigo === c.codigo
                        ? <input style={inputSm} value={editingQr.campo_modelo || ""} onChange={e => setEditingQr(x => ({ ...x, campo_modelo: e.target.value }))} placeholder="ex: nif_emitente" />
                        : <span style={{ fontFamily: "monospace", fontSize: 12, color: c.campo_modelo ? C.green : C.textLight }}>{c.campo_modelo || "—"}</span>}
                    </td>
                    <td style={tdStyle}><span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: C.bg, color: C.textMid, fontFamily: "monospace" }}>{c.tipo_dado}</span></td>
                    <td style={{ ...tdStyle, maxWidth: 160 }}>
                      {editingQr?.codigo === c.codigo
                        ? <input style={inputSm} value={editingQr.notas || ""} onChange={e => setEditingQr(x => ({ ...x, notas: e.target.value }))} placeholder="nota opcional" />
                        : <span style={{ fontSize: 12, color: C.textMid }}>{c.notas || ""}</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <button onClick={() => toggleQrAtivo(c)}
                        style={{ width: 36, height: 20, borderRadius: 10, background: c.ativo ? C.green : C.border, border: "none", cursor: "pointer", position: "relative", transition: ".2s" }}>
                        <div style={{ position: "absolute", top: 2, left: c.ativo ? 17 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: ".2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
                      </button>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {editingQr?.codigo === c.codigo
                        ? <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                            <button onClick={saveQr} disabled={saving} style={{ padding: "4px 10px", background: C.blue, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Guardar</button>
                            <button onClick={() => setEditingQr(null)} style={{ padding: "4px 8px", background: "none", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: "pointer", color: C.textMid }}>✕</button>
                          </div>
                        : <button onClick={() => setEditingQr({ codigo: c.codigo, campo_modelo: c.campo_modelo || "", notas: c.notas || "" })}
                            style={{ padding: "4px 10px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: "pointer", color: C.textMid, fontWeight: 600 }}>Editar</button>}
                    </td>
                  </tr>
                ))}
                {(!qrCampos || qrCampos.length === 0) && (
                  <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: C.textLight, padding: "32px 0" }}>Sem dados. Corre o agente uma vez para popular esta tabela.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Taxas IVA ── */}
      {subTab === "iva" && (
        <div>
          <div style={{ fontSize: 13, color: C.textMid, marginBottom: 16, lineHeight: 1.6 }}>
            Taxas de IVA por região e os campos do QR code AT que as identificam.
            Usadas pelo agente para calcular corretamente a base tributável e o IVA de cada fatura.
          </div>
          {Object.entries(taxasPorRegiao).map(([regiao, taxas]) => (
            <div key={regiao} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textMid, marginBottom: 8 }}>{regiao}</div>
              <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 580, borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Taxa</th>
                      <th style={thStyle}>Percentagem</th>
                      <th style={thStyle}>Campo QR Base</th>
                      <th style={thStyle}>Campo QR IVA</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Ativo</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxas.map(t => (
                      <tr key={t.id} style={{ background: t.ativo ? "#fff" : "#fafafa" }}>
                        <td style={tdStyle}><span style={{ fontWeight: 700, color: C.text }}>{t.taxa_nome}</span></td>
                        <td style={tdStyle}>
                          {editingIva?.id === t.id
                            ? <input style={{ ...inputSm, width: 80 }} type="number" step="0.01" value={editingIva.percentagem} onChange={e => setEditingIva(x => ({ ...x, percentagem: e.target.value }))} />
                            : <span style={{ fontFamily: "monospace", fontWeight: 700, color: C.blue }}>{t.percentagem}%</span>}
                        </td>
                        <td style={tdStyle}>
                          {editingIva?.id === t.id
                            ? <input style={inputSm} value={editingIva.codigo_qr_base || ""} onChange={e => setEditingIva(x => ({ ...x, codigo_qr_base: e.target.value }))} placeholder="ex: I2" />
                            : <span style={{ fontFamily: "monospace", fontSize: 12, color: t.codigo_qr_base ? C.text : C.textLight }}>{t.codigo_qr_base || "—"}</span>}
                        </td>
                        <td style={tdStyle}>
                          {editingIva?.id === t.id
                            ? <input style={inputSm} value={editingIva.codigo_qr_iva || ""} onChange={e => setEditingIva(x => ({ ...x, codigo_qr_iva: e.target.value }))} placeholder="ex: I3" />
                            : <span style={{ fontFamily: "monospace", fontSize: 12, color: t.codigo_qr_iva ? C.text : C.textLight }}>{t.codigo_qr_iva || "—"}</span>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button onClick={() => toggleIvaAtivo(t)}
                            style={{ width: 36, height: 20, borderRadius: 10, background: t.ativo ? C.green : C.border, border: "none", cursor: "pointer", position: "relative", transition: ".2s" }}>
                            <div style={{ position: "absolute", top: 2, left: t.ativo ? 17 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: ".2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
                          </button>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {editingIva?.id === t.id
                            ? <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                <button onClick={saveIva} disabled={saving} style={{ padding: "4px 10px", background: C.blue, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Guardar</button>
                                <button onClick={() => setEditingIva(null)} style={{ padding: "4px 8px", background: "none", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: "pointer", color: C.textMid }}>✕</button>
                              </div>
                            : <button onClick={() => setEditingIva({ id: t.id, percentagem: t.percentagem, codigo_qr_base: t.codigo_qr_base || "", codigo_qr_iva: t.codigo_qr_iva || "" })}
                                style={{ padding: "4px 10px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: "pointer", color: C.textMid, fontWeight: 600 }}>Editar</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {Object.keys(taxasPorRegiao).length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: C.textLight, fontSize: 13 }}>
              Sem taxas configuradas. Corre o agente uma vez para popular esta tabela.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Fornecedores (NIFs distintos das faturas, com nome personalizado) ─────
function FornecedoresCard() {
  const { data: lista, refresh, loading } = useApi("/api/fornecedores", [], []);
  const [editando, setEditando] = useState({}); // nif -> rascunho do nome
  const [salvando, setSalvando] = useState({});
  const [busca, setBusca] = useState("");

  const guardar = async (nif) => {
    const nome = (editando[nif] || "").trim();
    if (!nome) return;
    setSalvando(p => ({ ...p, [nif]: true }));
    try {
      const r = await apiFetch(`/api/fornecedores/${nif}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome }),
      });
      if (r.ok) {
        setEditando(p => { const n = { ...p }; delete n[nif]; return n; });
        refresh();
      }
    } catch {
      alert("Erro de rede.");
    }
    setSalvando(p => ({ ...p, [nif]: false }));
  };

  const listaFiltrada = (lista || []).filter(f => {
    if (!busca.trim()) return true;
    const q = busca.trim().toLowerCase();
    return f.nif.includes(q)
      || (f.nome_personalizado || "").toLowerCase().includes(q)
      || (f.nome_detectado || "").toLowerCase().includes(q);
  });

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <Label style={{ marginBottom: 2 }}>Fornecedores</Label>
          <p style={{ fontSize: 12, color: C.textMid, margin: 0 }}>
            Todos os NIFs de fornecedores vistos nas faturas. Na maioria dos casos o nome já vem certo (lido pela IA ou pelo QR code) — só precisas de editar quando aparecer "sem nome" (acontece quando o QR code não traz o nome e é a primeira vez que vemos este fornecedor).
          </p>
        </div>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Pesquisar NIF ou nome..."
          style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, width: 240, boxSizing: "border-box" }} />
      </div>

      {loading ? <Spinner /> : (!listaFiltrada.length ? <Empty msg="Nenhum fornecedor encontrado." /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <THead cols={["NIF", "Nome", "Faturas", "Última fatura"]} />
            <tbody>
              {listaFiltrada.map(f => {
                const draft = editando[f.nif];
                const aEditar = draft !== undefined;
                const nomeAtual = f.nome_personalizado || f.nome_detectado || "";
                const semNome = !nomeAtual || nomeAtual === f.nif;
                return (
                  <TR key={f.nif}>
                    <TD mono muted>{f.nif}</TD>
                    <td style={{ padding: "10px 16px", minWidth: 260 }}>
                      {aEditar ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <input autoFocus value={draft} onChange={e => setEditando(p => ({ ...p, [f.nif]: e.target.value }))}
                            onKeyDown={e => e.key === "Enter" && guardar(f.nif)}
                            style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
                          <Btn size="sm" variant="primary" onClick={() => guardar(f.nif)} disabled={salvando[f.nif] || !draft.trim()}>✓</Btn>
                          <Btn size="sm" onClick={() => setEditando(p => { const n = { ...p }; delete n[f.nif]; return n; })}>✕</Btn>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {semNome
                            ? <span style={{ fontSize: 12, color: C.orange, fontWeight: 600 }}>⚠ Sem nome — só temos o NIF</span>
                            : <span style={{ fontSize: 13, color: C.text }}>{nomeAtual}</span>}
                          <button onClick={() => setEditando(p => ({ ...p, [f.nif]: semNome ? "" : nomeAtual }))}
                            style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer" }}>Editar</button>
                        </div>
                      )}
                    </td>
                    <TD mono muted>{f.num_faturas}</TD>
                    <TD muted>{fmtDate(f.ultima_fatura)}</TD>
                  </TR>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </Card>
  );
}

// ─── Categorias (registo) ──────────────────────────────────────────────────────
function CategoriasCard() {
  const [cats, setCats] = useState([
    { id: 1, nome: "Telecomunicações", tipo: "despesa" },
    { id: 2, nome: "Energia", tipo: "despesa" },
    { id: 3, nome: "Honorários", tipo: "receita" },
  ]);
  const [form, setForm] = useState({ nome: "", tipo: "despesa" });

  const inputStyle = { padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const add = () => {
    if (!form.nome.trim()) return;
    setCats(cs => [...cs, { id: Date.now(), nome: form.nome.trim(), tipo: form.tipo }]);
    setForm({ nome: "", tipo: "despesa" });
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 28px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>Categorias de classificação</div>
      <div style={{ fontSize: 12, color: C.textMid, margin: "12px 0 16px" }}>
        Categorias usadas pelo agente para classificar despesas e receitas.
        <span style={{ color: C.textLight }}> · guardado localmente (endpoint de backend em preparação)</span>
      </div>

      {cats.map(c => (
        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text }}>{c.nome}</div>
          <Pill color={c.tipo === "receita" ? "green" : "blue"}>{c.tipo}</Pill>
          <button onClick={() => setCats(cs => cs.filter(x => x.id !== c.id))}
            style={{ padding: "5px 10px", background: "none", color: C.red, border: `1px solid ${C.redBorder}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>✕</button>
        </div>
      ))}

      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.textMid, marginBottom: 8 }}>Adicionar categoria</div>
        <div className="cats-add-form" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input style={{ ...inputStyle, flex: 1, minWidth: 140 }} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome da categoria" />
          <select style={inputStyle} value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
            <option value="despesa">Despesa</option>
            <option value="receita">Receita</option>
          </select>
          <button onClick={add} style={{ padding: "8px 18px", background: C.blue, color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>＋ Adicionar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Manutenção (painel de controlo central / super-admin) ──────────────────────
// Estado de cada anexo/documento dentro de um email → cor do Pill. Cobre os
// valores gravados em anexos_processados por _registar_resultado_anexo (ver
// src/main.py) — qualquer valor novo cai no "blue" por defeito.
const RESULTADO_ANEXO_COR = {
  sucesso: "green",
  ignorado_pro_forma: "gray",
  erro: "red",
  pendente_revisao: "orange",
  pendente_retry: "orange",
  pendente_classificacao_manual: "orange",
  pendente_formato_nao_suportado: "orange",
  classificado_a_reprocessar: "blue",
  ignorado_manualmente: "gray",
  ignorado_automaticamente: "gray",
  ignorado: "gray",
  sem_documentos: "gray",
  // Estado legado; novos formatos desconhecidos ficam pendentes e exigem
  // uma decisão humana explícita.
  ignorado_formato: "gray",
  // Extrato lido com sucesso, mas sem empresa identificável — só falta
  // escolher a empresa, em Conciliação. Não abre o formulário de classificar
  // fatura (ver RESULTADOS_ANEXO_COM_ACAO): esse não serve para extratos.
  pendente_empresa_extrato: "orange",
};

// Estados de anexo onde faz sentido oferecer uma ação — qualquer coisa que
// não seja sucesso nem um estado já resolvido/informativo (ignorado_pro_forma,
// ignorado, ignorado_manualmente, ignorado_automaticamente,
// classificado_a_reprocessar, sem_documentos — estes não têm ação própria aqui).
// Um formato não suportado pode ser relido depois de uma melhoria do motor ou
// ignorado conscientemente; não se oferece classificação sem conteúdo legível.
const RESULTADOS_ANEXO_COM_ACAO = ["erro", "pendente_classificacao_manual", "pendente_retry", "pendente_formato_nao_suportado", "ignorado_formato"];

const RESULTADO_ANEXO_LABEL = {
  sucesso: "Processado",
  erro: "Falhou",
  pendente_revisao: "Precisa de confirmação",
  pendente_retry: "Nova tentativa necessária",
  pendente_classificacao_manual: "Dados por corrigir",
  pendente_formato_nao_suportado: "Formato não suportado",
  classificado_a_reprocessar: "Correção guardada",
  ignorado_manualmente: "Ignorado pelo contabilista",
  ignorado_automaticamente: "Ignorado automaticamente",
  ignorado_pro_forma: "Pro forma ignorada",
  ignorado: "Ignorado",
  pendente_empresa_extrato: "Empresa por escolher",
  sem_documentos: "Nenhum documento identificado",
};

const TIPOS_DOCUMENTO_CLASSIFICACAO = [
  { value: "Fatura_Fornecedor", label: "Fatura Fornecedor" },
  { value: "Fatura_Cliente",    label: "Fatura Cliente" },
  { value: "Recibo_Generico",   label: "Recibo Genérico" },
  { value: "Extrato_Bancario",  label: "Extrato Bancário" },
];

// Extrai pistas (valor + descrição) do NOME do ficheiro para pré-preencher o
// formulário de classificação manual — os contabilistas nomeiam anexos de
// forma consistente (ex: "100,00€_EDP.pdf", "50€ Reembolso António.pdf").
// Só um ponto de partida: o formulário mostra sempre a sugestão a par dos
// campos editáveis, nunca é aceite sem confirmação humana.
function extrairPistasNomeFicheiro(filename) {
  if (!filename) return null;
  let nome = filename.replace(/^\.+/, "");
  nome = nome.replace(/\.(pdf|jpe?g|png)$/i, "");
  nome = nome.replace(/_compressed$/i, "");
  const m = nome.match(/^\s*(\d+(?:[.,]\d{1,2})?)\s*€\s*[_\s-]*(.+)$/);
  if (!m) return null;
  const valor = parseFloat(m[1].replace(",", "."));
  if (!Number.isFinite(valor) || valor <= 0) return null;
  const descricao = m[2].replace(/_+/g, " ").trim().replace(/^[-\s]+|[-\s]+$/g, "");
  if (!descricao) return null;
  return { valor, descricao };
}

// Formulário inline para classificar manualmente um anexo que a IA não
// conseguiu processar mesmo após retry — ver POST /api/anexos/{id}/classificar
// em platform/backend/api.py. Ao submeter, os dados ficam gravados e só
// entram na pipeline real (_processar_fatura) quando o email for reprocessado
// — por isso chama onClassificado no fim. O utilizador pode corrigir os
// restantes documentos antes de aplicar todas as correções no rodapé.
function FormClassificarAnexo({ anexo, empresas, onCancelar, onClassificado }) {
  // Pista a partir do nome do ficheiro (ex: "100,00€_EDP.pdf",
  // "50€ Reembolso António.pdf") — os contabilistas nomeiam os anexos assim
  // sistematicamente. Só um ponto de partida para o formulário: o humano vê
  // sempre a sugestão e confirma ou corrige, nunca é aceite às cegas.
  const pista = useMemo(() => extrairPistasNomeFicheiro(anexo?.anexo_filename), [anexo?.anexo_filename]);

  const [nifEmitente, setNifEmitente] = useState("");
  const [nomeEmitente, setNomeEmitente] = useState(pista?.descricao || "");
  const [nifRecetor, setNifRecetor] = useState(empresas?.[0]?.nif || "");
  const [tipoDocumento, setTipoDocumento] = useState("Fatura_Fornecedor");
  const [valorTotal, setValorTotal] = useState(pista ? String(pista.valor) : "");
  const [dataDocumento, setDataDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const campoId = (nome) => `classificar-${anexo?.id || "novo"}-${nome}`;

  useEffect(() => {
    if (!nifRecetor && empresas?.length) setNifRecetor(empresas[0].nif);
  }, [empresas, nifRecetor]);

  const submeter = async () => {
    if (!nifEmitente.trim() || !nifRecetor || !valorTotal || !dataDocumento) {
      setErro("Preenche pelo menos NIF emitente, empresa, valor e data.");
      return;
    }
    setEnviando(true); setErro(null);
    try {
      const r = await apiFetch(`/api/anexos/${anexo.id}/classificar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nif_emitente: nifEmitente.trim(),
          nome_emitente: nomeEmitente.trim() || null,
          nif_recetor: nifRecetor,
          tipo_documento: tipoDocumento,
          valor_total: parseFloat(valorTotal),
          data_documento: dataDocumento,
          numero_documento: numeroDocumento.trim() || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErro(typeof d?.detail === "string" ? d.detail : "Erro ao guardar a classificação.");
        setEnviando(false);
        return;
      }
      onClassificado();
    } catch (e) {
      setErro(`Erro de rede: ${e.message}`);
      setEnviando(false);
    }
  };

  const inp = { padding: "7px 9px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12.5, color: C.text, background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };
  const lbl = { fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3, display: "block" };

  return (
    <div style={{ marginTop: 10, padding: 12, background: C.bg, borderRadius: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      {pista && (
        <div style={{ fontSize: 11, color: C.textMid, padding: "6px 9px", background: "#fff", borderRadius: 6, border: `1px solid ${C.border}` }}>
          Sugestão a partir do nome do ficheiro: {fmt(pista.valor)} · {pista.descricao} — confirma ou corrige abaixo.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label htmlFor={campoId("nif-emitente")} style={lbl}>NIF emitente</label>
          <input id={campoId("nif-emitente")} style={inp} value={nifEmitente} onChange={e => setNifEmitente(e.target.value)} placeholder="ex: 600084779" />
        </div>
        <div>
          <label htmlFor={campoId("nome-emitente")} style={lbl}>Nome emitente</label>
          <input id={campoId("nome-emitente")} style={inp} value={nomeEmitente} onChange={e => setNomeEmitente(e.target.value)} placeholder="ex: Autoridade Tributária" />
        </div>
        <div>
          <label htmlFor={campoId("empresa")} style={lbl}>Empresa (recetor)</label>
          <select id={campoId("empresa")} style={inp} value={nifRecetor} onChange={e => setNifRecetor(e.target.value)}>
            {(empresas || []).map(emp => <option key={emp.nif} value={emp.nif}>{emp.nome}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={campoId("tipo")} style={lbl}>Tipo</label>
          <select id={campoId("tipo")} style={inp} value={tipoDocumento} onChange={e => setTipoDocumento(e.target.value)}>
            {TIPOS_DOCUMENTO_CLASSIFICACAO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={campoId("valor")} style={lbl}>Valor total (€)</label>
          <input id={campoId("valor")} style={inp} type="number" step="0.01" value={valorTotal} onChange={e => setValorTotal(e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label htmlFor={campoId("data")} style={lbl}>Data do documento</label>
          <input id={campoId("data")} style={inp} type="date" value={dataDocumento} onChange={e => setDataDocumento(e.target.value)} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label htmlFor={campoId("numero")} style={lbl}>Nº de documento (opcional)</label>
          <input id={campoId("numero")} style={inp} value={numeroDocumento} onChange={e => setNumeroDocumento(e.target.value)} placeholder="ex: FT 2026/001" />
        </div>
      </div>
      {erro && <div style={{ fontSize: 11.5, color: C.red }}>{erro}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="secondary" size="sm" onClick={onCancelar} disabled={enviando}>Cancelar</Btn>
        <Btn size="sm" onClick={submeter} disabled={enviando}>{enviando ? "A guardar…" : "Guardar correção"}</Btn>
      </div>
    </div>
  );
}

// Detalhe de um email: resultado por anexo/documento (de N anexos, quais
// foram bem, quais falharam e porquê) + botão para reler o email do Gmail e
// reprocessar só o que falta — os documentos já Concluído são saltados
// automaticamente pelo checkpoint em _processar_fatura (main.py), nunca
// duplicam trabalho.
function ModalDetalheEmail({ email, onClose, onReprocessado }) {
  const { data: anexos, loading, refresh } = useApi(`/api/emails/${encodeURIComponent(email.message_id)}/anexos`, [email.message_id], []);
  const { data: empresas } = useApi("/api/empresas", [], []);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [classificarAlvo, setClassificarAlvo] = useState(null); // id do anexo com o formulário aberto
  const [anexoSelecionadoId, setAnexoSelecionadoId] = useState(null);
  const [erroAcao, setErroAcao] = useState(null);
  const [acaoDocumento, setAcaoDocumento] = useState(null);
  const [ignorarAlvo, setIgnorarAlvo] = useState(null);
  const [motivoIgnorar, setMotivoIgnorar] = useState("");
  const [filtroAnexos, setFiltroAnexos] = useState("pendentes");
  const logsRef = useRef(null);
  const abrirNoGmail = () => {
    const gmailUrl = `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(email.message_id)}`;
    window.open(gmailUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => { if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight; }, [logs]);

  const reprocessar = async (modo) => {
    setLogs([]); setRunning(true);
    try {
      const response = await apiFetch("/api/agent/reprocessar-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_id: email.message_id, modo }),
      });
      // Mesmo problema do botão "Correr Agente": sem verificar o estado, um
      // erro do servidor lia-se como uma execução instantânea e bem-sucedida.
      if (!response.ok) {
        let detalhe = `HTTP ${response.status}`;
        try { const j = await response.json(); if (j?.detail) detalhe = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch {}
        setLogs(p => [...p, `❌ O servidor recusou o pedido: ${detalhe}`]);
        setRunning(false);
        return false;
      }
      if (!response.body) throw new Error("O servidor não devolveu o acompanhamento da execução.");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        decoder.decode(value).split("\n").filter(l => l.startsWith("data: ")).forEach(line => {
          const msg = line.replace("data: ", "").trim();
          if (msg) setLogs(p => [...p, msg]);
        });
      }
      refresh();
      onReprocessado();
      setRunning(false);
      return true;
    } catch (e) {
      setLogs(p => [...p, `❌ Erro: ${e.message}`]);
      setRunning(false);
      return false;
    }
  };

  // "pendentes": poupa a chamada à IA nos anexos já bem-sucedidos, só
  // reanalisa o que falhou ou ficou em falta (opção normal do dia-a-dia).
  // "todos": força a reanálise de tudo, incluindo anexos já concluídos —
  // só é preciso depois de uma alteração à lógica de extração que precises
  // de reconfirmar num documento já dado como sucesso.
  const confirmarReprocessar = (modo) => {
    const aviso = modo === "todos"
      ? `Reprocessar TUDO no email "${email.email_assunto || email.message_id}"? Isto força a reanálise de TODOS os anexos, mesmo os já bem-sucedidos — tem um custo de IA maior. Usa isto só depois de uma correção que precises de reconfirmar num documento já concluído.`
      : `Reprocessar o email "${email.email_assunto || email.message_id}"? Os anexos já bem processados não são repetidos — só os que falharam ou ficaram em falta.`;
    if (!window.confirm(aviso)) return;
    reprocessar(modo);
  };

  const abrirPdfAnexo = async (anexoId) => {
    // Abre a janela ainda dentro do gesto do utilizador. Se esperarmos pelo
    // fetch, Safari/Chrome podem interpretar window.open como popup e bloqueá-lo.
    const janela = window.open("", "_blank");
    setErroAcao(null);
    try {
      const r = await apiFetch(`/api/anexos/${anexoId}/pdf`);
      if (!r.ok) {
        janela?.close();
        setErroAcao("Não foi possível abrir o documento. O ficheiro pode já não existir no servidor.");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (janela) janela.location.href = url;
      else setErroAcao("O browser bloqueou a nova janela. Permite popups para abrir o documento.");
    } catch (e) {
      janela?.close();
      setErroAcao(`Erro ao abrir o documento: ${e.message}`);
    }
  };

  const ignorarAnexo = async (a) => {
    const resposta = motivoIgnorar.trim();
    if (!resposta) {
      setErroAcao("Indica o motivo para ignorar este documento.");
      return;
    }
    setAcaoDocumento("ignorar");
    setErroAcao(null);
    try {
      const r = await apiFetch(`/api/anexos/${a.id}/ignorar`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ motivo: resposta }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErroAcao(typeof d?.detail === "string" ? d.detail : "Não foi possível ignorar o documento.");
        return;
      }
      const d = await r.json().catch(() => ({}));
      await refresh();
      onReprocessado();
      setIgnorarAlvo(null);
      setMotivoIgnorar("");
      if (["Concluído", "Sem_Documento"].includes(d.email_estado)) onClose();
    } catch (e) {
      setErroAcao(`Erro ao ignorar o documento: ${e.message}`);
    } finally {
      setAcaoDocumento(null);
    }
  };

  // Limpa a decisão anterior deste anexo e reprocessa logo a seguir — a IA
  // volta a tentar ler o documento do zero (útil para falhas pontuais ou
  // quando o documento original foi entretanto corrigido).
  const relerAnexo = async (a) => {
    setAcaoDocumento("reler");
    setErroAcao(null);
    try {
      const r = await apiFetch(`/api/anexos/${a.id}/reler`, { method: "POST" });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErroAcao(typeof d?.detail === "string" ? d.detail : "Não foi possível preparar uma nova leitura.");
        return;
      }
      const processado = await reprocessar("pendentes");
      if (processado) onClose();
    } catch (e) {
      setErroAcao(`Erro ao tentar nova leitura: ${e.message}`);
    } finally {
      setAcaoDocumento(null);
    }
  };

  const RESULTADOS_IGNORADOS = ["ignorado", "ignorado_manualmente", "ignorado_automaticamente", "ignorado_pro_forma"];
  const okCount = (anexos || []).filter(a => a.resultado === "sucesso").length;
  const ignoradosCount = (anexos || []).filter(a => RESULTADOS_IGNORADOS.includes(a.resultado)).length;
  const resolvidosCount = okCount + ignoradosCount;
  const pendentesCount = Math.max(0, (anexos || []).length - resolvidosCount);
  const correcoesGuardadasCount = (anexos || []).filter(a => a.resultado === "classificado_a_reprocessar").length;
  const porDecidirCount = Math.max(0, pendentesCount - correcoesGuardadasCount);
  const anexosVisiveis = (anexos || []).filter(a => {
    if (filtroAnexos === "todos") return true;
    if (filtroAnexos === "processados") return a.resultado === "sucesso";
    if (filtroAnexos === "ignorados") return RESULTADOS_IGNORADOS.includes(a.resultado);
    return a.resultado !== "sucesso" && !RESULTADOS_IGNORADOS.includes(a.resultado);
  });
  const anexoSelecionado = anexosVisiveis.find(a => a.id === anexoSelecionadoId)
    || anexosVisiveis[0]
    || null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      {/* flex column: cabeçalho e rodapé fixos, só a lista de anexos faz scroll */}
      <div style={{ background: C.surface, borderRadius: 16, width: 1320, maxWidth: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.5)", border: `1px solid ${C.borderDark}` }}>

        {/* Cabeçalho */}
        <div style={{ padding: "20px 26px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 13, minWidth: 0 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: C.blueLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Mail size={20} color={C.blue} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16.5, fontWeight: 800, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.email_assunto || "(sem assunto)"}</div>
              <div style={{ fontSize: 12.5, color: C.textMid, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {email.email_remetente || "—"} · {email.attachment_count} anexo(s) recebido(s)
                {anexos?.length ? ` · ${anexos.length} registo(s) analisado(s)` : ""}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
            <button onClick={abrirNoGmail} aria-label="Abrir email no Gmail" title="Abrir este email no Gmail"
              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", cursor: "pointer", color: C.blue, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ExternalLink size={15} />
            </button>
            <button onClick={onClose} aria-label="Fechar"
              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, cursor: "pointer", color: C.textMid, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Corpo — documentos e acompanhamento lado a lado */}
        <div className="email-detail-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.45fr) minmax(360px, .85fr)", overflow: "hidden", flex: 1, minHeight: 0 }}>
        <div style={{ padding: "18px 26px", overflowY: "auto", minWidth: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
            {[
              { label: "Registos", valor: (anexos || []).length, cor: C.text },
              { label: "Processados", valor: okCount, cor: C.green },
              { label: "Ignorados", valor: ignoradosCount, cor: C.textMid },
              { label: "Por resolver", valor: pendentesCount, cor: pendentesCount ? C.orange : C.green },
            ].map(item => (
              <div key={item.label} style={{ padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 9, background: "#fff" }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, color: C.textLight, textTransform: "uppercase", letterSpacing: ".06em" }}>{item.label}</div>
                <div style={{ fontSize: 20, lineHeight: 1.1, fontWeight: 850, color: item.cor, marginTop: 3 }}>{item.valor}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>Documentos deste email</div>
              <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>Começa pelos documentos por resolver. Os concluídos e ignorados ficam separados para não gerar dúvidas.</div>
            </div>
            <div style={{ display: "flex", gap: 4, padding: 3, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, flexShrink: 0 }}>
              {[
                ["pendentes", `Por resolver · ${pendentesCount}`],
                ["processados", `Processados · ${okCount}`],
                ["ignorados", `Ignorados · ${ignoradosCount}`],
                ["todos", "Todos"],
              ].map(([id, label]) => (
                <button key={id} onClick={() => setFiltroAnexos(id)} style={{ padding: "6px 9px", border: 0, borderRadius: 6, background: filtroAnexos === id ? "#fff" : "transparent", boxShadow: filtroAnexos === id ? "0 1px 3px rgba(0,0,0,.08)" : "none", color: filtroAnexos === id ? C.text : C.textMid, fontSize: 10.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{label}</button>
              ))}
            </div>
          </div>
          {loading ? <Spinner /> : !anexos?.length ? <Empty msg="Sem registo de anexos para este email — pode ser de antes desta funcionalidade existir." /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {anexosVisiveis.length === 0 ? <Empty msg={filtroAnexos === "pendentes" ? "Este email não tem documentos por resolver." : "Não existem documentos neste grupo."} /> : anexosVisiveis.map((a, indice) => (
                <button type="button" key={a.id} onClick={() => { setAnexoSelecionadoId(a.id); setClassificarAlvo(null); setErroAcao(null); }}
                  style={{ width: "100%", textAlign: "left", border: `1px solid ${anexoSelecionado?.id === a.id ? C.blue : (a.exige_intervencao ? C.orangeBorder : C.border)}`, borderLeft: `4px solid ${a.exige_intervencao ? C.orange : C.green}`, borderRadius: 10, padding: "12px 14px", background: anexoSelecionado?.id === a.id ? C.blueLight : (a.exige_intervencao ? "#FFFCF5" : C.surface), cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                      <FileText size={15} color={a.resultado === "sucesso" ? C.green : C.textLight} style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <span style={{ color: C.textLight, marginRight: 7 }}>{indice + 1}.</span>{a.anexo_filename}
                        </div>
                        {(a.numero_documento || a.nome_emitente) && (
                          <div style={{ fontSize: 11.5, color: C.textMid, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {[a.nome_emitente, a.numero_documento].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                    <Pill color={RESULTADO_ANEXO_COR[a.resultado] || "blue"}>{RESULTADO_ANEXO_LABEL[a.resultado] || a.resultado}</Pill>
                  </div>
                  {a.explicacao && (
                    <div style={{ marginTop: 7, fontSize: 11.5, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.proximo_passo}</div>
                  )}
                </button>
              ))}
            </div>
          )}

        </div>

        <aside className="email-detail-activity" style={{ borderLeft: `1px solid ${C.border}`, background: running || logs.length ? "#0d1117" : "#fff", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, overflowY: "auto" }}>
          {running || logs.length ? <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 16px", borderBottom: "1px solid #1e293b", flexShrink: 0 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: running ? "#f59e0b" : logs.length ? "#22c55e" : "#64748b", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#c9d1d9", letterSpacing: .5 }}>{running ? "A REPROCESSAR…" : logs.length ? "EXECUÇÃO TERMINADA" : "ACOMPANHAMENTO"}</div>
              <div style={{ fontSize: 10.5, color: "#8b949e", marginTop: 2 }}>A atividade aparece aqui sem esconder os documentos.</div>
            </div>
          </div>
          <div ref={logsRef} style={{ flex: 1, minHeight: 220, overflowY: "auto", padding: "14px 16px", fontFamily: "'Fira Code', monospace", fontSize: 11, color: "#c9d1d9", lineHeight: 1.65 }}>
            {logs.map((l, i) => <div key={i} style={{ overflowWrap: "anywhere" }}>{l}</div>)}
          </div>
          </> : anexoSelecionado ? <div style={{ padding: 20 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: C.blue, textTransform: "uppercase", letterSpacing: ".06em" }}>Resolver documento</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginTop: 7, overflowWrap: "anywhere" }}>{anexoSelecionado.anexo_filename}</div>
            <div style={{ marginTop: 12, padding: "11px 12px", borderRadius: 8, background: C.bg, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{anexoSelecionado.explicacao}</div>
              <div style={{ fontSize: 11.5, color: C.textMid, marginTop: 4, lineHeight: 1.45 }}>{anexoSelecionado.proximo_passo}</div>
            </div>
            {anexoSelecionado.motivo && <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, fontWeight: 700, color: C.textMid, cursor: "pointer" }}>Detalhe técnico</summary>
              <div style={{ fontSize: 11, color: C.textMid, marginTop: 7, padding: "9px 11px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, lineHeight: 1.5, overflowWrap: "anywhere" }}>{anexoSelecionado.motivo}</div>
            </details>}
            {erroAcao && <div role="alert" style={{ marginTop: 10, padding: "9px 11px", borderRadius: 8, border: `1px solid ${C.redBorder}`, background: C.redLight, color: C.red, fontSize: 11.5 }}>{erroAcao}</div>}
            {(anexoSelecionado.acoes_disponiveis || []).includes("escolher_empresa") ?
              <div style={{ marginTop: 14, fontSize: 12, color: C.textMid }}>Escolhe a empresa desta conta na página de Conciliação.</div>
            : <>
              {classificarAlvo === anexoSelecionado.id ? <FormClassificarAnexo
                anexo={anexoSelecionado} empresas={empresas}
                onCancelar={() => setClassificarAlvo(null)}
                onClassificado={async () => {
                  const proximo = anexosVisiveis.find(a => a.id !== anexoSelecionado.id && a.resultado !== "classificado_a_reprocessar");
                  setClassificarAlvo(null);
                  if (proximo) setAnexoSelecionadoId(proximo.id);
                  await refresh();
                  onReprocessado();
                }}
              /> : <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                {(anexoSelecionado.acoes_disponiveis || []).includes("classificar") && <Btn onClick={(e) => { e?.preventDefault(); e?.stopPropagation(); setErroAcao(null); setClassificarAlvo(anexoSelecionado.id); }}>Preencher dados manualmente</Btn>}
                {anexoSelecionado.arquivo_local && <Btn variant="secondary" onClick={() => abrirPdfAnexo(anexoSelecionado.id)}>Abrir documento</Btn>}
                {(anexoSelecionado.acoes_disponiveis || []).includes("reler") && <Btn variant="secondary" disabled={!!acaoDocumento} onClick={() => relerAnexo(anexoSelecionado)}>{acaoDocumento === "reler" ? "A tentar nova leitura…" : "Tentar leitura automática"}</Btn>}
                {(anexoSelecionado.acoes_disponiveis || []).includes("ignorar") && <button type="button" disabled={!!acaoDocumento} onClick={() => { setErroAcao(null); setMotivoIgnorar(""); setIgnorarAlvo(anexoSelecionado); }} style={{ border: 0, background: "transparent", color: C.red, fontSize: 11.5, fontWeight: 700, cursor: acaoDocumento ? "not-allowed" : "pointer", padding: 7 }}>Ignorar este documento</button>}
              </div>}
            </>}
          </div> : <Empty msg="Seleciona um documento para consultar o detalhe." />}
        </aside>
        </div>

        {/* Rodapé fixo */}
        <div style={{ padding: "14px 26px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexShrink: 0, background: C.surface }}>
          <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.45 }}>
            <strong style={{ color: C.text }}>{porDecidirCount} por decidir.</strong>{correcoesGuardadasCount > 0 ? ` ${correcoesGuardadasCount} ${correcoesGuardadasCount === 1 ? "correção guardada" : "correções guardadas"}.` : ""} Aplica tudo no fim.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
          <Btn variant="orange" disabled={running || pendentesCount === 0} onClick={() => confirmarReprocessar("pendentes")} title="Só reanalisa os anexos que falharam ou ficaram em falta">
            {running ? "A processar…" : `Concluir processamento (${pendentesCount})`}
          </Btn>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .email-detail-grid { grid-template-columns: 1fr !important; overflow-y: auto !important; }
            .email-detail-grid > div { overflow-y: visible !important; }
            .email-detail-activity { border-left: 0 !important; border-top: 1px solid #1e293b; min-height: 260px !important; }
          }
        `}</style>
      </div>
      {ignorarAlvo && <div role="dialog" aria-modal="true" aria-label="Confirmar que o documento deve ser ignorado" style={{ position: "fixed", inset: 0, zIndex: 5100, background: "rgba(15,23,42,.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
        <div style={{ width: 460, maxWidth: "100%", padding: 22, borderRadius: 12, background: C.surface, boxShadow: "0 24px 70px rgba(15,23,42,.3)" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Ignorar este documento?</div>
          <div style={{ marginTop: 7, fontSize: 12.5, color: C.textMid, lineHeight: 1.5 }}>O ficheiro e o histórico serão preservados. O documento deixa de ser processado e o motivo fica registado para auditoria.</div>
          <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: C.text, overflowWrap: "anywhere" }}>{ignorarAlvo.anexo_filename}</div>
          <label htmlFor="motivo-ignorar-anexo" style={{ display: "block", marginTop: 14, fontSize: 11, fontWeight: 800, color: C.textMid }}>Motivo obrigatório</label>
          <textarea id="motivo-ignorar-anexo" autoFocus value={motivoIgnorar} onChange={e => setMotivoIgnorar(e.target.value)} rows={3} placeholder="Ex.: assinatura do email sem relevância contabilística" style={{ width: "100%", marginTop: 5, padding: 10, boxSizing: "border-box", border: `1px solid ${C.border}`, borderRadius: 8, resize: "vertical", fontSize: 12.5 }} />
          {erroAcao && <div role="alert" style={{ marginTop: 8, color: C.red, fontSize: 11.5 }}>{erroAcao}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Btn variant="secondary" disabled={!!acaoDocumento} onClick={() => { setIgnorarAlvo(null); setMotivoIgnorar(""); setErroAcao(null); }}>Cancelar</Btn>
            <Btn variant="danger" disabled={!!acaoDocumento || !motivoIgnorar.trim()} onClick={() => ignorarAnexo(ignorarAlvo)}>{acaoDocumento === "ignorar" ? "A registar…" : "Confirmar e preservar no histórico"}</Btn>
          </div>
        </div>
      </div>}
    </div>
  );
}

// Lista de emails processados pelo agente — de cada um, quantos anexos
// foram bem e quantos falharam (ver GET /api/emails), com drill-down para o
// motivo exacto de cada falha e um botão de manutenção para reprocessar.
function EmailsTab({ filtroInicial = "", apenasPendentes = false }) {
  const [filtroStatus, setFiltroStatus] = useState(filtroInicial);
  const { data: resumo, refresh: refreshResumo } = useApi("/api/emails/resumo-ingestao", [], null);
  const { data: emails, loading, refresh } = useApi(
    `/api/emails?${filtroStatus ? `status=${encodeURIComponent(filtroStatus)}&` : ""}${apenasPendentes ? "apenas_pendentes=true" : ""}`,
    [filtroStatus, apenasPendentes], []
  );
  const [detalheEmail, setDetalheEmail] = useState(null);
  const refreshTudo = () => { refresh(); refreshResumo(); };

  // A etiqueta Gmail 'Contabilidade/1_Aguardar_Revisao' cobre TRÊS estados
  // diferentes na BD (ver labelling em src/main.py) — a opção agregada junta
  // os três para bater certo com a contagem que se vê na pasta do Gmail; as
  // três seguintes permitem filtrar cada motivo individualmente.
  const STATUS_OPCOES = [
    { value: "", label: "Todos os estados" },
    { value: "Revisão_Manual,REVISAO_MANUAL,Sem_Faturas,Falhou_Permanente,Anexo_Nao_Suportado,Erro,Erro_Ingestao", label: "Requer intervenção" },
    { value: "Revisão_Manual,Sem_Faturas,Falhou_Permanente,Anexo_Nao_Suportado", label: "Aguardar revisão (todos)" },
    { value: "Revisão_Manual", label: "↳ com erros" },
    { value: "Sem_Faturas", label: "↳ sem faturas encontradas" },
    { value: "Falhou_Permanente", label: "↳ falhou permanentemente" },
    { value: "Anexo_Nao_Suportado", label: "↳ anexo em formato que não sabemos ler" },
    { value: "Concluído", label: "Concluído" },
    { value: "Erro_Ingestao", label: "Falhou a ingestão (vai ser re-tentado)" },
    { value: "Sem_Documento", label: "Sem documento (arquivado)" },
    { value: "Bloqueado_Nao_Autorizado", label: "Bloqueado" },
  ];
  const STATUS_COR = {
    "Concluído": "green",
    "Revisão_Manual": "orange",
    "Sem_Faturas": "gray",
    "Falhou_Permanente": "red",
    "Bloqueado_Nao_Autorizado": "red",
    // Trouxe anexos que o pipeline não sabe ler — pode ser uma fatura real
    // num formato novo, por isso conta como problema a resolver, não ruído.
    "Anexo_Nao_Suportado": "orange",
    "Erro_Ingestao": "orange",
    "Sem_Documento": "gray",
  };

  const fmtTs = (iso) => {
    try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div>
      {resumo && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        {[
          ["Registados", resumo.total_registados, "blue"],
          ["Resolvidos", resumo.resolvidos, "green"],
          ["Revisão humana", resumo.pendentes_revisao ?? resumo.revisao_humana, (resumo.pendentes_revisao ?? resumo.revisao_humana) ? "orange" : "gray"],
          ["Nova tentativa", resumo.pendentes_nova_tentativa ?? resumo.nova_tentativa, (resumo.pendentes_nova_tentativa ?? resumo.nova_tentativa) ? "orange" : "gray"],
          ["Sem estado final", resumo.emails_sem_estado_final, resumo.emails_sem_estado_final ? "red" : "green"],
        ].map(([label, value, color]) => <Card key={label} p="12px 14px">
          <div style={{ fontSize: 10, fontWeight: 800, color: C.textLight, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: color === "red" ? C.red : color === "orange" ? C.orange : color === "green" ? C.green : C.text, marginTop: 4 }}>{value}</div>
        </Card>)}
      </div>}
      {resumo && (!resumo.balanco_fecha || resumo.estado_desconhecido > 0 || resumo.emails_sem_estado_final > 0) && (
        <div style={{ padding: "10px 13px", borderRadius: 8, border: `1px solid ${C.redBorder}`, background: C.redLight, color: C.red, fontSize: 12.5, marginBottom: 14 }}>
          A entrada documental não fecha: existem emails sem estado final ou com um estado desconhecido. Devem ser revistos antes de considerar a ingestão concluída.
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12.5, color: C.textMid }}>De cada email, quais anexos foram bem processados, quais falharam e porquê.</div>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: "#fff", outline: "none" }}>
          {STATUS_OPCOES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <Card p="0">
        {loading ? <Spinner /> : !emails?.length ? <Empty msg="Sem emails processados." /> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}>
              <THead cols={["Assunto", "Remetente", "Anexos", "Estado", "Processado em"]} />
              <tbody>
                {emails.map(e => (
                  <TR key={e.message_id} onClick={() => setDetalheEmail(e)}>
                    <TD style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.email_assunto || "(sem assunto)"}</TD>
                    <TD muted>{e.email_remetente || "—"}</TD>
                    <TD>
                      {e.anexos_sucesso > 0 && <span style={{ color: C.green, fontWeight: 700, marginRight: 8 }}>{e.anexos_sucesso} ok</span>}
                      {e.anexos_erro > 0 && <span style={{ color: C.red, fontWeight: 700, marginRight: 8 }}>{e.anexos_erro} erro</span>}
                      {e.anexos_outro > 0 && <span style={{ color: C.textMid }}>{e.anexos_outro} outro</span>}
                      {!e.anexos_sucesso && !e.anexos_erro && !e.anexos_outro && <span style={{ color: C.textLight }}>—</span>}
                    </TD>
                    <TD><Pill color={STATUS_COR[e.status] || "blue"}>{e.status}</Pill></TD>
                    <TD muted>{fmtTs(e.processed_at)}</TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {detalheEmail && (
        <ModalDetalheEmail email={detalheEmail} onClose={() => setDetalheEmail(null)} onReprocessado={refreshTudo} />
      )}
    </div>
  );
}

function PageEmailsPendentes() {
  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>Documentos pendentes</h1>
        <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>Emails e anexos em que o agente precisa de revisão ou de uma nova tentativa.</p>
      </div>
      <EmailsTab apenasPendentes filtroInicial="Revisão_Manual,REVISAO_MANUAL,Sem_Faturas,Falhou_Permanente,Anexo_Nao_Suportado,Erro,Erro_Ingestao" />
    </div>
  );
}

const PLANO_ESTADO_LABEL = {
  proposto: "Pronto para análise",
  proposto_sem_empresa: "Empresa por identificar",
  proposto_multiplas_empresas: "Várias empresas",
};

function ValorPlano({ label, campo }) {
  if (!campo?.valor) return null;
  return (
    <div style={{ padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: C.textLight, textTransform: "uppercase", letterSpacing: .6 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 4 }}>{String(campo.valor)}</div>
      <div style={{ fontSize: 11, color: C.textMid, marginTop: 4 }}>
        Origem: {campo.origem || "—"}{campo.confianca != null ? ` · confiança ${Math.round(campo.confianca * 100)}%` : ""}
      </div>
      {campo.evidencia && <div style={{ fontSize: 11.5, color: C.textMid, marginTop: 6, fontStyle: "italic" }}>“{campo.evidencia}”</div>}
    </div>
  );
}

export function ModalDetalhePlano({ planoId, onClose, onReverEmail }) {
  const { data, loading, error } = useApi(`/api/agente/planos/${planoId}`, [planoId], null);
  const interpretacao = data?.interpretacao || {};
  const plano = data?.plano || {};
  const conflitos = [...(interpretacao.conflitos || [])];
  if (data?.conflito) conflitos.unshift(data.conflito);
  const ambiguidades = interpretacao.ambiguidades || [];
  const acoes = plano.acoes_propostas || [];
  const resolucao = data?.resolucao_revisao;

  return createPortal(
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,23,42,.48)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div style={{ width: "min(980px, 96vw)", maxHeight: "92vh", overflow: "auto", background: C.surface, borderRadius: 12, boxShadow: "0 24px 70px rgba(15,23,42,.28)" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 2, background: C.surface, padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Análise do email</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 3 }}>Modo de observação — nenhuma proposta foi executada pelo novo agente.</div>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{ border: 0, background: "transparent", cursor: "pointer", color: C.textMid }}><X size={19} /></button>
        </div>
        <div style={{ padding: 22 }}>
          {loading ? <Spinner /> : error ? <Empty msg={error} /> : data && <>
            <Card p="16px" style={{ marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(180px,1fr)", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{data.assunto || "(sem assunto)"}</div>
                  <div style={{ fontSize: 12, color: C.textMid, marginTop: 5 }}>{data.remetente_nome || data.remetente_email || "Remetente desconhecido"}</div>
                </div>
                <div style={{ fontSize: 12, color: C.textMid }}>
                  <div><strong>Empresa:</strong> {data.empresa_nome || data.empresas_documentos || (data.email_estado === "multiplas_empresas" ? "Várias empresas" : "Por identificar")}</div>
                  <div style={{ marginTop: 4 }}><strong>Recebido:</strong> {fmtTsSistema(data.recebido_em)}</div>
                  <div style={{ marginTop: 4 }}><strong>Versão do plano:</strong> {data.versao}</div>
                  <div style={{ marginTop: 4 }}><strong>Resultado atual:</strong> {data.pipeline_estado || "Ainda não registado"}</div>
                </div>
              </div>
            </Card>

            {!!data.documentos?.length && <>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 9 }}>Documentos reconhecidos ({data.documentos.length})</div>
              <Card p="0" style={{ marginBottom: 16, overflow: "hidden" }}>
                {data.documentos.map((d, i) => <div key={d.id} style={{ display: "grid", gridTemplateColumns: "minmax(180px,1.5fr) minmax(150px,1fr) 120px", gap: 12, padding: "11px 14px", borderTop: i ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
                  <div><div style={{ fontSize: 12.5, fontWeight: 700 }}>{d.numero_documento}</div><div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>{d.fornecedor_nome || d.fornecedor_nif}</div></div>
                  <div><div style={{ fontSize: 12, fontWeight: 700 }}>{d.empresa_nome}</div><div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>{d.empresa_nif}</div></div>
                  <div style={{ textAlign: "right" }}><div style={{ fontSize: 12.5, fontWeight: 800 }}>{fmt(d.valor_total)}</div>{d.revisao_motivo && <div style={{ fontSize: 11, color: C.orange, marginTop: 3 }}>{d.revisao_motivo}</div>}</div>
                </div>)}
              </Card>
            </>}

            <div style={{ padding: "12px 14px", borderRadius: 8, background: C.blueLight, border: `1px solid ${C.blueBorder}`, color: C.blue, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: .6 }}>Intenção detetada</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{interpretacao.intencao || "Não foi possível determinar"}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Tipo sugerido: {interpretacao.tipo_operacao || "indeterminado"}</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10, marginBottom: 16 }}>
              <ValorPlano label="Reembolso" campo={interpretacao.eh_reembolso} />
              <ValorPlano label="Estado de pagamento" campo={interpretacao.ja_pago} />
              <ValorPlano label="Beneficiário" campo={interpretacao.beneficiario_mencionado} />
              <ValorPlano label="Data mencionada" campo={interpretacao.data_mencionada} />
              <ValorPlano label="Descrição" campo={interpretacao.descricao} />
            </div>

            {(conflitos.length > 0 || ambiguidades.length > 0) && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, marginBottom: 16 }}>
              {conflitos.length > 0 && <Card p="14px" style={{ borderColor: C.redBorder, background: C.redLight }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.red, marginBottom: 8 }}>Conflitos</div>
                {conflitos.map((x, i) => <div key={i} style={{ fontSize: 12, color: C.text, marginTop: i ? 6 : 0 }}>{x}</div>)}
              </Card>}
              {ambiguidades.length > 0 && <Card p="14px" style={{ borderColor: C.orangeBorder, background: C.orangeLight }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.orange, marginBottom: 8 }}>Dúvidas a confirmar</div>
                {ambiguidades.map((x, i) => <div key={i} style={{ fontSize: 12, color: C.text, marginTop: i ? 6 : 0 }}>{x}</div>)}
              </Card>}
            </div>}

            {resolucao && <Card p="14px" style={{
              marginBottom: 16,
              borderColor: resolucao.estado === "resolvido" ? C.greenBorder : C.orangeBorder,
              background: resolucao.estado === "resolvido" ? C.greenLight : C.orangeLight,
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: resolucao.estado === "resolvido" ? C.green : C.orange, marginBottom: 5 }}>Como resolver</div>
              <div style={{ fontSize: 13, fontWeight: 750, color: C.text }}>{resolucao.titulo}</div>
              <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>{resolucao.descricao}</div>
              {resolucao.estado !== "resolvido" && onReverEmail && <div style={{ marginTop: 12 }}>
                <Btn size="sm" onClick={() => onReverEmail(data)}>Rever e corrigir documentos</Btn>
              </div>}
            </Card>}

            <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 9 }}>Anexos ({data.anexos?.length || 0})</div>
            <Card p="0" style={{ marginBottom: 16, overflow: "hidden" }}>
              {!data.anexos?.length ? <Empty msg="Este email não tinha anexos." /> : data.anexos.map((a, i) => (
                <div key={a.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "11px 14px", borderTop: i ? `1px solid ${C.border}` : "none" }}>
                  <div><div style={{ fontSize: 12.5, fontWeight: 700 }}>{a.nome || "(sem nome)"}</div><div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>{a.mime_detetado || a.mime_declarado || "Formato desconhecido"}</div>{a.ia_analise?.classificacao && <div style={{ fontSize: 11, color: C.blue, marginTop: 4 }}>IA: {a.ia_analise.classificacao} · {Math.round((a.ia_analise.confianca || 0) * 100)}%</div>}{a.ia_erro && <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>Análise IA falhou: {a.ia_erro}</div>}</div>
                  <div style={{ textAlign: "right" }}><Pill color={a.ia_estado === "concluida" ? "green" : a.ia_estado === "erro" ? "red" : "gray"}>{a.ia_estado === "concluida" ? "Analisado pela IA" : a.ia_estado === "erro" ? "Erro na IA" : "IA pendente"}</Pill><div style={{ marginTop: 5 }}><Pill color={a.classificacao === "ignorado" ? "gray" : "blue"}>{a.classificacao}</Pill></div>{a.motivo_ignorado && <div style={{ fontSize: 11, color: C.textMid, marginTop: 4 }}>{a.motivo_ignorado}</div>}</div>
                </div>
              ))}
            </Card>

            <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 9 }}>Ações que o agente proporia</div>
            <Card p="14px">
              {!acoes.length ? <div style={{ fontSize: 12, color: C.textMid }}>Nenhuma ação proposta.</div> : acoes.map((a, i) => (
                <div key={i} style={{ paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0, borderTop: i ? `1px solid ${C.border}` : "none" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{a.acao || a.tipo || String(a)}</div>
                  {a.porque && <div style={{ fontSize: 11.5, color: C.textMid, marginTop: 3 }}>{a.porque}</div>}
                  {a.exige_confirmacao && <div style={{ fontSize: 11, color: C.orange, fontWeight: 700, marginTop: 4 }}>Exige confirmação humana</div>}
                </div>
              ))}
            </Card>
          </>}
        </div>
      </div>
    </div>, document.body
  );
}

function PlanosAgenteTab() {
  const [apenasRevisao, setApenasRevisao] = useState(false);
  const [planoId, setPlanoId] = useState(null);
  const [emailParaRever, setEmailParaRever] = useState(null);
  const { data, loading, error, refresh: refreshPlanos } = useApi(`/api/agente/planos?apenas_revisao=${apenasRevisao ? "true" : "false"}`, [apenasRevisao], { planos: [] });
  const planos = data?.planos || [];
  return <div>
    <div style={{ padding: "12px 14px", borderRadius: 8, border: `1px solid ${C.blueBorder}`, background: C.blueLight, color: C.blue, marginBottom: 16, fontSize: 12.5 }}>
      <strong>Modo de observação.</strong> Aqui vês o que o novo agente entendeu e faria. Nenhuma destas propostas foi executada pelo novo agente.
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
      <div style={{ fontSize: 12.5, color: C.textMid }}>Última versão da análise de cada email.</div>
      <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: C.text, cursor: "pointer" }}>
        <input type="checkbox" checked={apenasRevisao} onChange={e => setApenasRevisao(e.target.checked)} /> Apenas os que precisam de revisão
      </label>
    </div>
    <Card p="0">
      {loading ? <Spinner /> : error ? <Empty msg={error} /> : !planos.length ? <Empty msg="Ainda não existem planos estruturados. Ativa o modo de observação para começar a recolher análises." /> : <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
          <THead cols={["Email", "Empresa", "Intenção", "Anexos", "Pipeline atual", "Revisão", "Estado", "Analisado em"]} />
          <tbody>{planos.map(p => <TR key={p.id} onClick={() => setPlanoId(p.id)}>
            <TD style={{ maxWidth: 260 }}><div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.assunto || "(sem assunto)"}</div><div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>{p.remetente_nome || p.remetente_email || "—"}</div></TD>
            <TD>{p.empresa_nome || p.empresas_documentos || <span style={{ color: C.orange }}>Por identificar</span>}</TD>
            <TD style={{ maxWidth: 280 }}><div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.intencao || "Não determinada"}</div><div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>{p.tipo_operacao}</div></TD>
            <TD>{p.anexos_total}{p.anexos_ignorados > 0 && <span style={{ color: C.textMid }}> · {p.anexos_ignorados} ignorado(s)</span>}</TD>
            <TD><Pill color={p.pipeline_estado === "Concluído" ? "green" : p.pipeline_estado ? "orange" : "gray"}>{p.pipeline_estado || "Sem resultado"}</Pill></TD>
            <TD>{p.exige_confirmacao ? <Pill color="orange">Necessária</Pill> : <Pill color="green">Sem alertas</Pill>}</TD>
            <TD><Pill color={p.estado === "proposto" ? "blue" : "orange"}>{PLANO_ESTADO_LABEL[p.estado] || p.estado}</Pill></TD>
            <TD muted>{fmtTsSistema(p.criado_em)}</TD>
          </TR>)}</tbody>
        </table>
      </div>}
    </Card>
    {planoId && <ModalDetalhePlano
      planoId={planoId}
      onClose={() => setPlanoId(null)}
      onReverEmail={email => {
        setPlanoId(null);
        setEmailParaRever({
          message_id: email.provider_message_id,
          email_assunto: email.assunto,
          email_remetente: email.remetente_nome || email.remetente_email,
          attachment_count: email.anexos?.length || 0,
        });
      }}
    />}
    {emailParaRever && <ModalDetalheEmail
      email={emailParaRever}
      onClose={() => setEmailParaRever(null)}
      onReprocessado={refreshPlanos}
    />}
  </div>;
}

const RUN_TRIGGER_LABEL = { scheduler: "Agendado", manual: "Manual", reprocessar_email: "Reprocessamento" };
const RUN_STATUS_COR    = { running: "orange", ok: "green", error: "red", timeout: "red" };
const RUN_STATUS_LABEL  = { running: "A correr…", ok: "Concluído", error: "Erro", timeout: "Timeout" };

function fmtTsSistema(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso; }
}

function ExecucoesTab() {
  const [filtroTrigger, setFiltroTrigger] = useState("");
  const { data: execData, loading } = useApi(
    `/api/sistema/execucoes${filtroTrigger ? `?trigger_tipo=${encodeURIComponent(filtroTrigger)}` : ""}`,
    [filtroTrigger], []
  );
  const { data: histData, loading: histLoading } = useApi(
    "/api/sistema/config-historico?chave=schedule_interval,schedule_hora_execucao,mock_mode", [], []
  );
  const execucoes = execData?.execucoes || [];
  const alteracoes = histData?.alteracoes || [];

  const duracao = (ini, fim) => {
    if (!fim) return "—";
    const ms = new Date(fim) - new Date(ini);
    if (!Number.isFinite(ms) || ms < 0) return "—";
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}min ${s % 60}s`;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12.5, color: C.textMid }}>Histórico de todas as execuções do agente — permite confirmar se a periodicidade configurada está mesmo a ser respeitada.</div>
        <select value={filtroTrigger} onChange={e => setFiltroTrigger(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: "#fff", outline: "none" }}>
          <option value="">Todas as origens</option>
          <option value="scheduler">Agendado</option>
          <option value="manual">Manual</option>
          <option value="reprocessar_email">Reprocessamento</option>
        </select>
      </div>

      <Card p="0">
        {loading ? <Spinner /> : !execucoes.length ? <Empty msg="Ainda não há execuções registadas." /> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse" }}>
              <THead cols={["Origem", "Início", "Duração", "Estado", "Detalhe"]} />
              <tbody>
                {execucoes.map(r => (
                  <TR key={r.id}>
                    <TD>{RUN_TRIGGER_LABEL[r.trigger_tipo] || r.trigger_tipo}</TD>
                    <TD muted>{fmtTsSistema(r.iniciado_em)}</TD>
                    <TD muted>{duracao(r.iniciado_em, r.terminado_em)}</TD>
                    <TD><Pill color={RUN_STATUS_COR[r.status] || "gray"}>{RUN_STATUS_LABEL[r.status] || r.status}</Pill></TD>
                    <TD muted style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detalhe || "—"}</TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ marginTop: 24, marginBottom: 12, fontSize: 13, fontWeight: 700, color: C.text }}>Alterações de configuração do agendamento</div>
      <Card p="0">
        {histLoading ? <Spinner /> : !alteracoes.length ? <Empty msg="Sem alterações registadas ainda." /> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse" }}>
              <THead cols={["Chave", "De", "Para", "Quando", "Por quem"]} />
              <tbody>
                {alteracoes.map(a => (
                  <TR key={a.id}>
                    <TD>{a.chave}</TD>
                    <TD muted>{a.valor_antigo ?? "—"}</TD>
                    <TD>{a.valor_novo}</TD>
                    <TD muted>{fmtTsSistema(a.alterado_em)}</TD>
                    <TD muted>{a.alterado_por || "sistema"}</TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const CHAT_TIPO_LABEL = { comando: "Comando", callback: "Clique em botão", envio_saida: "Envio de saída" };
const CHAT_CANAL_LABEL = { logs: "Logs", interacao: "Interação" };

function ChatTab() {
  const [filtroTipo, setFiltroTipo] = useState("");
  const { data, loading } = useApi(
    `/api/sistema/interacoes-chat${filtroTipo ? `?tipo=${encodeURIComponent(filtroTipo)}` : ""}`,
    [filtroTipo], []
  );
  const interacoes = data?.interacoes || [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12.5, color: C.textMid }}>Comandos recebidos, cliques em botões de cards e mensagens enviadas para o Google Chat.</div>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: "#fff", outline: "none" }}>
          <option value="">Todos os tipos</option>
          <option value="comando">Comandos</option>
          <option value="callback">Cliques em botões</option>
          <option value="envio_saida">Envios de saída</option>
        </select>
      </div>

      <Card p="0">
        {loading ? <Spinner /> : !interacoes.length ? <Empty msg="Ainda não há interações registadas." /> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}>
              <THead cols={["Tipo", "Canal", "Referência", "Estado", "Quando"]} />
              <tbody>
                {interacoes.map(i => (
                  <TR key={i.id}>
                    <TD>{CHAT_TIPO_LABEL[i.tipo] || i.tipo}</TD>
                    <TD muted>{CHAT_CANAL_LABEL[i.canal] || i.canal || "—"}</TD>
                    <TD muted style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.referencia || i.detalhe || "—"}</TD>
                    <TD><Pill color={i.sucesso ? "green" : "red"}>{i.sucesso ? "OK" : "Falhou"}</Pill></TD>
                    <TD muted>{fmtTsSistema(i.criado_em)}</TD>
                  </TR>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const CREDENCIAL_INFO = {
  GEMINI_API_KEY:                { label: "Gemini API Key",              nivel2motivo: null },
  GEMINI_API_KEY_PAGA:           { label: "Gemini API Key (paga, reserva)", nivel2motivo: null },
  GOOGLE_CHAT_WEBHOOK_LOGS:      { label: "Chat Webhook — Logs",         nivel2motivo: null },
  GOOGLE_CHAT_WEBHOOK_INTERACAO: { label: "Chat Webhook — Interação",    nivel2motivo: null },
  CHAT_CALLBACK_SECRET:          { label: "Chat Callback Secret",        nivel2motivo: null },
  EMAIL_ALERTAS:                 { label: "Email de Alertas",           nivel2motivo: null },
  DATABASE_URL:                  { label: "Base de Dados (DATABASE_URL)", nivel2motivo: "Necessário para o próprio container arrancar — um valor errado impede tudo de funcionar." },
  POSTGRES_PASSWORD:             { label: "Password do Postgres",       nivel2motivo: "Segredo de arranque da base de dados — nunca editável fora do .env." },
  JWT_SECRET:                    { label: "JWT Secret",                 nivel2motivo: "Mudar isto invalida a sessão de TODA a gente, incluindo a tua, no mesmo instante." },
  API_AUTH_TOKEN:                { label: "Token de API (legado)",      nivel2motivo: "Usado por scripts externos — mudar sem coordenar parte-os silenciosamente." },
  BACKEND_URL:                   { label: "URL pública do backend",     nivel2motivo: "Tem de bater certo com o domínio real e o nginx — errado aqui parte os botões do Chat." },
  API_CORS_ORIGINS:              { label: "Origens CORS permitidas",    nivel2motivo: "Um valor errado pode bloquear o próprio dashboard de falar com a API." },
};

function CredencialCard({ cred, onSaved }) {
  const info = CREDENCIAL_INFO[cred.chave] || { label: cred.chave, nivel2motivo: null };
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState("");
  const [testando, setTestando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);

  const testar = async () => {
    if (!valor.trim()) return;
    setTestando(true); setResultadoTeste(null);
    const r = await apiFetch(`/api/sistema/credenciais/${cred.chave}/testar`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ valor }),
    });
    const d = await r.json();
    setResultadoTeste(d);
    setTestando(false);
  };

  const guardar = async () => {
    if (!valor.trim()) return;
    setGuardando(true);
    const r = await apiFetch(`/api/sistema/credenciais/${cred.chave}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ valor }),
    });
    if (r.ok) {
      setEditando(false); setValor(""); setResultadoTeste(null);
      onSaved();
    } else {
      const d = await r.json().catch(() => ({}));
      setResultadoTeste({ ok: false, mensagem: d.detail || "Erro ao gravar." });
    }
    setGuardando(false);
  };

  const reverter = async () => {
    if (!window.confirm(`Reverter "${info.label}" para o valor anterior?`)) return;
    const r = await apiFetch(`/api/sistema/credenciais/${cred.chave}/reverter`, { method: "POST" });
    if (r.ok) onSaved();
  };

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{info.label}</div>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>
            {cred.configurado ? (
              <>● Configurada {cred.mascara && <span style={{ fontFamily: "monospace" }}>({cred.mascara})</span>}</>
            ) : "○ Não configurada"}
            {cred.override_ativo && <span style={{ color: C.blue, marginLeft: 8 }}>· editada no painel</span>}
          </div>
          {cred.atualizado_em && (
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
              Alterada por {cred.atualizado_por || "sistema"} em {fmtTsSistema(cred.atualizado_em)}
            </div>
          )}
          {cred.nivel === 2 && info.nivel2motivo && (
            <div style={{ fontSize: 11.5, color: C.textLight, marginTop: 6 }}>{info.nivel2motivo}</div>
          )}
        </div>
        {cred.nivel === 1 && !editando && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={() => setEditando(true)}
              style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.borderDark}`, background: "#fff", color: C.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Editar
            </button>
            {cred.override_ativo && (
              <button onClick={reverter}
                style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.orangeBorder}`, background: C.orangeLight, color: C.orange, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Reverter
              </button>
            )}
          </div>
        )}
      </div>

      {editando && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          <input type="password" value={valor} onChange={e => { setValor(e.target.value); setResultadoTeste(null); }}
            placeholder="Novo valor…" autoComplete="off"
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.borderDark}`, fontSize: 13, color: C.text, marginBottom: 10 }} />
          {resultadoTeste && (
            <div style={{ fontSize: 12, color: resultadoTeste.ok ? C.green : C.red, marginBottom: 10 }}>
              {resultadoTeste.ok ? "✓" : "✗"} {resultadoTeste.mensagem}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={testar} disabled={testando || !valor.trim()}
              style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.borderDark}`, background: "#fff", color: C.text, fontSize: 12.5, fontWeight: 700, cursor: testando ? "not-allowed" : "pointer" }}>
              {testando ? "A testar…" : "Testar"}
            </button>
            <button onClick={guardar} disabled={guardando || !valor.trim()}
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: C.blue, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: guardando ? "not-allowed" : "pointer" }}>
              {guardando ? "A gravar…" : "Guardar"}
            </button>
            <button onClick={() => { setEditando(false); setValor(""); setResultadoTeste(null); }}
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "transparent", color: C.textMid, fontSize: 12.5, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function CredenciaisTab() {
  const { data, loading, refresh } = useApi("/api/sistema/credenciais", []);
  const credenciais = data?.credenciais || [];
  const nivel1 = credenciais.filter(c => c.nivel === 1);
  const nivel2 = credenciais.filter(c => c.nivel === 2);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ fontSize: 12.5, color: C.textMid, marginBottom: 16 }}>
        Chaves e credenciais usadas pelo sistema. Nível 1 pode ser editado aqui (guardado encriptado);
        Nível 2 só muda via <code>.env</code> + reinício do servidor — arriscam demasiado para editar em runtime.
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
        Editáveis
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
        {nivel1.map(c => <CredencialCard key={c.chave} cred={c} onSaved={refresh} />)}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
        Só estado — editar via .env
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {nivel2.map(c => <CredencialCard key={c.chave} cred={c} onSaved={refresh} />)}
      </div>
    </div>
  );
}

// Separador "Configuração" do Sistema — mesma configuração do agente que a
// página Agente mostra ao lado do botão de correr, aqui isolada para quem
// vem ao Sistema só para configurar (foi este o pedido: "era esse tipo de
// configuração que queria meter na página Sistema"). Partilha o hook e o
// cartão com PageAgente, não é uma segunda implementação.
function ConfiguracaoTab() {
  const { cfg, saveCfg, savingCfg, erroCfg } = useAgenteConfig();
  return (
    <div style={{ width: "100%" }}>
      <div style={{ fontSize: 12.5, color: C.textMid, marginBottom: 16 }}>
        Como e quando o agente corre sozinho, quanto pode gastar em IA por dia, e se o assistente está disponível.
      </div>
      <AgenteConfigCard cfg={cfg} saveCfg={saveCfg} savingCfg={savingCfg} erroCfg={erroCfg} />
    </div>
  );
}

function PageSistema() {
  const [tab, setTab] = useState("configuracao");
  const TABS = [
    { id: "configuracao", label: "Configuração", Icon: SlidersHorizontal },
    { id: "execucoes",   label: "Execuções",   Icon: History },
    { id: "planos",      label: "Planos do Agente", Icon: FileText },
    { id: "chat",        label: "Chat",        Icon: MessageSquare },
    { id: "credenciais", label: "Credenciais", Icon: KeyRound },
  ];
  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>Sistema</h1>
        <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>Configuração do agente, histórico de execuções, interações e credenciais</p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 22, borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 15px", border: "none", background: "none", cursor: "pointer",
                       fontSize: 13, fontWeight: 700, color: active ? C.blue : C.textMid,
                       borderBottom: `2px solid ${active ? C.blue : "transparent"}`, marginBottom: -1 }}>
              <t.Icon size={15} strokeWidth={2.2} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "configuracao" && <ConfiguracaoTab />}
      {tab === "execucoes" && <ExecucoesTab />}
      {tab === "planos" && <PlanosAgenteTab />}
      {tab === "chat" && <ChatTab />}
      {tab === "credenciais" && <CredenciaisTab />}
    </div>
  );
}

function PageManutencao({ onSelectEmpresa }) {
  const [tab, setTab] = useState("empresas");
  const TABS = [
    { id: "empresas",     label: "Empresas",     Icon: Landmark },
    { id: "categorias",   label: "Categorias",   Icon: Table2 },
    { id: "fornecedores", label: "Fornecedores", Icon: Building2 },
    { id: "tabelas",      label: "Tabelas BD",   Icon: Table2 },
    { id: "seguranca",    label: "Segurança",    Icon: Shield },
    { id: "emails",       label: "Emails",       Icon: Mail },
  ];
  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>Manutenção</h1>
        <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>Painel de controlo central da plataforma</p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 22, borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 15px", border: "none", background: "none", cursor: "pointer",
                       fontSize: 13, fontWeight: 700, color: active ? C.blue : C.textMid,
                       borderBottom: `2px solid ${active ? C.blue : "transparent"}`, marginBottom: -1 }}>
              <t.Icon size={15} strokeWidth={2.2} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "empresas"     && <PageEmpresas onSelectEmpresa={onSelectEmpresa} />}
      {tab === "categorias"   && <CategoriasCard />}
      {tab === "fornecedores" && <FornecedoresCard />}
      {tab === "tabelas"      && <TabelasConfigCard />}
      {tab === "seguranca"    && <RemetentesAutorizadosCard />}
      {tab === "emails"       && <EmailsTab />}
      <style>{`
        @media (max-width: 700px) {
          .emp-card-row   { flex-wrap: wrap !important; gap: 10px !important; }
          .emp-card-stats { width: 100%; justify-content: flex-start !important; gap: 24px !important; }
          .emp-card-btns  { width: 100%; }
          .agente-config-grid { grid-template-columns: 1fr !important; }
          .socios-add-form { flex-wrap: wrap !important; }
          .cats-add-form  { flex-wrap: wrap !important; }
          .cats-add-form select { flex: 1; min-width: 120px; }
        }
      `}</style>
    </div>
  );
}

function PageUtilizadores({ currentUser }) {
  const { data: users, refresh, error } = useApi("/api/utilizadores", [], []);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const toggleAtivo = async (u) => {
    if (u.id === currentUser.id) {
      window.alert("Não te podes desactivar a ti próprio.");
      return;
    }
    if (!window.confirm(`${u.ativo ? "Desactivar" : "Activar"} ${u.email}?`)) return;
    const r = await apiFetch(`/api/utilizadores/${u.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ativo: !u.ativo }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      window.alert(d.detail || "Falha.");
    }
    refresh();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>Utilizadores</h1>
          <p style={{ color: C.textMid, fontSize: 13, marginTop: 4 }}>Gestão de acessos ao dashboard</p>
        </div>
        <Btn variant="primary" onClick={() => setShowCreate(true)}>＋ Novo utilizador</Btn>
      </div>

      <Card p="0">
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 580, borderCollapse: "collapse" }}>
          <THead cols={["Nome", "Email", "Acesso", "Estado", "Último login", "Criado em", "Acções"]} />
          <tbody>
            {(users || []).map(u => (
              <TR key={u.id}>
                <TD>
                  <strong>{u.nome}</strong>
                  {u.id === currentUser.id && <span style={{ marginLeft: 8, fontSize: 10, color: C.blue, fontWeight: 700 }}>(tu)</span>}
                </TD>
                <TD mono muted>{u.email}</TD>
                <TD>
                  <Pill color={u.role === "colaborador" ? "purple" : "blue"}>{u.role === "colaborador" ? "Colaborador" : "Acesso completo"}</Pill>
                </TD>
                <TD>
                  <Pill color={u.ativo ? "green" : "gray"}>{u.ativo ? "✓ Activo" : "Inactivo"}</Pill>
                </TD>
                <TD muted>{u.ultimo_login ? fmtDate(u.ultimo_login) : "—"}</TD>
                <TD muted>{fmtDate(u.criado_em)}</TD>
                <td style={{ padding: "12px 16px" }}>
                  <Btn size="sm" variant="secondary" onClick={() => setEditingId(u.id)} style={{ marginRight: 6 }}>Editar</Btn>
                  <Btn size="sm" variant={u.ativo ? "danger" : "success"} onClick={() => toggleAtivo(u)} disabled={u.id === currentUser.id}>
                    {u.ativo ? "Desactivar" : "Activar"}
                  </Btn>
                </td>
              </TR>
            ))}
          </tbody>
        </table>
        </div>
        {!users?.length && <Empty msg="Sem utilizadores." />}
      </Card>

      {showCreate && <UserModal onClose={() => setShowCreate(false)} onSaved={refresh} />}
      {editingId && <UserModal userId={editingId} onClose={() => setEditingId(null)} onSaved={refresh} />}
    </div>
  );
}

function UserModal({ userId, onClose, onSaved }) {
  const editing = !!userId;
  const { data: existing } = useApi(editing ? `/api/utilizadores` : null, [], []);
  const me = editing && Array.isArray(existing) ? existing.find(u => u.id === userId) : null;
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("admin");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (me) { setNome(me.nome); setEmail(me.email); setRole(me.role || "admin"); }
  }, [me]);

  const save = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      let r;
      if (editing) {
        const body = { nome, role };
        if (password) body.password = password;
        r = await apiFetch(`/api/utilizadores/${userId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } else {
        r = await apiFetch(`/api/utilizadores`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, nome, password, role }),
        });
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || "Falha ao guardar.");
        setSaving(false);
        return;
      }
      onSaved(); onClose();
    } catch {
      setError("Erro de rede.");
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 28, width: 380, boxShadow: "0 12px 40px rgba(0,0,0,.2)" }}>
        <h2 style={{ margin: 0, marginBottom: 18, fontSize: 18, fontWeight: 800, color: C.text }}>
          {editing ? "Editar utilizador" : "Novo utilizador"}
        </h2>
        <form onSubmit={save}>
          <label style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: 1, textTransform: "uppercase" }}>Nome</label>
          <input required value={nome} onChange={e => setNome(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.borderDark}`, borderRadius: 8, fontSize: 14, marginTop: 4, marginBottom: 12, boxSizing: "border-box" }} />

          <label style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: 1, textTransform: "uppercase" }}>Email</label>
          <input type="email" required disabled={editing} value={email} onChange={e => setEmail(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.borderDark}`, borderRadius: 8, fontSize: 14, marginTop: 4, marginBottom: 12, boxSizing: "border-box", background: editing ? C.bg : "#fff" }} />

          <label style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: 1, textTransform: "uppercase" }}>
            {editing ? "Nova password (deixa vazio para manter)" : "Password (mín. 8 chars)"}
          </label>
          <input type="password" required={!editing} minLength={editing ? 0 : 8} value={password} onChange={e => setPassword(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.borderDark}`, borderRadius: 8, fontSize: 14, marginTop: 4, marginBottom: 12, boxSizing: "border-box" }} />

          <label style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: 1, textTransform: "uppercase" }}>Tipo de acesso</label>
          <select value={role} onChange={e => setRole(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.borderDark}`, borderRadius: 8, fontSize: 14, marginTop: 4, marginBottom: 6, boxSizing: "border-box", background: "#fff" }}>
            <option value="admin">Acesso completo</option>
            <option value="colaborador">Colaborador (só despesas)</option>
          </select>
          <div style={{ fontSize: 11, color: C.textLight, marginBottom: 12 }}>
            {role === "colaborador"
              ? "Só consegue submeter despesas e ver o estado das suas próprias — sem acesso ao resto do dashboard."
              : "Vê e gere tudo — faturas, fundos, empresas, outros utilizadores, etc."}
          </div>

          {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{error}</div>}

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancelar</Btn>
            <Btn variant="primary" disabled={saving} style={{ flex: 1 }}>
              {saving ? "A guardar..." : (editing ? "Guardar" : "Criar")}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
// Login obrigatório sempre. Sem modo dev.
// ─── PDF Viewer ───────────────────────────────────────────────────────────────
function drivePreviewUrl(link) {
  if (!link) return null;
  const m = link.match(/\/file\/d\/([^\/\?]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  const q = link.match(/[?&]id=([^&]+)/);
  if (q) return `https://drive.google.com/file/d/${q[1]}/preview`;
  return link;
}

function openPDF(driveLink, title = "", meta = null) {
  const url = drivePreviewUrl(driveLink);
  if (!url) return;
  window.dispatchEvent(new CustomEvent("open-pdf", { detail: { url, title, original: driveLink, meta } }));
}

function openSheets(sheetsId, title = "") {
  if (!sheetsId) return;
  window.dispatchEvent(new CustomEvent("open-sheets", { detail: { sheetsId, title } }));
}

function PDFModal() {
  const [pdf, setPdf] = useState(null);

  useEffect(() => {
    const handler = (e) => setPdf(e.detail);
    window.addEventListener("open-pdf", handler);
    return () => window.removeEventListener("open-pdf", handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setPdf(null); };
    if (pdf) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pdf]);

  if (!pdf) return null;

  return (
    <div
      onClick={() => setPdf(null)}
      style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#1e1e2e", borderRadius: 16, width: "min(95vw, 1000px)", height: "min(90vh, 960px)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 32px 96px rgba(0,0,0,0.6)" }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0, background: "#2a2a3e", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>📄</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {(pdf.meta && pdf.meta.numero) || pdf.title || "Documento"}
              </div>
              {pdf.meta && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {pdf.meta.nome && <span>{pdf.meta.nome}</span>}
                  {pdf.meta.data && <span>{fmtDate(pdf.meta.data)}</span>}
                  {pdf.meta.nif && <span>NIF {pdf.meta.nif}</span>}
                  {pdf.meta.valor != null && <span style={{ fontWeight: 700, color: "#e2e8f0" }}>{fmt(pdf.meta.valor)}</span>}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <a
              href={pdf.original}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 11, color: "#94a3b8", textDecoration: "none", padding: "4px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontWeight: 600 }}
            >
              Abrir no Drive ↗
            </a>
            <button
              onClick={() => setPdf(null)}
              style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.08)", cursor: "pointer", fontSize: 14, color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              ✕
            </button>
          </div>
        </div>
        {/* Iframe */}
        <iframe
          src={pdf.url}
          title={pdf.title || "PDF"}
          style={{ flex: 1, border: "none", width: "100%", background: "#525659" }}
          allow="autoplay"
        />
      </div>
    </div>
  );
}

function SheetsModal() {
  const [sheet, setSheet] = useState(null);

  useEffect(() => {
    const handler = (e) => setSheet(e.detail);
    window.addEventListener("open-sheets", handler);
    return () => window.removeEventListener("open-sheets", handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSheet(null); };
    if (sheet) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet]);

  if (!sheet) return null;
  const embedUrl = `https://docs.google.com/spreadsheets/d/${sheet.sheetsId}/edit?usp=sharing&rm=minimal`;
  const directUrl = `https://docs.google.com/spreadsheets/d/${sheet.sheetsId}`;

  return (
    <div onClick={() => setSheet(null)}
      style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#1e1e2e", borderRadius: 16, width: "min(98vw, 1400px)", height: "min(94vh, 900px)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 32px 96px rgba(0,0,0,0.6)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0, background: "#1a3a1a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16 }}>📊</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{sheet.title || "Relatório mensal"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a href={directUrl} target="_blank" rel="noreferrer"
              style={{ fontSize: 11, color: "#94a3b8", textDecoration: "none", padding: "4px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontWeight: 600 }}>
              Abrir no Sheets ↗
            </a>
            <button onClick={() => setSheet(null)}
              style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.08)", cursor: "pointer", fontSize: 14, color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center" }}>
              ✕
            </button>
          </div>
        </div>
        <iframe src={embedUrl} title={sheet.title || "Sheets"} style={{ flex: 1, border: "none", width: "100%", background: "#fff" }} allow="autoplay" />
      </div>
    </div>
  );
}

// ─── Detalhe de documento (genérico, reutilizável a partir de qualquer fatura) ──
function openDetalhe(faturaId) {
  if (!faturaId) return;
  window.dispatchEvent(new CustomEvent("open-detalhe", { detail: { id: faturaId } }));
}

function DetalheLinha({ label, ok, okLabel, naoLabel, cor }) {
  const corOk = cor || "#157347";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F0F2F5" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: ok ? corOk : "#9AA3B0", display: "flex", alignItems: "center", gap: 6, textAlign: "right" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: ok ? corOk : "#D1D5DB", flexShrink: 0 }} />
        {ok ? okLabel : naoLabel}
      </div>
    </div>
  );
}

// Pontuação de proximidade — mesmo espírito do motor de matching automático
// (valor + data + texto), usado para ordenar candidatos no picker de
// movimento bancário abaixo. Simétrico: fatura↔movimento não importa qual
// dos dois é o "alvo".
//
// NÃO copiar para aqui a JANELA_CANDIDATO_DIAS do motor (reconciliation_engine.py).
// Lá a janela decide se o par EXISTE; aqui isto serve só para ordenar uma
// lista que a pessoa está a escolher à mão — e o casamento manual é
// precisamente a saída para os pagamentos que caem fora da janela
// (fornecedor pago com 60 dias de atraso, etc.). Esconder candidatos aqui
// deixaria esses casos sem forma nenhuma de serem conciliados.
function scoreProximidadeMovimento(mov, fatura) {
  let score = 0;
  const fv = Math.abs(fatura.valor_total || 0);
  const mv = Math.abs(mov.valor || 0);
  if (fv > 0 && mv > 0) {
    const diff = Math.abs(fv - mv) / mv;
    if (diff < 0.001) score += 60;
    else if (diff < 0.01) score += 30;
    else if (diff < 0.05) score += 15;
    else if (diff < 0.10) score += 6;
  }
  const fd = fatura.data_documento ? new Date(fatura.data_documento).getTime() : NaN;
  const md = mov.data_mov ? new Date(mov.data_mov).getTime() : NaN;
  if (!Number.isNaN(fd) && !Number.isNaN(md)) {
    const dias = Math.abs(fd - md) / 86400000;
    if (dias === 0) score += 30;
    else if (dias <= 3) score += 24;
    else if (dias <= 7) score += 16;
    else if (dias <= 14) score += 8;
    else if (dias <= 30) score += 3;
  }
  const nome = (fatura.nome_emitente || "").toLowerCase();
  const desc = (mov.descricao || "").toLowerCase();
  if (nome && desc) {
    const palavras = nome.split(/\s+/).filter(w => w.length > 3);
    if (palavras.length) {
      const hits = palavras.filter(p => desc.includes(p)).length;
      score += 20 * (hits / palavras.length);
    }
  }
  return score;
}

// Picker de movimento bancário a partir de uma fatura — o inverso do fluxo
// já existente na Conciliação (que parte do movimento para escolher a
// fatura). Reutiliza o mesmo endpoint de listagem do extrato e a mesma
// pontuação de proximidade, só invertida.
function PickerMovimentoBancario({ fatura, onEscolher, onCancelar, aAssociar }) {
  const { data: movimentos, loading } = useApi(`/api/conciliacao/extrato?empresa=${fatura.nif_recetor}`, [fatura.nif_recetor]);
  const [filtro, setFiltro] = useState("");
  const candidatos = useMemo(() => {
    const lista = (movimentos || []).filter(m => m.match_estado !== "confirmado");
    const f = filtro.trim().toLowerCase();
    const filtrados = !f ? lista : lista.filter(m => (m.descricao || "").toLowerCase().includes(f));
    return [...filtrados]
      .sort((a, b) => scoreProximidadeMovimento(b, fatura) - scoreProximidadeMovimento(a, fatura))
      .slice(0, 30);
  }, [movimentos, filtro, fatura]);

  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, marginTop: 10, overflow: "hidden" }}>
      <div style={{ padding: "9px 12px", background: "#f8fafc", borderBottom: "1px solid #E5E7EB" }}>
        <input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Procurar na descrição do movimento…" autoFocus
          style={{ width: "100%", padding: "7px 9px", borderRadius: 7, border: "1px solid #E5E7EB", fontSize: 12.5, boxSizing: "border-box" }} />
      </div>
      <div style={{ maxHeight: 220, overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "#9AA3B0" }}>A carregar movimentos…</div>
        ) : candidatos.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "#9AA3B0" }}>Sem movimentos bancários disponíveis para esta empresa.</div>
        ) : candidatos.map((m, i) => {
          const melhor = i === 0 && !filtro && scoreProximidadeMovimento(m, fatura) >= 60;
          return (
            <div key={m.id} onClick={() => !aAssociar && onEscolher(m)}
              style={{ padding: "9px 12px", borderBottom: "1px solid #F0F2F5", cursor: aAssociar ? "default" : "pointer", display: "flex", justifyContent: "space-between", gap: 10, opacity: aAssociar ? .6 : 1 }}
              onMouseEnter={e => !aAssociar && (e.currentTarget.style.background = "#f8fafc")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1A1F2E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.descricao}</div>
                  {melhor && <span style={{ fontSize: 9, fontWeight: 700, color: "#157347", background: "#EDF7F1", border: "1px solid #A3D9B7", borderRadius: 20, padding: "1px 6px", flexShrink: 0 }}>mais provável</span>}
                </div>
                <div style={{ fontSize: 11, color: "#9AA3B0", marginTop: 1 }}>{fmtDate(m.data_mov)}{m.banco ? ` · ${m.banco}` : ""}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1F2E", flexShrink: 0 }}>{fmt(m.valor)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ padding: "8px 12px", borderTop: "1px solid #E5E7EB", textAlign: "right" }}>
        <button onClick={onCancelar} disabled={aAssociar} style={{ fontSize: 12, color: "#6B7280", background: "none", border: "none", cursor: aAssociar ? "default" : "pointer" }}>Cancelar</button>
      </div>
    </div>
  );
}

// Botão de ação secundário, usado nas várias secções do painel de definições.
function BtnAcao({ onClick, disabled, danger, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: "7px 13px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer",
        border: `1px solid ${danger ? "#F5C0BF" : "#D8DDE6"}`, background: danger ? "#FEF0F0" : "#fff",
        color: danger ? "#C0302A" : "#374151", opacity: disabled ? .55 : 1, whiteSpace: "nowrap",
      }}>
      {children}
    </button>
  );
}

// Painel de definições da fatura — a partir daqui dá para resolver o
// documento por completo: editar dados, enviar para fundo, marcar/desmarcar
// Nota de Crédito, aprovar/rejeitar reembolso, casar (ou desfazer) com um
// movimento bancário, sincronizar ou remover a linha do Sheets, e apagar.
// Reutiliza os modais já existentes (Editar/Fundo/Nota de Crédito) como
// sub-modais em vez de duplicar essa UI.
function DocDetalheModal() {
  const [alvo, setAlvo]       = useState(null);
  const [dados, setDados]     = useState(null);
  const [loading, setLoading] = useState(false);
  const { data: empresasList } = useApi("/api/empresas", [], []);
  const { data: sociosList }   = useApi("/api/socios", [], []);

  const [subModal, setSubModal] = useState(null); // "editar" | "fundo" | "nota-credito"
  const [showPicker, setShowPicker] = useState(false);
  const [showSocioPicker, setShowSocioPicker] = useState(false);
  const [socioIdInput, setSocioIdInput] = useState("");
  const [acaoEmCurso, setAcaoEmCurso] = useState(null);
  const [msg, setMsg] = useState(null);
  const [modalGrupo, setModalGrupo] = useState(null);

  const carregar = async (id) => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/faturas/${id}/detalhe`);
      const data = r.ok ? await r.json() : null;
      setDados(data);
    } catch {
      setDados(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = (e) => { setAlvo(e.detail); setMsg(null); setShowPicker(false); };
    window.addEventListener("open-detalhe", handler);
    return () => window.removeEventListener("open-detalhe", handler);
  }, []);

  useEffect(() => {
    if (!alvo) { setDados(null); return; }
    carregar(alvo.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alvo]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !subModal) setAlvo(null); };
    if (alvo) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [alvo, subModal]);

  if (!alvo) return null;

  const recarregar = () => {
    carregar(alvo.id);
    window.dispatchEvent(new CustomEvent("fatura-atualizada", { detail: { id: alvo.id } }));
  };

  // Ações de um só passo (sem formulário) — todas seguem o mesmo padrão:
  // POST/DELETE, mostrar aviso devolvido pelo backend, recarregar o painel.
  const correr = async (path, { method = "POST", confirmMsg } = {}) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setAcaoEmCurso(path);
    setMsg(null);
    try {
      const r = await apiFetch(`/api/faturas/${dados.id}${path}`, { method });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setMsg({ ok: true, texto: d.aviso || "Feito." });
        recarregar();
      } else {
        setMsg({ ok: false, texto: (typeof d.detail === "string" && d.detail) || "Erro ao processar." });
      }
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  // Desmarcar NC precisa de enviar um body — trata-se à parte do helper
  // genérico `correr` (que só faz POST/DELETE sem payload).
  const desmarcarNotaCreditoDetalhe = async () => {
    setAcaoEmCurso("nc-off");
    setMsg(null);
    try {
      const r = await apiFetch(`/api/faturas/${dados.id}/marcar-nota-credito`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eh_nota_credito: false }),
      });
      if (r.ok) { setMsg({ ok: true, texto: "Nota de Crédito desmarcada." }); recarregar(); }
      else setMsg({ ok: false, texto: "Erro ao desmarcar." });
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  const escolherMovimento = async (mov) => {
    setAcaoEmCurso("associar-movimento");
    setMsg(null);
    try {
      const r = await apiFetch(`/api/reconciliacao/matches/manual?fatura_id=${dados.id}&extrato_id=${mov.id}`, { method: "POST" });
      if (r.ok) {
        setMsg({ ok: true, texto: "Associada ao movimento bancário — marcada como paga." });
        setShowPicker(false);
        recarregar();
      } else {
        const d = await r.json().catch(() => ({}));
        setMsg({ ok: false, texto: (typeof d.detail === "string" && d.detail) || "Erro ao associar." });
      }
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  const desfazerMatch = async () => {
    if (!dados.match_id || !window.confirm("Desfazer esta associação ao extrato? A fatura volta a ficar por pagar.")) return;
    setAcaoEmCurso("desfazer-match");
    setMsg(null);
    try {
      const r = await apiFetch(`/api/reconciliacao/matches/${dados.match_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado: "rejeitado" }),
      });
      if (r.ok) { setMsg({ ok: true, texto: "Associação desfeita." }); recarregar(); }
      else setMsg({ ok: false, texto: "Erro ao desfazer." });
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  // Um match de grupo (ver reconciliacao_tipo==="grupo") liga TODAS as
  // faturas do grupo a TODOS os movimentos ao mesmo tempo — desfazer tem de
  // reverter o grupo inteiro de uma vez, não só esta fatura (senão o grupo
  // fica com faturas pagas e outras não, inconsistente).
  const desfazerMatchGrupo = async () => {
    if (!dados.grupo_id || !window.confirm("Desfazer a reconciliação de todo o conjunto? Todas as faturas do grupo voltam a ficar por pagar.")) return;
    setAcaoEmCurso("desfazer-match");
    setMsg(null);
    try {
      const r = await apiFetch(`/api/faturas/grupo/${dados.grupo_id}/desfazer-conciliacao`, { method: "POST" });
      if (r.ok) { setMsg({ ok: true, texto: "Reconciliação do conjunto desfeita." }); recarregar(); }
      else setMsg({ ok: false, texto: "Erro ao desfazer." });
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  // Corrige o sócio quando o agente identificou mal o beneficiário — associa
  // por socio_id real (não um nome de texto solto sem valor relacional).
  const corrigirSocio = async () => {
    if (!socioIdInput) return;
    setAcaoEmCurso("socio");
    setMsg(null);
    try {
      const r = await apiFetch(`/api/faturas/${dados.id}/socio`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ socio_id: parseInt(socioIdInput, 10) }),
      });
      if (r.ok) { setMsg({ ok: true, texto: "Sócio corrigido." }); setShowSocioPicker(false); recarregar(); }
      else setMsg({ ok: false, texto: "Erro ao corrigir o sócio." });
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  // Apagar -- extraída para ser chamada tanto pelo menu ⋮ do cabeçalho como,
  // no futuro, por qualquer outro sítio, sem duplicar a lógica de confirmação.
  const apagarDoc = async () => {
    if (!window.confirm(`Apagar a fatura ${dados.numero_documento}? O PDF no Drive é movido para "Apagados", não é eliminado.`)) return;
    setAcaoEmCurso("apagar");
    setMsg(null);
    try {
      const r = await apiFetch(`/api/faturas/${dados.id}`, { method: "DELETE" });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        const msgs = Object.values(d?.avisos || {}).filter(Boolean);
        if (msgs.length) window.alert(`Fatura apagada, mas com avisos:\n\n${msgs.join("\n")}`);
        window.dispatchEvent(new CustomEvent("fatura-atualizada", { detail: { id: dados.id } }));
        setAlvo(null);
      } else setMsg({ ok: false, texto: "Erro ao apagar." });
    } catch {
      setMsg({ ok: false, texto: "Erro de rede." });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  return (
    <div onClick={() => !subModal && setAlvo(null)}
      style={{ position: "fixed", inset: 0, zIndex: 3500, background: "rgba(15,23,42,.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, width: "min(94vw, 560px)", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,.25)" }}>
        {loading || !dados ? (
          <div style={{ padding: 48, textAlign: "center" }}><Spinner /></div>
        ) : (
          <>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #E8ECF2", display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1A1F2E" }}>{dados.numero_documento}</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 3 }}>{dados.nome_emitente} · {fmtDate(dados.data_documento)}</div>
              </div>
              {/* Ações menos frequentes (editar, marcar NC, apagar) ficam num
                  só menu -- mesmo padrão já usado nos cartões do Arquivo, em
                  vez de espalhadas pelo rodapé a competir visualmente com as
                  ações principais de cada secção. */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <FaturaAcoesMenu
                  onApagar={apagarDoc}
                  onEditar={() => setSubModal("editar")}
                  onEnviarFundo={() => setSubModal("fundo")}
                  onMarcarNotaCredito={() => dados.eh_nota_credito ? desmarcarNotaCreditoDetalhe() : setSubModal("nota-credito")}
                  ehNotaCredito={!!dados.eh_nota_credito}
                  mostrarNotaCredito={!!dados.eh_nota_credito || pareceNotaCredito(dados.numero_documento)}
                />
                <button onClick={() => setAlvo(null)}
                  style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #E5E7EB", background: "#F8FAFC", cursor: "pointer", fontSize: 14, color: "#6B7280", flexShrink: 0 }}>✕</button>
              </div>
            </div>

            <div style={{ padding: "8px 24px" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#1A1F2E", padding: "12px 0" }}>{fmt(dados.valor_total)}</div>

              <DetalheLinha label="Arquivo" ok={!!(dados.drive_ok && dados.sheets_ok)}
                okLabel="Drive + Sheets" naoLabel="Incompleto" />
              <DetalheLinha label="Pagamento" ok={!!dados.pago}
                okLabel={dados.data_pagamento ? `Pago em ${fmtDate(dados.data_pagamento)}` : "Pago"} naoLabel="Por pagar" />

              {!!dados.grupo_id && (
                <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F2F5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Grupo</div>
                  <div onClick={() => setModalGrupo(dados.grupo_id)}
                    style={{ fontSize: 12, fontWeight: 700, color: C.blue, cursor: "pointer", textDecoration: "underline dashed" }}>
                    🔗 {dados.grupo_descricao || labelGrupo(dados)}
                  </div>
                </div>
              )}

              {dados.nif_emitente === PLACEHOLDER_NIF_DESCONHECIDO && (
                <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F2F5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>NIF do emitente</div>
                  <div onClick={() => setSubModal("editar")} title="O Gemini não identificou o NIF real do fornecedor — clica para corrigir"
                    style={{ fontSize: 12, fontWeight: 700, color: "#92640C", cursor: "pointer", textDecoration: "underline dashed" }}>
                    ⚠ Por confirmar
                  </div>
                </div>
              )}

              {/* Conciliação — com ação inline para casar/desfazer. Um match
                  de grupo (ver reconciliacao_tipo) não é uma correspondência
                  1:1 precisa — é a soma de várias faturas do conjunto a
                  bater certo com a soma de vários movimentos — por isso tem
                  visual e ação de desfazer próprios, em vez de mostrar só um
                  dos pares como se fosse um casamento exato. */}
              <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F2F5" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Extrato bancário</div>
                  {dados.reconciliacao_tipo === "grupo" ? (
                    <BtnAcao danger onClick={desfazerMatchGrupo} disabled={acaoEmCurso === "desfazer-match"}>
                      {acaoEmCurso === "desfazer-match" ? "…" : "Desfazer conjunto"}
                    </BtnAcao>
                  ) : dados.reconciliacao_estado === "confirmado" ? (
                    <BtnAcao danger onClick={desfazerMatch} disabled={acaoEmCurso === "desfazer-match"}>
                      {acaoEmCurso === "desfazer-match" ? "…" : "Desfazer"}
                    </BtnAcao>
                  ) : (
                    <BtnAcao onClick={() => setShowPicker(v => !v)}>
                      {showPicker ? "Fechar" : "Casar com movimento"}
                    </BtnAcao>
                  )}
                </div>
                {dados.reconciliacao_tipo === "grupo" ? (
                  <div style={{ fontSize: 11.5, color: "#9AA3B0", marginTop: 4 }}>
                    <div>
                      Conjunto reconciliado — {dados.reconciliacao_movimentos?.length || 0} movimento(s) bancário(s) · {fmt(dados.reconciliacao_movimentos_total)} no total
                    </div>
                    {!!dados.reconciliacao_movimentos?.length && (
                      <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                        {dados.reconciliacao_movimentos.slice(0, 4).map(m => (
                          <div key={m.id}>{m.descricao} · {fmtDate(m.data_mov)} · {fmt(m.valor)}</div>
                        ))}
                        {dados.reconciliacao_movimentos.length > 4 && (
                          <div>+ {dados.reconciliacao_movimentos.length - 4} movimento(s)…</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : dados.reconciliacao_estado === "confirmado" && dados.extrato_id ? (
                  <div style={{ fontSize: 11.5, color: "#9AA3B0", marginTop: 4 }}>
                    {dados.extrato_descricao} · {fmtDate(dados.extrato_data_mov)} · {fmt(dados.extrato_valor)}
                  </div>
                ) : (
                  <div style={{ fontSize: 11.5, color: "#9AA3B0", marginTop: 4 }}>
                    {dados.reconciliacao_estado === "pendente" ? "Há uma sugestão por confirmar na Conciliação." : "Sem correspondência no extrato."}
                  </div>
                )}
                {showPicker && (
                  <PickerMovimentoBancario
                    fatura={dados}
                    onEscolher={escolherMovimento}
                    onCancelar={() => setShowPicker(false)}
                    aAssociar={acaoEmCurso === "associar-movimento"}
                  />
                )}
              </div>

              {!!dados.eh_reembolso && (
                <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F2F5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Reembolso</div>
                      <div style={{ fontSize: 11.5, color: "#5B21B6", marginTop: 2, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                        {dados.nome_socio_reembolso ? `De ${dados.nome_socio_reembolso}` : "Beneficiário por identificar"}
                        {dados.reembolso_aprovado ? " · Aprovado" : " · Submetido"}
                        {dados.socio_id == null && <span title="Sem sócio real associado — só um nome de texto" style={{ color: "#C0302A", fontSize: 12 }}>⚠</span>}
                      </div>
                    </div>
                    {/* Uma única ação primária consoante o estado — nunca
                        "Aprovar"/"Desaprovar"/"Rejeitar" lado a lado ao mesmo
                        tempo, que é o que tornava isto confuso. */}
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <BtnAcao onClick={() => { setSocioIdInput(dados.socio_id != null ? String(dados.socio_id) : ""); setShowSocioPicker(v => !v); }}>
                        {showSocioPicker ? "Fechar" : "Corrigir sócio"}
                      </BtnAcao>
                      {!dados.reembolso_aprovado ? (
                        <BtnAcao onClick={() => correr("/aprovar-reembolso")} disabled={acaoEmCurso === "/aprovar-reembolso"}>Aprovar</BtnAcao>
                      ) : (
                        <BtnAcao onClick={() => correr("/desaprovar-reembolso")} disabled={acaoEmCurso === "/desaprovar-reembolso"}>Voltar a submetido</BtnAcao>
                      )}
                    </div>
                  </div>
                  {showSocioPicker && (
                    <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                      <select value={socioIdInput} onChange={e => setSocioIdInput(e.target.value)}
                        style={{ flex: 1, padding: "7px 10px", borderRadius: 7, border: "1px solid #D8DDE6", fontSize: 12.5 }}>
                        <option value="">Escolhe o sócio…</option>
                        {(sociosList || []).map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                      </select>
                      <BtnAcao onClick={corrigirSocio} disabled={!socioIdInput || acaoEmCurso === "socio"}>
                        {acaoEmCurso === "socio" ? "…" : "Confirmar"}
                      </BtnAcao>
                    </div>
                  )}
                  {/* Ação rara e distinta ("isto nunca devia ter sido tratado
                      como reembolso") — separada visualmente da aprovação de
                      propósito, para não parecer "mais uma opção igual". */}
                  <button
                    onClick={() => correr("/rejeitar-reembolso", { confirmMsg: "Esta fatura não é, afinal, um reembolso? Volta a comportar-se como uma fatura normal." })}
                    disabled={acaoEmCurso === "/rejeitar-reembolso"}
                    style={{ marginTop: 8, background: "none", border: "none", padding: 0, fontSize: 11, color: "#9AA3B0", cursor: "pointer", textDecoration: "underline dashed" }}>
                    Isto não é um reembolso — tratar como fatura normal
                  </button>
                </div>
              )}

              {/* Nota de Crédito já marcada mas sem fatura associada era um
                  beco sem saída -- esta linha só mostrava texto ("Ainda não
                  associada a nenhuma fatura"), sem nenhum botão; a única
                  opção no menu ⋮ era "Desmarcar" (perde a marcação toda). Só
                  reabrindo o mesmo picker de "Marcar como Nota de Crédito"
                  (aceita re-associar um documento já marcado, o backend só
                  grava fatura_referenciada_id) resolve sem ter de desmarcar
                  primeiro. */}
              {!!dados.eh_nota_credito && (
                <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F2F5", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Nota de Crédito</div>
                  {dados.fatura_referenciada_id ? (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#C0302A", textAlign: "right" }}>Corrige a fatura {dados.documento_referenciado}</div>
                  ) : (
                    <BtnAcao onClick={() => setSubModal("nota-credito")}>Associar a fatura</BtnAcao>
                  )}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F0F2F5" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Fundo / Rubrica</div>
                  <div style={{ fontSize: 11.5, color: dados.rubrica_nome ? "#157347" : "#9AA3B0", marginTop: 2, fontWeight: 600 }}>
                    {dados.rubrica_nome ? `${dados.fundo_nome} → ${dados.rubrica_nome}` : "Sem associação"}
                  </div>
                </div>
                {/* Ação move-se para o menu ⋮ do cabeçalho -- "Enviar para fundo". */}
              </div>

              {!!dados.sheets_id && (
                <div style={{ padding: "10px 0", borderBottom: "1px solid #F0F2F5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Google Sheets</div>
                    <BtnAcao onClick={() => correr("/sincronizar-sheets")} disabled={acaoEmCurso === "/sincronizar-sheets"}>
                      {acaoEmCurso === "/sincronizar-sheets" ? "…" : "Sincronizar"}
                    </BtnAcao>
                  </div>
                  {/* Rara e destrutiva -- link discreto em vez de um botão do
                      mesmo tamanho que "Sincronizar", para não pesar como se
                      fosse uma alternativa igualmente comum. */}
                  <button
                    onClick={() => correr("/remover-do-sheets", { confirmMsg: "Remover a linha desta fatura do Sheets? A fatura continua no sistema." })}
                    disabled={acaoEmCurso === "/remover-do-sheets"}
                    style={{ marginTop: 6, background: "none", border: "none", padding: 0, fontSize: 11, color: "#C0302A", cursor: "pointer", textDecoration: "underline dashed" }}>
                    {acaoEmCurso === "/remover-do-sheets" ? "A remover…" : "Remover linha do Sheets"}
                  </button>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F0F2F5" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Ignorar da conciliação</div>
                {dados.conciliacao_ignorada ? (
                  <BtnAcao onClick={() => correr("/reativar-conciliacao")} disabled={acaoEmCurso === "/reativar-conciliacao"}>Reativar</BtnAcao>
                ) : (
                  <BtnAcao onClick={() => correr("/ignorar-conciliacao")} disabled={acaoEmCurso === "/ignorar-conciliacao"}>Ignorar</BtnAcao>
                )}
              </div>

              <div style={{ fontSize: 11, color: "#9AA3B0", padding: "10px 0 4px" }}>
                Estado: <b style={{ color: "#1A1F2E" }}>{dados.estado}</b> · Empresa {dados.nome_empresa || dados.nif_recetor} · Submetido {fmtDate(dados.criada_em)}
              </div>

              {msg && (
                <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 8, fontSize: 12, background: msg.ok ? "#EDF7F1" : "#FEF0F0", color: msg.ok ? "#157347" : "#C0302A", border: `1px solid ${msg.ok ? "#A3D9B7" : "#F5C0BF"}` }}>
                  {msg.texto}
                </div>
              )}
            </div>

            {/* Rodapé só com as duas ações que fazem sentido para QUALQUER
                documento -- ver o PDF e fechar. Editar/Nota de Crédito/Apagar
                vivem agora no menu ⋮ do cabeçalho (ver acima). */}
            <div style={{ padding: "16px 24px 20px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {!!dados.drive_link && (
                <button onClick={() => openPDF(dados.drive_link, dados.numero_documento, { numero: dados.numero_documento, nome: dados.nome_emitente, data: dados.data_documento, nif: dados.nif_emitente, valor: dados.valor_total })}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #D8DDE6", background: "#fff", fontSize: 12, fontWeight: 700, color: "#374151", cursor: "pointer" }}>
                  Ver PDF
                </button>
              )}
              <button onClick={() => setAlvo(null)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#03363D", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Fechar
              </button>
            </div>
          </>
        )}
      </div>

      {subModal === "editar" && (
        <ModalEditarFatura fatura={dados} empresas={empresasList} socios={sociosList} onClose={() => setSubModal(null)} onSaved={recarregar} />
      )}
      {subModal === "fundo" && (
        <ModalEnviarFundo fatura={dados} empresas={empresasList} onClose={() => setSubModal(null)} onSaved={recarregar} />
      )}
      {subModal === "nota-credito" && (
        <ModalMarcarNotaCredito fatura={dados} onClose={() => setSubModal(null)} onSaved={recarregar} />
      )}
      {modalGrupo && (
        <ModalGrupoFaturas grupoId={modalGrupo} onClose={() => setModalGrupo(null)} />
      )}
    </div>
  );
}

// ─── Modal "Todos os Reembolsos" (unifica reembolsos + faturas com eh_reembolso) ──
function openTodosReembolsos() {
  window.dispatchEvent(new CustomEvent("open-todos-reembolsos"));
}

const REEMBOLSO_CATS = ["alimentação", "transporte", "alojamento", "material", "software", "outro"];

function ModalTodosReembolsos() {
  const { empresa } = useEmpresa();
  const qsEmpresa = empresa ? `?empresa=${empresa}` : "";
  const [aberto, setAberto]         = useState(false);
  const [lista, setLista]           = useState([]);
  const [loading, setLoading]       = useState(false);
  // Abre sempre nos casos por decidir — "Todos" fica só a um clique, mas não
  // é preciso escolher isso todas as vezes só para ver o que falta tratar.
  const [fEstado, setFEstado]       = useState("submetido");
  const [modalNovo, setModalNovo]   = useState(false);
  const [savingNovo, setSavingNovo] = useState(false);
  const [formNovo, setFormNovo]     = useState({ colaborador: "", data_despesa: "", descricao: "", categoria: "outro", valor: "", empresa_nif: "", notas: "" });
  const { data: empresasList } = useApi("/api/empresas", [], []);
  const { data: sociosList }   = useApi("/api/socios", [], []);
  const [editandoSocio, setEditandoSocio] = useState(null); // { origem, id } da linha em edição
  const [novoSocioId, setNovoSocioId]     = useState("");
  const [gravandoSocio, setGravandoSocio] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/reembolsos/todos${qsEmpresa}`);
      const data = r.ok ? await r.json() : [];
      setLista(data || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    const handler = () => { setAberto(true); carregar(); };
    window.addEventListener("open-todos-reembolsos", handler);
    return () => window.removeEventListener("open-todos-reembolsos", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setAberto(false); };
    if (aberto) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto]);

  if (!aberto) return null;

  const filtrada = fEstado ? lista.filter(r => r.estado === fEstado) : lista;
  const total = filtrada.reduce((s, r) => s + (r.valor || 0), 0);

  const aprovar = async (id) => {
    await apiFetch(`/api/reembolsos/${id}${qsEmpresa}`, { method: "PATCH", body: JSON.stringify({ estado: "aprovado" }) });
    carregar();
  };
  const marcarPago = async (id) => {
    await apiFetch(`/api/reembolsos/${id}${qsEmpresa}`, { method: "PATCH", body: JSON.stringify({ estado: "pago" }) });
    carregar();
  };
  const apagar = async (id) => {
    if (!window.confirm("Apagar este reembolso?")) return;
    await apiFetch(`/api/reembolsos/${id}${qsEmpresa}`, { method: "DELETE" });
    carregar();
  };
  const aprovarFatura = async (id) => {
    await apiFetch(`/api/faturas/${id}/aprovar-reembolso${qsEmpresa}`, { method: "POST" });
    carregar();
  };
  const rejeitarFatura = async (id) => {
    if (!window.confirm("Esta fatura não é, afinal, um reembolso? Volta a comportar-se como uma fatura normal.")) return;
    await apiFetch(`/api/faturas/${id}/rejeitar-reembolso${qsEmpresa}`, { method: "POST" });
    carregar();
  };
  // Corrige o sócio quando o agente (ou quem submeteu) identificou mal o
  // beneficiário — associa por socio_id real, nunca por texto solto.
  const abrirEdicaoSocio = (r) => {
    setEditandoSocio({ origem: r.origem, id: r.id });
    setNovoSocioId(r.socio_id != null ? String(r.socio_id) : "");
  };
  const confirmarSocio = async () => {
    if (!editandoSocio || !novoSocioId) return;
    setGravandoSocio(true);
    try {
      const path = editandoSocio.origem === "reembolsos"
        ? `/api/reembolsos/${editandoSocio.id}/socio${qsEmpresa}`
        : `/api/faturas/${editandoSocio.id}/socio${qsEmpresa}`;
      const r = await apiFetch(path, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ socio_id: parseInt(novoSocioId, 10) }),
      });
      if (r.ok) { setEditandoSocio(null); carregar(); }
      else alert("Erro ao corrigir o sócio.");
    } catch {
      alert("Erro de rede.");
    } finally {
      setGravandoSocio(false);
    }
  };
  const submeterNovo = async (e) => {
    e.preventDefault();
    setSavingNovo(true);
    try {
      await apiFetch("/api/reembolsos", { method: "POST", body: JSON.stringify({ ...formNovo, valor: parseFloat(formNovo.valor) }) });
      setModalNovo(false);
      setFormNovo({ colaborador: "", data_despesa: "", descricao: "", categoria: "outro", valor: "", empresa_nif: empresa || "", notas: "" });
      carregar();
    } catch (err) {}
    setSavingNovo(false);
  };

  const inpSF = { width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #D8DDE6", fontSize: 13, color: "#1A1F2E", background: "#fff", outline: "none" };
  const thS   = { padding: "9px 12px", background: "#F8FAFC", borderBottom: "2px solid #E8ECF2", textAlign: "left", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9AA3B0" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3200, background: "#fff", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid #E8ECF2", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#1A1F2E" }}>Todos os Reembolsos</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Submissões manuais + deteção automática por email · {filtrada.length} registos · {fmt(total)}</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => { setFormNovo(f => ({ ...f, empresa_nif: empresa || "" })); setModalNovo(true); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#03363D", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            + Novo Reembolso
          </button>
          <button onClick={() => setAberto(false)}
            style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #D8DDE6", background: "#fff", cursor: "pointer", fontSize: 15, color: "#6B7280" }}>✕</button>
        </div>
      </div>

      <div style={{ padding: "12px 24px", borderBottom: "1px solid #E8ECF2", display: "flex", gap: 6, flexShrink: 0 }}>
        {[{ id: "", label: "Todos" }, ...Object.entries(REEMB_EST).map(([id, v]) => ({ id, label: v.label }))].map(e => (
          <button key={e.id} onClick={() => setFEstado(e.id)}
            style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
              border: fEstado === e.id ? "1.5px solid #03363D" : "1px solid #D8DDE6",
              background: fEstado === e.id ? "#BDD9D7" : "#fff",
              color: fEstado === e.id ? "#03363D" : "#6B7280",
              fontWeight: fEstado === e.id ? 700 : 500 }}>
            {e.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center" }}><Spinner /></div>
        ) : filtrada.length === 0 ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: "#9AA3B0", fontSize: 13 }}>Sem reembolsos{fEstado ? " com este estado" : ""}.</div>
        ) : (
          <div style={{ border: "1px solid #E8ECF2", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Nome", "Origem", "Descrição", "Valor", "Empresa", "Fundo/Rubrica", "Estado", "Ações"].map(h => <th key={h} style={thS}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filtrada.map(r => {
                  const est = REEMB_EST[r.estado] || REEMB_EST.submetido;
                  const manual = r.origem === "reembolsos";
                  const aEditarSocio = editandoSocio && editandoSocio.origem === r.origem && editandoSocio.id === r.id;
                  return (
                    <tr key={`${r.origem}-${r.id}`} style={{ borderTop: "1px solid #F0F2F5" }}>
                      <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "#1A1F2E" }}>
                        {aEditarSocio ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <select value={novoSocioId} onChange={e => setNovoSocioId(e.target.value)}
                              style={{ fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #D8DDE6" }}>
                              <option value="">Escolhe o sócio…</option>
                              {(sociosList || []).map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                            </select>
                            <button onClick={confirmarSocio} disabled={!novoSocioId || gravandoSocio}
                              title="Confirmar" style={{ width: 20, height: 20, borderRadius: 5, border: "none", background: "#03363D", color: "#fff", fontSize: 10, cursor: "pointer" }}>✓</button>
                            <button onClick={() => setEditandoSocio(null)} disabled={gravandoSocio}
                              title="Cancelar" style={{ width: 20, height: 20, borderRadius: 5, border: "1px solid #D8DDE6", background: "#fff", color: "#6B7280", fontSize: 10, cursor: "pointer" }}>✕</button>
                          </div>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {r.nome}
                            {r.socio_id == null && <span title="Sem sócio real associado — só um nome de texto" style={{ color: "#C0302A", fontSize: 11 }}>⚠</span>}
                            <button onClick={() => abrirEdicaoSocio(r)} title="Corrigir sócio"
                              style={{ width: 16, height: 16, borderRadius: 4, border: "none", background: "none", color: "#9AA3B0", fontSize: 10, cursor: "pointer", padding: 0 }}>✎</button>
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: manual ? "#F1F5F9" : "rgba(91,33,182,0.08)", color: manual ? "#64748b" : "#5B21B6", border: `1px solid ${manual ? "#E2E8F0" : "rgba(91,33,182,0.25)"}` }}>
                          {manual ? "Manual" : "Email"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "#374151", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.descricao}</td>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 800, color: "#1A1F2E", whiteSpace: "nowrap" }}>{fmt(r.valor)}</td>
                      <td style={{ padding: "10px 12px", fontSize: 11, color: "#6B7280" }}>{r.nome_empresa || r.empresa_nif || "—"}</td>
                      <td style={{ padding: "10px 12px", fontSize: 11, color: "#6B7280" }}>{r.rubrica_nome ? `${r.fundo_nome} → ${r.rubrica_nome}` : "—"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: est.bg, color: est.cor, border: `1px solid ${est.bord}` }}>{est.label}</span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          {manual && r.estado === "submetido" && (
                            <button onClick={() => aprovar(r.id)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #9FC5C1", background: "#BDD9D7", color: "#03363D", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Aprovar</button>
                          )}
                          {manual && r.estado === "aprovado" && (
                            <button onClick={() => marcarPago(r.id)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #A3D9B7", background: "#EDF7F1", color: "#157347", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Pago</button>
                          )}
                          {manual && (
                            <button onClick={() => apagar(r.id)} style={{ padding: "3px 7px", borderRadius: 6, border: "1px solid #F5B8B8", background: "#FEF0F0", color: "#C0302A", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✕</button>
                          )}
                          {!manual && r.estado === "submetido" && (
                            <button onClick={() => aprovarFatura(r.id)} title="Aprovar para poder entrar num batch de pagamento"
                              style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #9FC5C1", background: "#BDD9D7", color: "#03363D", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Aprovar</button>
                          )}
                          {!manual && r.estado !== "pago" && (
                            <button onClick={() => rejeitarFatura(r.id)} title="Não é, afinal, um reembolso"
                              style={{ padding: "3px 7px", borderRadius: 6, border: "1px solid #F5B8B8", background: "#FEF0F0", color: "#C0302A", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Não é reembolso</button>
                          )}
                          {!manual && (
                            <button onClick={() => openDetalhe(r.id)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #D8DDE6", background: "#fff", color: "#374151", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Detalhe</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalNovo && (
        <div onClick={() => setModalNovo(false)} style={{ position: "fixed", inset: 0, zIndex: 3300, background: "rgba(15,23,42,.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 32, width: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1A1F2E", marginBottom: 20 }}>Novo Reembolso</div>
            <form onSubmit={submeterNovo} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Colaborador *</div>
                  <input required value={formNovo.colaborador} onChange={e => setFormNovo(f => ({ ...f, colaborador: e.target.value }))} style={inpSF} placeholder="Nome do colaborador" />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Data *</div>
                  <input required type="date" value={formNovo.data_despesa} onChange={e => setFormNovo(f => ({ ...f, data_despesa: e.target.value }))} style={inpSF} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Descrição *</div>
                <input required value={formNovo.descricao} onChange={e => setFormNovo(f => ({ ...f, descricao: e.target.value }))} style={inpSF} placeholder="Ex: Jantar cliente Lisboa" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Categoria</div>
                  <select value={formNovo.categoria} onChange={e => setFormNovo(f => ({ ...f, categoria: e.target.value }))} style={inpSF}>
                    {REEMBOLSO_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Valor (€) *</div>
                  <input required type="number" step="0.01" min="0" value={formNovo.valor} onChange={e => setFormNovo(f => ({ ...f, valor: e.target.value }))} style={inpSF} placeholder="0.00" />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Empresa</div>
                <select value={formNovo.empresa_nif} onChange={e => setFormNovo(f => ({ ...f, empresa_nif: e.target.value }))} style={inpSF}>
                  <option value="">— Selecionar —</option>
                  {(empresasList || []).map(e => <option key={e.nif} value={e.nif}>{e.nome}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9AA3B0", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Notas</div>
                <input value={formNovo.notas} onChange={e => setFormNovo(f => ({ ...f, notas: e.target.value }))} style={inpSF} placeholder="Opcional…" />
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                <button type="button" onClick={() => setModalNovo(false)} style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #D8DDE6", background: "#fff", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                <button type="submit" disabled={savingNovo} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "#03363D", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {savingNovo ? "A guardar…" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal "Histórico de Batches" ──────────────────────────────────────────────
const BATCH_EST = {
  gerado:    { label: "Pendente", cor: "#92640C", bg: "#FEF8EC", bord: "#F5DFA0" },
  pago:      { label: "Pago",     cor: "#157347", bg: "#EDF7F1", bord: "#A3D9B7" },
  cancelado: { label: "Cancelado", cor: "#C0302A", bg: "#FEF0F0", bord: "#F5B8B8" },
};

// Agrupa os itens (snapshot de batch_itens) por destinatário (nome + IBAN) —
// espelha exatamente a consolidação feita no Excel (ver
// _agrupar_itens_batch_por_linha no backend): quem tem várias despesas para
// o MESMO IBAN sai como UMA transferência bancária, mas aqui continuamos a
// mostrar quais faturas/reembolsos originais a compõem.
function GruposLinhaBancaria({ itens, podeRemover = false, onRemover }) {
  const [abertos, setAbertos] = useState({});
  const grupos = [];
  const porChave = {};
  for (const it of itens) {
    const chave = `${(it.iban || "").replace(/\s/g, "").toUpperCase()}__${(it.nome || "").trim().toUpperCase()}`;
    if (!porChave[chave]) {
      const g = { chave, nome: it.nome, iban: it.iban, valor: 0, membros: [] };
      porChave[chave] = g;
      grupos.push(g);
    }
    porChave[chave].valor += it.valor || 0;
    porChave[chave].membros.push(it);
  }

  return grupos.map(g => {
    const consolidado = g.membros.length > 1;
    return (
      <div key={g.chave} style={{ borderBottom: "1px solid #F0F2F5" }}>
        <div
          onClick={consolidado ? () => setAbertos(p => ({ ...p, [g.chave]: !p[g.chave] })) : undefined}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", fontSize: 12, cursor: consolidado ? "pointer" : "default" }}>
          <span style={{ color: "#1A1F2E", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
            {consolidado && <span style={{ fontSize: 10, color: "#9AA3B0" }}>{abertos[g.chave] ? "▾" : "▸"}</span>}
            {g.nome}
            {consolidado && (
              <span title="Uma única transferência bancária para este IBAN — soma destes itens"
                style={{ fontSize: 9.5, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: "#BDD9D7", color: "#03363D", border: "1px solid #9FC5C1", whiteSpace: "nowrap" }}>
                {g.membros.length} itens numa linha
              </span>
            )}
          </span>
          <span style={{ color: "#6B7280", flexShrink: 0 }}>{!consolidado ? g.membros[0].descricao : ""}</span>
          <span style={{ color: "#1A1F2E", fontWeight: 700, flexShrink: 0 }}>{fmt(g.valor)}</span>
          {podeRemover && !consolidado && (
            <button onClick={(e) => { e.stopPropagation(); onRemover?.(g.membros[0]); }} title="Retirar do batch"
              style={{ width: 26, height: 26, display: "grid", placeItems: "center", border: "1px solid #F5B8B8", borderRadius: 6, background: "#fff", color: "#C0302A", cursor: "pointer" }}>
              <Trash2 size={13} />
            </button>
          )}
        </div>
        {consolidado && abertos[g.chave] && (
          <div style={{ paddingLeft: 16, paddingBottom: 6 }}>
            {g.membros.map(m => (
              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: 11.5, borderTop: "1px dashed #F0F2F5" }}>
                <span style={{ color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.descricao || "—"}</span>
                <span style={{ color: "#1A1F2E", fontWeight: 600, flexShrink: 0 }}>{fmt(m.valor)}</span>
                {podeRemover && (
                  <button onClick={() => onRemover?.(m)} title="Retirar do batch"
                    style={{ width: 24, height: 24, display: "grid", placeItems: "center", border: "1px solid #F5B8B8", borderRadius: 6, background: "#fff", color: "#C0302A", cursor: "pointer" }}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  });
}

function LinhaBatch({ batch, onChanged }) {
  const { empresa } = useEmpresa();
  const qsEmpresa = empresa ? `?empresa=${empresa}` : "";
  const [aberto, setAberto]     = useState(false);
  const [itens, setItens]       = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [extratos, setExtratos] = useState(null);
  const [extratoSel, setExtratoSel] = useState(batch.extrato_sugerido?.id || null);
  const [aConfirmar, setAConfirmar] = useState(false);
  const [editandoNome, setEditandoNome] = useState(false);
  const [nomeBatch, setNomeBatch] = useState(batch.referencia || "");
  const [guardandoNome, setGuardandoNome] = useState(false);
  const [descarregando, setDescarregando] = useState(false);
  useEffect(() => {
    if (!editandoNome) setNomeBatch(batch.referencia || "");
  }, [batch.referencia, editandoNome]);
  const est = BATCH_EST[batch.estado] || BATCH_EST.gerado;
  const temSugestao = !!batch.extrato_sugerido;

  const toggle = async () => {
    const abrir = !aberto;
    setAberto(abrir);
    if (abrir && itens === null) {
      const r = await apiFetch(`/api/batch/historico/${batch.id}/itens${qsEmpresa}`);
      setItens(r.ok ? await r.json() : []);
    }
  };

  const cancelar = async () => {
    if (!window.confirm(`Cancelar o batch "${batch.referencia || batch.id}"? Os itens voltam a aparecer como pendentes.`)) return;
    setCancelando(true);
    try {
      const r = await apiFetch(`/api/batch/historico/${batch.id}/cancelar${qsEmpresa}`, { method: "POST" });
      if (r.ok) {
        window.dispatchEvent(new CustomEvent("batch-pendentes-mudou"));
        onChanged();
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || "Erro ao cancelar.");
      }
    } catch { alert("Erro de rede."); }
    setCancelando(false);
  };

  const abrirConfirmar = async () => {
    setConfirmando(true);
    if (temSugestao) return; // já temos a correspondência exata, não precisa de ir buscar candidatos
    if (extratos === null) {
      const r = await apiFetch(`/api/batch/historico/${batch.id}/extratos-sugeridos${qsEmpresa}`);
      setExtratos(r.ok ? await r.json() : []);
    }
  };

  const confirmarPagamento = async () => {
    setAConfirmar(true);
    try {
      const r = await apiFetch(`/api/batch/historico/${batch.id}/confirmar-pagamento${qsEmpresa}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ extrato_id: extratoSel || null }),
      });
      if (r.ok) {
        window.dispatchEvent(new CustomEvent("batch-pendentes-mudou"));
        setConfirmando(false);
        onChanged();
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || "Erro ao confirmar pagamento.");
      }
    } catch { alert("Erro de rede."); }
    setAConfirmar(false);
  };

  const guardarNome = async () => {
    if (!nomeBatch.trim()) return;
    setGuardandoNome(true);
    const r = await apiFetch(`/api/batch/historico/${batch.id}${qsEmpresa}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ referencia: nomeBatch.trim() }),
    });
    setGuardandoNome(false);
    if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.detail || "Erro ao alterar o nome."); return; }
    setEditandoNome(false);
    onChanged();
  };

  const descarregar = async () => {
    setDescarregando(true);
    try {
      const r = await apiFetch(`/api/batch/historico/${batch.id}/download${qsEmpresa}`);
      if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.detail || "Erro ao gerar o ficheiro."); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `batch_pagamentos_${batch.id}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } finally { setDescarregando(false); }
  };

  const removerItem = async (item) => {
    if (!window.confirm(`Retirar "${item.descricao || item.nome}" deste batch? O documento volta aos pagamentos pendentes.`)) return;
    const r = await apiFetch(`/api/batch/historico/${batch.id}/itens/${item.id}${qsEmpresa}`, { method: "DELETE" });
    if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.detail || "Erro ao retirar o item."); return; }
    const atualizados = await apiFetch(`/api/batch/historico/${batch.id}/itens${qsEmpresa}`);
    setItens(atualizados.ok ? await atualizados.json() : []);
    window.dispatchEvent(new CustomEvent("batch-pendentes-mudou"));
    onChanged();
  };

  return (
    <div style={{ border: "1px solid #E8ECF2", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
      <div onClick={toggle} style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editandoNome ? (
            <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input value={nomeBatch} onChange={e => setNomeBatch(e.target.value)} autoFocus
                style={{ flex: 1, minWidth: 160, padding: "6px 8px", border: "1px solid #D8DDE6", borderRadius: 6, fontSize: 12.5 }} />
              <button onClick={guardarNome} disabled={guardandoNome} style={{ padding: "6px 9px", border: "none", borderRadius: 6, background: C.blue, color: "#fff", fontSize: 11, fontWeight: 700 }}>Guardar</button>
              <button onClick={() => { setEditandoNome(false); setNomeBatch(batch.referencia || ""); }} style={{ padding: "6px 9px", border: "1px solid #D8DDE6", borderRadius: 6, background: "#fff", fontSize: 11 }}>Cancelar</button>
            </div>
          ) : <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1F2E" }}>{batch.referencia || `Batch #${batch.id}`}</div>}
          <div style={{ fontSize: 11, color: "#9AA3B0", marginTop: 2 }}>
            {fmtDate(batch.criado_em)} · {batch.num_itens} item{batch.num_itens !== 1 ? "s" : ""} · {batch.criado_por || "—"}
            {batch.estado === "pago" && batch.pago_em && ` · pago em ${fmtDate(batch.pago_em)}`}
          </div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1A1F2E", whiteSpace: "nowrap" }}>{fmt(batch.valor_total)}</div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: est.bg, color: est.cor, border: `1px solid ${est.bord}` }}>{est.label}</span>
        {temSugestao && (
          <span title={`${batch.extrato_sugerido.descricao} · ${fmtDate(batch.extrato_sugerido.data_mov)}`}
            style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#DCFCE7", color: "#157347", border: "1px solid #A3D9B7", whiteSpace: "nowrap" }}>
            ✓ Extrato encontrado
          </span>
        )}
        {batch.estado === "gerado" && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setEditandoNome(true); }}
              style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #D8DDE6", background: "#fff", color: C.textMid, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Renomear</button>
            <button onClick={(e) => { e.stopPropagation(); abrirConfirmar(); }}
              style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #A3D9B7", background: "#EDF7F1", color: "#157347", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              Confirmar pagamento
            </button>
            <button onClick={(e) => { e.stopPropagation(); cancelar(); }} disabled={cancelando}
              style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #F5B8B8", background: "#FEF0F0", color: "#C0302A", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              {cancelando ? "…" : "Cancelar"}
            </button>
          </>
        )}
        <button onClick={(e) => { e.stopPropagation(); descarregar(); }} disabled={descarregando}
          style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #D8DDE6", background: "#fff", color: C.blue, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          {descarregando ? "A gerar…" : "Descarregar Excel"}
        </button>
        <span style={{ fontSize: 12, color: "#9AA3B0" }}>{aberto ? "▲" : "▼"}</span>
      </div>
      {confirmando && (
        <div onClick={e => e.stopPropagation()} style={{ borderTop: "1px solid #F0F2F5", padding: "12px 14px", background: "#F7FDF9" }}>
          {temSugestao ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#157347", marginBottom: 8 }}>✓ Encontrámos uma linha do extrato com o valor exato</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#DCFCE7", border: "1px solid #A3D9B7", borderRadius: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: "#1A1F2E", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{batch.extrato_sugerido.descricao}</span>
                <span style={{ fontSize: 11, color: "#6B7280", whiteSpace: "nowrap" }}>{fmtDate(batch.extrato_sugerido.data_mov)}</span>
                <strong style={{ fontSize: 13, color: "#1A1F2E", whiteSpace: "nowrap" }}>{fmt(Math.abs(batch.extrato_sugerido.valor))}</strong>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1F2E", marginBottom: 2 }}>Casar com uma linha do extrato</div>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10 }}>
                Ainda não encontrámos uma correspondência exata. O extrato só mostra uma linha para o valor total transferido — valor do batch: <strong>{fmt(batch.valor_total)}</strong>
              </div>
              {extratos === null ? (
                <div style={{ padding: 10, textAlign: "center" }}><Spinner /></div>
              ) : extratos.length === 0 ? (
                <div style={{ fontSize: 12, color: "#9AA3B0", marginBottom: 10 }}>Nenhuma linha próxima em valor encontrada. Podes confirmar sem associar, e ligar mais tarde quando o extrato chegar.</div>
              ) : (
                <div style={{ maxHeight: 170, overflowY: "auto", marginBottom: 10, border: "1px solid #E8ECF2", borderRadius: 8 }}>
                  {extratos.map(e => (
                    <label key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", cursor: "pointer", background: extratoSel === e.id ? "#DCFCE7" : "#fff", borderBottom: "1px solid #F0F2F5" }}>
                      <input type="radio" name={`extrato-${batch.id}`} checked={extratoSel === e.id} onChange={() => setExtratoSel(e.id)} />
                      <span style={{ fontSize: 12, color: "#1A1F2E", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.descricao}</span>
                      <span style={{ fontSize: 11, color: "#6B7280", whiteSpace: "nowrap" }}>{fmtDate(e.data_mov)}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1A1F2E", whiteSpace: "nowrap" }}>{fmt(Math.abs(e.valor))}</span>
                      <span style={{ fontSize: 10, color: "#B45309", whiteSpace: "nowrap" }}>Δ {fmt(e.diferenca)}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => { setConfirmando(false); setExtratoSel(batch.extrato_sugerido?.id || null); }}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #D8DDE6", background: "#fff", color: "#6B7280", fontSize: 12, cursor: "pointer" }}>
              Cancelar
            </button>
            <button onClick={confirmarPagamento} disabled={aConfirmar}
              style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#157347", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              {aConfirmar ? "…" : extratoSel ? "Confirmar pagamento" : "Confirmar sem linha do extrato"}
            </button>
          </div>
        </div>
      )}
      {aberto && (
        <div style={{ borderTop: "1px solid #F0F2F5", padding: "8px 14px", background: "#FAFBFC" }}>
          {itens === null ? (
            <div style={{ padding: 12, textAlign: "center" }}><Spinner /></div>
          ) : itens.length === 0 ? (
            <div style={{ padding: "8px 0", fontSize: 12, color: "#9AA3B0" }}>Sem itens.</div>
          ) : (
            <GruposLinhaBancaria itens={itens} podeRemover={batch.estado === "gerado"} onRemover={removerItem} />
          )}
        </div>
      )}
    </div>
  );
}

function PageBatches() {
  const { empresa } = useEmpresa();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/batch/historico${empresa ? `?empresa=${empresa}` : ""}`);
      setLista(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  }, [empresa]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    const atualizar = () => carregar();
    window.addEventListener("batch-pendentes-mudou", atualizar);
    return () => window.removeEventListener("batch-pendentes-mudou", atualizar);
  }, [carregar]);
  const visiveis = filtro === "todos" ? lista : lista.filter(b => b.estado === filtro);
  const contagens = { gerado: lista.filter(b => b.estado === "gerado").length, pago: lista.filter(b => b.estado === "pago").length, cancelado: lista.filter(b => b.estado === "cancelado").length };
  return (
    <div>
      <PageHeading eyebrow="Pagamentos" title="Batches de pagamento" description="Prepara transferências, consulta os documentos associados e acompanha a confirmação no extrato." />
      <BatchPagamentosCard />
      <section style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "15px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div><div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>Batches criados</div><div style={{ fontSize: 11.5, color: C.textLight, marginTop: 2 }}>Abre um batch para veres todas as faturas e reembolsos incluídos.</div></div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["todos", "Todos", lista.length], ["gerado", "Pendentes", contagens.gerado], ["pago", "Pagos", contagens.pago], ["cancelado", "Cancelados", contagens.cancelado]].map(([id, label, n]) => (
              <button key={id} onClick={() => setFiltro(id)} style={{ padding: "6px 10px", borderRadius: 7, border: `1px solid ${filtro === id ? C.blue : C.border}`, background: filtro === id ? "#E8F3F2" : "#fff", color: filtro === id ? C.blue : C.textMid, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>{label} · {n}</button>
            ))}
          </div>
        </div>
        <div style={{ padding: 16 }}>
          {loading ? <div style={{ padding: 36, textAlign: "center" }}><Spinner /></div> : visiveis.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: C.textLight, fontSize: 13 }}>Não existem batches neste estado.</div> : visiveis.map(b => <LinhaBatch key={b.id} batch={b} onChanged={carregar} />)}
        </div>
      </section>
    </div>
  );
}

function BatchResumoCard({ onNav }) {
  const { empresa } = useEmpresa();
  const { data: pendentes } = useApi(`/api/batch/pendentes${empresa ? `?empresa=${empresa}` : ""}`, [empresa]);
  const { data: historico } = useApi(`/api/batch/historico${empresa ? `?empresa=${empresa}` : ""}`, [empresa]);
  const porConfirmar = (historico || []).filter(b => b.estado === "gerado").length;
  return (
    <button onClick={() => onNav("batches")} style={{ width: "100%", marginBottom: 18, padding: "15px 18px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, display: "flex", alignItems: "center", gap: 14, cursor: "pointer", textAlign: "left" }}>
      <div style={{ width: 38, height: 38, borderRadius: 9, background: "#E8F3F2", color: C.blue, display: "grid", placeItems: "center", flexShrink: 0 }}><Receipt size={18} /></div>
      <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Pagamentos</div><div style={{ fontSize: 11.5, color: C.textMid, marginTop: 2 }}>{(pendentes || []).length} item(ns) por incluir · {porConfirmar} batch(es) por confirmar</div></div>
      <ChevronRight size={17} color={C.textLight} />
    </button>
  );
}

// ─── Desbloqueio por PIN (PWA) ────────────────────────────────────────────────

function PontosPin({ tamanho, preenchidos, erro }) {
  return (
    <div style={{ display: "flex", gap: 14, justifyContent: "center", animation: erro ? "pin-shake 0.4s" : "none" }}>
      {Array.from({ length: tamanho }).map((_, i) => (
        <div key={i} style={{
          width: 14, height: 14, borderRadius: "50%", boxSizing: "border-box",
          background: i < preenchidos ? (erro ? C.red : C.blue) : "transparent",
          border: `2px solid ${erro ? C.red : i < preenchidos ? C.blue : C.borderDark}`,
          transition: "all 0.12s",
        }} />
      ))}
    </div>
  );
}

function TecladoPin({ onDigito, onApagar, desativado }) {
  const Tecla = ({ children, onClick, aria }) => (
    <button type="button" onClick={onClick} disabled={desativado} aria-label={aria} className="pin-tecla"
      style={{
        width: 72, height: 72, borderRadius: "50%", border: "none",
        background: C.surface, color: C.text, fontSize: 24, fontWeight: 600,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)", touchAction: "manipulation",
      }}>
      {children}
    </button>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: "14px 22px", justifyContent: "center" }}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
        <Tecla key={n} onClick={() => onDigito(String(n))} aria={`Dígito ${n}`}>{n}</Tecla>
      ))}
      <div />
      <Tecla onClick={() => onDigito("0")} aria="Dígito 0">0</Tecla>
      <button type="button" onClick={onApagar} disabled={desativado} aria-label="Apagar" className="pin-tecla"
        style={{ width: 72, height: 72, borderRadius: "50%", border: "none", background: "transparent", color: C.textMid, fontSize: 15, fontWeight: 700, cursor: "pointer", touchAction: "manipulation" }}>
        ⌫
      </button>
      <style>{`
        .pin-tecla:active { transform: scale(0.92); background: ${C.blueLight}; }
        @keyframes pin-shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-8px); } 40%,80% { transform: translateX(8px); } }
      `}</style>
    </div>
  );
}

const PIN_TAMANHO = 4;

// Ecrã de bloqueio: a sessão memorizada só é libertada com o PIN certo.
// 5 falhas seguidas = apaga tudo e volta ao login.
function EcraPinLock({ user, onUnlock, onLogout }) {
  const [pin, setPin] = useState("");
  const [erro, setErro] = useState(false);
  const [falhas, setFalhas] = useState(0);
  const [aVerificar, setAVerificar] = useState(false);

  const submeter = async (p) => {
    setAVerificar(true);
    if (await verificarPin(p)) { onUnlock(); return; }
    const n = falhas + 1;
    setFalhas(n);
    setErro(true);
    setPin("");
    setAVerificar(false);
    if (navigator.vibrate) navigator.vibrate(90);
    if (n >= 5) {
      limparPin();
      clearSession();
      onLogout();
      return;
    }
    setTimeout(() => setErro(false), 450);
  };

  const digito = (d) => {
    if (aVerificar) return;
    const novo = (pin + d).slice(0, PIN_TAMANHO);
    setPin(novo);
    if (novo.length === PIN_TAMANHO) submeter(novo);
  };

  return (
    <div style={{ minHeight: "100dvh", background: C.bg, color: C.text, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", padding: "calc(48px + env(safe-area-inset-top, 0px)) 24px calc(28px + env(safe-area-inset-bottom, 0px))", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg, #0A5C68, #03363D)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", boxShadow: "0 6px 18px rgba(3, 54, 61,0.3)" }}>
          <Receipt size={26} color="#fff" />
        </div>
        <div style={{ fontSize: 17, fontWeight: 800 }}>Olá, {(user?.nome || user?.email || "").split(" ")[0]}</div>
        <div style={{ fontSize: 12.5, color: C.textMid, marginTop: 4 }}>
          {erro ? <span style={{ color: C.red, fontWeight: 600 }}>PIN errado{falhas >= 3 ? ` — ${5 - falhas} tentativa(s) restante(s)` : ""}</span> : "Introduz o teu PIN"}
        </div>
      </div>

      <PontosPin tamanho={PIN_TAMANHO} preenchidos={pin.length} erro={erro} />

      <div>
        <TecladoPin onDigito={digito} onApagar={() => setPin(p => p.slice(0, -1))} desativado={aVerificar} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 22 }}>
          {/* Só limpa a sessão — o PIN mantém-se para o próximo login do mesmo user */}
          <button onClick={() => { clearSession(); onLogout(); }}
            style={{ background: "none", border: "none", color: C.textMid, fontSize: 12.5, cursor: "pointer", padding: 8 }}>
            Entrar com palavra-passe
          </button>
        </div>
      </div>
    </div>
  );
}

// Criação do PIN (1º login com sessão memorizada): escolher → confirmar.
function EcraPinSetup({ user, onDone }) {
  const [passo, setPasso] = useState(1); // 1 escolher | 2 confirmar
  const [pin1, setPin1] = useState("");
  const [pin, setPin] = useState("");
  const [erro, setErro] = useState(false);
  const [aGuardar, setAGuardar] = useState(false);

  const concluir = async (pinFinal) => {
    setAGuardar(true);
    await guardarPin(pinFinal, user?.email || null);
    onDone();
  };

  const digito = (d) => {
    if (aGuardar) return;
    const novo = (pin + d).slice(0, PIN_TAMANHO);
    setPin(novo);
    if (novo.length < PIN_TAMANHO) return;
    if (passo === 1) {
      setPin1(novo);
      setPin("");
      setPasso(2);
    } else if (passo === 2) {
      if (novo === pin1) {
        concluir(novo);
      } else {
        setErro(true);
        setPin("");
        if (navigator.vibrate) navigator.vibrate(90);
        setTimeout(() => { setErro(false); setPin1(""); setPasso(1); }, 700);
      }
    }
  };

  return (
    <div style={{ minHeight: "100dvh", background: C.bg, color: C.text, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", padding: "calc(48px + env(safe-area-inset-top, 0px)) 24px calc(28px + env(safe-area-inset-bottom, 0px))", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg, #0A5C68, #03363D)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", boxShadow: "0 6px 18px rgba(3, 54, 61,0.3)" }}>
          <Shield size={26} color="#fff" />
        </div>
        <div style={{ fontSize: 17, fontWeight: 800 }}>
          {passo === 2 ? "Confirma o PIN" : "Cria um PIN de acesso"}
        </div>
        <div style={{ fontSize: 12.5, color: C.textMid, marginTop: 4, maxWidth: 260, lineHeight: 1.5 }}>
          {erro
            ? <span style={{ color: C.red, fontWeight: 600 }}>Os PINs não coincidem — tenta de novo.</span>
            : "Da próxima vez entras com este PIN, sem email nem palavra-passe."}
        </div>
      </div>

      <PontosPin tamanho={PIN_TAMANHO} preenchidos={pin.length} erro={erro} />
      <div>
        <TecladoPin onDigito={digito} onApagar={() => setPin(p => p.slice(0, -1))} desativado={aGuardar} />
        <div style={{ textAlign: "center", marginTop: 22 }}>
          <button onClick={() => { localStorage.setItem("hub_pin_skip", "1"); onDone(); }}
            style={{ background: "none", border: "none", color: C.textMid, fontSize: 12.5, cursor: "pointer", padding: 8 }}>
            Saltar este passo
          </button>
        </div>
      </div>
    </div>
  );
}

export { ModalDetalheEmail };

export default function Root() {
  const [phase, setPhase] = useState("loading"); // loading | login | locked | pin_setup | empresa_select | app | server_error
  const [user, setUser] = useState(null);
  const [usersExist, setUsersExist] = useState(true);
  const [serverError, setServerError] = useState("");

  // Único ponto de decisão "para onde vai depois de autenticado" -- só
  // role === "admin" passa pelo ecrã de escolha de empresa (nunca existiu
  // para colaboradores, que só usam Despesa Rápida e não têm noção de
  // "empresa selecionada" na app). Chamado nos dois sítios onde isso
  // acontece: login novo e sessão restaurada ao abrir a página.
  // Admin com uma empresa já escolhida NESTA sessão (sessionStorage --
  // sobrevive a um refresh, morre ao fechar o separador) salta o ecrã de
  // escolha direto para "app": um refresh de quem já está a trabalhar fica
  // impercetível. Um separador/login novo nunca tem nada guardado aqui, por
  // isso continua sempre a passar pelo ecrã obrigatório -- não é o mesmo
  // que "lembrar-se para sempre" (isso seria localStorage, usado só para
  // pré-realçar, nunca para saltar). A validação a sério do NIF guardado
  // (ainda existe? não foi manipulado nas devtools?) acontece dentro do
  // EmpresaProvider assim que a lista real de empresas chega -- ver
  // useEmpresa()/EmpresaContext.js.
  const irParaAppOuEscolherEmpresa = (u) => {
    const salta = u?.role === "admin" && temEmpresaGuardadaNaSessao();
    setPhase(u?.role === "admin" && !salta ? "empresa_select" : "app");
  };

  useEffect(() => {
    if (IS_MOCK) { setUser(MOCK_USER); irParaAppOuEscolherEmpresa(MOCK_USER); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/status`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const s = await r.json();
        setUsersExist(!!s.users_exist);

        if (!s.auth_enabled) {
          setServerError(
            "O servidor não tem JWT_SECRET configurado. " +
            "Gera com `python -c \"import secrets; print(secrets.token_urlsafe(48))\"` " +
            "e adiciona ao .env, depois reinicia o backend."
          );
          setPhase("server_error");
          return;
        }

        const session = loadSession();
        if (session?.token) {
          // Sessão memorizada + PIN configurado (do MESMO utilizador) → ecrã
          // de bloqueio; o token só é validado/renovado após o desbloqueio.
          const cfgPin = lerConfigPin();
          if (session.persistente && session.user && cfgPin &&
              (!cfgPin.email || cfgPin.email === session.user.email)) {
            setUser(session.user);
            setPhase("locked");
            return;
          }
          const me = await apiFetch("/api/auth/me");
          if (me.ok) {
            const u = await me.json();
            setUser(u);
            irParaAppOuEscolherEmpresa(u);
            return;
          }
          clearSession();
        }
        setPhase("login");
      } catch (e) {
        setServerError(`Não consigo ligar à API (${e?.message || "rede"}). Verifica que o backend está a correr e que REACT_APP_API_URL aponta para ele.`);
        setPhase("server_error");
      }
    })();
  }, []);

  const handleLogin = (u, lembrar = false) => {
    setUser(u);
    if (lembrar && u?.role === "colaborador") {
      const cfg = lerConfigPin();
      // PIN de OUTRO utilizador neste dispositivo → descarta e propõe novo.
      if (cfg && cfg.email && cfg.email !== u.email) limparPin();
      // Se o PIN deste utilizador já existe (ex: fez logout e voltou a entrar),
      // reutiliza-o em silêncio — só propõe criar na primeira vez.
      if (!pinConfigurado() && !localStorage.getItem("hub_pin_skip")) {
        setPhase("pin_setup");
        return;
      }
    }
    irParaAppOuEscolherEmpresa(u);
  };
  // Logout NÃO apaga o PIN: pertence ao dispositivo/utilizador, não à sessão —
  // senão cada logout obrigava a reconfigurar tudo no login seguinte.
  const handleLogout = () => { clearSession(); setUser(null); setPhase("login"); };

  // Desbloqueio por PIN. Regra de ouro: quem acabou de acertar o
  // PIN só volta ao ecrã de password se o token estiver MESMO inválido (401)
  // — qualquer outra falha (backend antigo sem /renovar, rede em baixo) não
  // pode expulsar o utilizador. Usa fetch direto (não apiFetch) porque o
  // apiFetch reage a 401 com reload da página, o que roubava o controlo
  // deste fluxo.
  const handleUnlock = async () => {
    const session = loadSession();
    const headers = session?.token ? { Authorization: `Bearer ${session.token}` } : {};
    try {
      const r = await fetch(`${API}/api/auth/renovar`, { method: "POST", headers });
      if (r.ok) {
        const d = await r.json();
        atualizarTokenSessao(d.token);
        setUser(d.user);
        setPhase("app");
        return;
      }
      if (r.status !== 401) {
        // ex: 404 num backend ainda sem /renovar — valida o token com /me
        const me = await fetch(`${API}/api/auth/me`, { headers });
        if (me.ok) {
          setUser(await me.json());
          setPhase("app");
          return;
        }
      }
      // 401 verdadeiro: token expirou (30 dias sem abrir) → login normal,
      // que já começa no passo da password com o email preenchido.
    } catch {
      // Sem rede: entra com o token que temos — as páginas mostram os seus
      // próprios erros de rede; mandar para o login offline não ajudaria.
      if (session?.token && session.user) {
        setUser(session.user);
        setPhase("app");
        return;
      }
    }
    clearSession();
    setUser(null);
    setPhase("login");
  };

  if (phase === "loading") {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.textLight, fontFamily: "'Inter', sans-serif", background: C.bg }}>A carregar...</div>;
  }
  if (phase === "server_error") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Inter', sans-serif", background: C.bg }}>
        <div style={{ maxWidth: 480, padding: 28, border: `1px solid ${C.redBorder}`, background: C.redLight, borderRadius: 12, color: C.red }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>⚠️ Servidor não configurado</div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>{serverError}</div>
        </div>
      </div>
    );
  }
  if (phase === "login") {
    return <Login onLogin={handleLogin} needsBootstrap={!usersExist} />;
  }
  if (phase === "locked") {
    return <EcraPinLock user={user} onUnlock={handleUnlock} onLogout={() => { setUser(null); setPhase("login"); }} />;
  }
  if (phase === "pin_setup") {
    return <EcraPinSetup user={user} onDone={() => setPhase("app")} />;
  }
  // empresa_select e app partilham a MESMA instância de EmpresaProvider --
  // se cada fase tivesse o seu próprio Provider, a empresa escolhida em
  // EcraEmpresaSelect perdia-se ao passar para o Dashboard (o Provider
  // seria desmontado/remontado do zero). Como o elemento <EmpresaProvider>
  // ocupa a mesma posição na árvore nas duas fases, o React só troca o
  // filho -- o estado do Provider (empresa selecionada, lista já
  // carregada) sobrevive à transição.
  return (
    <EmpresaProvider>
      {phase === "empresa_select"
        ? <EcraEmpresaSelect user={user} onContinue={() => setPhase("app")} onLogout={handleLogout} />
        : <Dashboard currentUser={user} onLogout={handleLogout} />}
    </EmpresaProvider>
  );
}

// ─── Botão + flutuante ────────────────────────────────────────────────────────
function FloatingAdd({ onOpenExtrato, onNavEfatura }) {
  const [open, setOpen] = useState(false);
  const opts = [
    { icon: "🏦", label: "Extrato bancário", action: () => { setOpen(false); onOpenExtrato(); } },
    { icon: "📋", label: "E-Fatura AT",       action: () => { setOpen(false); onNavEfatura(); } },
  ];
  return (
    <>
      {open && <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />}
      <div style={{ position: "fixed", bottom: 32, right: 32, zIndex: 200, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
        {open && opts.map(o => (
          <button key={o.label} onClick={o.action} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", background: C.surface, border: `1px solid ${C.borderDark}`, borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,.4)", fontSize: 13, fontWeight: 600, color: C.text, cursor: "pointer", whiteSpace: "nowrap" }}>
            <span style={{ fontSize: 18 }}>{o.icon}</span>{o.label}
          </button>
        ))}
        <button onClick={() => setOpen(v => !v)} style={{ width: 52, height: 52, borderRadius: "50%", background: C.blue, border: "none", color: "#fff", fontSize: 26, cursor: "pointer", boxShadow: "0 4px 18px rgba(37,99,235,.35)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform .15s", transform: open ? "rotate(45deg)" : "none" }}>
          +
        </button>
      </div>
    </>
  );
}

// ─── Assistente (chat de consulta) ─────────────────────────────────────────────
const ASSISTENTE_SUGESTOES = [
  "Tenho reembolsos pendentes?",
  "Que faturas estão prontas para pagar?",
  "Resumo financeiro deste mês",
];

function AssistenteChat() {
  const { data: cfg } = useApi("/api/agente/config", []);
  const [open, setOpen] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [input, setInput] = useState("");
  const [enviando, setEnviando] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensagens, enviando]);

  const enviar = async (e) => {
    e.preventDefault();
    const pergunta = input.trim();
    if (!pergunta || enviando) return;
    setMensagens(m => [...m, { role: "user", texto: pergunta }]);
    setInput("");
    setEnviando(true);
    try {
      const r = await apiFetch("/api/assistente/perguntar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pergunta }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setMensagens(m => [...m, { role: "assistente", texto: data.resposta || "…" }]);
      } else {
        setMensagens(m => [...m, { role: "assistente", texto: data.detail || "Erro ao processar a pergunta.", erro: true }]);
      }
    } catch {
      setMensagens(m => [...m, { role: "assistente", texto: "Erro de rede.", erro: true }]);
    }
    setEnviando(false);
  };

  if (cfg && cfg.assistente_ativo === false) return null;

  return (
    <>
      <style>{`
        .assistente-fab   { position: fixed; bottom: 32px; left: 32px; z-index: 200; }
        .assistente-panel { position: fixed; bottom: 96px; left: 32px; z-index: 200; }
        @media (min-width: 769px) {
          .assistente-fab   { bottom: 100px; }
          .assistente-panel { bottom: 164px; }
        }
      `}</style>
      <div className="assistente-fab">
        <button onClick={() => setOpen(v => !v)} title="Assistente"
          style={{ width: 52, height: 52, borderRadius: "50%", background: open ? "#374151" : "#03363D", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", boxShadow: "0 4px 18px rgba(3, 54, 61,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {open ? "✕" : "💬"}
        </button>
      </div>

      {open && (
        <div className="assistente-panel" style={{ width: "min(92vw, 360px)", height: "min(70vh, 480px)", background: "#fff", borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,.25)", border: "1px solid #E5E7EB", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: "#03363D", color: "#fff", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 15 }}>💬</span>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Assistente</div>
            <span style={{ fontSize: 9, marginLeft: "auto", background: "rgba(255,255,255,0.18)", padding: "2px 8px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>beta · só consulta</span>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {mensagens.length === 0 && (
              <div style={{ textAlign: "center", padding: "12px 4px" }}>
                <div style={{ fontSize: 12, color: "#9AA3B0", marginBottom: 12 }}>Pergunta-me sobre reembolsos, faturas por pagar ou o resumo do mês.</div>
                {ASSISTENTE_SUGESTOES.map(s => (
                  <button key={s} onClick={() => setInput(s)}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", marginBottom: 6, borderRadius: 8, border: "1px solid #E8ECF2", background: "#F8FAFC", fontSize: 12, color: "#374151", cursor: "pointer" }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {mensagens.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <div style={{
                  padding: "8px 12px", borderRadius: 12,
                  background: m.role === "user" ? "#03363D" : m.erro ? "#FEF0F0" : "#F1F5F9",
                  color: m.role === "user" ? "#fff" : m.erro ? "#C0302A" : "#1A1F2E",
                  fontSize: 13, lineHeight: 1.45, whiteSpace: "pre-wrap",
                }}>
                  {m.texto}
                </div>
              </div>
            ))}
            {enviando && (
              <div style={{ alignSelf: "flex-start" }}>
                <div style={{ padding: "8px 12px", borderRadius: 12, background: "#F1F5F9", fontSize: 13, color: "#9AA3B0" }}>A pensar…</div>
              </div>
            )}
          </div>

          <form onSubmit={enviar} style={{ display: "flex", gap: 8, padding: 10, borderTop: "1px solid #E8ECF2", flexShrink: 0 }}>
            <input value={input} onChange={e => setInput(e.target.value)} placeholder="Escreve a tua pergunta…"
              style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: "1px solid #D8DDE6", fontSize: 13, outline: "none" }} />
            <button type="submit" disabled={!input.trim() || enviando}
              style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: "#03363D", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (!input.trim() || enviando) ? 0.5 : 1 }}>
              ➤
            </button>
          </form>
        </div>
      )}
    </>
  );
}

// Mesmo tratamento de banco/conta que o UploadExtrato da página de
// Conciliação (ver platform/frontend/src/features/conciliacao/ExtratoBancarioTab.tsx):
// tenta identificar o banco e a conta sozinho a partir do ficheiro, e só pede
// para o utilizador indicar quando a deteção automática falha.
function ModalExtrato({ onClose, onSuccess, onNav }) {
  const { empresa, empresas } = useEmpresa();
  const nomeEmpresa = (empresas || []).find(e => e.nif === empresa)?.nome;
  const [file, setFile]       = useState(null);
  const [status, setStatus]   = useState(null); // null | "loading" | "ok" | "error"
  const [msg, setMsg]         = useState("");
  const [pedirBanco, setPedirBanco] = useState(false);
  const [pedirConta, setPedirConta] = useState(false);
  const [bancoInput, setBancoInput] = useState("");
  const [contaInput, setContaInput] = useState("");
  // Motor de conciliação corre sempre no import (não é um portão opt-in) --
  // isto só guarda o resumo para o utilizador ver o que aconteceu. Havendo
  // matches por rever, o modal fica aberto com um atalho em vez de fechar
  // sozinho como se estivesse tudo resolvido.
  const [matchesPendentes, setMatchesPendentes] = useState(0);
  const [matchesConfirmados, setMatchesConfirmados] = useState(0);
  // Empresa ativa não bate com o NIF encontrado no próprio documento -- ver
  // guarda em api.py::importar_arquivo (caso real: extrato de uma empresa
  // importado com outra ativa no topo, criou uma conta bancária duplicada).
  // Nunca bloqueia -- só pede confirmação antes de repetir com confirmar_empresa=true.
  const [conflitoEmpresa, setConflitoEmpresa] = useState(null);

  const submit = async (bancoManual, contaManual, confirmarEmpresa) => {
    if (!empresa || !file) { setMsg("Escolhe uma empresa (não \"Todas as Empresas\") e o ficheiro."); return; }
    setStatus("loading"); setMsg("");
    const fd = new FormData();
    fd.append("empresa_nif", empresa);
    fd.append("arquivo", file);
    if (bancoManual?.trim()) fd.append("banco", bancoManual.trim());
    if (contaManual?.trim()) fd.append("conta", contaManual.trim());
    if (confirmarEmpresa) fd.append("confirmar_empresa", "true");
    try {
      const r  = await apiFetch("/api/extratos/importar-arquivo", { method: "POST", body: fd });
      const txt = await r.text();
      let d = {};
      try { d = JSON.parse(txt); } catch {}
      if (r.ok) {
        setConflitoEmpresa(null);
        const sheets = d.sheets_escritos > 0 ? ` · ${d.sheets_escritos} no Sheets` : "";
        const confirmados = d.matches_confirmados ?? 0;
        const pendentes = d.matches_pendentes ?? 0;
        const resumo = `${d.inseridos ?? 0} movimento(s) importados${sheets}. ${d.ja_existiam ?? 0} já existiam.`;
        const faltaBanco = !d.banco;
        const faltaConta = !d.conta;
        setMatchesConfirmados(confirmados);
        setMatchesPendentes(pendentes);
        onSuccess();
        if (faltaBanco || faltaConta) {
          setStatus(null);
          setPedirBanco(faltaBanco);
          setPedirConta(faltaConta);
          const emFalta = [faltaBanco && "o banco", faltaConta && "o número de conta"].filter(Boolean).join(" e ");
          setMsg(`${resumo} Não consegui identificar ${emFalta} automaticamente — indica abaixo.`);
        } else {
          setStatus("ok");
          setMsg(`✅ ${resumo}`);
          // Sem matches por rever, o resumo já diz tudo -- fecha sozinho
          // como sempre fez. Havendo pendentes, fica aberto à espera de
          // decisão (fechar ou ir rever).
          if (pendentes === 0) setTimeout(onClose, 3000);
        }
      } else if (r.status === 409) {
        setStatus(null);
        setConflitoEmpresa(d?.detail?.msg || "O extrato parece ser de outra empresa.");
      } else {
        setStatus("error");
        setMsg(mensagemErroImportacao(d?.detail, `Erro HTTP ${r.status} — verifica os logs do servidor.`));
      }
    } catch (e) { setStatus("error"); setMsg(`Erro de rede: ${e?.message || e}`); }
  };

  const confirmarBancoConta = () => {
    if (bancoInput.trim() || contaInput.trim()) submit(bancoInput, contaInput);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: C.surface, borderRadius: 16, padding: 36, width: 460, boxShadow: "0 20px 60px rgba(0,0,0,.5)", border: `1px solid ${C.borderDark}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>🏦 Importar Extrato</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>Qualquer formato que o banco exporte</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textLight }}>✕</button>
        </div>

        {/* Sem seletor de empresa -- importa sempre para a empresa ativa na
            app (ver EcraEmpresaSelect/useEmpresa). Antes deste modal tinha um
            dropdown independente, sem ligação nenhuma à empresa que se
            estava a ver -- dava para importar um extrato da Empresa B a
            olhar para o dashboard da Empresa A, sem aviso nenhum. */}
        <div style={{ marginBottom: 16, padding: "9px 12px", borderRadius: 8, background: empresa ? C.blueLight : C.orangeLight, border: `1px solid ${empresa ? C.blueBorder : C.orangeBorder}`, fontSize: 12.5, color: empresa ? C.blue : C.orange, fontWeight: 600 }}>
          {empresa ? `A importar para: ${nomeEmpresa || empresa}` : "Escolhe uma empresa (não \"Todas as Empresas\") para importar."}
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMid, marginBottom: 6 }}>Ficheiro do extrato</div>
          <label style={{ display: "block", border: `2px dashed ${file ? C.blue : C.borderDark}`, borderRadius: 10, padding: "20px 16px", textAlign: "center", cursor: "pointer", background: file ? C.blueLight : "#fafafa" }}>
            <input type="file" accept={ACCEPT_EXTRATO} onChange={e => setFile(e.target.files[0])} style={{ display: "none" }} />
            {file ? <><div style={{ fontSize: 14, fontWeight: 700, color: C.blue }}>📎 {file.name}</div><div style={{ fontSize: 11, color: C.textMid }}>{(file.size/1024).toFixed(0)} KB</div></>
                  : <><div style={{ fontSize: 14, color: C.textMid }}>Clica para seleccionar</div><div style={{ fontSize: 11, color: C.textLight }}>{LEGENDA_FORMATOS_EXTRATO}</div></>}
          </label>
        </div>

        {msg && <div style={{ fontSize: 13, color: status === "ok" ? C.green : status === "error" ? C.red : C.textMid, marginBottom: 16, background: status === "ok" ? C.greenLight : status === "error" ? C.redLight : "transparent", padding: msg ? "10px 14px" : 0, borderRadius: 8 }}>{msg}</div>}

        {status === "ok" && (matchesConfirmados > 0 || matchesPendentes > 0) && (
          <div style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 8, background: C.bg, border: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 6 }}>
            {matchesConfirmados > 0 && (
              <div style={{ fontSize: 13, color: "#157347", fontWeight: 700 }}>✅ {matchesConfirmados} confirmado{matchesConfirmados !== 1 ? "s" : ""} automaticamente</div>
            )}
            {matchesPendentes > 0 && (
              <div style={{ fontSize: 13, color: "#92640C", fontWeight: 700 }}>⚠ {matchesPendentes} por rever</div>
            )}
          </div>
        )}

        {(pedirBanco || pedirConta) && (
          <div style={{ marginBottom: 16, padding: "10px 14px", background: "#FFF8E6", border: "1px solid #F5D98B", borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {pedirBanco && (
              <input value={bancoInput} onChange={e => setBancoInput(e.target.value)} placeholder="Qual é o banco?"
                style={{ flex: "2 1 160px", padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.borderDark}`, fontSize: 13, color: C.text, background: C.bg, outline: "none" }} />
            )}
            {pedirConta && (
              <input value={contaInput} onChange={e => setContaInput(e.target.value)} placeholder="Número de conta?"
                style={{ flex: "1 1 140px", padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.borderDark}`, fontSize: 13, color: C.text, background: C.bg, outline: "none" }} />
            )}
            <button onClick={confirmarBancoConta} disabled={status === "loading" || !(bancoInput.trim() || contaInput.trim())}
              style={{ padding: "8px 16px", borderRadius: 7, border: "none", background: C.blue, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              Guardar
            </button>
          </div>
        )}

        {conflitoEmpresa && (
          <div style={{ marginBottom: 16, padding: "10px 14px", background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ flex: "1 1 220px", fontSize: 12.5, color: C.red }}>⚠ {conflitoEmpresa}</span>
            <button onClick={() => setConflitoEmpresa(null)} disabled={status === "loading"}
              style={{ padding: "8px 14px", borderRadius: 7, border: `1px solid ${C.borderDark}`, background: "none", color: C.textMid, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
              Cancelar
            </button>
            <button onClick={() => submit(undefined, undefined, true)} disabled={status === "loading"}
              style={{ padding: "8px 14px", borderRadius: 7, border: "none", background: C.red, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              Importar mesmo assim
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 18px", background: "none", border: `1px solid ${C.borderDark}`, borderRadius: 8, fontSize: 13, cursor: "pointer", color: C.textMid }}>
            {status === "ok" ? "Fechar" : "Cancelar"}
          </button>
          {status === "ok" && matchesPendentes > 0 ? (
            <button onClick={() => { onClose(); onNav && onNav("conciliacao"); }}
              style={{ padding: "10px 22px", background: C.blue, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}>
              Ver Conciliação
            </button>
          ) : (
            <button onClick={() => submit()} disabled={status === "loading" || status === "ok"}
              style={{ padding: "10px 22px", background: C.blue, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", opacity: status === "loading" ? .6 : 1 }}>
              {status === "loading" ? "A importar..." : "Importar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Pesquisa global (header) ───────────────────────────────────────────────
function GlobalSearch({ onNav }) {
  const [q, setQ]       = useState("");
  const [open, setOpen] = useState(false);
  const { data: faturas } = useApi("/api/faturas/com-duplicados?limit=300", [], []);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const term = q.trim().toLowerCase();
  const results = term.length >= 2
    ? (faturas || []).filter(f =>
        (f.numero_documento || "").toLowerCase().includes(term) ||
        (f.nome_emitente   || "").toLowerCase().includes(term) ||
        (f.nome_empresa    || "").toLowerCase().includes(term)
      ).slice(0, 7)
    : [];

  const pick = (f) => {
    setOpen(false); setQ("");
    if (f.drive_link) openPDF(f.drive_link, f.numero_documento);
    else onNav("arquivo");
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 9, background: "rgba(255,255,255,0.05)", border: `1px solid rgba(255,255,255,${open ? 0.18 : 0.08})`, width: 230, transition: "border-color .15s" }}>
        <Search size={15} style={{ color: "#71717a", flexShrink: 0 }} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Pesquisar faturas, fornecedores…"
          style={{ background: "none", border: "none", outline: "none", color: "#e4e4e7", fontSize: 13, width: "100%", padding: 0 }}
        />
      </div>
      {open && term.length >= 2 && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 360, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 16px 48px rgba(0,0,0,0.18)", overflow: "hidden", zIndex: 400 }}>
          {results.length === 0 ? (
            <div style={{ padding: "16px 16px", fontSize: 13, color: C.textLight }}>Sem resultados para “{q}”.</div>
          ) : results.map((f, i) => (
            <button key={f.id ?? i} onClick={() => pick(f)} style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", border: "none", borderBottom: i < results.length - 1 ? `1px solid ${C.border}` : "none", background: "#fff", cursor: "pointer" }} className="ds-row">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.blue, fontFamily: "monospace" }}>{f.numero_documento || "—"}</div>
                <div style={{ fontSize: 11, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 230 }}>{f.nome_emitente}{f.nome_empresa ? ` · ${f.nome_empresa}` : ""}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmt(f.valor_total)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sino de notificações (header) ──────────────────────────────────────────
function NotificationsBell({ alertas, onNav }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const list = (alertas || []);
  const SEV = { critical: C.red, warn: C.yellow, warning: C.yellow, info: C.blue };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Notificações"
        style={{ position: "relative", padding: 8, borderRadius: 8, border: "none", background: open ? "rgba(255,255,255,0.08)" : "transparent", color: "#a1a1aa", cursor: "pointer", display: "flex" }}
      >
        <Bell size={17} />
        {list.length > 0 && <span style={{ position: "absolute", top: 6, right: 6, width: 7, height: 7, borderRadius: "50%", background: C.red, boxShadow: "0 0 0 2px #09090b" }} />}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 16px 48px rgba(0,0,0,0.18)", overflow: "hidden", zIndex: 400 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>Notificações</span>
            {list.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.textLight }}>{list.length}</span>}
          </div>
          {list.length === 0 ? (
            <div style={{ padding: "28px 16px", textAlign: "center" }}>
              <CheckCircle2 size={22} style={{ color: C.green, marginBottom: 6 }} />
              <div style={{ fontSize: 13, color: C.textMid }}>Sem notificações</div>
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {list.slice(0, 8).map((a, i) => {
                const cor = SEV[a.severidade || a.nivel] || C.blue;
                return (
                  <button key={i} onClick={() => { setOpen(false); onNav("agente"); }} className="ds-row" style={{ width: "100%", textAlign: "left", display: "flex", gap: 10, padding: "11px 14px", border: "none", borderBottom: i < Math.min(list.length, 8) - 1 ? `1px solid ${C.border}` : "none", background: "#fff", cursor: "pointer" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: cor, flexShrink: 0, marginTop: 5 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.titulo || a.msg || a.mensagem || "Alerta"}</div>
                      {(a.empresa || a.empresa_nif) && <div style={{ fontSize: 11, color: C.textLight, marginTop: 1 }}>{a.empresa || a.empresa_nif}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <button onClick={() => { setOpen(false); onNav("agente"); }} style={{ width: "100%", padding: "11px 16px", border: "none", borderTop: `1px solid ${C.border}`, background: C.bg, color: C.blue, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Ver tudo no Agente →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Página: Despesa Rápida ────────────────────────────────────────────────────

// Parse QR code fiscal português (Portaria n.º 195/2020): A:NIF*F:YYYYMMDD*O:total*G:nDoc*...
function parseFiscalQR(text) {
  if (!text.includes("*") || !text.includes(":")) return null;
  const fields = {};
  text.split("*").forEach(part => {
    const idx = part.indexOf(":");
    if (idx > 0) fields[part.slice(0, idx)] = part.slice(idx + 1);
  });
  if (!fields.A && !fields.O) return null;
  let data = null;
  if (fields.F?.match(/^\d{8}$/))
    data = `${fields.F.slice(0,4)}-${fields.F.slice(4,6)}-${fields.F.slice(6,8)}`;
  const valor = fields.O ? parseFloat(fields.O) : null;
  // Campo B = NIF do adquirente — permite associar a despesa à empresa certa
  // sem o colaborador escolher nada ("999999990" = consumidor final, ignora-se).
  const nifCliente = fields.B && fields.B !== "999999990" ? fields.B : "";
  return { data, valor, docId: fields.G || "", nifEmitente: fields.A || "", nifCliente, tipoDoc: fields.D || "" };
}

// Categorias e estados partilhados entre o formulário de despesa, o dashboard
// do colaborador e a página de detalhe.
const CATS_DESPESA = [
  { id: "transporte",   Icon: Car,             label: "Transporte"   },
  { id: "alimentacao",  Icon: UtensilsCrossed, label: "Alimentação"  },
  { id: "alojamento",   Icon: BedDouble,       label: "Alojamento"   },
  { id: "material",     Icon: Package,         label: "Material"     },
  { id: "obra",         Icon: HardHat,         label: "Obra"         },
  { id: "outro",        Icon: Paperclip,       label: "Outro"        },
];

const EST_DESPESA = {
  submetido: { label: "Submetido", cor: "#92640C", bg: "#FEF8EC", bord: "#F5DFA0" },
  aprovado:  { label: "Aprovado",  cor: "#03363D", bg: "#BDD9D7", bord: "#9FC5C1" },
  pago:      { label: "Pago",      cor: "#157347", bg: "#EDF7F1", bord: "#A3D9B7" },
};

function PageDespesaRapida({ empresa, empresas, currentUser, bottomOffset = 0 }) {
  const hoje = new Date().toISOString().slice(0, 10);
  const [valor, setValor] = useState("");
  const [data, setData] = useState(hoje);
  const [descricao, setDescricao] = useState("");
  const [categoria, setCategoria] = useState("transporte");
  const colaborador = currentUser?.nome || currentUser?.email || "";
  const [ficheiro, setFicheiro] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [analisando, setAnalisando] = useState(false);
  const [extraido, setExtraido] = useState(null);
  const [reembolsar, setReembolsar] = useState(true);
  const [erroAnalise, setErroAnalise] = useState(null); // { mensagem, detalhe } — painel de erro, não toast
  const [justificacao, setJustificacao] = useState("");  // obrigatório na categoria "outro"
  const [projeto, setProjeto] = useState("");            // obrigatório na categoria "obra"
  const fileRef    = useRef(null); // câmara (image/* capture)
  const uploadRef  = useRef(null); // ficheiro (image/* + pdf, sem capture)

  // Barra de progresso da análise — o tempo real depende do Gemini e não é
  // previsível, por isso a barra sobe de forma assimptótica até ~92% e só
  // fecha a 100% quando a resposta chega de facto (nunca mente "concluído").
  const [progresso, setProgresso] = useState(0);
  const progRef = useRef(null);
  const iniciarProgresso = () => {
    clearInterval(progRef.current);
    setProgresso(4);
    progRef.current = setInterval(() => {
      setProgresso(p => (p >= 92 ? p : p + Math.max(0.4, (92 - p) * 0.05)));
    }, 120);
  };
  const terminarProgresso = () => {
    clearInterval(progRef.current);
    setProgresso(100);
    setTimeout(() => setProgresso(0), 700);
  };
  useEffect(() => () => clearInterval(progRef.current), []);

  const CATS = CATS_DESPESA;

  // Empresa detetada automaticamente pelo NIF do adquirente no talão (campo B
  // do QR fiscal, ou extraído pela IA) — substitui o antigo select manual.
  const empresaDetectada = useMemo(() => {
    if (!extraido) return null;
    if (extraido.empresa_nif) return { nif: extraido.empresa_nif, nome: extraido.empresa_nome };
    const nif = String(extraido.nifCliente || extraido.nif_cliente || "");
    if (!nif) return null;
    return (empresas || []).find(e => e.nif === nif) || null;
  }, [extraido, empresas]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Decodifica a imagem uma única vez para um canvas (reduzido para no máx. MAX px
  // no lado maior) — serve tanto para a leitura do QR como para gerar o JPEG mais
  // leve que é enviado para a IA (evita descodificar a foto duas vezes e evita
  // enviar a foto original de vários MB directamente da câmara pela rede móvel).
  const decodeParaCanvas = (file, MAX = 1600) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas);
      } catch { resolve(null); }
      finally { URL.revokeObjectURL(url); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

  // Processa imagem ou PDF: tenta QR (só imagens), depois Gemini Vision
  const processarFoto = async (file) => {
    const isPdf = file.type === "application/pdf";
    if (!isPdf && !file.type.startsWith("image/")) {
      showToast("Formato não suportado. Usa imagem ou PDF.", "error");
      return;
    }
    setAnalisando(true);
    setErroAnalise(null);
    iniciarProgresso();
    try {
      let uploadBlob = file;

      // 1. QR code fiscal — só para imagens
      if (!isPdf) {
        const canvas = await decodeParaCanvas(file);
        if (canvas) {
          let qrEncontrado = false;
          try {
            const { default: jsQR } = await import("jsqr");
            const ctx = canvas.getContext("2d");
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height);
            if (code?.data) {
              const parsed = parseFiscalQR(code.data);
              if (parsed) {
                setExtraido({ ...parsed, fonte: "qr" });
                if (parsed.valor != null) setValor(parsed.valor.toFixed(2));
                if (parsed.data) setData(parsed.data);
                if (parsed.docId) setDescricao(prev => prev || `Fatura ${parsed.docId}`);
                qrEncontrado = true;
                showToast("QR fiscal detetado — campos preenchidos", "success");
              }
            }
          } catch { /* jsqr não carregou — continua para IA */ }
          if (qrEncontrado) return;

          // Sem QR: usa o mesmo canvas já reduzido para gerar um JPEG bem mais
          // leve do que a foto original — upload muito mais rápido em rede móvel.
          const comprimido = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.85));
          if (comprimido) uploadBlob = comprimido;
        }
      }

      // 2. Gemini Vision / análise IA
      const fd = new FormData();
      fd.append("imagem", uploadBlob, isPdf ? file.name : "recibo.jpg");
      let res;
      try {
        res = await apiFetch("/api/despesa/analisar-recibo", { method: "POST", body: fd });
      } catch {
        setErroAnalise({ mensagem: "Sem ligação ao servidor. Verifica a tua rede e tenta novamente.", detalhe: null });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setErroAnalise({
          mensagem: "Não foi possível analisar o recibo neste momento.",
          detalhe: err.detail || `Erro ${res.status}`,
        });
        return;
      }
      const dados = await res.json();
      if (dados.ok) {
        setExtraido({ ...dados, fonte: "ai" });
        if (dados.valor != null) setValor(Number(dados.valor).toFixed(2));
        if (dados.data) setData(dados.data);
        if (dados.descricao) setDescricao(prev => prev || dados.descricao);
        if (dados.categoria) setCategoria(dados.categoria);
        showToast(`${isPdf ? "PDF" : "Recibo"} analisado — campos preenchidos`, "success");
      } else {
        setErroAnalise({ mensagem: "A IA não conseguiu extrair dados desta imagem. Tenta uma foto mais nítida, com boa luz e o talão inteiro visível.", detalhe: null });
      }
    } catch (e) {
      setErroAnalise({ mensagem: "Ocorreu um erro inesperado ao processar o recibo.", detalhe: e?.message || null });
    } finally {
      setAnalisando(false);
      terminarProgresso();
    }
  };

  const reset = () => {
    setValor(""); setDescricao(""); setCategoria("transporte");
    setFicheiro(null); setExtraido(null); setReembolsar(true);
    setErroAnalise(null); setJustificacao(""); setProjeto("");
    setData(new Date().toISOString().slice(0, 10));
    if (fileRef.current) fileRef.current.value = "";
    if (uploadRef.current) uploadRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!colaborador.trim()) return showToast("Insere o nome do colaborador.", "error");
    if (!ficheiro) return showToast("Tira uma foto do recibo primeiro.", "error");
    if (!extraido) return showToast("Aguarda a análise do recibo.", "error");
    if (extraido.valor == null) return showToast("Não foi possível extrair o valor. Tira uma nova foto.", "error");
    if (categoria === "outro" && !justificacao.trim()) return showToast("Justifica a despesa antes de submeter.", "error");
    if (categoria === "obra" && !projeto.trim()) return showToast("Indica o projeto/obra desta despesa.", "error");
    const valorNum = Number(extraido.valor);
    const dataFinal = extraido.data || new Date().toISOString().slice(0, 10);
    const descFinal = extraido.docId ? `Fatura ${extraido.docId}` : (extraido.descricao || `Despesa ${CATS.find(c => c.id === categoria)?.label || categoria}`);
    setSubmitting(true);
    try {
      const notasParts = [
        categoria === "obra" && projeto.trim() ? `Projeto: ${projeto.trim()}` : null,
        categoria === "outro" && justificacao.trim() ? `Justificação: ${justificacao.trim()}` : null,
        ficheiro ? `Talão: ${ficheiro.name}` : null,
        extraido?.nifEmitente ? `NIF emitente: ${extraido.nifEmitente}` : null,
        extraido?.docId ? `Doc: ${extraido.docId}` : null,
        extraido?.fonte === "ai" ? "Extraído por IA" : extraido?.fonte === "qr" ? "QR fiscal" : null,
      ].filter(Boolean);
      const res = await apiFetch("/api/reembolsos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          colaborador: colaborador.trim(),
          data_despesa: dataFinal,
          descricao: descFinal,
          categoria,
          valor: valorNum,
          // Empresa detetada pelo NIF do adquirente no talão; se não houver,
          // usa a empresa do contexto (admin) e em último caso fica por atribuir.
          empresa_nif: empresaDetectada?.nif || empresa || null,
          reembolsar: reembolsar ? 1 : 0,
          notas: notasParts.length ? notasParts.join(" | ") : undefined,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Erro ${res.status}`);
      }
      showToast("Despesa registada com sucesso");
      reset();
    } catch (e) {
      showToast(e.message || "Erro ao submeter", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="despesa-rapida-shell" style={{ maxWidth: bottomOffset ? 520 : 760, margin: "0 auto", position: "relative" }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: `calc(${90 + bottomOffset}px + env(safe-area-inset-bottom, 0px))`, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? C.red : C.green,
          color: "#fff", borderRadius: 12, padding: "13px 22px",
          fontSize: 14, fontWeight: 600, zIndex: 9999,
          boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
          display: "flex", alignItems: "center", gap: 8,
          animation: "dr-fade-in 0.22s ease",
          maxWidth: "calc(100vw - 32px)",
          textAlign: "center",
        }}>
          {toast.msg}
        </div>
      )}

      {/* Form area — o scroll é feito pelo <main> do layout; o padding em baixo
          só garante que a barra fixa de submeter não tapa o último campo */}
      <div style={{ paddingBottom: `calc(${88 + bottomOffset}px + env(safe-area-inset-bottom, 0px))` }}>

        {/* Upload area — botão único de câmara */}
        <div
          onClick={() => !analisando && fileRef.current?.click()}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: 10, padding: "20px 16px", borderRadius: 14, textAlign: "center",
            border: `2px dashed ${extraido ? C.greenBorder : ficheiro ? C.border : C.blueBorder}`,
            background: extraido ? C.greenLight : ficheiro ? C.surface : C.blueLight,
            cursor: analisando ? "wait" : "pointer", transition: "all 0.18s",
            marginBottom: 16,
          }}
        >
          <div style={{ width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: extraido ? C.greenBorder : ficheiro ? C.bg : C.blueBorder }}>
            {analisando
              ? <div style={{ width: 22, height: 22, border: `3px solid ${C.blue}`, borderTopColor: "transparent", borderRadius: "50%", animation: "dr-spin 0.7s linear infinite" }} />
              : <Camera size={22} color={extraido ? C.green : ficheiro ? C.textMid : C.blue} />
            }
          </div>
          <div style={{ width: "100%" }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: extraido ? C.green : ficheiro ? C.text : C.blue }}>
              {analisando ? "A analisar recibo" : extraido ? "Recibo processado ✓" : ficheiro ? ficheiro.name.slice(0, 22) + (ficheiro.name.length > 22 ? "…" : "") : "Fotografar recibo"}
            </p>
            {(analisando || progresso > 0) ? (
              <div style={{ maxWidth: 260, margin: "10px auto 2px" }}>
                <div style={{ height: 6, borderRadius: 99, background: "rgba(3, 54, 61,0.12)", overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${progresso}%`, borderRadius: 99,
                    background: progresso >= 100 ? C.green : "linear-gradient(90deg, #0A5C68, #2E7BE8)",
                    transition: "width 0.18s ease, background 0.3s",
                  }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                  <span style={{ fontSize: 10.5, color: C.textLight }}>
                    {progresso >= 100 ? "Concluído" : progresso < 25 ? "A preparar a imagem…" : "A extrair dados com IA…"}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: progresso >= 100 ? C.green : C.blue, fontVariantNumeric: "tabular-nums" }}>
                    {Math.round(progresso)}%
                  </span>
                </div>
              </div>
            ) : (
              <p style={{ margin: "3px 0 0", fontSize: 11, color: C.textLight }}>
                {extraido ? (extraido.fonte === "qr" ? "QR fiscal detectado" : "Dados extraídos pela IA") : ficheiro ? "Toca para trocar" : "QR fiscal ou análise automática pela IA"}
              </p>
            )}
          </div>
          {(ficheiro || extraido) && !analisando && (
            <button onClick={e => { e.stopPropagation(); setFicheiro(null); setExtraido(null); if (fileRef.current) fileRef.current.value = ""; }}
              style={{ padding: "3px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, cursor: "pointer", color: C.textMid, fontSize: 11 }}>
              Remover
            </button>
          )}
        </div>

        {/* Opção secundária — PDF ou galeria */}
        {!ficheiro && !analisando && (
          <button
            onClick={() => uploadRef.current?.click()}
            style={{ display: "block", width: "100%", textAlign: "center", padding: "8px", marginTop: -8, marginBottom: 12, background: "none", border: "none", color: C.textMid, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}
          >
            Ou enviar PDF / imagem da galeria
          </button>
        )}

        {/* Inputs ocultos */}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; if (f) { setFicheiro(f); processarFoto(f); } }} />
        <input ref={uploadRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; if (f) { setFicheiro(f); processarFoto(f); } }} />

        {/* Dados extraídos — blocos de leitura */}
        {extraido ? (
          <Card p="0" style={{ marginBottom: 12, overflow: "hidden" }}>
            <div style={{ padding: "10px 16px 6px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 6 }}>
              <CheckCircle2 size={13} color={C.green} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.green }}>
                {extraido.fonte === "qr" ? "QR Fiscal" : "Lido pela IA"}
              </span>
            </div>
            {/* Valor — destaque */}
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, marginBottom: 4 }}>Valor</div>
              <div style={{ fontSize: 36, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums" }}>
                {extraido.valor != null ? `${Number(extraido.valor).toFixed(2)} €` : <span style={{ color: C.textLight, fontSize: 20 }}>—</span>}
              </div>
            </div>
            {/* Data + NIF */}
            <div style={{ display: "flex" }}>
              <div style={{ flex: 1, padding: "12px 16px", borderRight: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, marginBottom: 4 }}>Data</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{extraido.data ? fmtDate(extraido.data) : "—"}</div>
              </div>
              <div style={{ flex: 1, padding: "12px 16px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, marginBottom: 4 }}>NIF Emitente</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: "monospace" }}>{extraido.nifEmitente || extraido.nif_emitente || "—"}</div>
              </div>
            </div>
            {(extraido.descricao || extraido.docId) && (
              <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, marginBottom: 4 }}>Descrição</div>
                <div style={{ fontSize: 13, color: C.textMid }}>{extraido.docId ? `Fatura ${extraido.docId}` : extraido.descricao}</div>
              </div>
            )}
            {/* Empresa — detetada pelo NIF do adquirente, sem seleção manual */}
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, marginBottom: 4 }}>Empresa</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: empresaDetectada ? C.text : C.textLight, display: "flex", alignItems: "center", gap: 6 }}>
                  <Landmark size={13} color={empresaDetectada ? C.blue : C.textLight} />
                  {empresaDetectada ? empresaDetectada.nome : "Não identificada no talão"}
                </div>
              </div>
              {empresaDetectada && (
                <span style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`, whiteSpace: "nowrap" }}>
                  Detetada pelo NIF
                </span>
              )}
            </div>
          </Card>
        ) : erroAnalise && !analisando ? (
          /* Painel de erro — substitui o antigo toast vermelho gigante */
          <div style={{ marginBottom: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
            <div style={{ padding: "18px 16px 14px", textAlign: "center" }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                <AlertTriangle size={21} color={C.orange} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Não foi possível ler o recibo</div>
              <div style={{ fontSize: 12.5, color: C.textMid, marginTop: 5, lineHeight: 1.5 }}>{erroAnalise.mensagem}</div>
              {erroAnalise.detalhe && (
                <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 8, fontFamily: "monospace", wordBreak: "break-word" }}>{erroAnalise.detalhe}</div>
              )}
            </div>
            <div style={{ display: "flex", borderTop: `1px solid ${C.border}` }}>
              <button onClick={() => ficheiro && processarFoto(ficheiro)}
                style={{ flex: 1, padding: "12px", border: "none", borderRight: `1px solid ${C.border}`, background: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: C.blue }}>
                Tentar novamente
              </button>
              <button onClick={() => { setFicheiro(null); setErroAnalise(null); if (fileRef.current) fileRef.current.value = ""; fileRef.current?.click(); }}
                style={{ flex: 1, padding: "12px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.textMid }}>
                Nova foto
              </button>
            </div>
          </div>
        ) : null}

        {/* Categoria chips */}
        <Card p="14px 16px" style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <Tag size={11} /> Categoria
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {CATS.map(cat => (
              <button
                key={cat.id}
                onClick={() => setCategoria(cat.id)}
                style={{
                  padding: "8px 14px", borderRadius: 20, border: `1.5px solid`,
                  borderColor: categoria === cat.id ? C.blue : C.border,
                  background: categoria === cat.id ? C.blueLight : C.surface,
                  color: categoria === cat.id ? C.blue : C.textMid,
                  fontSize: 13, fontWeight: categoria === cat.id ? 700 : 500,
                  cursor: "pointer", transition: "all 0.15s",
                  display: "flex", alignItems: "center", gap: 5,
                }}
              >
                <cat.Icon size={14} strokeWidth={categoria === cat.id ? 2.4 : 2} /> {cat.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Campos condicionais por categoria: "obra" pede o projeto de
            destino, "outro" pede justificação. fontSize 16 — abaixo disso o
            iOS faz zoom automático ao focar o input. */}
        {(categoria === "obra" || categoria === "outro") && (
          <Card p="14px 16px" style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, display: "block", marginBottom: 10 }}>
              {categoria === "obra" ? "Projeto" : "Justificação"}
            </label>
            <input
              value={categoria === "obra" ? projeto : justificacao}
              onChange={e => (categoria === "obra" ? setProjeto(e.target.value) : setJustificacao(e.target.value))}
              placeholder={categoria === "obra" ? "A que projeto/obra se destina?" : "Descreve o motivo desta despesa…"}
              style={{ width: "100%", boxSizing: "border-box", padding: "12px 13px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 16, outline: "none", color: C.text, background: C.surface }}
            />
          </Card>
        )}

        {/* Reembolsar */}
        <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, cursor: "pointer", marginBottom: 12 }}>
          <div
            onClick={() => setReembolsar(r => !r)}
            style={{
              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
              border: `2px solid ${reembolsar ? C.blue : C.border}`,
              background: reembolsar ? C.blue : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            {reembolsar && <span style={{ color: "#fff", fontSize: 13, fontWeight: 900, lineHeight: 1 }}>✓</span>}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Solicitar reembolso</div>
            <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>
              {reembolsar ? "Despesa será incluída no próximo ficheiro batch de pagamentos" : "Registo apenas — sem reembolso"}
            </div>
          </div>
        </label>

      </div>

      {/* Sticky submit button — quando há tab bar por baixo (colaborador), é
          ELA que absorve o safe-area-inset; somar o inset aqui outra vez fazia
          as duas barras sobreporem-se em iPhones com home indicator */}
      <div style={{
        position: bottomOffset ? "fixed" : "sticky", left: 0, right: 0,
        bottom: bottomOffset ? `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))` : 0,
        padding: bottomOffset ? "12px 16px" : "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
        background: C.surface,
        borderTop: `1px solid ${C.border}`,
        boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
        zIndex: 100,
      }}>
        <div style={{ maxWidth: bottomOffset ? 520 : 760, margin: "0 auto", display: "flex", gap: 10 }}>
          <button
            onClick={reset}
            disabled={submitting}
            style={{
              flex: "0 0 auto", padding: "14px 20px", borderRadius: 12,
              border: `1px solid ${C.borderDark}`, background: C.surface,
              color: C.textMid, fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            Limpar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || analisando || !extraido}
            style={{
              flex: 1, padding: "14px 20px", borderRadius: 12, border: "none",
              background: (submitting || analisando || !extraido) ? C.textLight : C.blue, color: "#fff",
              fontSize: 16, fontWeight: 700, cursor: (submitting || analisando || !extraido) ? "not-allowed" : "pointer",
              transition: "background 0.15s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {submitting ? "A enviar…" : analisando ? `A analisar recibo… ${Math.round(progresso)}%` : !ficheiro ? "Fotografa o recibo primeiro" : !extraido ? (erroAnalise ? "Recibo não lido — tenta de novo" : "Aguarda a análise…") : <><PlusCircle size={18} /> Registar Despesa</>}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes dr-fade-in { from { opacity:0; transform:translateX(-50%) translateY(12px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes dr-spin { to { transform: rotate(360deg); } }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}

// Navegação ligada ao URL do browser — sem lib de routing (a app não usa
// nenhuma), só History API. Sem isto, um refresh perdia sempre a página
// actual e voltava ao Dashboard.
const VALID_PAGES = new Set([
  "dashboard", "conciliacao", "arquivo", "efatura", "estatisticas",
  "despesa-rapida", "fundos", "socios", "empresas", "manutencao", "agente", "utilizadores", "definicoes",
  "processamento-assistido", "emails-pendentes",
]);

function pageFromPath(pathname) {
  const slug = (pathname || "").replace(/^\//, "").split("/")[0];
  return VALID_PAGES.has(slug) ? slug : "dashboard";
}

function useUrlPage() {
  const [page, setPageState] = useState(() => pageFromPath(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setPageState(pageFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const setPage = (next) => {
    setPageState(next);
    const path = `/${next}`;
    if (window.location.pathname !== path) {
      window.history.pushState({}, "", path);
    }
  };

  return [page, setPage];
}

// ─── Colaborador — dashboard só de consulta ────────────────────────────────────
const COLAB_TAB_BAR_HEIGHT = 64;

// Detalhe de uma despesa em ecrã inteiro (experiência mobile). Mostra tudo o
// que a BD sabe sobre ela e permite apagar enquanto ainda está "submetido"
// (depois de aprovada/paga só o admin pode mexer — o backend valida o mesmo).
function DetalheDespesaColab({ despesa, onClose, onApagada }) {
  const [apagando, setApagando] = useState(false);
  const [erro, setErro] = useState(null);
  const est = EST_DESPESA[despesa.estado] || EST_DESPESA.submetido;
  const cat = CATS_DESPESA.find(c => c.id === despesa.categoria);
  const CatIcon = cat?.Icon || Paperclip;

  const apagar = async () => {
    if (!window.confirm("Apagar esta despesa? Esta ação não pode ser revertida.")) return;
    setApagando(true);
    setErro(null);
    try {
      const r = await apiFetch(`/api/reembolsos/${despesa.id}`, { method: "DELETE" });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Erro ${r.status}`);
      }
      onApagada();
    } catch (e) {
      setErro(e.message || "Erro ao apagar a despesa.");
      setApagando(false);
    }
  };

  const ordem = { submetido: 0, aprovado: 1, pago: 2 };
  const nivel = ordem[despesa.estado] ?? 0;
  const passos = [
    { label: "Submetida", quando: despesa.criado_em, extra: despesa.colaborador },
    { label: "Aprovada",  quando: despesa.aprovado_em, extra: despesa.aprovado_por },
    { label: "Paga",      quando: despesa.pago_em, extra: null },
  ];

  const Row = ({ titulo, children }) => (
    <div style={{ padding: "11px 0", borderBottom: `1px solid ${C.bg}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.textMid, flexShrink: 0 }}>{titulo}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, textAlign: "right", minWidth: 0 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: C.bg, display: "flex", flexDirection: "column", color: C.text, animation: "ddc-slide-up 0.22s ease" }}>
      {/* Cabeçalho */}
      <div style={{ background: "linear-gradient(135deg, #0A5C68, #03363D)", padding: "14px 20px 20px", paddingTop: "calc(14px + env(safe-area-inset-top, 0px))", color: "#fff", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Detalhe da despesa</div>
          <button onClick={onClose} aria-label="Fechar"
            style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 30, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmt(despesa.valor)}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{despesa.descricao}</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: "rgba(255,255,255,0.92)", color: est.cor, flexShrink: 0 }}>{est.label}</span>
        </div>
      </div>

      {/* Corpo */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px", paddingBottom: "calc(28px + env(safe-area-inset-bottom, 0px))" }}>
        {/* Percurso submetida → aprovada → paga */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: C.textLight, marginBottom: 12 }}>Estado</div>
          {passos.map((p, i) => {
            const feito = i <= nivel;
            return (
              <div key={p.label} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", background: feito ? C.green : C.bg, border: `2.5px solid ${feito ? C.green : C.borderDark}`, boxSizing: "border-box" }} />
                  {i < passos.length - 1 && <div style={{ width: 2, height: 26, background: i < nivel ? C.green : C.border }} />}
                </div>
                <div style={{ paddingBottom: i < passos.length - 1 ? 10 : 0, marginTop: -2 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: feito ? C.text : C.textLight }}>{p.label}</div>
                  {feito && (p.quando || p.extra) && (
                    <div style={{ fontSize: 11, color: C.textLight, marginTop: 1 }}>
                      {p.quando ? fmtDate(p.quando) : ""}{p.extra ? `${p.quando ? " · " : ""}${p.extra}` : ""}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Dados */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "4px 16px", marginBottom: 12 }}>
          <Row titulo="Data da despesa">{fmtDate(despesa.data_despesa)}</Row>
          <Row titulo="Categoria">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <CatIcon size={14} color={C.blue} /> {cat?.label || despesa.categoria}
            </span>
          </Row>
          <Row titulo="Empresa">{despesa.nome_empresa || <span style={{ color: C.textLight }}>Por atribuir</span>}</Row>
          <Row titulo="Reembolso">{despesa.reembolsar === 0 ? "Não pedido" : "Pedido"}</Row>
          {despesa.notas && (
            <div style={{ padding: "11px 0" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.textMid, marginBottom: 5 }}>Notas</div>
              <div style={{ fontSize: 12, color: C.textLight, lineHeight: 1.6, wordBreak: "break-word" }}>{despesa.notas}</div>
            </div>
          )}
        </div>

        {erro && (
          <div style={{ marginBottom: 12, padding: "11px 14px", background: "#FEF0F0", border: "1px solid #F5B8B8", borderRadius: 10, fontSize: 12, color: C.red }}>{erro}</div>
        )}

        {despesa.estado === "submetido" && (
          <button onClick={apagar} disabled={apagando}
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "1px solid #F5B8B8", background: "#FEF0F0", color: C.red, fontSize: 14, fontWeight: 700, cursor: apagando ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Trash2 size={16} /> {apagando ? "A apagar…" : "Apagar despesa"}
          </button>
        )}
      </div>
      <style>{`@keyframes ddc-slide-up { from { transform: translateY(28px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

function PageColaboradorDashboard({ onNovaDespesa, ativa = true }) {
  const { data: despesas, loading, refresh } = useApi("/api/reembolsos/minhas", []);
  const [detalhe, setDetalhe] = useState(null);

  // As tabs ficam sempre montadas (para o formulário não perder estado), por
  // isso o refetch é feito quando esta tab volta a ficar visível — assim a
  // despesa acabada de submeter aparece logo na lista.
  const montadoRef = useRef(false);
  useEffect(() => {
    if (montadoRef.current && ativa) refresh();
    montadoRef.current = true;
  }, [ativa]); // eslint-disable-line react-hooks/exhaustive-deps

  const lista = despesas || [];
  const pendentes = lista.filter(d => d.estado !== "pago");
  const pagas = lista.filter(d => d.estado === "pago");
  const totalPendente = pendentes.reduce((s, d) => s + (d.valor || 0), 0);
  const totalPago = pagas.reduce((s, d) => s + (d.valor || 0), 0);

  const EST = EST_DESPESA;

  return (
    <div style={{ padding: "18px 20px", paddingBottom: `calc(${COLAB_TAB_BAR_HEIGHT + 24}px + env(safe-area-inset-bottom, 0px))` }}>
      {/* Cartão de destaque */}
      <div style={{ background: "linear-gradient(135deg, #0A5C68 0%, #03363D 55%, #021F24 100%)", borderRadius: 18, padding: "22px 20px", marginBottom: 20, color: "#fff", boxShadow: "0 8px 24px rgba(3, 54, 61,0.25)" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.75)", marginBottom: 6 }}>Pendente de pagamento</div>
        <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{loading ? "…" : fmt(totalPendente)}</div>
        <div style={{ display: "flex", gap: 24, marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
          <div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.65)", marginBottom: 2 }}>Já pago</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{loading ? "…" : fmt(totalPago)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.65)", marginBottom: 2 }}>Despesas</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{loading ? "…" : lista.length}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <Zap size={13} color={C.textMid} />
        <span style={{ fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: "uppercase", letterSpacing: 0.6 }}>Ações rápidas</span>
      </div>
      <button onClick={onNovaDespesa} style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px 16px",
        background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 22,
        cursor: "pointer", textAlign: "left", boxShadow: "0 1px 3px rgba(0,0,0,.04)",
      }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: C.blueLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <PlusCircle size={19} color={C.blue} />
        </div>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>Nova Despesa</div>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 1 }}>Tira uma foto do recibo e submete</div>
        </div>
      </button>

      <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
        Despesas recentes
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.textLight, fontSize: 13 }}>A carregar…</div>
      ) : lista.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.textLight }}>
          <div style={{ width: 52, height: 52, borderRadius: 16, background: C.blueLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Receipt size={24} color={C.blue} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.textMid }}>Ainda não submeteste nenhuma despesa.</div>
          <div style={{ fontSize: 11.5, marginTop: 4 }}>Toca em "Nova Despesa" para registar a primeira.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lista.map(d => {
            const est = EST[d.estado] || EST.submetido;
            return (
              <div key={d.id} onClick={() => setDetalhe(d)}
                style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "13px 12px 13px 15px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, cursor: "pointer" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.descricao}</div>
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{fmtDate(d.data_despesa)}{d.nome_empresa ? ` · ${d.nome_empresa}` : ""}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(d.valor)}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: est.bg, color: est.cor, border: `1px solid ${est.bord}`, display: "inline-block", marginTop: 4 }}>{est.label}</span>
                </div>
                <ChevronRight size={16} color={C.textLight} style={{ flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}

      {detalhe && (
        <DetalheDespesaColab
          despesa={detalhe}
          onClose={() => setDetalhe(null)}
          onApagada={() => { setDetalhe(null); refresh(); }}
        />
      )}
    </div>
  );
}

function AppColaborador({ currentUser, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const { data: empresas } = useApi("/api/empresas");

  const TABS = [
    { id: "dashboard", label: "Dashboard",    Icon: LayoutDashboard },
    { id: "nova",      label: "Nova Despesa", Icon: PlusCircle },
  ];

  // color explícito: o body é escuro com texto quase-branco (index.css, para o
  // sidebar do admin) e só <main> repõe texto escuro — esta app não usa <main>,
  // por isso sem isto qualquer texto sem cor inline ficava branco sobre claro.
  return (
    <div style={{ minHeight: "100dvh", background: C.bg, color: C.text, display: "flex", flexDirection: "column" }}>
      {/* Topbar — paddingTop com safe-area: em modo standalone no iOS o
          conteúdo passa por baixo da status bar (black-translucent) */}
      <div style={{ background: "linear-gradient(135deg, #0A5C68, #03363D)", padding: "14px 20px", paddingTop: "calc(14px + env(safe-area-inset-top, 0px))", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Receipt size={17} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Olá, {(currentUser?.nome || currentUser?.email || "").split(" ")[0]}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Hub Financeiro</div>
          </div>
        </div>
        <button onClick={() => { if (window.confirm("Terminar sessão?")) onLogout(); }} title="Terminar sessão"
          style={{ width: 34, height: 34, borderRadius: 8, border: "none", background: "rgba(255,255,255,0.12)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <LogOut size={16} />
        </button>
      </div>

      {/* Conteúdo — as DUAS tabs ficam montadas (display none/block): trocar
          de tab não desmonta o formulário, por isso a foto/análise de uma
          despesa a meio nunca se perde ao espreitar o dashboard. */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ display: tab === "nova" ? "block" : "none", padding: "18px 20px" }}>
          {/* A empresa deixou de ser escolhida à mão — é detetada pelo NIF do
              adquirente lido no talão (QR fiscal ou IA), dentro do formulário. */}
          <PageDespesaRapida empresa="" empresas={empresas} currentUser={currentUser} bottomOffset={COLAB_TAB_BAR_HEIGHT} ativa={tab === "nova"} />
        </div>
        <div style={{ display: tab === "dashboard" ? "block" : "none" }}>
          <PageColaboradorDashboard onNovaDespesa={() => setTab("nova")} ativa={tab === "dashboard"} />
        </div>
      </div>

      {/* Navegação por abas em baixo. Altura = 64px de conteúdo + safe-area:
          o box-sizing global é border-box, por isso com height fixo o
          paddingBottom da safe-area COMIA a altura dos botões (menu cortado
          ao meio em telemóveis com home indicator / gesture bar). */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        height: `calc(${COLAB_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
        background: "#fff", borderTop: `1px solid ${C.border}`,
        boxShadow: "0 -4px 16px rgba(0,0,0,0.06)", display: "flex", zIndex: 100,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}>
        {TABS.map(t => {
          const Icon = t.Icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, border: "none", background: "none", cursor: "pointer", color: active ? C.blue : C.textLight, transition: "color 0.15s" }}>
              <span style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: active ? 28 : 0, height: 3, borderRadius: "0 0 3px 3px", background: C.blue, transition: "width 0.2s ease" }} />
              <Icon size={20} strokeWidth={active ? 2.4 : 2} />
              <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 500 }}>{t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Dashboard({ currentUser, onLogout }) {
  const [page, setPage]           = useUrlPage();
  const { empresa, empresas, setEmpresa } = useEmpresa();
  const [showExtrato, setShowExtrato] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => adminToolsVisiveis(currentUser));
  const { data: alertas }  = useApi("/api/alertas");
  const alertCount = (alertas || []).length;

  useEffect(() => {
    const onChanged = (e) => setShowAdmin(!!e.detail);
    window.addEventListener("admin-tools-changed", onChanged);
    return () => window.removeEventListener("admin-tools-changed", onChanged);
  }, []);

  useEffect(() => {
    if (!showAdmin && ADMIN_PAGES.has(page)) setPage("dashboard");
  }, [showAdmin, page, setPage]);

  const handleSelectEmpresa = (nif) => { setEmpresa(nif); setPage("dashboard"); };

  // Acesso restrito — experiência mobile própria, sem sidebar/páginas de admin.
  // (Fica depois dos hooks de cima, nunca antes — early return antes de hooks
  // parte as Rules of Hooks e falha o lint do `npm run build`.)
  if (currentUser?.role === "colaborador") {
    return <AppColaborador currentUser={currentUser} onLogout={onLogout} />;
  }

  const pages = {
    // A maioria das páginas já lê a empresa diretamente de useEmpresa() --
    // só recebem props aqui as que ainda precisam (onNav, user) ou que de
    // propósito ficam fora do contexto (Despesa Rápida, ver EcraEmpresaSelect
    // e a nota em PageDespesaRapida: é a mesma página que os colaboradores
    // usam, sem noção de "empresa selecionada").
    dashboard:       <PageDashboard  onNav={setPage} user={currentUser} />,
    conciliacao:     <ConciliacaoView />,
    batches:         <PageBatches />,
    arquivo:         <PageArquivo />,
    efatura:         <PageEfatura />,
    estatisticas:    <PageEstatisticas />,
    "despesa-rapida": <PageDespesaRapida empresa={empresa} empresas={empresas} currentUser={currentUser} />,
    fundos:          <PageFundos />,
    socios:          <PageSocios />,
    empresas:        <PageEmpresas   onSelectEmpresa={handleSelectEmpresa} />,
    manutencao:      <PageManutencao onSelectEmpresa={handleSelectEmpresa} />,
    agente:          <PageAgente />,
    utilizadores:    <PageUtilizadores currentUser={currentUser} />,
    definicoes:      <PageDefinicoes onNav={setPage} />,
    sistema:         <PageSistema />,
    "emails-pendentes": <PageEmailsPendentes />,
    "processamento-assistido": <PageProcessamentoAssistido />,
  };

  return (
    <Layout
      page={page}
      setPage={setPage}
      user={currentUser}
      onLogout={onLogout}
      alertCount={alertCount}
      showAdmin={showAdmin}
      searchSlot={<GlobalSearch onNav={setPage} />}
      notifSlot={<NotificationsBell alertas={alertas} onNav={setPage} />}
    >
      <div className="page-content-wrapper" style={{ padding: "32px 36px", paddingBottom: 110, minHeight: "100%", maxWidth: 1680, margin: "0 auto", background: "#F4F7F9" }}>
        <style>{`.page-content-wrapper { padding: 28px 32px; } @media (max-width: 768px) { .page-content-wrapper { padding: 14px 12px !important; } }`}</style>
        {pages[page]}
      </div>
      <FloatingAdd onOpenExtrato={() => setShowExtrato(true)} onNavEfatura={() => setPage("efatura")} />
      <AssistenteChat />
      {showExtrato && <ModalExtrato onClose={() => setShowExtrato(false)} onSuccess={() => {}} onNav={setPage} />}
      <PDFModal />
      <SheetsModal />
      <DocDetalheModal />
      <ModalTodosReembolsos />
    </Layout>
  );
}
